import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTierGate } from '@/hooks/useTierGate';
import TierLockedView from '@/components/TierLockedView';
import SEO from '../components/SEO';
import { apiService, getApiBaseUrl } from '../services/api.js';
import { eligibilityAsyncService } from '../services/eligibilityAsyncService';
import { getSessionEmail, normalizeEmail } from '@/lib/userSession';
import { recordExportFile } from '@/lib/exportFilesTracking';
import {
  buildPDFExportViewModel,
  formatPercent,
  getCanonicalGrantKey,
  getGrantDisplayName,
} from '@/lib/pdfExportViewModel';
import { forceUnlockDocumentScroll } from '../utils/bodyScrollLock';

// Debug flag - DISABLED by default for performance
const DEBUG = false; // import.meta.env.VITE_DEBUG_LOGS === 'true';
const normalizeAssessmentStatus = (status) => String(status || '').trim().toLowerCase();
const isCompletedAssessmentStatus = (status) =>
  ['done', 'completed', 'complete', 'success', 'succeeded'].includes(normalizeAssessmentStatus(status));
const isErrorAssessmentStatus = (status) =>
  ['error', 'failed', 'failure', 'cancelled', 'canceled'].includes(normalizeAssessmentStatus(status));

// PDF export composition. These were setter-less useState values; the export UI only exposes the
// report-mode choice, so they are fixed here.
const PDF_INCLUDE_LOWER_PRIORITY = true;
const PDF_INCLUDE_TECHNICAL_APPENDIX = false;
const PDF_INCLUDE_DETAILED_SCORING = false;
const PDF_INCLUDE_EVIDENCE_TRACE = false;
const PDF_INCLUDE_FUNDING_SNAPSHOTS = true;
const PDF_INCLUDE_DOCUMENT_CHECKLIST = true;
const CONTACT_EMAIL = 'contact@optra-labs.com';
const toDomId = (value) => String(value || '').replace(/[^a-zA-Z0-9_-]/g, '-');

// Feature flags
const FEATURE_THRESHOLD_SLIDER = true;
const FEATURE_CONFIDENCE_BREAKDOWN = true;
const FEATURE_USE_READINESS = false;
// Utility functions for defensive coding
const _safeNumber = (num, fallback = 0) => {
  return typeof num === 'number' && !isNaN(num) ? num : fallback;
};

const HISTORY_STORAGE_LIMIT = 20;

// Namespaced to this product (Optra SG) — bare `eligibility_history_*` keys
// are shared with a different product on the same localhost origin.
const getHistoryStorageKey = (email) => {
  const normalized = normalizeEmail(email);
  return normalized ? `optra_sg_eligibility_history_${normalized}` : 'optra_sg_eligibility_history';
};

const resolveGrantKey = (grant) => {
  if (!grant || typeof grant !== 'object') return null;
  return (
    grant.id ||
    grant.grantId ||
    grant.grant_id ||
    grant.code ||
    grant.acronym ||
    grant.name ||
    grant.displayName ||
    grant.key ||
    null
  );
};

const _normalizeOpportunityStatus = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const _closedOpportunityStatuses = new Set([
  'closed',
  'closed_current_cycle',
  'expired',
  'deadline_passed',
  'applications_closed',
  'closed_recurrence_unconfirmed',
  'recurrence_unconfirmed',
  'selection_in_progress',
  'paused',
  'suspended',
  'discontinued',
  'cancelled',
  'canceled',
  'unknown',
  'unverified',
  'unavailable',
]);

const _nonClientSurfacePriorities = new Set(['monitor', 'diagnostic_only', 'suppressed']);
const _nonClientEligibilityStates = new Set(['unavailable', 'not_eligible', 'ineligible']);

// Fit bands shared with the backend contract (signalMatchLabel / strategicFitLabel / PDF High-Medium-Low):
// Strong/High >= 72 · Promising/Medium 60–71 · Partial/Low 45–59 · Weak < 45.
const FIT_BAND_STRONG_MIN = 72;
const FIT_BAND_PROMISING_MIN = 60;
const FIT_BAND_PARTIAL_MIN = 45;
const FIT_BANDS_TEXT = 'Strong/High >=72 · Promising/Medium 60–71 · Partial/Low 45–59 · Weak <45';

const scoreBandLabel = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 'Not evidenced';
  if (n >= FIT_BAND_STRONG_MIN) return 'Strong';
  if (n >= FIT_BAND_PROMISING_MIN) return 'Promising';
  if (n >= FIT_BAND_PARTIAL_MIN) return 'Partial';
  return 'Weak';
};

// ---------------------------------------------------------------------------
// Prerequisite / reference-data helpers (new backend contract; every accessor
// tolerates older stored results where the fields are absent).
// ---------------------------------------------------------------------------
const NOT_YET_VERIFIED_TEXT = 'Not yet verified — check the official page';
const FUNDING_BOILERPLATE_RE = /support level depends on eligible cost category|funding details available upon application|depends on scheme approval/i;

const hasReferenceValue = (value) => {
  const text = String(value || '').trim();
  return Boolean(text) && text !== NOT_YET_VERIFIED_TEXT && text.toLowerCase() !== 'unverified';
};

const isBoilerplateReferenceText = (text) => {
  const s = String(text || '').trim();
  if (!s) return true;
  return FUNDING_BOILERPLATE_RE.test(s);
};

const getGrantReferenceData = (grant) => {
  const ref = grant?.referenceData || grant?.reference_data;
  return ref && typeof ref === 'object' ? ref : {};
};

const getGrantPrerequisiteChecks = (grant) => {
  const raw = grant?.prerequisiteChecks ?? grant?.prerequisite_checks;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      code: String(item.code || item.id || '').trim(),
      label: String(item.label || item.title || item.code || '').trim(),
      status: (() => {
        const st = String(item.status || '').toLowerCase().trim();
        if (st === 'met' || st === 'pass' || st === 'passed') return 'met';
        if (st === 'unmet' || st === 'fail' || st === 'failed' || st === 'not_met') return 'unmet';
        return 'unknown';
      })(),
      blocking: Boolean(item.blocking),
      detail: String(item.detail || item.evidence || '').trim(),
      howToResolve: String(item.howToResolve || item.how_to_resolve || '').trim(),
      sourceField: String(item.sourceField || item.source_field || '').trim(),
    }))
    .filter((item) => item.label);
};

const isBlockedGrant = (grant) => {
  if (!grant || typeof grant !== 'object') return false;
  if (grant.blocked === true) return true;
  const after = String(grant.eligibilityAfterPrerequisites || grant.eligibility_after_prerequisites || '').toLowerCase();
  if (after === 'blocked') return true;
  return getGrantPrerequisiteChecks(grant).some((check) => check.blocking && check.status === 'unmet');
};

// Schemes a client cannot apply to right now: invitation-only programmes and
// call-based programmes with no open call. They must never be shown as
// "Eligible" just because their score band is high.
const getGrantNotOpenReason = (grant) => {
  if (!grant || typeof grant !== 'object') return '';
  const timing = getGrantReferenceData(grant).timing || {};
  const windowRaw = String(timing.window || '').toLowerCase();
  if (/^(invitation_only|invite_only)$/.test(windowRaw)) {
    return 'Invitation only — not open for application.';
  }
  const applicationsOpen = timing.applicationsOpen ?? timing.applications_open;
  if (windowRaw === 'call_based' && applicationsOpen === false) {
    return 'Call-based programme with no open call at the moment.';
  }
  // An "unknown" window with applicationsOpen=false only means the window was
  // not re-verified — that is not a closure, so it stays in its score tier.
  if (windowRaw === 'closed') {
    return 'Application window is currently closed.';
  }
  if (grant.applicable === false) {
    return String(getGrantReferenceData(grant).applicabilityNote || 'Not currently open for application.').trim();
  }
  return '';
};

const isNotOpenGrant = (grant) => Boolean(getGrantNotOpenReason(grant));

const isLegacyForNewApplicationsGrant = (grant) => {
  const status = String(
    grant?.schemeStatus ||
    grant?.scheme_status ||
    grant?.current_status ||
    grant?.currentStatus ||
    ''
  ).toLowerCase();
  return status === 'legacy' || status === 'legacy_for_new_applications' || status === 'legacy-for-new-applications';
};

const getClientVerdictForGrant = ({ grantStatus, isBlockedCard, isLegacyForNewApplications, whenApplyRaw }) => {
  if (isLegacyForNewApplications) {
    return {
      label: 'Use EDGE for new projects',
      detail: 'This route is mainly relevant for existing approvals, submissions, or claims. New projects should be assessed under EDGE first.',
    };
  }
  if (isBlockedCard || grantStatus === 'blocked') {
    return {
      label: 'Resolve a prerequisite first',
      detail: 'Do not treat this route as application-ready until the blocking condition is fixed.',
    };
  }
  if (grantStatus === 'ineligible') {
    return {
      label: 'Do not pursue now',
      detail: 'The submitted facts do not currently meet the minimum route requirements.',
    };
  }
  if (whenApplyRaw === 'apply_now' || grantStatus === 'eligible') {
    return {
      label: 'Pursue now',
      detail: 'This is a stronger route to progress, subject to final evidence checks and official scheme terms.',
    };
  }
  if (whenApplyRaw === 'not_recommended' || grantStatus === 'weakFit') {
    return {
      label: 'Low priority',
      detail: 'Spend effort here only after stronger routes have been reviewed or the project scope changes.',
    };
  }
  return {
    label: 'Validate first',
    detail: 'The route may be relevant, but the evidence or fit should be tightened before application work starts.',
  };
};

const getGrantBlockReason = (grant) => {
  const direct = String(grant?.blockReason || grant?.block_reason || '').trim();
  if (direct) return direct;
  const firstBlocking = getGrantPrerequisiteChecks(grant).find((check) => check.blocking && check.status === 'unmet');
  if (firstBlocking) return firstBlocking.detail || firstBlocking.label;
  return getGrantNotOpenReason(grant);
};

const getGrantEligibilityAfterPrerequisites = (grant) => {
  const raw = String(grant?.eligibilityAfterPrerequisites || grant?.eligibility_after_prerequisites || '').toLowerCase();
  if (raw === 'eligible_now' || raw === 'blocked' || raw === 'conditional' || raw === 'unknown') return raw;
  if (isBlockedGrant(grant)) return 'blocked';
  const checks = getGrantPrerequisiteChecks(grant);
  if (!checks.length) return '';
  if (checks.some((c) => c.status === 'unknown')) return 'conditional';
  if (checks.every((c) => c.status === 'met')) return 'eligible_now';
  return 'conditional';
};

const getGrantOfficialUrl = (grant) => {
  if (!grant || typeof grant !== 'object') return '';
  const candidates = [
    grant.officialUrl,
    grant.official_url,
    getGrantReferenceData(grant).officialUrl,
    getGrantReferenceData(grant).official_url,
    Array.isArray(grant.citations) ? grant.citations[0]?.url || grant.citations[0]?.link : null,
    Array.isArray(getGrantReferenceData(grant).sources) ? getGrantReferenceData(grant).sources[0]?.url : null,
    grant.official_link,
    grant.link,
  ];
  for (const candidate of candidates) {
    const url = String(candidate || '').trim();
    if (url && url !== '#' && /^https?:\/\//i.test(url)) return url;
  }
  return '';
};

const getGrantTransitionNote = (grant) =>
  String(grant?.transitionNote || grant?.transition_note || '').trim();

const getSignalMatchPct = (grant) => {
  const raw = grant?.signalMatchPct ?? grant?.signal_match_pct;
  const n = Number(raw);
  if (Number.isFinite(n)) return Math.round(Math.min(100, Math.max(0, n)));
  return null;
};

const formatSgdCap = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `S$${Math.round(n).toLocaleString('en-SG')}`;
};

const formatIsoDate = (value) => {
  const s = String(value || '').trim();
  if (!s) return '';
  // Plain ISO dates (YYYY-MM-DD) are calendar dates, not instants: build them from
  // parts so a negative-offset timezone does not shift them back a day.
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const d = plain
    ? new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]))
    : new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** snake_case / camelCase key → "Sentence case" label (always capitalised, unlike humanizeInputValue). */
const labelFromKey = (key) => {
  const words = String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const formatTimingWindow = (window) => {
  const w = String(window || '').toLowerCase().trim();
  if (w === 'rolling') return 'Rolling — applications accepted year-round';
  if (w === 'call_based') return 'Call-based — no open call at the moment';
  if (w === 'invitation_only' || w === 'invite_only') return 'Invitation only — not open for application';
  if (w === 'closed') return 'Closed — the current window has ended';
  return '';
};

/**
 * Structured funding / timing / portal rows from referenceData. Never emits the
 * generic "Support level depends on eligible cost category…" boilerplate: when a
 * value is missing or unverified-and-boilerplate we say so instead.
 */
const buildReferenceRows = (grant) => {
  const ref = getGrantReferenceData(grant);
  const funding = ref.funding && typeof ref.funding === 'object' ? ref.funding : {};
  const timing = ref.timing && typeof ref.timing === 'object' ? ref.timing : {};
  const fundingTextRaw = typeof ref.funding === 'string' ? ref.funding : funding.text;
  const timingTextRaw = typeof ref.timing === 'string' ? ref.timing : timing.text;

  const supportPct = Number(funding.supportPct ?? funding.support_pct);
  const supportLevel = Number.isFinite(supportPct) && supportPct > 0
    ? `Up to ${Math.round(supportPct)}% of qualifying costs`
    : !isBoilerplateReferenceText(fundingTextRaw)
      ? String(fundingTextRaw).trim()
      : NOT_YET_VERIFIED_TEXT;
  const capText = formatSgdCap(funding.capSgd ?? funding.cap_sgd);
  const fundingCap = capText || NOT_YET_VERIFIED_TEXT;

  const windowText = formatTimingWindow(timing.window);
  const deadline = formatIsoDate(timing.deadline);
  const applicationWindow = windowText
    ? deadline ? `${windowText} (deadline ${deadline})` : windowText
    : !isBoilerplateReferenceText(timingTextRaw)
      ? String(timingTextRaw).trim()
      : deadline ? `Deadline ${deadline}` : NOT_YET_VERIFIED_TEXT;
  const processingTime = String(timing.processingTime || timing.processing_time || '').trim() || NOT_YET_VERIFIED_TEXT;
  const portal = String(ref.portal || '').trim();
  const lastVerifiedRaw = ref.lastVerified || ref.last_verified;
  const lastVerified = formatIsoDate(lastVerifiedRaw) || '';
  const isInvitationOnly = /^(invitation_only|invite_only)$/i.test(String(timing.window || ''));
  const hasAnyReferenceData = Boolean(
    (Number.isFinite(supportPct) && supportPct > 0) ||
    capText ||
    windowText ||
    deadline ||
    (timing.processingTime || timing.processing_time) ||
    portal ||
            lastVerified ||
    (!isBoilerplateReferenceText(fundingTextRaw)) ||
    (!isBoilerplateReferenceText(timingTextRaw))
  );

  return {
    supportLevel,
    fundingCap,
    applicationWindow,
    processingTime,
    portal,
    lastVerified,
    isInvitationOnly,
    hasAnyReferenceData,
    fundingVerified: funding.verified === true,
    timingVerified: timing.verified === true,
  };
};

// Labels for the "Your submission" echo (results.inputsUsed). Order matters: it is the display order.
const SUBMISSION_FIELD_LABELS = [
  ['project_type', 'Project Type'],
  ['project_maturity', 'Project Maturity'],
  ['project_description', 'Project Description'],
  ['funding_goal', 'Funding Goal'],
  ['primary_use_of_funds', 'Primary Use of Funds'],
  ['deployment_environment', 'Deployment Environment'],
  ['vendor_selected', 'Vendor Selected'],
  ['quotation_count', 'Quotation Count'],
  ['target_market', 'Target Market'],
  ['market_scope', 'Market Scope'],
  ['industry', 'Industry'],
  ['employees', 'Employees'],
  ['annual_revenue_sgd', 'Annual Revenue (SGD)'],
  ['project_budget_sgd', 'Project Budget (SGD)'],
  ['local_ownership_pct', 'Local Ownership (%)'],
  ['incorporation_year', 'Incorporation Year'],
];

const SUBMISSION_FIELD_ALIASES = {
  employees: ['employees', 'employee_count'],
  annual_revenue_sgd: ['annual_revenue_sgd', 'annual_revenue', 'annualRevenue'],
  project_budget_sgd: ['project_budget_sgd', 'project_budget', 'projectBudget'],
  local_ownership_pct: ['local_ownership_pct', 'local_ownership_percentage'],
  project_description: ['project_description', 'project_scope', 'projectScope'],
};

const parseInputNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const normalized = String(value).replace(/[^\d.-]/g, '').trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstInputNumber = (...values) => {
  let zeroValue = null;
  for (const value of values) {
    const parsed = parseInputNumber(value);
    if (parsed === null) continue;
    if (parsed !== 0) return parsed;
    zeroValue = 0;
  }
  return zeroValue;
};

const humanizeInputValue = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map((v) => humanizeInputValue(v)).filter(Boolean).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString('en-SG') : '';
  const s = String(value).trim();
  if (!s) return '';
  // snake_case enum → Sentence case (e.g. product_build → Product build); leave free text untouched.
  if (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(s)) {
    const words = s.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return s;
};

const buildSubmissionRows = (inputsUsed) => {
  if (!inputsUsed || typeof inputsUsed !== 'object') return [];
  const rows = [];
  for (const [key, label] of SUBMISSION_FIELD_LABELS) {
    const aliases = SUBMISSION_FIELD_ALIASES[key] || [key];
    let value;
    for (const alias of aliases) {
      const candidate = inputsUsed[alias];
      if (candidate !== undefined && candidate !== null && String(candidate).trim() !== '' && candidate !== 0) {
        value = candidate;
        break;
      }
    }
    if (value === undefined) continue;
    let text = humanizeInputValue(value);
    if (!text) continue;
    if (/(^|_)year($|_)/i.test(key) && Number.isFinite(Number(value))) {
      text = String(Math.trunc(Number(value)));
    }
    if (key === 'annual_revenue_sgd' || key === 'project_budget_sgd') {
      const numeric = parseInputNumber(value);
      if (numeric !== null) text = `S$${numeric.toLocaleString('en-SG')}`;
    }
    if (key === 'local_ownership_pct' && Number.isFinite(Number(value))) text = `${Number(value)}%`;
    rows.push({ key, label, value: text });
  }
  return rows;
};

const findGrantByKey = (grants, key) => {
  const needle = String(key || '').trim().toLowerCase();
  if (!needle) return null;
  return (grants || []).find((g) => {
    const candidates = [g?.code, g?.grantId, g?.grant_id, g?.id, g?.acronym, g?.key, g?.name, g?.grantFullName];
    return candidates.some((c) => String(c || '').trim().toLowerCase() === needle);
  }) || null;
};

const grantDisplayNameForKey = (grants, key) => {
  const match = findGrantByKey(grants, key);
  if (!match) return String(key || '').trim();
  return String(match.grantFullName || match.grant_full_name || match.name || match.displayName || key).trim();
};

const normalizeProgrammeTransitions = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      affectedKeys: Array.isArray(item.affectedKeys || item.affected_keys) ? (item.affectedKeys || item.affected_keys).map((k) => String(k)) : [],
      successor: String(item.successor || item.name || 'EDGE').trim() || 'EDGE',
      timing: String(item.timing || '').trim(),
      whatItMeansNow: String(item.whatItMeansNow || item.what_it_means_now || item.note || '').trim(),
      sourceUrl: String(item.sourceUrl || item.source_url || '').trim(),
      verified: item.verified === true,
    }))
    .filter((item) => item.whatItMeansNow || item.affectedKeys.length > 0);
};

const normalizeEligibilitySummary = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const toKeyList = (list) => (Array.isArray(list) ? list : []).map((item) => (typeof item === 'string' ? { key: item, reason: '' } : { key: String(item?.key || item?.code || ''), reason: String(item?.reason || '').trim() })).filter((item) => item.key);
  return {
    eligibleNow: toKeyList(raw.eligibleNow || raw.eligible_now),
    conditional: toKeyList(raw.conditional),
    blocked: toKeyList(raw.blocked),
    excluded: toKeyList(raw.excluded),
  };
};

const normalizeMethodology = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const signal = raw.signalMatch || raw.signal_match || {};
  const strategic = raw.strategicFit || raw.strategic_fit || {};
  const approval = raw.approvalLikelihood || raw.approval_likelihood || {};
  const bandsFrom = (source) => (Array.isArray(source?.bands) ? source.bands : []).filter((b) => b && b.label);
  const weightsFrom = (source) => {
    const w = source?.weights;
    if (!w || typeof w !== 'object') return [];
    // Already normalised (idempotent when a processed result is re-processed).
    if (Array.isArray(w)) {
      return w
        .filter((item) => item && typeof item === 'object' && Number.isFinite(Number(item.pct)))
        .map((item) => ({ key: String(item.key || item.label || ''), label: String(item.label || labelFromKey(item.key || '')), pct: Math.round(Number(item.pct)) }));
    }
    return Object.entries(w)
      .filter(([, v]) => Number.isFinite(Number(v)))
      .map(([k, v]) => {
        const n = Number(v);
        const pct = n <= 1 ? Math.round(n * 100) : Math.round(n);
        return { key: k, label: labelFromKey(k), pct };
      });
  };
  const result = {
    signalMatch: { description: String(signal.description || '').trim(), weights: weightsFrom(signal), bands: bandsFrom(signal) },
    strategicFit: { description: String(strategic.description || '').trim(), source: String(strategic.source || '').trim(), bands: bandsFrom(strategic) },
    approvalLikelihood: { description: String(approval.description || '').trim() },
  };
  if (!result.signalMatch.description && !result.strategicFit.description && !result.approvalLikelihood.description) return null;
  return result;
};

const normalizeGlossary = (raw) => {
  if (Array.isArray(raw)) {
    return raw
      .filter((item) => item && typeof item === 'object' && item.term && item.definition)
      .map((item) => ({ term: String(item.term).trim(), definition: String(item.definition).trim() }));
  }
  if (raw && typeof raw === 'object') {
    return Object.entries(raw)
      .filter(([, v]) => typeof v === 'string' && v.trim())
      .map(([k, v]) => ({ term: labelFromKey(k), definition: v.trim() }));
  }
  return [];
};

/** Result-level fields from the rebuilt backend contract (all optional; older results simply omit them). */
const extractResultLevelContractFields = (source) => {
  const src = source && typeof source === 'object' ? source : {};
  const toKeys = (list) => (Array.isArray(list) ? list.map((k) => (typeof k === 'string' ? k : String(k?.key || k?.code || ''))).filter(Boolean) : []);
  return {
    methodology: normalizeMethodology(src.methodology),
    glossary: normalizeGlossary(src.glossary),
    eligibilitySummary: normalizeEligibilitySummary(src.eligibilitySummary || src.eligibility_summary),
    programmeTransitions: normalizeProgrammeTransitions(src.programmeTransitions || src.programme_transitions),
    recommended: toKeys(src.recommended || src.recommended_keys),
    backups: toKeys(src.backups || src.backup_keys),
    suppressedDiagnostics: Array.isArray(src.suppressedDiagnostics || src.suppressed_diagnostics)
      ? (src.suppressedDiagnostics || src.suppressed_diagnostics)
      : [],
  };
};

const certaintyBandLabel = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 'Insufficient certainty';
  if (n >= 80) return 'High certainty';
  if (n >= 65) return 'Good certainty';
  if (n >= 45) return 'Moderate certainty';
  return 'Low certainty';
};

// eslint-disable-next-line react-refresh/only-export-components
export const isClientSurfaceableOpportunity = (opportunity) => {
  if (!opportunity || typeof opportunity !== 'object') return false;
  if (opportunity.isGovernmentGrant) return false;
  if (opportunity.allowed_to_surface === false || opportunity.allowedToSurface === false) return false;
  if (opportunity.suppression_reason || opportunity.suppressionReason) return false;

  const canonical = _normalizeOpportunityStatus(
    opportunity.canonical_availability_status || opportunity.canonicalAvailabilityStatus
  );
  if (canonical && canonical !== 'available') return false;

  const statusCandidates = [
    opportunity.availability_status,
    opportunity.availabilityStatus,
    opportunity.current_status,
    opportunity.currentStatus,
    opportunity.status,
  ].map(_normalizeOpportunityStatus).filter(Boolean);
  const timelineStatus = _normalizeOpportunityStatus(opportunity.timelineEstimate);
  if (timelineStatus && _closedOpportunityStatuses.has(timelineStatus)) {
    statusCandidates.push(timelineStatus);
  }
  if (statusCandidates.some((status) => _closedOpportunityStatuses.has(status))) return false;

  const priority = _normalizeOpportunityStatus(opportunity.surface_priority || opportunity.surfacePriority);
  if (_nonClientSurfacePriorities.has(priority)) return false;

  const eligibility = _normalizeOpportunityStatus(opportunity.eligibility_status || opportunity.eligibilityStatus);
  if (_nonClientEligibilityStates.has(eligibility)) return false;

  const deadlineText = opportunity.deadline || opportunity.current_deadline || opportunity.currentDeadline;
  if (deadlineText) {
    const parsed = Date.parse(deadlineText);
    if (!Number.isNaN(parsed) && parsed < Date.now()) return false;
  }

  return true;
};

const parseHistoryTimestamp = (value) => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const mergeHistoryEntries = (...lists) => {
  const map = new Map();

  lists.flat().forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    if (!entry.id) return;

    const existing = map.get(entry.id);
    if (!existing) {
      map.set(entry.id, { ...entry });
      return;
    }

    const existingTs = parseHistoryTimestamp(existing.timestamp);
    const incomingTs = parseHistoryTimestamp(entry.timestamp);

    if (incomingTs >= existingTs) {
      const merged = { ...existing, ...entry };
      if (!merged.ownerEmail && entry.ownerEmail) {
        merged.ownerEmail = entry.ownerEmail;
      }
      map.set(entry.id, merged);
    } else {
      const merged = { ...entry, ...existing };
      if (!merged.ownerEmail && existing.ownerEmail) {
        merged.ownerEmail = existing.ownerEmail;
      }
      map.set(entry.id, merged);
    }
  });

  return Array.from(map.values()).sort((a, b) => parseHistoryTimestamp(b.timestamp) - parseHistoryTimestamp(a.timestamp));
};

/** Legacy LLM/boilerplate filler; keep in sync with backend _GENERIC_ADVISORY_FILLER. */
const ADVISORY_FILLER_FRAGMENTS = [
  'Eligibility and project scope were considered based on your inputs.',
  'Eligibility and project scope were considered based on your inputs',
  'Eligibility and project narrative were considered based on your inputs.',
  'Eligibility and project narrative were considered based on your inputs',
];

const stripAdvisoryFiller = (text) => {
  if (text == null || typeof text !== 'string') return '';
  let out = text;
  for (const frag of ADVISORY_FILLER_FRAGMENTS) {
    const escaped = frag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(new RegExp(escaped, 'gi'), ' ');
  }
  return out.replace(/\s+/g, ' ').trim();
};

const resolveProjectScopeText = (results, formData) => {
  const raw =
    results?.inputsUsed?.project_scope ||
    results?.inputsUsed?.projectScope ||
    results?.inputsUsed?.project_description ||
    results?.inputsUsed?.projectDescription ||
    formData?.project_scope ||
    formData?.projectScope ||
    formData?.project_description ||
    formData?.projectDescription ||
    '';
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
  }
  return String(raw || '').trim();
};

const isInternalCalibrationFactorName = (factor) => {
  const low = String(factor || '').toLowerCase();
  return /modifier|calibration|rounding|model adjustment|reconcil/i.test(low);
};

const displayBreakdownFactorName = (factor) => {
  const n = String(factor || '');
  if (isInternalCalibrationFactorName(n)) return '';
  return n;
};

const displayBreakdownEvidence = (evidence) => {
  const t = stripAdvisoryFiller(String(evidence || '').trim());
  if (!t) return '';
  if (/^calibration$/i.test(t) || /weighted factor|internal cap|model adjustment/i.test(t)) return '';
  return t;
};

/** Insight lines: ban generic EL boilerplate and "Address:" churn */
const INSIGHT_BAN_REGEX =
  /review\s+ineligible\s+reasons|align\s+project\s+scope|confirm\s+detailed\s+eligibility\s+(criteria\s+)?with\s+enterprise\s*sg/i;

const normalizeInsightKey = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[—–]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

const stripFactorColonPrefix = (text) => {
  const t = stripAdvisoryFiller(String(text || '').replace(/\s+/g, ' ').trim());
  if (!t) return '';
  return t
    .replace(
      /^(objective\s+fit|company\s+eligibility|project\s+alignment|budget\s+feasibility|documentation\s+readiness|factor\s+\d+)\s*:\s*/i,
      ''
    )
    .trim();
};

/**
 * Remove LLM-style mini-headings inside a grant-card line so copy reads as one description
 * (e.g. "Thematic fit for PSG – Reposition…" → "Reposition…"; "Scale: …" only when safe).
 */
const stripGrantCardLeadInTitle = (text) => {
  const raw = String(text || '').replace(/\s+/g, ' ').trim();
  if (!raw) return '';

  const attempt = (s) => {
    let t = s;
    t = t.replace(/^\*\*[^*]{2,120}\*\*\s*[–—-]\s+/u, '');
    t = t.replace(/^\*[^*]{2,120}\*\s*[–—-]\s+/u, '');
    const headOk = (head, tail) => {
      const h = head.trim();
      const tailTrim = tail.trim();
      if (tailTrim.length < 15) return false;
      if (/^\d+%?\s+/.test(h)) return false;
      const hw = h.split(/\s+/).filter(Boolean).length;
      if (hw < 2) return false;
      if (hw < 3 && h.length < 16) return false;
      if (h.includes('.') && !/^[A-Z]\.\s*$/.test(h)) return false;
      return true;
    };
    const mDash = /^(.{3,120})\s+[–—]\s+(.+)$/u.exec(t);
    if (mDash && headOk(mDash[1], mDash[2])) return mDash[2].trim();
    const mColon = /^([^.:]{3,120}):\s+(.+)$/u.exec(t);
    if (mColon && headOk(mColon[1], mColon[2])) return mColon[2].trim();
    const mHyph = /^(.{3,120})\s+-\s+(.+)$/.exec(t);
    if (mHyph && headOk(mHyph[1], mHyph[2])) return mHyph[2].trim();
    return t;
  };

  const stripped = attempt(raw);
  if (stripped.length >= 24 || stripped.split(/\s+/).length >= 6) {
    return stripped;
  }
  return raw;
};

/**
 * Remove mid-bullet consultant labels (remediation, bold strength/fit tags) so copy reads as one paragraph.
 * Only matches label + punctuation (not bare words mid-sentence) except plain "remediation:".
 */
const mergeGrantCardInlineLabelsIntoProse = (text) => {
  let s = String(text || '').replace(/\s+/g, ' ').trim();
  if (!s) return '';

  const stripBoldLabel = (label) => {
    const esc = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`\\*\\*\\s*${esc}\\s*\\*\\*\\s*[:–—]\\s*`, 'gi');
    s = s.replace(re, ' ');
    const re1 = new RegExp(`\\*\\s*${esc}\\s*\\*\\s*[:–—]\\s*`, 'gi');
    s = s.replace(re1, ' ');
  };

  stripBoldLabel('remediation');
  stripBoldLabel('key strength');
  stripBoldLabel('key strengths');
  stripBoldLabel('strength');
  stripBoldLabel('strengths');
  stripBoldLabel('why this fits');
  stripBoldLabel('why this fit');
  stripBoldLabel('positive signal');
  stripBoldLabel('fit signal');

  s = s.replace(/\bremediation\s*[:–—]\s*/gi, ' ');

  s = s.replace(/\s{2,}/g, ' ').trim();
  s = s.replace(/,\s*\./g, '.');
  return s.trim();
};

const WHERE_IMPROVE_PLACEHOLDER_REGEX =
  /the assessment used your submitted profile fields|see factor scores|programme notes for specifics|officers usually look for exactly this kind of concrete, checkable fact tied to your annexes/i;

const SCOPE_MISSING_BOILERPLATE_RES = [
  /project\s+scope\s*\/\s*description\s+was\s+not\s+provided/i,
  /scope\s*\/\s*description\s+was\s+not\s+provided/i,
  /measurable\s+outcomes\s+[^.!?]{0,120}grant[- ]intent/i,
  /grant[- ]intent\s+mapping/i,
];

const isScopeMissingBoilerplateFragment = (text) => {
  const raw = String(text || '').trim();
  if (!raw) return false;
  const low = raw.toLowerCase();
  if (SCOPE_MISSING_BOILERPLATE_RES.some((re) => re.test(raw))) return true;
  if (low.includes('less certain')) {
    if (
      low.includes('not provided') ||
      low.includes('grant-intent') ||
      low.includes('grant intent') ||
      low.includes('measurable outcomes') ||
      low.includes('scope/description') ||
      /\bmapping\b/i.test(low)
    ) {
      return true;
    }
  }
  return false;
};

/** Remove sentences that claim missing scope/description or grant-intent uncertainty (never show on results UI). */
const stripScopeMissingBoilerplateSentences = (text) => {
  const raw = String(text || '').trim();
  if (!raw) return '';
  const parts = raw.split(/(?<=[.!?])\s+/).map((p) => p.trim()).filter(Boolean);
  const kept = parts.filter((p) => !isBannedInsightLine(p) && !isScopeMissingBoilerplateFragment(p));
  return kept.join(' ').replace(/\s+/g, ' ').trim();
};

const isBannedInsightLine = (text) => {
  const t = String(text || '').trim();
  if (!t) return true;
  if (INSIGHT_BAN_REGEX.test(t)) return true;
  if (WHERE_IMPROVE_PLACEHOLDER_REGEX.test(t)) return true;
  if (/^address:\s*/i.test(t)) return true;
  const low = t.toLowerCase();
  if (
    low.includes('project scope/description was not provided') ||
    low.includes('scope/description was not provided') ||
    low.includes('grant-intent mapping') ||
    low.includes('grant intent mapping') ||
    low.includes('measurable outcomes and grant-intent') ||
    low.includes('measurable outcomes and grant intent') ||
    low.includes('no project description, expected outcomes, or objectives were captured') ||
    low.includes('expected outcomes, or objectives were captured in your inputs')
  ) {
    return true;
  }
  if (isScopeMissingBoilerplateFragment(t)) return true;
  return false;
};

const improvementOverlapsStrength = (improvementRaw, positiveReasons) => {
  const imp = normalizeInsightKey(
    mergeGrantCardInlineLabelsIntoProse(stripGrantCardLeadInTitle(stripFactorColonPrefix(improvementRaw)))
  );
  if (imp.length < 18) return false;
  for (const pos of positiveReasons) {
    const pk = normalizeInsightKey(pos);
    if (pk.length < 22) continue;
    if (imp.includes(pk) || pk.includes(imp)) return true;
  }
  return false;
};

const stripConsultantChurnTail = (text) => {
  let s = String(text || '').trim();
  s = s.replace(/\s*Strengthen evidence or adjust scope here before applying\.?$/i, '');
  s = s.replace(/\s*Strengthen evidence or adjust scope before applying\.?$/i, '');
  return s.replace(/\s+/g, ' ').replace(/\.\s*\.$/, '.').trim();
};

const filterConsultantInsightList = (items, positiveReasons, cleanLine) => {
  const seen = new Set();
  const out = [];
  for (const raw of items) {
    let line = cleanLine(
      mergeGrantCardInlineLabelsIntoProse(stripGrantCardLeadInTitle(stripFactorColonPrefix(raw)))
    );
    line = stripConsultantChurnTail(line);
    if (!line || isBannedInsightLine(line)) continue;
    if (improvementOverlapsStrength(line, positiveReasons)) continue;
    const k = normalizeInsightKey(line);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(line);
  }
  return out;
};

/** Normalize a single why-match bullet — no template tail; strip legacy officer boilerplate if present. */
const enrichWhyMatchConsultantLine = (line) => {
  let compact = mergeGrantCardInlineLabelsIntoProse(stripGrantCardLeadInTitle(line))
    .replace(/\s+/g, ' ')
    .trim();
  if (!compact) return '';
  const boilerplate =
    /\.?\s*In a .+ application, officers usually look for exactly this kind of concrete, checkable fact[\s\S]*$/i;
  compact = compact.replace(boilerplate, '').trim();
  if (!compact) return '';
  return compact.endsWith('.') ? compact : `${compact}.`;
};

/** Strip markdown fences that confuse xhtml2pdf CSS parsing (LLM-injected styles, content: rules). */
function sanitizeMarkdownForPdfExport(md) {
  let s = String(md || '')
    .replace(/\u2013/g, '-')
    .replace(/\u2014/g, '-');
  const fenceRe = new RegExp('^```\\s*([a-z0-9_+-]*)\\s*\\n[\\s\\S]*?^```\\s*$', 'gim');
  return s.replace(fenceRe, (block, lang) => {
    const L = String(lang || '').toLowerCase();
    if (['css', 'scss', 'sass', 'html', 'xml'].includes(L)) return '\n\n';
    if (/\bcontent\s*:/i.test(block)) return '\n\n';
    return block;
  });
}

// Helper functions to extract rich data from backend analysis
const getEligibilityBullets = (grant) => {
  try {
    // First try to get from rules_fired (passed rules)
    if (grant.rules_fired && Array.isArray(grant.rules_fired)) {
      const passedRules = grant.rules_fired.filter(rule => rule.outcome === 'pass');
      if (passedRules.length > 0) {
        return passedRules.map(rule => rule.reason || rule.evidence || `Rule ${rule.id} passed`);
      }
    }
    
    // If no passed rules, get from missing information
    if (grant.missing_information && Array.isArray(grant.missing_information)) {
      return grant.missing_information.map(info => info.field || info);
    }
    
    // Fallback to basic eligibility info
    return ['Eligibility criteria available upon application'];
  } catch (error) {
    console.error('Error in getEligibilityBullets:', error);
    return ['Error loading eligibility criteria'];
  }
};

const getDocsChecklist = (grant) => {
  try {
    // First try tailored recommendations
    if (grant.tailored_recommendations && grant.tailored_recommendations.documents_to_prepare && Array.isArray(grant.tailored_recommendations.documents_to_prepare)) {
      return grant.tailored_recommendations.documents_to_prepare;
    }
    
    // Fallback to missing information
    if (grant.missing_information && Array.isArray(grant.missing_information)) {
      return grant.missing_information.map(info => info.field || info);
    }
    
    // Default documents list
    return ['Company registration documents', 'Financial statements', 'Project proposal'];
  } catch (error) {
    console.error('Error in getDocsChecklist:', error);
    return ['Error loading document requirements'];
  }
};

const getApplySteps = (grant) => {
  try {
    // First try tailored recommendations
    if (grant.tailored_recommendations && grant.tailored_recommendations.next_steps && Array.isArray(grant.tailored_recommendations.next_steps)) {
      return grant.tailored_recommendations.next_steps;
    }
    
    // Fallback to official references
    if (grant.official_references && Array.isArray(grant.official_references)) {
      return [
        `Visit official page: ${grant.official_references[0].label}`,
        'Review application requirements',
        'Prepare required documents',
        'Submit application'
      ];
    }
    
    // Default application steps
    return [
      'Review grant requirements',
      'Prepare required documents',
      'Submit application online',
      'Wait for approval'
    ];
  } catch (error) {
    console.error('Error in getApplySteps:', error);
    return ['Error loading application steps'];
  }
};

// Two-sentence formatter for "What this means for you" (28-55 words total)
const formatTailoredText = (grant, companyProfile) => {
  if (!grant || typeof grant !== 'object') {
    return {
      quickVerdict: 'Unable to generate tailored analysis',
      whyMatch: grant?.why_match || [],
      remediationActions: [],
      documentsNeeded: [],
      budgetSnapshot: { estimatedCoFunding: 'N/A', effortLevel: 'Unknown', timelineWeeks: 'N/A', roiPotential: 'Unknown' },
      risks: [],
      nextSteps: []
    };
  }
  
  // Get eligibility strength based on score or success probability
  // Use success probability if available (more accurate), otherwise fall back to score
  const getEligibilityStrength = (grant) => {
    // Prefer success probability or certainty if available
    const successProb = grant.successProbability || grant.success_probability;
    const certainty = grant.certainty;
    
    // If we have success probability (0-100), use that
    if (typeof successProb === 'number' && !isNaN(successProb)) {
      if (successProb >= 70) return 'strong';
      if (successProb >= 50) return 'good';
      if (successProb >= 30) return 'possible';
      return 'low';
    }
    
    // If we have certainty (0-1), convert to percentage
    if (typeof certainty === 'number' && !isNaN(certainty)) {
      const certaintyPct = certainty > 1 ? certainty : certainty * 100;
      if (certaintyPct >= 70) return 'strong';
      if (certaintyPct >= 50) return 'good';
      if (certaintyPct >= 30) return 'possible';
      return 'low';
    }
    
    // Fall back to score
    const score = typeof grant.score === 'number' && !isNaN(grant.score) ? grant.score : 0;
    if (score >= 70) return 'strong';
    if (score >= 50) return 'good';
    if (score >= 30) return 'possible';
    return 'low';
  };

  // Safely get data with fallbacks
  const companyName = companyProfile?.company_name || companyProfile?.companyName || 'Your company';
  const sector = companyProfile?.industry || '';
  const employees = parseInt(companyProfile?.employee_count || companyProfile?.employees) || 0;
  const revenue = firstInputNumber(
    companyProfile?.annual_revenue_sgd,
    companyProfile?.annual_revenue,
    companyProfile?.annualRevenue
  ) || 0;
  const projectType = companyProfile?.project_type || companyProfile?.projectType || '';
  const projectScope = companyProfile?.project_scope || companyProfile?.projectScope || '';
  const projectBudget = parseFloat(companyProfile?.project_budget || companyProfile?.projectBudget) || 0;
  
  const grantName = grant.name || grant.code || 'this grant';
  const _score = typeof grant.score === 'number' && !isNaN(grant.score) ? grant.score : 0;
  const strength = getEligibilityStrength(grant);
  
  // Build Sentence 1: company + eligibility strength + why (top 1-2 drivers)
  let drivers = [];
  
  if (sector) drivers.push(`${sector} alignment`);
  
  if (employees > 0) {
    const headcountPhrase = employees <= 10 ? 'small team size' : 
                            employees <= 50 ? `${employees}-person team` :
                            employees <= 200 ? `${employees}-person headcount` :
                            'enterprise scale';
    drivers.push(headcountPhrase);
  }
  
  if (revenue >= 1000000) {
    const revenueM = Math.round(revenue / 1000000);
    drivers.push(`S$${revenueM}M annual revenue`);
  } else if (revenue > 0 && revenue < 1000000) {
    drivers.push('early-stage revenue profile');
  }
  
  if (projectType && projectType.toLowerCase() !== 'general') {
    drivers.push(`${projectType} initiative`);
  }
  if (projectScope && projectScope.toLowerCase() !== String(projectType || '').toLowerCase()) {
    drivers.push(`${projectScope} scope`);
  }
  
  if (projectBudget >= 100000) {
    const budgetK = Math.round(projectBudget / 1000);
    drivers.push(`S$${budgetK}K project scope`);
  }
  
  // Take top 2 drivers
  const topDrivers = drivers.slice(0, 2);
  const driversText = topDrivers.length > 0 ? 
    `based on ${topDrivers.join(' and ')}` : 
    'based on your business profile';
  
  const sentence1 = `${companyName} shows ${strength} eligibility for ${grantName} ${driversText}.`;
  
  // Build Sentence 2: what grant helps + concrete benefit + nudge
  let supportDetails = '';
  
  // Check for co-funding info
  if (grant.funding_snapshot && typeof grant.funding_snapshot === 'string') {
    const fundingText = grant.funding_snapshot.toLowerCase();
    if (fundingText.includes('up to') || fundingText.includes('%') || fundingText.includes('s$')) {
      supportDetails = grant.funding_snapshot.split('.')[0];
    }
  }
  
  // Check for coverage info from detailed_info or summary
  let coverage = '';
  const detailedText = (grant.detailed_info || grant.summary || '').toLowerCase();
  if (detailedText.includes('software')) coverage = 'software and implementation';
  else if (detailedText.includes('consultancy')) coverage = 'consultancy and advisory services';
  else if (detailedText.includes('equipment')) coverage = 'equipment and infrastructure';
  else if (detailedText.includes('training')) coverage = 'training and capability development';
  else if (detailedText.includes('innovation')) coverage = 'innovation and R&D activities';
  else if (detailedText.includes('digital')) coverage = 'digital transformation initiatives';
  
  // Generic support statement if no specific details
  if (!supportDetails && coverage) {
    supportDetails = `can support ${coverage}`;
  } else if (!supportDetails) {
    supportDetails = 'can provide financial support for qualifying activities';
  }
  
  const sentence2 = `This grant ${supportDetails}. Review the detailed analysis to confirm scope and prepare required documents.`;
  
  // Combine sentences (28-55 words approximately)
  const quickVerdict = `${sentence1} ${sentence2}`;
  
  return {
    quickVerdict,
    whyMatch: grant.why_match || grant.why_eligible || [],
    remediationActions: grant.whats_missing || [],
    documentsNeeded: grant.docs_checklist || [],
    budgetSnapshot: grant.budget_snapshot || { estimatedCoFunding: 'N/A', effortLevel: 'Unknown', timelineWeeks: 'N/A', roiPotential: 'Unknown' },
    risks: grant.risks || [],
    nextSteps: grant.next_steps || grant.apply_steps || []
  };
};

import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
// ScoreTooltip removed - no hover tooltips needed
import ThumbsFeedback from '../components/feedback/ThumbsFeedback';
// StarRatingFeedback removed - keeping feedback simple with thumbs only
import {
  XCircle,
  AlertTriangle,
  Download,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Eye,
  EyeOff,
  Filter,
  ArrowUpDown,
  Building2,
  Lightbulb,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Target,
  Calendar,
  Hash,
  Award,
  Clock,
  Users,
  DollarSign,
  Check,
  CheckCircle,
  X,
  CheckSquare,
  Square,
  ExternalLink,
  ShieldAlert,
  HelpCircle
} from 'lucide-react';

// Utility functions
const _formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleDateString('en-SG', {
    timeZone: 'Asia/Singapore',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const generateSnapshotId = () => {
  return Math.random().toString(36).substr(2, 8).toUpperCase();
};

const getEnhancedAdvisorAnalysis = (detailedAnalysis = {}) => {
  const payload = detailedAnalysis?.enhanced_advisor_analysis || detailedAnalysis?.enhancedAdvisorAnalysis || {};
  const rawSections = Array.isArray(payload?.detailed_analysis_sections)
    ? payload.detailed_analysis_sections
    : Array.isArray(payload?.detailedAnalysisSections)
      ? payload.detailedAnalysisSections
      : [];
  const sections = rawSections.map((s) => {
    if (!s || typeof s !== 'object') return s;
    const analysis = stripScopeMissingBoilerplateSentences(
      stripAdvisoryFiller(String(s.analysis || ''))
    );
    const evList = Array.isArray(s.evidence)
      ? s.evidence
          .map((x) => stripScopeMissingBoilerplateSentences(stripAdvisoryFiller(String(x))))
          .filter(Boolean)
      : s.evidence;
    const riskList = Array.isArray(s.risks)
      ? s.risks
          .map((x) => stripAdvisoryFiller(String(x)))
          .filter((x) => x && !isBannedInsightLine(x) && !isScopeMissingBoilerplateFragment(x))
      : s.risks;
    return {
      ...s,
      factor: s.factor,
      analysis,
      evidence: evList,
      risks: riskList,
    };
  });
  const recommendations = Array.isArray(payload?.actionable_recommendations)
    ? payload.actionable_recommendations
    : Array.isArray(payload?.actionableRecommendations)
      ? payload.actionableRecommendations
      : [];
  const strList = (v) =>
    Array.isArray(v)
      ? v
          .map((x) => stripScopeMissingBoilerplateSentences(stripAdvisoryFiller(String(x))))
          .filter(Boolean)
      : [];
  return {
    verdict: payload?.eligibility_verdict || payload?.eligibilityVerdict || '',
    executiveSummary: stripScopeMissingBoilerplateSentences(
      stripAdvisoryFiller(payload?.executive_summary || payload?.executiveSummary || '')
    ),
    summaryOfKeyFactors: stripScopeMissingBoilerplateSentences(
      stripAdvisoryFiller(payload?.summary_of_key_factors || payload?.summaryOfKeyFactors || '')
    ),
    fitRationale: stripScopeMissingBoilerplateSentences(
      stripAdvisoryFiller(payload?.fit_rationale || payload?.fitRationale || '')
    ),
    strategicPursuitNote: stripScopeMissingBoilerplateSentences(
      stripAdvisoryFiller(payload?.strategic_pursuit_note || payload?.strategicPursuitNote || '')
    ),
    assumptions: strList(payload?.assumptions),
    missingInformation: strList(payload?.missing_information || payload?.missingInformation),
    matchedCriteriaAdvisor: strList(payload?.matched_criteria || payload?.matchedCriteria),
    unmetCriteriaAdvisor: strList(payload?.unmet_criteria || payload?.unmetCriteria),
    sections,
    recommendations: recommendations
      .map((r) =>
        stripScopeMissingBoilerplateSentences(stripAdvisoryFiller(typeof r === 'string' ? r : String(r)))
      )
      .filter(Boolean),
    referenceInputs: stripScopeMissingBoilerplateSentences(
      stripAdvisoryFiller(payload?.reference_inputs || payload?.referenceInputs || '')
    ),
    submissionStrategy: (() => {
      const s = payload?.submission_strategy || payload?.submissionStrategy;
      if (!s || typeof s !== 'object') return null;
      return s;
    })(),
    advisoryTraceability: (() => {
      const a = payload?.advisory_traceability || payload?.advisoryTraceability;
      if (!a || typeof a !== 'object') return null;
      const kc = a.key_conclusions || a.keyConclusions;
      if (!Array.isArray(kc) || kc.length === 0) return null;
      return { keyConclusions: kc };
    })(),
  };
};

const getGrantPurposeText = (item) => {
  if (!item || typeof item !== 'object') return '';
  const referenceData = item.referenceData || item.reference_data || {};
  const codeKey = String(
    item.code ||
    item.id ||
    item.grantId ||
    item.grant_id ||
    item.acronym ||
    ''
  ).toUpperCase().trim();
  const nameKey = String(item.display_name || item.displayName || item.name || '').toLowerCase();

  const staticGrantPurposeMap = {
    EDG: 'The Enterprise Development Grant (EDG) is a Singapore government initiative managed by Enterprise Singapore that supports local SMEs in upgrading capabilities, innovation, and overseas expansion through co-funding of qualifying project costs.',
    PSG: 'The Productivity Solutions Grant (PSG) helps Singapore SMEs adopt pre-approved digital and productivity solutions, reducing implementation cost for practical business improvement.',
    MRA: 'The Market Readiness Assistance (MRA) grant supports Singapore SMEs entering new overseas markets by co-funding eligible market promotion, setup, and market development activities.',
    SFEC: 'The SkillsFuture Enterprise Credit (SFEC) helps eligible Singapore employers offset out-of-pocket costs for approved workforce and enterprise transformation initiatives.',
    ECI: 'The Enterprise Compute Initiative (ECI) helps digitally mature enterprises accelerate AI projects through cloud service provider partnerships, tools, training, and expert support.',
    EIS: 'The Enterprise Innovation Scheme (EIS) is a tax incentive that supports qualifying innovation, R&D, training, and IP activities through deductions and allowances rather than cash grant reimbursement.',
    ESP: 'The Enterprise Sustainability Programme (ESP) helps Singapore companies build sustainability capability and prepare for green growth opportunities.',
    ADS: 'Advanced Digital Solutions helps SMEs adopt advanced or integrated digital solutions that go beyond basic tools and improve how operations run.',
    EFS: 'The Enterprise Financing Scheme (EFS) helps Singapore enterprises access business financing through participating financial institutions, with government risk-sharing rather than direct grant reimbursement.',
    EFS_GREEN: 'The Enterprise Financing Scheme - Green supports qualifying green financing needs through participating financial institutions and is not a direct cash grant.',
    BEIS: 'Business Events in Singapore supports qualifying MICE and business event projects in Singapore, including pre-event activities that strengthen the event pipeline.',
    HRF: 'The Hotel Rejuvenation Fund supports eligible hotels that are renovating or refreshing their properties to improve guest experience and competitiveness.',
    CDF: 'The Cruise Development Fund supports cruise deployment and development initiatives that help grow Singapore’s cruise industry.',
    TPDF: 'The Tourism Product Development Fund supports the creation or rejuvenation of tourism products that attract visitors and increase visitor spend.',
    LEAD: 'The Local Enterprise and Association Development Programme supports TAC-led industry initiatives in productivity, sustainability, technology, and collaboration.',
    GSFP: 'The Germany-Singapore SME Funding Programme supports Singapore companies and German counterparts pursuing joint innovation projects with market potential.',
    STARTUPSG: 'Startup SG is a government-backed umbrella programme for Singapore startups, covering routes such as Startup SG Founder and Startup SG Tech. It supports funding, mentorship, ecosystem access, and capability building, with eligibility and application routes varying by track.',
    STARTUPSG_TECH: 'Startup SG Tech is a track within the Startup SG umbrella programme. It supports proof-of-concept and proof-of-value technology development for startups building proprietary solutions.',
    SCALEUPSG: 'Scale-up SG is a selective Enterprise Singapore growth programme for high-potential local companies. It supports leadership development, business strategy, operational scaling, and regional or global expansion, and is typically invite-only rather than an open grant.',
  };

  const resolveStaticPurpose = () => {
    if (staticGrantPurposeMap[codeKey]) return staticGrantPurposeMap[codeKey];
    if (nameKey.includes('enterprise development grant')) return staticGrantPurposeMap.EDG;
    if (nameKey.includes('productivity solutions grant')) return staticGrantPurposeMap.PSG;
    if (nameKey.includes('market readiness assistance')) return staticGrantPurposeMap.MRA;
    if (nameKey.includes('skillsfuture enterprise credit')) return staticGrantPurposeMap.SFEC;
    if (nameKey.includes('enterprise compute initiative')) return staticGrantPurposeMap.ECI;
    if (nameKey.includes('enterprise innovation scheme')) return staticGrantPurposeMap.EIS;
    if (nameKey.includes('enterprise sustainability programme')) return staticGrantPurposeMap.ESP;
    if (nameKey.includes('advanced digital solutions')) return staticGrantPurposeMap.ADS;
    if (nameKey.includes('enterprise financing scheme - green')) return staticGrantPurposeMap.EFS_GREEN;
    if (nameKey.includes('enterprise financing scheme')) return staticGrantPurposeMap.EFS;
    if (nameKey.includes('business events in singapore')) return staticGrantPurposeMap.BEIS;
    if (nameKey.includes('hotel rejuvenation fund')) return staticGrantPurposeMap.HRF;
    if (nameKey.includes('cruise development fund')) return staticGrantPurposeMap.CDF;
    if (nameKey.includes('tourism product development fund')) return staticGrantPurposeMap.TPDF;
    if (nameKey.includes('local enterprise and association development programme')) return staticGrantPurposeMap.LEAD;
    if (nameKey.includes('germany-singapore sme funding programme')) return staticGrantPurposeMap.GSFP;
    return '';
  };
  const looksUserProfileSpecific = (value) => {
    const text = String(value || '').toLowerCase();
    if (!text) return false;
    return (
      text.includes('fits your profile') ||
      text.includes('internal fit score') ||
      text.includes('key strengths') ||
      text.includes('strengths:') ||
      text.includes('annual revenue') ||
      text.includes('project budget') ||
      text.includes('shows moderate alignment') ||
      text.includes('your responses') ||
      text.includes('your business appears') ||
      text.includes('based on your') ||
      text.includes('submitted profile')
    );
  };

  const candidates = [
    referenceData.closedCardDescription,
    item.whatThisGrantIsFor,
    item.what_this_grant_is_for,
    referenceData.overview,
    referenceData.description,
    item.sourceNotes,
    item.grantPurposeSummary,
    item.grant_purpose_summary,
  ];

  for (const candidate of candidates) {
    const text = String(candidate || '').trim();
    if (!text) continue;
    if (looksUserProfileSpecific(text)) continue;
    return text;
  }
  const staticPurpose = resolveStaticPurpose();
  if (staticPurpose) return staticPurpose;
  return 'This grant supports qualifying Singapore businesses by co-funding approved capability, productivity, or growth-related projects under scheme-specific criteria.';
};

const getGrantCardBadges = (item) => {
  if (!item || typeof item !== 'object') return ['Government'];
  const referenceData = item.referenceData || item.reference_data || {};
  const codeKey = String(
    item.code ||
    item.id ||
    item.grantId ||
    item.grant_id ||
    item.acronym ||
    ''
  ).toUpperCase().trim();
  const programmeType = String(referenceData.programmeType || referenceData.programme_type || item.programmeType || item.programme_type || '').toLowerCase();
  const accessMode = String(referenceData.accessMode || referenceData.access_mode || item.accessMode || item.access_mode || '').toLowerCase();
  const displayType = String(referenceData.displayType || referenceData.display_type || item.displayType || item.display_type || '').toLowerCase();
  const fundingDisplay = String(referenceData.fundingDisplay || referenceData.funding_display || item.fundingDisplay || item.funding_display || '').toLowerCase();
  const isFinancing = Boolean(referenceData.isFinancing || referenceData.is_financing || item.isFinancing || item.is_financing || displayType === 'financing' || fundingDisplay.includes('financ'));
	  const cashGrantFlag = referenceData.cashGrant ?? referenceData.cash_grant ?? item.cashGrant ?? item.cash_grant;
	  const badges = ['Government'];
	  if (isLegacyForNewApplicationsGrant(item)) badges.push('Legacy scheme');
	  const isProgramme =
    Boolean(referenceData.isProgramme || referenceData.is_programme || item.isProgramme || item.is_programme) ||
    programmeType === 'programme' ||
    ['STARTUPSG', 'STARTUPSG_TECH', 'SCALEUPSG'].includes(codeKey);
  if (isProgramme) badges.push('Programme');
  if (programmeType === 'tax_scheme' || displayType === 'tax incentive' || codeKey === 'EIS') badges.push('Tax Incentive');
  if (isFinancing || codeKey === 'EFS' || codeKey === 'EFS_GREEN') badges.push('Financing');
  if (cashGrantFlag === false && !badges.includes('Tax Incentive')) badges.push('Not Cash Grant');
  if (codeKey === 'SCALEUPSG' || accessMode === 'invite_only') {
    badges.push('Invite Only');
  }
  return badges;
};

const getGrantCardTags = (item) => {
  if (!item || typeof item !== 'object') return [];
  const referenceData = item.referenceData || item.reference_data || {};
  const badgeLabels = new Set(
    getGrantCardBadges(item).map((badge) => String(badge || '').trim().toLowerCase())
  );
  const rawTags = Array.isArray(referenceData.tags) ? referenceData.tags : Array.isArray(item.tags) ? item.tags : [];
  const tags = rawTags
    .map((tag) => String(tag || '').trim())
    .filter((tag) => {
      if (!tag) return false;
      return !badgeLabels.has(tag.toLowerCase());
    });
  return Array.from(new Set(tags));
};

const getProgrammeCardGuidance = (item) => {
  if (!item || typeof item !== 'object') return null;
  const referenceData = item.referenceData || item.reference_data || {};
  const codeKey = String(
    item.code ||
    item.id ||
    item.grantId ||
    item.grant_id ||
    item.acronym ||
    ''
  ).toUpperCase().trim();
  const programmeType = String(referenceData.programmeType || referenceData.programme_type || item.programmeType || item.programme_type || '').toLowerCase();
  const accessMode = String(referenceData.accessMode || referenceData.access_mode || item.accessMode || item.access_mode || '').toLowerCase();
  const isProgramme =
    Boolean(referenceData.isProgramme || referenceData.is_programme || item.isProgramme || item.is_programme) ||
    programmeType === 'programme' ||
    ['STARTUPSG', 'STARTUPSG_TECH', 'SCALEUPSG'].includes(codeKey);
  const isTaxIncentive =
    Boolean(referenceData.isTaxIncentive || referenceData.is_tax_incentive || item.isTaxIncentive || item.is_tax_incentive) ||
    programmeType === 'tax_scheme' ||
    codeKey === 'EIS';
  const isFinancing = String(referenceData.displayType || referenceData.display_type || item.displayType || item.display_type || '').toLowerCase() === 'financing' || codeKey === 'EFS' || codeKey === 'EFS_GREEN';

  if (!isProgramme && !isTaxIncentive && !isFinancing && !['ESP', 'ADS', 'BEIS', 'HRF', 'CDF', 'TPDF', 'LEAD', 'GSFP'].includes(codeKey)) return null;

  if (codeKey === 'SCALEUPSG' || accessMode === 'invite_only') {
    return {
      disclaimer:
        'Scale-Up SG is not a standard open government grant. It is a selective Enterprise Singapore growth programme for high-potential local companies, with participation typically based on suitability, growth potential, and programme selection rather than automatic eligibility or direct grant approval.',
      strategyNote:
        'Companies are typically identified, referred, invited, or assessed by Enterprise Singapore and its programme partners based on growth potential, leadership readiness, financial strength, business traction, and ability to scale regionally or globally.',
    };
  }

  if (['STARTUPSG', 'STARTUPSG_TECH'].includes(codeKey)) {
    return {
      disclaimer:
        'Startup SG is an umbrella initiative with different support tracks, not a single standard grant. Funding or support depends on the specific pathway, such as Startup SG Tech for POC or POV projects, and may require assessment through Enterprise Singapore, approved partners, or scheme-specific application processes.',
      strategyNote:
        'Startup SG applications depend on the selected track. For Startup SG Tech, companies generally prepare a project proposal covering proprietary technology, development stage, technical feasibility, commercial potential, project milestones, team capability, and budget before assessment through Enterprise Singapore or the relevant channel.',
    };
  }

  if (codeKey === 'ECI') {
    return {
      disclaimer:
        'Enterprise Compute Initiative is a programme for AI adoption and compute enablement, not a direct cash grant. It is most relevant where the project shows AI, automation, analytics, cloud, or implementation-stage signals.',
      strategyNote:
        'Applicants should frame ECI around the AI use case, implementation path, and capability lift, not around generic website or branding work.',
      fundingNote:
        'Support is usually tied to the programme structure and partner pathway rather than a simple reimbursement model.',
    };
  }

  if (codeKey === 'ESP') {
    return {
      disclaimer:
        'Enterprise Sustainability Programme supports sustainability capability building and green growth, so it should be framed as ESG readiness and capability support rather than generic digitalisation.',
      strategyNote:
        'Use ESP when the project is about sustainability capability, environmental readiness, or green growth opportunities.',
      fundingNote:
        'Support is tied to capability development and programme participation rather than a pure cash grant narrative.',
    };
  }

  if (codeKey === 'ADS') {
    return {
      disclaimer:
        'Advanced Digital Solutions is aimed at advanced or integrated digital adoption, not at a simple website refresh or basic branding update.',
      strategyNote:
        'Position ADS around implementation-ready digital transformation, system integration, or advanced solution adoption with a clear business process impact.',
      fundingNote:
        'Support depends on the live IMDA solution category and call terms.',
    };
  }

  if (codeKey === 'EFS' || codeKey === 'EFS_GREEN') {
    return {
      disclaimer:
        'Enterprise Financing Scheme is a financing pathway, not a direct cash grant. EFS Green is the green-financing variant, so both should be described as risk-shared financing through participating financial institutions.',
      strategyNote:
        'Frame the request around the financing need, facility type, and business use case. For EFS Green, also make the sustainability or decarbonisation angle explicit.',
      fundingNote:
        'Support is provided through bank or financial institution participation, with government risk-sharing rather than grant reimbursement.',
    };
  }

  if (['BEIS', 'HRF', 'CDF', 'TPDF'].includes(codeKey)) {
    return {
      disclaimer:
        'This is a sector-specific tourism pathway, so it is not a general business grant. The project must be anchored to the relevant MICE, hospitality, cruise, or tourism product use case.',
      strategyNote:
        'Keep the application tightly tied to the live sector rules, the project timing, and the qualifying tourism deliverables.',
      fundingNote:
        'Support usually depends on project category and approved costs rather than general company transformation spend.',
    };
  }

  if (codeKey === 'LEAD') {
    return {
      disclaimer:
        'LEAD is TAC-led and industry-development oriented, so it is not a standard company cash grant. It is usually relevant where a trade association or chamber is driving the initiative.',
      strategyNote:
        'The strongest case is a collective industry initiative with clear membership, collaboration, or sector development benefits.',
      fundingNote:
        'Support is tied to programme participation and the TAC-led route.',
    };
  }

  if (codeKey === 'GSFP') {
    return {
      disclaimer:
        'The Germany-Singapore SME Funding Programme is a joint cross-border innovation pathway, not a general grant. It needs a Singapore-Germany partnership or equivalent collaborative project structure.',
      strategyNote:
        'Spell out the German counterpart, the joint innovation workplan, and the commercialisation pathway so the programme fit is obvious.',
      fundingNote:
        'Support is linked to the joint programme structure and the approved collaboration scope.',
    };
  }

  if (isTaxIncentive) {
    return {
      disclaimer:
        'Enterprise Innovation Scheme is a tax incentive, not a direct cash grant. It works through deductions, allowances, and tax filing treatment for qualifying activity rather than an application for reimbursement.',
      strategyNote:
        'Applicants should frame EIS around qualifying spend, supporting records, and the tax period in which the claim will be made, rather than around direct project funding or grant-style approval.',
      fundingNote:
        'Support depends on qualifying tax treatment and the activity category recognised under IRAS rules.',
    };
  }

  return {
    disclaimer:
      'This is a government programme rather than a standard open grant. Eligibility, access, and support depend on the specific track, route, or application process.',
    strategyNote:
      'Programme access and submission steps can vary by track or partner route, so applicants should confirm the exact pathway before preparing documents.',
    fundingNote:
      'Support details vary by programme track and approved scope.',
  };
};

const stripRepresentativePlaceholderSuffix = (name) =>
  String(name || '')
    .replace(/\s*\(Representative Placeholder\)\s*$/i, '')
    .trim();
const NON_GOV_PROGRAMMES_FALLBACK = 'No validated independent funding pathways were surfaced for this profile.';
const NON_GOV_PROGRAMMES_CONTEXT_NOTE =
  'These pathway references are drawn from OPTRA’s validated private funding catalogue rather than placeholder examples.';
const MIN_NON_GOV_PROGRAMMES_TO_SHOW = 3;
const MAX_NON_GOV_PROGRAMMES_TO_SHOW = 5;
const NON_GOV_TYPED_EXAMPLE_PROGRAMMES = {};

const NON_GOV_ID_EXAMPLE_PROGRAMMES = {};

const EXCLUDED_GRANT_CODE_SET = new Set([
  'SCSG',
  'ECCG',
  'EHVCG',
  'GRT_OA',
  'MCF_IA',
  'MCF_MA',
  'MCF_OA',
  'TIS',
  'LTA',
  'LTA_MOBILITY',
  'HDB',
  'HDB_SMART',
]);

const EXCLUDED_GRANT_NAME_FRAGMENTS = [
  'electric vehicle common charger grant',
  'electric heavy vehicle charger grant',
  'global ready talent programme',
  'talent@maritimesingapore',
  'maritime cluster fund',
  'lorry crane',
  'electric charger',
  'ev charger',
  'housing development board',
  'land transport authority',
  'technology innovation scheme',
];

const MAIN_GRANT_DISPLAY_LABELS = {
  EDG: 'Enterprise Development Grant (EDG)',
  EEG: 'Enterprise Energy Grant (EEG)',
  MRA: 'Market Readiness Assistance (MRA)',
  PSG: 'Productivity Solutions Grant (PSG)',
  SFEC: 'SkillsFuture Enterprise Credit (SFEC)',
  GIA: 'Global Innovation Alliance (GIA)',
  ESP: 'Enterprise Sustainability Programme (ESP)',
  ADS: 'Advanced Digital Solutions (ADS)',
  EFS: 'Enterprise Financing Scheme (EFS)',
  EFS_GREEN: 'Enterprise Financing Scheme - Green (EFS Green)',
  BEIS: 'Business Events in Singapore (BEIS)',
  HRF: 'Hotel Rejuvenation Fund (HRF)',
  CDF: 'Cruise Development Fund (CDF)',
  TPDF: 'Tourism Product Development Fund (TPDF)',
  LEAD: 'Local Enterprise and Association Development Programme (LEAD)',
  GSFP: 'Germany-Singapore SME Funding Programme (GSFP)',
};

const CANONICAL_PARENT_ID_MAP = {
  WDG_JR_PLUS: 'WDG_JR_PLUS',
  WDG: 'WDG_JR_PLUS',
  ACT: 'ACT',
  BETC: 'BETC',
  BIF: 'BIF',
  ESF: 'ESF',
  SRG: 'SRG',
  WCS: 'WCS',
  LEF: 'LEF',
  'A*STAR': 'A*STAR',
  ASTAR: 'A*STAR',
  MAS_FINTECH: 'MAS',
  MAS: 'MAS',
  SMESGODIGITAL: 'SMESGODIGITAL',
  STARTUPSG: 'STARTUPSG',
  SCALEUPSG: 'SCALEUPSG',
  ECI: 'ECI',
  ESP: 'ESP',
  ADS: 'ADS',
  EFS: 'EFS',
  EFS_GREEN: 'EFS_GREEN',
  BEIS: 'BEIS',
  HRF: 'HRF',
  CDF: 'CDF',
  TPDF: 'TPDF',
  LEAD: 'LEAD',
  GSFP: 'GSFP',
  STB_TOURISM: 'STB',
  STB: 'STB',
  TIS: 'EIS',
  EIS: 'EIS',
  NEA_3R: 'NEA',
  NEA: 'NEA',
};

const MAIN_GRANT_LABELS = {
  EDG: 'Enterprise Development Grant (EDG)',
  EEG: 'Enterprise Energy Grant (EEG)',
  MRA: 'Market Readiness Assistance (MRA)',
  PSG: 'Productivity Solutions Grant (PSG)',
  SFEC: 'SkillsFuture Enterprise Credit (SFEC)',
  GIA: 'Global Innovation Alliance (GIA)',
};

const normalizeMainGrantAcronym = (value) => {
  const token = String(value || '').toUpperCase().trim();
  if (!token) return 'OTHER';
  if (CANONICAL_PARENT_ID_MAP[token]) return CANONICAL_PARENT_ID_MAP[token];
  const firstToken = token.split(/[_\s-]+/)[0];
  if (CANONICAL_PARENT_ID_MAP[firstToken]) return CANONICAL_PARENT_ID_MAP[firstToken];
  if (MAIN_GRANT_LABELS[firstToken]) return firstToken;
  if (/^[A-Z0-9*+]{2,20}$/.test(firstToken)) return firstToken;
  if (/^[A-Z0-9*+]{2,20}$/.test(token)) return token;
  return firstToken || token;
};

const FUNDING_TYPE_DISPLAY_LABELS = {
  grant: 'Government',
  foundation_grant: 'Foundation Grant',
  accelerator: 'Accelerator',
  equity_programme: 'Equity Programme',
  venture_debt: 'Venture Debt',
  revenue_based_financing: 'Revenue-Based Financing',
  debt_financing: 'Debt Financing',
  pilot_funding: 'Pilot Funding',
  innovation_challenge: 'Innovation Challenge',
  prize: 'Prize',
  private_non_dilutive: 'Private Non-Dilutive',
};

const LABEL_ACRONYM_MAP = {
  ai: 'AI',
  apac: 'APAC',
  sg: 'SG',
  sme: 'SME',
  poc: 'POC',
  mvp: 'MVP',
  rbf: 'RBF',
  trl: 'TRL',
};

const LABEL_PHRASE_REPLACEMENTS = [
  [/Non Dilutive/gi, 'Non-Dilutive'],
  [/Revenue Based/gi, 'Revenue-Based'],
  [/Proof Of Concept/gi, 'Proof-of-Concept'],
];

const normalizeLabelForComparison = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatDisplayLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  let formatted = raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((part) => {
      const lower = part.toLowerCase();
      if (LABEL_ACRONYM_MAP[lower]) return LABEL_ACRONYM_MAP[lower];
      if (/^[A-Z0-9&/+]+$/.test(part) && part.length <= 5) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');
  LABEL_PHRASE_REPLACEMENTS.forEach(([pattern, replacement]) => {
    formatted = formatted.replace(pattern, replacement);
  });
  return formatted;
};

const formatAvailabilityLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const normalizedRange = raw
    .replace(/\s*[-–—]\s*/g, ' - ')
    .replace(/\b(weeks?|months?|days?)\b/gi, (match) => match.charAt(0).toUpperCase() + match.slice(1).toLowerCase())
    .replace(/\s+/g, ' ')
    .trim();
  if (/^\d+(?:\.\d+)?\s+-\s+\d+(?:\.\d+)?\s+(Weeks?|Months?|Days?)$/i.test(normalizedRange)) {
    return normalizedRange;
  }
  const directMap = {
    rolling: 'Rolling',
    open: 'Open',
    'rolling/open': 'Rolling / Open',
    closed: 'Closed',
    closed_current_cycle: 'Closed Current Cycle',
    closed_recurrence_unconfirmed: 'Closed, Recurrence Unconfirmed',
    selection_in_progress: 'Selection In Progress',
    nomination_required: 'Nomination Required',
    unknown: 'Availability Unconfirmed',
  };
  const lowered = raw.toLowerCase();
  if (directMap[lowered]) return directMap[lowered];
  return formatDisplayLabel(raw.replace(/\//g, ' / '));
};

const GRANT_FAMILY_DISPLAY = {
  WDG_JR_PLUS: {
    title: 'SkillsFuture Workforce Development Grant (Job Redesign+)',
    acronym: 'WDG JR+',
    description: 'Supports companies in redesigning roles, workflows, and processes to improve workforce productivity and adapt to changing industry demands. Businesses benefit by reducing manual work, strengthening employee capabilities, and creating more future-ready job functions that support long-term operational efficiency.',
  },
  ACT: {
    title: 'Agri-Food Cluster Transformation Fund',
    acronym: 'ACT',
    description: 'Helps agri-food businesses invest in technology, automation, and sustainable production methods to strengthen local food resilience. The scheme enables companies to modernize operations, increase output quality, and scale more competitively within the food production ecosystem.',
  },
  BETC: {
    title: 'Built Environment Technology and Capability Grant',
    acronym: 'BETC',
    description: 'Encourages firms in the built environment sector to adopt advanced construction technologies and strengthen technical capabilities. It allows businesses to improve project delivery, enhance productivity, and stay aligned with evolving industry standards and digital construction practices.',
  },
  BIF: {
    title: 'Business Improvement Fund',
    acronym: 'BIF',
    description: 'Supports tourism-related businesses in upgrading technology, redesigning services, and improving operational processes. The grant helps companies enhance customer experiences, streamline operations, and remain competitive in a rapidly evolving visitor economy.',
  },
  ESF: {
    title: 'Experience Step-Up Fund',
    acronym: 'ESF',
    description: 'Assists tourism and lifestyle operators in developing higher-value visitor experiences and innovative offerings. Businesses benefit through stronger market differentiation, increased visitor engagement, and opportunities to attract premium audiences.',
  },
  SRG: {
    title: 'Sustainability Reporting Grant',
    acronym: 'SRG',
    description: 'Helps companies build sustainability reporting capabilities and align with environmental disclosure requirements. The scheme enables businesses to strengthen ESG transparency, improve investor confidence, and prepare for evolving compliance expectations.',
  },
  WCS: {
    title: 'Workplace Safety & Health Grant',
    acronym: 'WCS',
    description: 'Provides support for adopting workplace safety technologies, risk management systems, and safer operational practices. Businesses benefit from reduced workplace incidents, stronger compliance standards, and improved operational continuity.',
  },
  LEF: {
    title: 'Leisure Events Fund',
    acronym: 'LEF',
    description: 'Supports the development and execution of events that enhance the local leisure and entertainment landscape. The fund enables organizers to create larger-scale, higher-quality experiences that attract audiences, drive tourism activity, and generate commercial visibility.',
  },
  'A*STAR': {
    title: 'A*STAR Research Grants',
    acronym: 'A*STAR',
    description: 'Funds collaborative research, deep-tech innovation, and commercialization projects between companies and research institutions. Businesses gain access to advanced R&D expertise, accelerate product innovation, and reduce the cost of developing emerging technologies.',
  },
  MAS: {
    title: 'MAS FinTech Regulatory Sandbox',
    acronym: 'MAS',
    description: 'Allows fintech companies to test innovative financial products or services within a controlled regulatory environment. This gives businesses the flexibility to validate new solutions, refine business models, and enter regulated markets with lower initial compliance barriers.',
  },
  SMESGODIGITAL: {
    title: 'SMEs Go Digital',
    acronym: 'SMESGODIGITAL',
    description: 'Helps SMEs adopt digital tools, e-commerce systems, cybersecurity solutions, and digital transformation strategies. Businesses benefit through improved efficiency, stronger online capabilities, and better readiness for a technology-driven operating environment.',
  },
  STARTUPSG: {
    title: 'Startup SG',
    acronym: 'STARTUPSG',
    description: 'Provides funding, mentorship, and ecosystem support for early-stage startups and entrepreneurs. The initiative helps young businesses access capital, build credibility, and accelerate growth through structured government-backed support.',
  },
  STB: {
    title: 'STB Tourism Innovation',
    acronym: 'STB',
    description: 'Encourages tourism businesses to develop innovative products, digital solutions, and enhanced visitor experiences. Companies benefit by improving service quality, increasing operational agility, and staying competitive within the tourism sector.',
  },
  EIS: {
    title: 'Enterprise Innovation Scheme',
    acronym: 'EIS',
    description: 'Offers tax deductions and allowances for qualifying innovation, research, training, and intellectual property activities. The scheme reduces the cost of innovation while keeping support tied to tax treatment rather than direct grant reimbursement.',
  },
  ECI: {
    title: 'Enterprise Compute Initiative',
    acronym: 'ECI',
    description: 'Helps digitally mature enterprises accelerate AI projects through cloud service providers, tools, training, and expert support. The programme is designed for AI adoption and implementation-focused transformation rather than generic digitalisation or branding work.',
  },
  NEA: {
    title: 'NEA 3R Fund',
    acronym: 'NEA',
    description: 'Supports projects focused on reducing waste, increasing recycling, and improving resource sustainability. Businesses benefit through lower waste management costs, stronger sustainability positioning, and more environmentally efficient operations.',
  },
  EDG: {
    title: 'Enterprise Development Grant',
    acronym: 'EDG',
    description: 'Assists businesses in upgrading capabilities, pursuing innovation, and expanding into overseas markets. The grant enables companies to undertake strategic transformation projects that strengthen competitiveness and support sustainable business growth.',
  },
  EEG: {
    title: 'Enterprise Energy Grant',
    acronym: 'EEG',
    description: 'Helps businesses adopt energy-efficient equipment and improve overall energy management practices. Companies benefit from reduced utility costs, lower carbon emissions, and greater resilience against rising energy expenses.',
  },
  MRA: {
    title: 'Market Readiness Assistance',
    acronym: 'MRA',
    description: 'Supports SMEs entering overseas markets through funding for market promotion, business development, and international partnerships. The scheme lowers expansion risks and helps businesses establish a stronger global presence more efficiently.',
  },
  PSG: {
    title: 'Productivity Solutions Grant',
    acronym: 'PSG',
    description: 'Provides funding support for pre-approved digital and productivity-enhancing solutions across various industries. Businesses can adopt proven technologies more affordably to improve efficiency, automate routine processes, and strengthen day-to-day operations.',
  },
  SFEC: {
    title: 'SkillsFuture Enterprise Credit',
    acronym: 'SFEC',
    description: 'Offers additional funding support for enterprise and workforce transformation initiatives. Businesses benefit by offsetting the cost of upgrading capabilities, investing in staff development, and implementing productivity improvements.',
  },
  GIA: {
    title: 'Global Innovation Alliance',
    acronym: 'GIA',
    description: 'Connects Singapore-based companies with overseas innovation hubs, partners, and market opportunities. The initiative helps businesses access international networks, validate solutions abroad, and accelerate cross-border growth and collaboration.',
  },
  ESP: {
    title: 'Enterprise Sustainability Programme',
    acronym: 'ESP',
    description: 'Supports sustainability capability building and green growth readiness for Singapore companies. Businesses benefit by strengthening ESG capability and preparing for sustainability-led opportunities rather than treating this as a generic digitalisation route.',
  },
  ADS: {
    title: 'Advanced Digital Solutions',
    acronym: 'ADS',
    description: 'Supports SMEs adopting advanced or integrated digital solutions that go beyond basic tools. Businesses benefit by improving operational workflows, implementation readiness, and the quality of their digital transformation.',
  },
  EFS: {
    title: 'Enterprise Financing Scheme',
    acronym: 'EFS',
    description: 'Provides government-assisted financing support through participating financial institutions rather than direct cash grant reimbursement. Businesses benefit by improving access to working capital, trade, fixed-asset, project, or growth financing.',
  },
  EFS_GREEN: {
    title: 'Enterprise Financing Scheme - Green',
    acronym: 'EFS Green',
    description: 'Provides risk-shared financing support for qualifying green projects and sustainability adoption. Businesses benefit by financing decarbonisation or green growth initiatives through the banking channel rather than through a cash grant.',
  },
  BEIS: {
    title: 'Business Events in Singapore',
    acronym: 'BEIS',
    description: 'Supports qualifying MICE and business event projects in Singapore, including pre-event development work that helps anchor event activity locally. Businesses benefit by defraying event-related development costs for sector-specific projects.',
  },
  HRF: {
    title: 'Hotel Rejuvenation Fund',
    acronym: 'HRF',
    description: 'Supports hotels undertaking renovation or refurbishment works to improve guest experience and competitiveness. Businesses benefit by refreshing property assets and strengthening hospitality appeal.',
  },
  CDF: {
    title: 'Cruise Development Fund',
    acronym: 'CDF',
    description: 'Supports cruise deployment and cruise industry development initiatives that help grow Singapore’s cruise ecosystem. Businesses benefit by backing cruise growth, deployment, and related commercial activity.',
  },
  TPDF: {
    title: 'Tourism Product Development Fund',
    acronym: 'TPDF',
    description: 'Supports the creation or rejuvenation of tourism products that attract visitors and lift visitor spend. Businesses benefit by funding visitor-facing product development and upgrades rather than generic corporate transformation.',
  },
  LEAD: {
    title: 'Local Enterprise and Association Development Programme',
    acronym: 'LEAD',
    description: 'Supports TAC-led industry initiatives in productivity, sustainability, technology, and collaboration. Businesses and associations benefit when the project is collective and industry-development oriented rather than purely company-specific.',
  },
  GSFP: {
    title: 'Germany-Singapore SME Funding Programme',
    acronym: 'GSFP',
    description: 'Supports Singapore companies and German counterparts pursuing joint innovation projects with market potential. Businesses benefit from cross-border collaboration, commercialisation, and internationalisation support.',
  },
  SCALEUPSG: {
    title: 'Scale-up SG',
    acronym: 'SCALEUPSG',
    description: 'Supports high-growth local companies in scaling operations, leadership capabilities, and international expansion strategies. Businesses gain structured guidance, strategic mentoring, and access to networks that support sustainable scaling.',
  },
  BIZADAPT: {
    title: 'Business Adaptation Grant',
    acronym: 'BIZADAPT',
    description: 'Helps companies adjust to regulatory, market, or industry shifts through capability upgrades and operational adaptation projects. The grant enables businesses to remain competitive by responding more effectively to changing commercial conditions and compliance requirements.',
  },
};

const SPECIAL_GRANT_TITLE_MAP = {
  SCALEUPSG: 'Scale-up SG',
  SCALEUP_SG: 'Scale-up SG',
  SCALE_UP_SG: 'Scale-up SG',
  BIZADAPT: 'Business Adaptation Grant',
  STARTUPSG: 'Startup SG',
  STARTUP_SG: 'Startup SG',
  'STARTUP SG': 'Startup SG',
  SMESGODIGITAL: 'SMEs Go Digital',
  SMES_GO_DIGITAL: 'SMEs Go Digital',
  'SMES GO DIGITAL': 'SMEs Go Digital',
};

const VERIFIED_PROGRAMME_SOURCE_ALLOWLIST = new Set([
  'supabase_verified',
  'dataset_verified',
  'dataset_verified_live_miss',
  'verified_catalogue',
  'csv_reputable_examples',
  'curated_fallback',
  'curated_fallback_live_miss',
  'curated_fallback_scrape_disabled',
]);

const isExcludedGrantForDisplay = (grant) => {
  if (!grant || typeof grant !== 'object') return false;
  const code = String(grant.code || grant.acronym || grant.grantId || grant.grant_id || '').toUpperCase().trim();
  if (EXCLUDED_GRANT_CODE_SET.has(code)) return true;
  const searchable = [
    grant.name,
    grant.grantFullName,
    grant.displayName,
    grant.description,
    grant.whatThisGrantIsFor,
    grant.whyItMayFit,
  ]
    .map((v) => String(v || '').toLowerCase())
    .join(' ');
  return EXCLUDED_GRANT_NAME_FRAGMENTS.some((fragment) => searchable.includes(fragment));
};

const getGrantTrackLabel = (grant) => {
  const codeRaw = String(grant?.grantId || grant?.grant_id || grant?.code || grant?.acronym || '').toUpperCase().trim();
  const nameRaw = String(grant?.grantFullName || grant?.grant_full_name || grant?.name || grant?.displayName || '').trim();
  const mapped = [
    [/PSG.*IT.*SOLUTIONS|IT SOLUTIONS/i, 'IT Solutions'],
    [/PSG.*EQUIPMENT|EQUIPMENT/i, 'Equipment'],
    [/PSG.*PRE.*APPROVED.*DIGITAL/i, 'Pre-approved Digital Solutions'],
    [/MRA.*OVERSEAS.*MARKET.*PROMOTION/i, 'Overseas Market Promotion'],
    [/MRA.*OVERSEAS.*BUSINESS.*DEVELOPMENT/i, 'Overseas Business Development'],
    [/MRA.*OVERSEAS.*MARKET.*SETUP/i, 'Overseas Market Setup'],
    [/EDG.*CORE.*CAPABILITIES/i, 'Core Capabilities'],
    [/EDG.*INNOVATION.*PRODUCTIVITY/i, 'Innovation and Productivity'],
    [/EDG.*MARKET.*ACCESS/i, 'Market Access'],
    [/STARTUP.*FOUNDER/i, 'Founder'],
    [/STARTUP.*TECH/i, 'Tech'],
    [/WDG.*JR|JOB.*REDESIGN/i, 'Job Redesign+'],
    [/EEG.*BASE.*BCA/i, 'Base - BCA'],
    [/EEG.*BASE.*NEA/i, 'Base - NEA'],
    [/EEG.*BASE.*(ESG|ENTERPRISESG)/i, 'Base - EnterpriseSG'],
    [/EEG.*ADVANCED.*BCA/i, 'Advanced - BCA'],
    [/EEG.*ADVANCED.*NEA/i, 'Advanced - NEA'],
  ];
  const parentForPrefix = (() => {
    const token = codeRaw.split(/[_\s-]+/)[0] || '';
    if (['PSG', 'MRA', 'EDG', 'EEG', 'SFEC', 'WDG', 'STARTUPSG', 'STARTUP'].includes(token)) {
      if (token === 'STARTUPSG' || token === 'STARTUP') return 'STARTUP SG';
      if (token === 'WDG') return 'WDG JR+';
      return token;
    }
    return '';
  })();
  for (const [pattern, label] of mapped) {
    if (pattern.test(codeRaw) || pattern.test(nameRaw.toUpperCase())) {
      return parentForPrefix ? `${parentForPrefix} - ${label}` : label;
    }
  }
  const parentId = (() => {
    const token = String(codeRaw || '').toUpperCase().trim();
    if (!token) return '';
    const first = token.split(/[_\s-]+/)[0];
    return first || token;
  })();
  if (codeRaw === parentId || !codeRaw) {
    return toDisplayGrantTitle(nameRaw || parentId || 'Grant');
  }
  const fallback = toDisplayGrantTitle(nameRaw || codeRaw);
  return parentForPrefix && !fallback.toUpperCase().startsWith(parentForPrefix)
    ? `${parentForPrefix} - ${fallback}`
    : fallback;
};

const TITLE_ACRONYMS = new Set(['EDG', 'EEG', 'MRA', 'PSG', 'SFEC', 'GIA', 'SME', 'KPI', 'AI', 'API', 'R&D']);

const toDisplayGrantTitle = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Grant';
  const compact = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  const upper = compact.toUpperCase();
  if (MAIN_GRANT_DISPLAY_LABELS[upper]) return MAIN_GRANT_DISPLAY_LABELS[upper];
  if (SPECIAL_GRANT_TITLE_MAP[upper]) return SPECIAL_GRANT_TITLE_MAP[upper];
  const underscoredUpper = raw.replace(/\s+/g, '_').toUpperCase();
  if (SPECIAL_GRANT_TITLE_MAP[underscoredUpper]) return SPECIAL_GRANT_TITLE_MAP[underscoredUpper];
  const parts = compact.split(' ').filter(Boolean);
  return parts
    .map((part) => {
      const token = part.trim();
      const m = /^([^A-Za-z0-9]*)([A-Za-z0-9&/+. -]*?)([^A-Za-z0-9]*)$/.exec(token) || [];
      const prefix = m[1] || '';
      const core = (m[2] || token).trim();
      const suffix = m[3] || '';
      const up = core.toUpperCase();
      if (TITLE_ACRONYMS.has(up)) return `${prefix}${up}${suffix}`;
      if (/^\d+$/.test(token)) return token;
      if (!core) return token;
      return `${prefix}${core.charAt(0).toUpperCase()}${core.slice(1).toLowerCase()}${suffix}`;
    })
    .join(' ');
};

const CLIENT_FALLBACK_NON_GOV_OPPORTUNITIES = [];

const _readBool = (v) => v === true || String(v || '').toLowerCase() === 'true';

const buildClientFallbackFundingEngine = (formSource, grants = []) => {
  const profile = formSource && typeof formSource === 'object' ? formSource : {};
  const geography = String(
    profile.market_scope || profile.marketScope || profile.operational_presence || 'singapore_only'
  ).toLowerCase();
  const fundingGoal = String(profile.funding_goal || profile.project_type || '').toLowerCase();
  const fundingPreference = String(profile.funding_preference || '').toLowerCase();
  const businessModel = String(profile.business_model || '').toLowerCase();
  const traction = String(profile.traction_stage || '').toLowerCase();
  const revenueStatus = String(profile.revenue_status || '').toLowerCase();
  const impactIntent = _readBool(profile.impact_intent);
  const pilotReady = _readBool(profile.pilot_ready);
  const vcBacked = _readBool(profile.vc_backed);
  const singaporeAligned =
    geography.includes('singapore') || geography.includes('asean') || geography.includes('market');
  const revenueReady = ['early_revenue', 'revenue_generating', 'profitable'].includes(revenueStatus);

  const grouped = {
    recommended_grants: (Array.isArray(grants) ? grants : [])
      .slice()
      .sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0))
      .slice(0, 3)
      .map((grant) => ({
        id: grant?.id || grant?.code || grant?.grant_id || grant?.name,
        name: grant?.name || grant?.display_name || grant?.displayName || 'Government',
        score: grant?.score ?? 0,
        confidence: grant?.confidence ?? 'medium',
        summary: grant?.summary || '',
        eligible: grant?.eligible ?? false,
        source: 'current_grant_engine',
      })),
    additional_funding_opportunities: [],
    growth_and_investment_opportunities: [],
    financing_options: [],
  };

  const push = (bucket, item) => grouped[bucket].push(item);

  for (const opportunity of CLIENT_FALLBACK_NON_GOV_OPPORTUNITIES) {
    const req = opportunity.requires || {};
    if (!singaporeAligned) continue;
    if (req.impactIntent && !impactIntent) continue;
    if (req.pilotReady && !pilotReady) continue;
    if (req.vcBacked && !vcBacked) continue;
    if (req.revenueReady && !revenueReady) continue;

    let score = 62;
    const reasons = [];
    if (fundingGoal.includes('pilot') && ['pilot_funding', 'innovation_challenge'].includes(opportunity.fundingType)) {
      score += 16;
      reasons.push('Your funding goal emphasizes pilot deployment, which aligns with this opportunity type.');
    }
    if (impactIntent && opportunity.fundingType === 'foundation_grant') {
      score += 18;
      reasons.push('Your impact intent aligns with impact-led independent funding criteria.');
    }
    if (vcBacked && ['venture_debt', 'equity_programme'].includes(opportunity.fundingType)) {
      score += 14;
      reasons.push('VC-backed profile improves fit for this investment-oriented pathway.');
    }
    if (revenueReady && ['revenue_based_financing', 'debt_financing'].includes(opportunity.fundingType)) {
      score += 12;
      reasons.push('Current revenue stage supports financing-oriented pathway suitability.');
    }
    if (fundingPreference.includes('debt') && ['venture_debt', 'revenue_based_financing', 'debt_financing'].includes(opportunity.fundingType)) {
      score += 10;
      reasons.push('Your capital preference indicates openness to financing options.');
    }
    if ((businessModel.includes('deeptech') || businessModel.includes('enterprise')) && ['accelerator', 'innovation_challenge', 'pilot_funding'].includes(opportunity.fundingType)) {
      score += 8;
      reasons.push('Business model signals align with programme formats that value applied deployment potential.');
    }
    if (traction.includes('active_pilots') || traction.includes('paying_customers') || traction.includes('recurring_revenue')) {
      score += 5;
    }
    score = Math.min(92, score);

    const confidence = score >= 80 ? 'high' : score >= 70 ? 'medium' : 'low';
    const whyMatch = (() => {
      if (reasons.length > 1) {
        return `${reasons[0]} ${reasons[1]} This is best treated as a focused adjacent pathway to validate in parallel with your core grant shortlist.`;
      }
      if (reasons.length === 1) {
        return `${reasons[0]} This is best treated as a focused adjacent pathway to validate in parallel with your core grant shortlist.`;
      }
      return 'This opportunity was surfaced as an adjacent independent funding pathway based on your funding profile and routing signals. It should be treated as a targeted secondary pathway that may unlock practical funding options beyond grant-only routes.';
    })();
    const whatToExpect = {
      foundation_grant:
        'Expect mission-fit and outcome credibility to be assessed alongside commercial viability. A practical next step is preparing a concise impact logic note with measurable outcomes and delivery evidence.',
      accelerator:
        'Expect structured programme evaluation focused on founder execution and traction potential. A practical next step is preparing a short operating plan showing milestones, owner accountability, and traction proof.',
      equity_programme:
        'Expect investor-style scrutiny on growth narrative, scale readiness, and market positioning. A practical next step is preparing an investor-grade narrative with clear unit economics and deployment priorities.',
      venture_debt:
        'Expect financing evaluation focused on repayment capacity, investor support, and capital discipline. A practical next step is preparing a repayment-capacity view with downside assumptions and governance over spend.',
      revenue_based_financing:
        'Expect underwriting centered on revenue quality, repayment profile, and consistency of commercial traction. A practical next step is preparing recent revenue-quality evidence and repayment-headroom assumptions.',
      debt_financing:
        'Expect lender-style assessment of financial discipline, repayment clarity, and use-of-funds quality. A practical next step is preparing a specific use-of-funds plan linked to repayment confidence.',
      pilot_funding:
        'Expect deployment-readiness checks on practical implementation, timeline feasibility, and pilot value capture. A practical next step is preparing a pilot brief with milestones, decision KPIs, and deployment ownership.',
      innovation_challenge:
        'Expect problem-solution fit assessment tied to specific challenge statements and delivery practicality. A practical next step is preparing a challenge-fit memo mapping your solution to the sponsor problem statement.',
      prize:
        'Expect differentiation and demonstrable value to matter more than standard grant-eligibility framing. A practical next step is preparing a concise evidence-led narrative on outcomes and defensible differentiation.',
    }[opportunity.fundingType];

    const typicalSupportStructure = {
      foundation_grant:
        'Support often combines mission-aligned grant funding with impact reporting expectations and periodic programme engagement. In practice, funders usually expect a clear delivery narrative, measurable outcomes, and regular progress evidence that links spend to impact results.',
      accelerator:
        'Support usually combines structured mentorship, capability-building sessions, and commercial or investor exposure across a cohort timeline. Founders are typically expected to execute against milestone schedules and demonstrate progress rhythm throughout the programme window.',
      equity_programme:
        'Support is often investment-led, alongside strategic advisory and scale-readiness guidance expected by growth investors. Teams are commonly expected to maintain investor-grade reporting discipline and clear scale execution priorities.',
      venture_debt:
        'Support is typically financing-led, with emphasis on facility structure, disciplined capital deployment, and lender monitoring. In practice, stronger applicants present clear repayment planning and downside safeguards before committing to facility drawdown.',
      revenue_based_financing:
        'Support is generally repayment-linked to business performance, with periodic financial reporting and execution tracking. Businesses usually need consistent operating cadence and transparent revenue-quality monitoring to keep this route sustainable.',
      debt_financing:
        'Support is commonly debt-capital access with underwriting review, repayment structure clarity, and use-of-funds discipline. Approval quality generally improves when founders can show clear spend governance and realistic repayment assumptions.',
      pilot_funding:
        'Support is usually tied to real-world deployment milestones, practical validation checkpoints, and measurable pilot outcomes. Sponsors typically expect named implementation owners, objective KPI design, and a credible evidence plan for pilot value.',
      innovation_challenge:
        'Support is challenge-led, where funding is linked to solving a defined sponsor problem with practical delivery evidence. Teams are usually expected to map each challenge criterion to concrete delivery activities and measurable outcomes.',
      prize:
        'Support is typically award-style funding with visibility benefits and, in some programmes, follow-on ecosystem access. Stronger candidates usually combine differentiation narrative with tangible evidence that outcomes can be demonstrated within judging timelines.',
    }[opportunity.fundingType];

    const capitalTermsSnapshot = {
      foundation_grant:
        'Usually non-dilutive and tied to outcome accountability. Stronger cases show clear impact logic with realistic delivery plans and practical reporting readiness before application.',
      accelerator:
        'Capital can vary by programme, while value often comes from structured support and network access rather than large direct funding. Founders should assess programme intensity, reporting commitments, and expected milestone cadence before applying.',
      equity_programme:
        'Usually investment-linked or dilutive, so founders should expect scrutiny on growth trajectory, governance quality, and scaling discipline. A practical diligence step is confirming acceptable dilution, investor expectations, and use-of-funds priorities in advance.',
      venture_debt:
        'Debt-like capital with repayment obligations. Lenders typically assess downside resilience and disciplined use of proceeds, so repayment headroom and scenario planning should be prepared before commitment.',
      revenue_based_financing:
        'Repayment usually scales with revenue performance, so visibility on sales consistency and margin durability matters materially. Founders should pressure-test repayment sensitivity under slower growth and protect operating runway.',
      debt_financing:
        'Repayment-focused financing where cashflow strength and use-of-funds clarity are usually central to underwriting decisions. Stronger applications usually provide disciplined capital governance and a realistic repayment pathway.',
      pilot_funding:
        'Commonly non-dilutive pilot support tied to milestone delivery and tangible evidence of deployment value. Before applying, define decision KPIs and milestone acceptance criteria to improve sponsor confidence.',
      innovation_challenge:
        'Funding is usually tied to challenge criteria and practical problem-fit, not broad grant-style eligibility framing. A practical diligence step is mapping each requirement to your delivery plan and evidence pathway.',
      prize:
        'Usually non-dilutive award capital, often released against judging outcomes and programme-specific criteria. Founders should validate judging standards early and prepare proof points that demonstrate differentiated outcomes.',
    }[opportunity.fundingType];

    const riskWatchoutNote = {
      foundation_grant:
        'Risk increases if impact claims are broad but measurement methods and beneficiary outcomes are not sufficiently concrete. Mitigation: define one measurable impact KPI, one evidence source, and one accountable owner before submission.',
      accelerator:
        'A common risk is underestimating programme intensity and not showing enough founder bandwidth to execute quickly. Mitigation: assign execution ownership and a milestone cadence that demonstrates operational commitment.',
      equity_programme:
        'A key risk is pursuing this route before investor-grade traction and scale narrative are mature enough for diligence. Mitigation: strengthen core traction proof and clarify the scale plan before prioritizing this pathway.',
      venture_debt:
        'A key risk is taking debt-like capital without clear downside planning for repayment under slower growth conditions. Mitigation: document repayment sensitivity, downside triggers, and capital controls before commitment.',
      revenue_based_financing:
        'A common risk is committing too early before revenue consistency can reliably support repayment-linked obligations. Mitigation: validate revenue quality and repayment headroom under conservative performance assumptions.',
      debt_financing:
        'A key risk is weak repayment visibility if capital objectives are broad and short-term cashflow planning is thin. Mitigation: tighten use-of-funds scope and define a clear repayment plan with accountability.',
      pilot_funding:
        'Risk increases when deployment milestones and success KPIs are not explicit enough for sponsor validation. Mitigation: set a concrete deployment timeline, measurable KPI thresholds, and named implementation owner.',
      innovation_challenge:
        'A common risk is weak challenge-specific fit, where the solution is credible in general but not tightly matched to the brief. Mitigation: explicitly map your solution to challenge criteria and evidence of practical delivery.',
      prize:
        'A key risk is limited differentiation evidence, especially when judges evaluate applied outcomes within a short timeline. Mitigation: prepare concise proof points that demonstrate distinct value and outcome credibility.',
    }[opportunity.fundingType];

    push(opportunity.bucket, {
      id: opportunity.id,
      name: opportunity.name,
      provider: opportunity.provider,
      fundingType: opportunity.fundingType,
      isGovernmentGrant: false,
      opportunityCategoryLabel: 'Independent Funding Pathway',
      subtype: opportunity.fundingType,
      score,
      confidence,
      reasons: reasons.length ? reasons : ['Profile-level fit signals indicate relevance for this pathway.'],
      blockers: [],
      minFundingAmount: null,
      maxFundingAmount: null,
      currency: 'SGD',
      timelineEstimate: 'Varies by programme',
      applicationComplexity: 'medium',
      sourceNotes: 'Client fallback routing (private assessment compatibility mode).',
      whatThisGrantIsFor: opportunity.whatThisGrantIsFor,
      why_it_matches: whyMatch,
      what_to_expect: whatToExpect,
      typicalSupportStructure,
      capitalTermsSnapshot,
      riskWatchoutNote,
    });
  }

  for (const bucket of [
    'additional_funding_opportunities',
    'growth_and_investment_opportunities',
    'financing_options',
  ]) {
    grouped[bucket].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }

  const testedByType = {
    grant: grouped.recommended_grants.length,
    foundation_grant: grouped.additional_funding_opportunities.filter((x) => x.fundingType === 'foundation_grant').length,
    accelerator: grouped.growth_and_investment_opportunities.filter((x) => x.fundingType === 'accelerator').length,
    equity_programme: grouped.growth_and_investment_opportunities.filter((x) => x.fundingType === 'equity_programme').length,
    venture_debt: grouped.financing_options.filter((x) => x.fundingType === 'venture_debt').length,
    revenue_based_financing: grouped.financing_options.filter((x) => x.fundingType === 'revenue_based_financing').length,
    debt_financing: grouped.financing_options.filter((x) => x.fundingType === 'debt_financing').length,
    pilot_funding: grouped.additional_funding_opportunities.filter((x) => x.fundingType === 'pilot_funding').length,
    innovation_challenge: grouped.additional_funding_opportunities.filter((x) => x.fundingType === 'innovation_challenge').length,
    prize: grouped.additional_funding_opportunities.filter((x) => x.fundingType === 'prize').length,
  };

  const nonGrantCount =
    grouped.additional_funding_opportunities.length +
    grouped.growth_and_investment_opportunities.length +
    grouped.financing_options.length;

  return {
    version: 'client-fallback-1',
    grouped_matches: grouped,
    summary: {
      recommended_grants_count: grouped.recommended_grants.length,
      additional_count: grouped.additional_funding_opportunities.length,
      growth_count: grouped.growth_and_investment_opportunities.length,
      financing_count: grouped.financing_options.length,
      tested_counts: {
        total_opportunities: CLIENT_FALLBACK_NON_GOV_OPPORTUNITIES.length + grouped.recommended_grants.length,
        non_grant_opportunities: CLIENT_FALLBACK_NON_GOV_OPPORTUNITIES.length,
        by_funding_type: testedByType,
      },
    },
    note: nonGrantCount > 0
      ? 'Client fallback funding-engine view is active for this result payload.'
      : 'No validated independent funding pathways matched the current profile.',
  };
};

const buildPrivateFundingEngineFromPathways = (pathways = []) => {
  const sentence = (value = '') => {
    const text = String(value || '').trim().replace(/\s+/g, ' ');
    if (!text) return '';
    return /[.!?]$/.test(text) ? text : `${text}.`;
  };

  const getFamilyName = (pathway) => {
    const raw = String(pathway?.program_name || pathway?.name || '').trim();
    if (!raw) return 'Independent Funding Pathway';
    const match = raw.match(/^(.+?)\s[-–:]\s.+$/);
    return match?.[1] ? match[1].trim() : raw;
  };

  const getVariantLabel = (pathway) => {
    const raw = String(pathway?.program_name || pathway?.name || '').trim();
    const family = getFamilyName(pathway);
    if (!raw || raw === family) return raw || family;
    if (raw.startsWith(`${family} - `) || raw.startsWith(`${family} – `) || raw.startsWith(`${family}: `)) {
      return raw.slice(family.length + 3).trim();
    }
    return raw;
  };

  const buildPrivateWhy = (pathway) => {
    const lines = [];
    if (pathway.why_shown) lines.push(sentence(pathway.why_shown));
    if (Array.isArray(pathway.matched_criteria) && pathway.matched_criteria.length > 0) {
      lines.push(`Key fit signals: ${pathway.matched_criteria.slice(0, 3).map((item) => sentence(item)).join(' ')}`);
    }
    return lines.filter(Boolean).join(' ');
  };

  const buildPrivateExpectation = (pathway) => {
    const lines = [];
    if (pathway.funding_amount_text) {
      lines.push(`Support format: ${sentence(pathway.funding_amount_text)}`);
    }
    if (pathway.availability_status) {
      lines.push(`Current availability: ${formatAvailabilityLabel(pathway.availability_status)}.`);
    }
    if (pathway.application_route) {
      lines.push(`Application route: ${formatDisplayLabel(pathway.application_route)}.`);
    }
    if (Array.isArray(pathway.requirements) && pathway.requirements.length > 0) {
      lines.push(`Important requirements: ${pathway.requirements.slice(0, 3).map((item) => sentence(item)).join(' ')}`);
    }
    if (Array.isArray(pathway.unknown_criteria) && pathway.unknown_criteria.length > 0) {
      lines.push(`Needs verification: ${pathway.unknown_criteria.slice(0, 2).map((item) => sentence(item)).join(' ')}`);
    }
    if (pathway.conditional_fit_reason) {
      lines.push(sentence(pathway.conditional_fit_reason));
    }
    return lines.filter(Boolean).join(' ');
  };

  const buildPrivateSupport = (pathway) => {
    const lines = [];
    if (pathway.singapore_eligibility_basis) {
      lines.push(`Singapore applicability: ${String(pathway.singapore_eligibility_basis).trim()}`);
    }
    if (pathway.program_scope) {
      lines.push(`Programme scope: ${formatDisplayLabel(pathway.program_scope)}.`);
    }
    if (pathway.startup_only) {
      lines.push('This programme is intended for startup-stage or clearly early-stage venture profiles.');
    }
    return lines.filter(Boolean).join(' ');
  };

  const getIndependentPathwayDisplayName = (familyName, pathway) => {
    const rawName = String(pathway?.program_name || '').trim();
    const source = `${familyName} ${rawName}`.toLowerCase();
    if (source.includes('seeding the future')) {
      return 'Seeding The Future Global Food System Challenge';
    }
    return familyName || rawName || 'Independent Funding Pathway';
  };

  const buildPrivateProgrammeBackground = (pathway, familyName, sortedVariants = []) => {
    const displayName = getIndependentPathwayDisplayName(familyName, pathway);
    const independentPathwayDescriptions = [
      {
        match: /cartier women'?s initiative/i,
        text:
          "The Cartier Women's Initiative is a global entrepreneurship programme supporting women-led and women-owned businesses that combine commercial growth with measurable social or environmental impact. Its regional and thematic awards cover ventures across sectors, while its science and technology pathway recognises businesses built around substantial scientific or technological innovation. Singapore-based companies can participate within the programme's global applicant pool, making it relevant to women entrepreneurs in Singapore with an established impact-driven business and evidence of traction. For the Regional Awards, the programme looks beyond an early idea: applicants are expected to demonstrate revenue, team capacity and a credible ability to scale their impact.",
      },
      {
        match: /climate curve prize/i,
        text:
          "The Climate Curve Prize: Methane is a global, non-dilutive climate innovation prize supporting solutions that can materially reduce methane emissions across food, agriculture and waste systems. It is intended for active projects with evidence behind the proposed solution rather than untested concepts, with applicants expected to demonstrate measurable emissions-reduction potential and a credible path to functional and financial scale. Singapore-based climate-tech, agri-food, waste-management and circular-economy companies can be relevant because the prize operates globally, but the strongest fit is for businesses that can quantify actual methane mitigation rather than simply demonstrating a broad sustainability benefit.",
      },
      {
        match: /dbs foundation grant/i,
        text:
          "DBS Foundation provides non-dilutive funding and capability support to social enterprises and SMEs using commercially sustainable business models to create measurable social impact. Its funding pathways include the DBS Foundation Grant and related initiatives such as the Impact Beyond Award, with different programmes targeting areas including essential needs, financial inclusion and solutions for ageing societies. Singapore businesses have particularly direct relevance because Singapore is explicitly one of the markets accepted by DBS Foundation, rather than being included only through a generic global eligibility rule. This pathway is strongest for businesses where impact is embedded in the core business model; conventional SMEs with standalone CSR activities are unlikely to demonstrate the same programme fit.",
      },
      {
        match: /grantham foundation detox awards/i,
        text:
          "The Grantham Foundation Detox Awards are a non-dilutive deep-science and sustainability funding opportunity supporting breakthrough technologies designed to reduce toxicity across agriculture, food systems, packaging and related environmental systems. The programme sits within the Future Fit Asia ecosystem and is particularly suited to research-intensive companies and teams developing defensible technologies with potential for real-world deployment. The pathway has strong relevance to Singapore's food-tech, biotech and deep-tech ecosystem: the 2026 awards were presented at Future Fit Asia in Singapore and included a team from the National University of Singapore among the winners. It should therefore be viewed as a specialist innovation opportunity rather than a general sustainability grant for SMEs.",
      },
      {
        match: /halcyon equitytech fellowship/i,
        text:
          "The Halcyon EquityTech Fellowship is a global accelerator and fellowship for for-profit technology startups developing scalable solutions in areas such as EdTech and the Future of Work. It is designed for companies beyond the concept stage: applicants are expected to have a working MVP and evidence of early validation through users, pilots, letters of intent or revenue, alongside a credible path to scale and impact. Singapore founders are explicitly within scope because the programme accepts companies globally, although participation requires commitment to both virtual programming and in-person residency periods in the United States. This makes it most relevant to Singapore startups that already have early traction and can commit founder time to the full international programme.",
      },
      {
        match: /hello tomorrow global challenge/i,
        text:
          "The Hello Tomorrow Global Challenge is a major international competition for early-stage deep-tech companies turning scientific discoveries, novel technologies or complex engineering into commercially viable businesses. Strong candidates typically have scientific validation, a proof of concept or prototype and a technology capable of producing substantial industry, societal or environmental impact. Singapore deep-tech startups are directly relevant to the global challenge, and Singapore ranked second globally on a per-capita basis in Hello Tomorrow's 2026 Deep Tech Pioneers analysis, demonstrating the strength of the country's representation within its ecosystem. The opportunity is therefore particularly relevant to Singapore ventures in areas such as biotech, advanced materials, climate technology, robotics, energy and other science-led fields, rather than conventional software or service businesses.",
      },
      {
        match: /keeling curve prize/i,
        text:
          "The Keeling Curve Prize is a global climate award supporting active solutions capable of reducing, avoiding or removing greenhouse-gas emissions at meaningful scale. It spans areas including carbon sinks, energy, finance, transportation and wider pathways to decarbonisation, with applicants expected to demonstrate more than an early idea: the project must be active and provide a credible basis for quantifying emissions impact and scalability. Singapore-based companies and organisations can participate because applications are welcomed from projects around the world where funding can legally be distributed. For Singapore climate-tech businesses, the strongest cases will therefore be those with measurable decarbonisation outcomes and evidence that the solution can scale beyond a local pilot.",
      },
      {
        match: /milken[-– ]motsepe prize in circular economy/i,
        text:
          "The Milken-Motsepe Prize in Circular Economy is a global innovation competition for established, technology-enabled companies building commercially viable circular-economy solutions with measurable impact in Africa. It covers areas including agriculture and food systems, plastics and packaging, electronics, textiles, and construction, with assessment focused on commercial viability, scalability, circularity, environmental impact and workforce outcomes. A Singapore company can participate, but being Singapore-based alone is not sufficient: the current criteria require the solution to already be deployed on the African continent, alongside operating history, commercial traction, partnerships and evidence of social impact and job creation. This makes it especially relevant to Singapore companies already expanding into or operating within African markets, rather than companies merely considering Africa as a future market.",
      },
      {
        match: /seeding the future global food system challenge/i,
        text:
          "The Seeding The Future Global Food System Challenge is a global, non-dilutive funding competition for innovations capable of creating safer, more nutritious, sustainable and equitable food systems. Its Seed Grant, Growth Grant and Grand Prize tracks collectively support solutions at different levels of maturity, allowing promising innovations to progress from proof of concept toward greater validation and scale. Singapore-based startups and companies can be directly relevant because early-stage and emerging for-profit businesses from any eligible country can participate. For Singapore's agri-food, food-tech, alternative-protein and food-system innovation sectors, fit is strongest where the business can demonstrate both a meaningful food-system outcome and a credible pathway from innovation to practical adoption and commercial scale.",
      },
      {
        match: /shell gamechanger/i,
        text:
          "Shell GameChanger is an early-stage technology innovation programme that works with startups developing unproven technologies and solutions with potential relevance to energy, sustainability and Shell's wider business challenges. Selected companies can receive non-dilutive seed funding alongside access to Shell expertise and support in progressing technology toward proof of concept and commercial validation. Singapore-based startups can be relevant when a global GameChanger call matches their technology, particularly in areas such as energy transition, industrial technology, sustainability, circularity and related digital solutions. It should not be treated as a general startup grant: opportunities are typically driven by specific technical or business challenges, so alignment with the active call is a major part of programme fit.",
      },
      {
        match: /techcrunch startup battlefield 200/i,
        text:
          "TechCrunch Startup Battlefield 200 is a global startup competition and showcase for ambitious early-stage companies with an MVP and strong potential to create a significant new product, market or category. Selected startups gain exposure through TechCrunch Disrupt, founder programming, investor access and opportunities to pitch, with the leading companies competing for equity-free prize funding. Singapore startups are eligible on the same global basis as companies from other markets, with TechCrunch accepting applications across industries and geographies. It is particularly relevant to Singapore companies at the pre-seed or seed stage that already have a demonstrable product and are seeking international investor exposure, media visibility and access to the US technology ecosystem, not simply businesses looking for grant capital.",
      },
      {
        match: /the earthshot prize/i,
        text:
          "The Earthshot Prize is a global environmental prize designed to identify and accelerate proven solutions capable of producing significant environmental impact at scale across five areas: protecting nature, cleaning the air, reviving oceans, building a waste-free world and fixing the climate. Candidates need more than an early concept: the Prize looks for solutions with proven prototypes, signs of success and strong potential to scale, with five winners ultimately receiving £1 million each and finalists entering the Earthshot Fellowship. Singapore-based companies and innovators can be considered within the Prize's global search, but this is not a conventional open grant application: candidates are identified through Earthshot's network of Official Nominators. For Singapore companies, relevance is therefore strongest when the solution already has demonstrable environmental outcomes and sufficient maturity to support large-scale adoption.",
      },
    ];
    for (const item of independentPathwayDescriptions) {
      if (item.match.test(displayName)) {
        return item.text;
      }
    }

    const sourceText = String([
      pathway.summary,
      pathway.eligibility_summary,
      pathway.key_filters,
      pathway.analyst_evidence_note,
      pathway.program_type,
      pathway.pathway_type,
    ]
      .filter(Boolean)
      .join(' '))
      .toLowerCase();

    const stageTags = Array.isArray(pathway.target_stage_tags)
      ? pathway.target_stage_tags.map((item) => formatDisplayLabel(String(item || '').replace(/_/g, ' '))).filter(Boolean)
      : [];
    const sectorTags = Array.isArray(pathway.sector_tags)
      ? pathway.sector_tags.map((item) => formatDisplayLabel(String(item || '').replace(/_/g, ' '))).filter(Boolean)
      : [];
    const objectiveTags = Array.isArray(pathway.objective_tags)
      ? pathway.objective_tags.map((item) => formatDisplayLabel(String(item || '').replace(/_/g, ' '))).filter(Boolean)
      : [];

    const inferredAudience = [];
    const addAudience = (value) => {
      const label = String(value || '').trim();
      if (label && !inferredAudience.includes(label)) inferredAudience.push(label);
    };

    stageTags.slice(0, 2).forEach(addAudience);
    sectorTags.slice(0, 2).forEach(addAudience);
    objectiveTags.slice(0, 2).forEach(addAudience);

    if (inferredAudience.length === 0) {
      if (/(startup|early-stage|early stage|seed|prototype|proof of concept|proof-of-concept|poc|mvp)/i.test(sourceText)) {
        addAudience('startups and early-stage ventures');
      }
      if (/(growth|scale|scaleup|validated|commerciali[sz]ation)/i.test(sourceText)) {
        addAudience('emerging companies preparing to scale');
      }
      if (/(deep tech|science|engineering)/i.test(sourceText)) {
        addAudience('science-led and technically ambitious companies');
      }
      if (/(food systems|agri|agriculture|nutrition|food)/i.test(sourceText)) {
        addAudience('food systems and agri-food innovators');
      }
      if (/(climate|carbon|energy|decarbon|methane|circular|waste)/i.test(sourceText)) {
        addAudience('climate, energy, and circular-economy solutions');
      }
      if (/(edtech|future of work|workforce|learning|education)/i.test(sourceText)) {
        addAudience('education, workforce, and future-of-work ventures');
      }
      if (/(impact|social|environmental|women-led|women owned)/i.test(sourceText)) {
        addAudience('mission-led founders and impact ventures');
      }
    }

    const trackLabels = Array.from(new Set(sortedVariants.map((variant) => getVariantLabel(variant)).filter(Boolean)));
    const statusLabel = formatAvailabilityLabel(pathway.availability_status || '');
    const geographyLabel = String(pathway.singapore_eligibility_basis || '').trim();
    const sourceNote = String(pathway.eligibility_summary || pathway.key_filters || pathway.analyst_evidence_note || '').trim();
    const isChallengeStyle = /(challenge|prize|award)/i.test(displayName);
    const isFellowshipStyle = /(fellowship|accelerator)/i.test(displayName);

    const paragraphOneParts = [];
    if (displayName) {
      if (isChallengeStyle) {
        paragraphOneParts.push(`${displayName} is a private, non-dilutive challenge or award route.`);
      } else if (isFellowshipStyle) {
        paragraphOneParts.push(`${displayName} is a private, non-dilutive fellowship or accelerator-style pathway.`);
      } else {
        paragraphOneParts.push(`${displayName} is a private, non-dilutive funding pathway.`);
      }
    }
    if (inferredAudience.length > 0) {
      paragraphOneParts.push(`It is most relevant to ${inferredAudience.slice(0, 3).join(', ')}.`);
    }
    if (trackLabels.length > 1) {
      paragraphOneParts.push(`The wider programme brings together ${trackLabels.slice(0, 3).join(', ')} as related tracks.`);
    } else if (trackLabels.length === 1 && trackLabels[0] && trackLabels[0] !== displayName) {
      paragraphOneParts.push(`The current card reflects the ${trackLabels[0]} track within the wider programme.`);
    }
    if (pathway.program_scope) {
      const scopeLabel = formatDisplayLabel(pathway.program_scope).toLowerCase();
      if (scopeLabel === 'global') {
        paragraphOneParts.push('The programme has global reach.');
      } else if (scopeLabel === 'regional') {
        paragraphOneParts.push('The programme has regional reach.');
      } else if (scopeLabel === 'singapore') {
        paragraphOneParts.push('The programme is centred on Singapore.');
      } else {
        paragraphOneParts.push(`The programme has ${scopeLabel} reach.`);
      }
    }

    const paragraphTwoParts = [];
    if (sourceNote) {
      paragraphTwoParts.push(sentence(sourceNote));
    }
    if (statusLabel) {
      const statusSentence = {
        Open: 'Applications are currently open.',
        Rolling: 'Applications are accepted on a rolling basis.',
        'Rolling / Open': 'Applications are currently open and may also be accepted on a rolling basis.',
        'Closed Current Cycle': 'The current application cycle is closed.',
        Closed: 'The opportunity is currently closed.',
        'Closed, Recurrence Unconfirmed': 'The current cycle is closed and a future return has not been confirmed.',
        'Selection In Progress': 'Selection is currently in progress.',
        'Nomination Required': 'A nomination is required before application.',
        'Availability Unconfirmed': 'Current availability could not be verified from the imported catalogue.',
      }[statusLabel] || `Current availability is ${statusLabel.toLowerCase()}.`;
      paragraphTwoParts.push(statusSentence);
    }
    if (geographyLabel) {
      const geographyLower = geographyLabel.toLowerCase();
      if (geographyLower.includes('no singapore-specific exclusion')) {
        paragraphTwoParts.push('No Singapore-specific exclusion has been identified in the current programme data.');
      } else if (geographyLower.includes('global') && geographyLower.includes('singapore')) {
        paragraphTwoParts.push('Singapore applicants appear to fall within the programme’s stated international scope.');
      } else {
        paragraphTwoParts.push(geographyLabel.endsWith('.') ? geographyLabel : `${geographyLabel}.`);
      }
    }
    if (!sourceNote) {
      paragraphTwoParts.push("The current catalogue does not include a verified beneficiary or alumni dataset, so the assessment is grounded in published scope and eligibility signals rather than historical award patterns.");
    }

    return [paragraphOneParts.join(' '), paragraphTwoParts.join(' ')].filter(Boolean).join('\n\n');
  };

  const buildPrivateCapitalTerms = (pathway) => {
    const lines = [];
    if (pathway.funding_amount_text) {
      lines.push(`Funding structure: ${String(pathway.funding_amount_text).trim()}`);
    }
    if (pathway.currency) {
      lines.push(`Primary currency: ${String(pathway.currency).trim()}.`);
    }
    if (pathway.requires_nomination) {
      lines.push('This route is nomination-based rather than a standard direct application.');
    }
    return lines.filter(Boolean).join(' ');
  };

  const buildPrivateRisk = (pathway) => {
    const lines = [];
    if (Array.isArray(pathway.unmet_criteria) && pathway.unmet_criteria.length > 0) {
      lines.push(`Known gaps: ${pathway.unmet_criteria.slice(0, 2).map((item) => sentence(item)).join(' ')}`);
    }
    if (Array.isArray(pathway.unknown_criteria) && pathway.unknown_criteria.length > 0) {
      lines.push(`Unconfirmed criteria: ${pathway.unknown_criteria.slice(0, 2).map((item) => sentence(item)).join(' ')}`);
    }
    return lines.filter(Boolean).join(' ');
  };

  const groupedPathways = Array.isArray(pathways)
    ? Array.from(
        pathways.reduce((map, pathway) => {
          const family = getFamilyName(pathway);
          const list = map.get(family) || [];
          list.push(pathway);
          map.set(family, list);
          return map;
        }, new Map()).values()
      )
    : [];

  const items = groupedPathways.map((familyGroup) => {
    const sortedVariants = [...familyGroup].sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0));
    const primary = sortedVariants[0] || {};
    const familyName = getFamilyName(primary);
    const variantNames = sortedVariants.map((variant) => variant?.program_name || variant?.name).filter(Boolean);
    const variantSummaryLine =
      sortedVariants.length > 1
        ? `Included tracks: ${sortedVariants.map((variant) => `${getVariantLabel(variant)} (${variant?.score ?? 0}%)`).join('; ')}.`
        : '';
    const variantDifferentiation =
      sortedVariants.length > 1
        ? sortedVariants
            .map((variant) => {
              const label = getVariantLabel(variant);
              const summary = String(variant?.summary || variant?.why_shown || '').trim();
              return summary ? `${label}: ${sentence(summary)}` : `${label}.`;
            })
            .join(' ')
        : '';
    const allMatched = sortedVariants.flatMap((variant) => variant?.matched_criteria || []).filter(Boolean);
    const allUnmet = sortedVariants.flatMap((variant) => variant?.unmet_criteria || []).filter(Boolean);
    const allUnknown = sortedVariants.flatMap((variant) => variant?.unknown_criteria || []).filter(Boolean);
    const allRequirements = sortedVariants.flatMap((variant) => variant?.requirements || []).filter(Boolean);
    const allNextSteps = sortedVariants.flatMap((variant) => variant?.next_steps || []).filter(Boolean);
    const allCitations = sortedVariants.flatMap((variant) => variant?.citations || []).filter(Boolean);
    const distinctFundingFormats = Array.from(new Set(sortedVariants.map((variant) => String(variant?.funding_amount_text || '').trim()).filter(Boolean)));
    const distinctAvailability = Array.from(new Set(sortedVariants.map((variant) => String(variant?.availability_status || '').trim()).filter(Boolean)));
    const whatThisOpportunityIsFor = buildPrivateProgrammeBackground(primary, familyName, sortedVariants);

    return {
        id: primary.program_id || primary.id || familyName,
        name: familyName,
        provider: primary.administering_body || primary.provider || 'Independent programme',
        fundingType: 'private_non_dilutive',
        opportunityCategoryLabel: sortedVariants.length > 1 ? 'Independent pathway family' : formatDisplayLabel(primary.pathway_type || 'Private Non-Dilutive'),
        isGovernmentGrant: false,
        subtype: primary.pathway_type || primary.layer_type || 'private_non_dilutive',
        score: primary.score ?? 0,
        confidence: primary.confidence || primary.confidence_score || 'medium',
        reasons: [
          ...(primary.why_shown ? [primary.why_shown] : []),
          ...(variantSummaryLine ? [variantSummaryLine] : []),
          ...allMatched.slice(0, 3),
        ].filter(Boolean),
        blockers: allUnmet,
        matched_criteria: allMatched,
        unmet_criteria: allUnmet,
        unknown_criteria: allUnknown,
        requirements: allRequirements,
        next_steps: allNextSteps,
        minFundingAmount: primary.cash_amount_max || null,
        maxFundingAmount: primary.funding_amount_max || primary.cash_amount_max || null,
        currency: primary.currency || '',
        timelineEstimate: formatAvailabilityLabel(primary.availability_status) || 'Varies by programme',
        applicationComplexity: 'medium',
        sourceNotes: primary.summary || '',
        whatThisGrantIsFor: whatThisOpportunityIsFor,
        why_it_matches: [
          buildPrivateWhy(primary),
          variantDifferentiation ? `Track differentiation: ${variantDifferentiation}` : '',
        ].filter(Boolean).join(' '),
        what_to_expect: [
          distinctFundingFormats.length > 0 ? `Support scope differs by track: ${distinctFundingFormats.slice(0, 3).join('; ')}.` : '',
          distinctAvailability.length > 1 ? `Availability varies across the family: ${distinctAvailability.map((status) => formatAvailabilityLabel(status)).join(', ')}.` : buildPrivateExpectation(primary),
        ].filter(Boolean).join(' '),
        typicalSupportStructure: [
          buildPrivateSupport(primary),
          sortedVariants.length > 1 ? `This family contains ${sortedVariants.length} related tracks aimed at different maturity levels or application outcomes.` : '',
        ].filter(Boolean).join(' '),
        capitalTermsSnapshot: buildPrivateCapitalTerms(primary),
        riskWatchoutNote: buildPrivateRisk(primary),
        official_url: primary.official_url || '',
        citations: allCitations,
        funding_amount_text: primary.funding_amount_text || '',
        application_route: primary.application_route || '',
        availability_status: primary.availability_status || '',
        eligibility_status: primary.eligibility_status || '',
        program_scope: primary.program_scope || '',
        startup_only: sortedVariants.some((variant) => Boolean(variant?.startup_only)),
        knownProgrammes: variantNames,
        known_programmes: variantNames,
        knownProgrammesSource: variantNames.length > 0 ? 'verified_catalogue' : '',
        known_programmes_source: variantNames.length > 0 ? 'verified_catalogue' : '',
        family_variants: sortedVariants.map((variant) => ({
          name: variant.program_name || variant.name || familyName,
          label: getVariantLabel(variant),
          score: variant.score ?? 0,
          availability_status: variant.availability_status || '',
          summary: variant.summary || '',
        })),
      };
    });

  return {
    version: 'private-nondilutive-1',
    grouped_matches: {
      recommended_grants: [],
      additional_funding_opportunities: items,
      growth_and_investment_opportunities: [],
      financing_options: [],
    },
    summary: {
      recommended_grants_count: 0,
      additional_count: items.length,
      growth_count: 0,
      financing_count: 0,
      tested_counts: {
        total_opportunities: items.length,
        non_grant_opportunities: items.length,
        by_funding_type: { private_non_dilutive: items.length },
      },
    },
    note:
      items.length > 0
        ? 'Private non-dilutive funding pathways were evaluated separately from Singapore government grants.'
        : 'No private non-dilutive pathways were surfaced for this profile.',
  };
};

const hashString32 = (input = '') => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36).toUpperCase();
};

// Score-only badge tiers (hard ineligible uses fitBadgeForGrant). Aligned with the
// backend fit bands so UI buckets match the labels the engine emits:
// Eligible = Strong/High (>=72), Likely = Promising/Medium (60–71),
// Less likely = Partial/Low (45–59), Weak fit = Weak (<45).
const TIER_ELIGIBLE_MIN = FIT_BAND_STRONG_MIN;
const TIER_LIKELY_MIN = FIT_BAND_PROMISING_MIN;
const TIER_LESS_LIKELY_MIN = FIT_BAND_PARTIAL_MIN;
const TIER_WEAK_MIN = 1;
export const RESULT_TIER_BANDS = Object.freeze({
  eligibleMin: TIER_ELIGIBLE_MIN,
  likelyMin: TIER_LIKELY_MIN,
  lessLikelyMin: TIER_LESS_LIKELY_MIN,
  weakMin: TIER_WEAK_MIN,
});

/** Pure band resolver (score → UI tier); exported so tests can assert the contract buckets. */
// eslint-disable-next-line react-refresh/only-export-components
export const resolveScoreTier = (score) => {
  const n = Number(score);
  if (!Number.isFinite(n)) return 'weakFit';
  if (n >= TIER_ELIGIBLE_MIN) return 'eligible';
  if (n >= TIER_LIKELY_MIN) return 'likely';
  if (n >= TIER_LESS_LIKELY_MIN) return 'lessLikely';
  return 'weakFit';
};

const getFitBadge = (score) => {
  const s = Math.min(100, Math.max(0, score));
  if (s >= TIER_ELIGIBLE_MIN) return { label: 'Eligible', color: 'bg-blue-500/20 border-blue-400/30', icon: CheckCircle, textColor: '#60A5FA' };
  if (s >= TIER_LIKELY_MIN) return { label: 'Likely', color: 'bg-sky-500/20 border-sky-400/30', icon: CheckCircle, textColor: '#38BDF8' };
  if (s >= TIER_LESS_LIKELY_MIN) return { label: 'Less likely', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30', icon: AlertTriangle };
  if (s >= TIER_WEAK_MIN) return { label: 'Weak fit', color: 'bg-amber-500/15 text-amber-200 border-amber-400/25', icon: AlertTriangle };
  return { label: 'Weak fit', color: 'bg-amber-500/15 text-amber-200 border-amber-400/25', icon: AlertTriangle };
};

/** Backend: eligibility_status is only "ineligible" for mandatory hard-rule failures; display_label mirrors that. */
const isHardIneligibleGrant = (grant) => {
  if (!grant || typeof grant !== 'object') return false;
  const st = String(grant.eligibilityStatus || grant.eligibility_status || '').toLowerCase();
  if (st === 'ineligible') return true;
  const dl = String(grant.displayLabel || grant.display_label || '');
  return dl.toLowerCase().includes('not eligible');
};

const fitBadgeForGrant = (grant, score0to100) => {
  if (isHardIneligibleGrant(grant)) {
    return { label: 'Not eligible', color: 'bg-red-500/20 text-red-300 border-red-400/30', icon: XCircle };
  }
  return getFitBadge(score0to100);
};

const _getOverallStatus = (grants, cutoff) => {
  const visibleGrants = grants.filter(grant => grant.score >= cutoff);
  if (visibleGrants.length === 0) return 'ineligible';
  if (visibleGrants.some(grant => grant.score >= 80)) return 'eligible';
  return 'pending';
};

/** Admin view: whose results these are, and the way back to that client's page. */
function AdminViewingBanner({ targetClient, backPath }) {
  const name = String(targetClient?.name || targetClient?.email || '').trim();
  return (
    <div
      className="admin-viewing-banner mb-5 flex flex-col gap-2 rounded-2xl border bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      style={{ borderColor: '#BFDBFE' }}
      data-testid="admin-viewing-banner"
      role="note"
    >
      <p className="text-sm min-w-0" style={{ color: '#1F2937' }}>
        <span className="font-semibold">
          {name ? `Viewing ${name}'s results` : "Viewing a client's results"}
        </span>
        {targetClient?.email && targetClient?.name ? (
          <span className="break-all" style={{ color: '#6B7280' }}>{` · ${targetClient.email}`}</span>
        ) : null}
        <span className="block text-xs" style={{ color: '#6B7280' }}>
          Read-only admin view. Nothing you do here changes the client&apos;s record.
        </span>
      </p>
      <Link
        to={backPath}
        className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium"
        style={{ color: '#60A5FA' }}
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
        Back to client
      </Link>
    </div>
  );
}

/** Admin view: the grants the client chose to pursue (from their ops record), read-only. */
function ClientSelectedGrantsReadOnly({ grants, targetClient }) {
  const rows = Array.isArray(grants) ? grants.filter((row) => row && (row.name || row.code)) : [];
  const who = String(targetClient?.name || '').trim() || 'The client';
  return (
    <div
      className="mb-8 rounded-3xl border bg-white p-5 sm:p-8 shadow-xl"
      style={{ borderColor: '#BFDBFE' }}
      data-testid="client-selected-grants"
    >
      <p className="text-sm font-semibold" style={{ color: '#60A5FA' }}>Client&apos;s selection</p>
      <h2 className="mt-1 text-xl font-semibold leading-tight" style={{ color: '#60A5FA' }}>
        Grants selected to pursue
      </h2>
      {rows.length ? (
        <ul className="mt-4 flex flex-wrap gap-2" aria-label="Selected grants">
          {rows.map((row) => {
            const score = Number(row.score);
            return (
              <li
                key={String(row.code || row.name)}
                className="inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-sm"
                style={{ borderColor: '#BFDBFE', color: '#1F2937', backgroundColor: '#FFFFFF' }}
              >
                <span className="min-w-0 break-words font-medium">{row.name || row.code}</span>
                {row.score !== null && row.score !== undefined && Number.isFinite(score) ? (
                  <span className="shrink-0 text-xs font-semibold" style={{ color: '#60A5FA' }}>
                    {Math.round(score)}%
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2 text-sm" style={{ color: '#6B7280' }}>
          {who} has not selected any grants yet.
        </p>
      )}
    </div>
  );
}

/**
 * Full eligibility results.
 * - `embedded`: the signed-in client's own results inside the dashboard shell.
 * - `adminView` (with `embedded`): the admin reading one client's run at
 *   /dashboard/clients/:clientAccountId/assessments/:jobId. Read-only: nothing is saved for the
 *   admin (no grant selection, no history, no scheduling prefill), and `clientSelectedGrants`
 *   (the client's own choice from the overview) is shown instead of the selection controls.
 * - neither: the public /shared/eligibility/:token view of links already sent.
 */
export default function EligibilityResults({
  embedded = false,
  adminView = false,
  targetClient = null,
  clientSelectedGrants = null,
} = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { token: sharedTokenParam, jobId: routeJobIdParam } = useParams();
  const adminJobId = adminView ? String(routeJobIdParam || '').trim() : '';
  const adminClientPath = adminView && targetClient?.id
    ? `/dashboard/clients/${encodeURIComponent(String(targetClient.id))}`
    : '/dashboard';
  const { isLocked, requiredTier, signedIn } = useTierGate();
  const locationState = useMemo(() => location.state || {}, [location.state]);
  const {
    result: locationResult,
    assessmentData: locationAssessmentData,
    formData: locationFormData,
    jobId: locationJobId,
    traceId: locationTraceId,
    clientId: locationClientId,
    scrollToTop: locationScrollToTop,
  } = locationState;
  const [searchParams] = useSearchParams();
  const assessmentEntryPath = '/dashboard/eligibility-assessment';
  // Canonical for the noindex SEO tag: a real route, never the share token.
  const seoUrl = sharedTokenParam ? '/shared/eligibility' : '/dashboard/results';

  // Store data in local state to prevent loss during re-renders
  const [assessmentData, setAssessmentData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isClient, setIsClient] = useState(() => typeof document !== 'undefined');
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [localExpandedGrants, setLocalExpandedGrants] = useState(new Set());
  const [expandedScoreBreakdowns, setExpandedScoreBreakdowns] = useState(new Set());
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [hasSavedHistory, setHasSavedHistory] = useState(false);
  const [customSelectedGrantIds, setCustomSelectedGrantIds] = useState(() => new Set());
  // Set when a stored job turns out failed/expired so the error card can say so.
  const [loadError, setLoadError] = useState('');
  // Inline export feedback (replaces window.alert).
  const [exportError, setExportError] = useState('');
  const exportDialogRef = useRef(null);
  const exportDialogTitleId = 'eligibility-export-dialog-title';
  // Stable per-mount ids so feedback rows from one visit can be correlated.
  const feedbackMountTs = useMemo(() => Date.now(), []);
  // Collapsible section states for lower-priority grants
  const [isLessLikelyExpanded, setIsLessLikelyExpanded] = useState(false);
  const [isWeakFitExpanded, setIsWeakFitExpanded] = useState(false);
  const [isBlockedExpanded, setIsBlockedExpanded] = useState(true);
  const [isIneligibleExpanded, setIsIneligibleExpanded] = useState(false);
  const [isApprovedExpanded, setIsApprovedExpanded] = useState(false);
  const [showLegacySchemes, setShowLegacySchemes] = useState(false);
  const [eligibleSurfaceFilter, setEligibleSurfaceFilter] = useState('all');
  // showDetailedFeedback removed - simplified feedback to thumbs only

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [grantSelectionSaving, setGrantSelectionSaving] = useState(false);
  const [grantSelectionSaved, setGrantSelectionSaved] = useState(false);
  const [grantSelectionError, setGrantSelectionError] = useState("");
  const grantSelectionPreloadedRef = useRef(false);
  const [sharedFetchCompleted, setSharedFetchCompleted] = useState(false);
  const [sharedFetchError, setSharedFetchError] = useState("");
  const [pdfReportMode, setPdfReportMode] = useState('standard_advisory');
  const minimumScoreThreshold = 0;

  const customSelectionCount = customSelectedGrantIds.size;

  const assessmentSource = useMemo(
    () => assessmentData ?? locationResult ?? locationAssessmentData ?? null,
    [assessmentData, locationResult, locationAssessmentData]
  );

  const formSource = useMemo(
    () => formData ?? locationFormData ?? null,
    [formData, locationFormData]
  );

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Capture data from location.state when component mounts
  useEffect(() => {
    if (!isClient) return; // Wait for client-side hydration

    if (DEBUG) {
      console.log('🔍 EligibilityResults mounted/updated:', {
        hasLocationState: !!locationState,
        hasAssessmentData: !!locationAssessmentData,
      currentAssessmentData: !!assessmentData,
      timestamp: new Date().toISOString()
    });
    }
    
    // Handle both 'result' and 'assessmentData' from location.state
    const dataFromState = locationResult || locationAssessmentData;
    
    if (dataFromState && !assessmentData) {
      if (DEBUG) {
        console.log('📥 Capturing assessment data from location.state');
      }
      
      // Set the assessment data directly (no mock creation)
      setAssessmentData(dataFromState);
      setFormData(locationFormData);
      
      // Debug logging for form data (only in DEBUG mode)
      if (DEBUG && locationFormData) {
        const fd = locationFormData;
        console.log('📋 FORM DATA CAPTURED (Results Page):', fd);
        console.log('  - companyName:', fd.companyName || fd.company_name);
        console.log('  - annual_revenue_sgd:', fd.annual_revenue_sgd);
      }
    }
  }, [locationState, locationAssessmentData, locationResult, locationFormData, assessmentData, isClient]);

  useEffect(() => {
    if (!isClient) return;
    const sharedToken = String(sharedTokenParam || '').trim();
    if (!sharedToken) return;
    let mounted = true;
    setSharedFetchCompleted(false);
    setSharedFetchError("");
    (async () => {
      try {
        const shared = await apiService.getSharedAssessmentByToken(sharedToken);
        if (!mounted) return;
        const rawResult = shared?.result;
        const unwrappedResult =
          rawResult && typeof rawResult === "object" && rawResult.result && !rawResult.grants && !rawResult.eligibility_results
            ? rawResult.result
            : rawResult;
        if (unwrappedResult) {
          setAssessmentData(unwrappedResult);
          setFormData(shared.form_data || {});
          setSharedFetchCompleted(true);
          return;
        }
        setSharedFetchError("Shared assessment results are unavailable for this link.");
        setSharedFetchCompleted(true);
      } catch (error) {
        if (mounted) {
          console.error('Failed to fetch shared assessment:', error);
          setSharedFetchError(String(error?.message || "Failed to load shared assessment link."));
          setSharedFetchCompleted(true);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isClient, sharedTokenParam]);

  // Fetch result from API if missing (deep link support)
  useEffect(() => {
    if (!isClient) return;
    
    let alive = true;
    const DEBUG = import.meta.env.VITE_DEBUG_LOGS === 'true';
    
    (async () => {
      // Fetch when data is missing: router-state jobId, then ?job_id= deep link,
      // then the jobId stored on the client-ops record (dashboard refresh recovery).
      let jobId = adminView
        ? adminJobId
        : locationJobId || String(searchParams.get('job_id') || '').trim();
      if (sharedTokenParam) return;
      if (!assessmentSource && !jobId && embedded && !adminView) {
        try {
          const payload = await apiService.getMyClientOpsRecord();
          jobId = String(payload?.record?.eligibility_assessment?.jobId || '').trim();
        } catch {
          // fall through with no jobId
        }
        if (!alive) return;
      }
      if (!assessmentSource && jobId) {
        if (DEBUG) console.log('🔄 Fetching result for jobId:', jobId);
        
        try {
          const statusResponse = await eligibilityAsyncService.getJobStatus(jobId, locationTraceId);
          if (!alive) return;

          if (DEBUG) console.log('✅ Fetched job status:', statusResponse);

          if (isCompletedAssessmentStatus(statusResponse.status) && statusResponse.result) {
            setAssessmentData(statusResponse.result);
            // /status returns the run's input form (form_data) to the owner and the admin.
            setFormData(
              locationFormData
                || (statusResponse.form_data && typeof statusResponse.form_data === 'object' ? statusResponse.form_data : null)
                || {}
            );
          } else if (isErrorAssessmentStatus(statusResponse.status)) {
            console.error('❌ Assessment job failed:', statusResponse.error);
            setLoadError('This assessment did not complete successfully. Please re-run it to get a fresh set of results.');
          } else {
            console.warn('⚠️ Assessment is not completed yet. Current status:', statusResponse.status);
            setLoadError('This assessment has not finished yet. Return to the assessment page to check its progress or re-run it.');
          }
        } catch (e) {
          if (alive) {
            console.error('❌ Failed to fetch result:', e);
            setLoadError('These results could not be loaded. The assessment may have expired or been deleted, so please re-run it.');

            // Send to Sentry
            try {
              const { captureException } = await import('../utils/errorTracking');
              captureException(e, {
                component: 'EligibilityResults',
                action: 'fetchJobStatus',
                jobId: locationJobId,
                traceId: locationTraceId,
                hasAssessmentData: !!assessmentSource
              });
            } catch {
              // Silently fail if Sentry not available
            }
          }
        }
      }
    })();

    return () => { alive = false; };
  }, [assessmentSource, locationJobId, locationTraceId, locationFormData, isClient, sharedTokenParam, searchParams, embedded, adminView, adminJobId]);
  
  useEffect(() => {
    if (!isClient) return;
    forceUnlockDocumentScroll();
    if (locationScrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isClient, locationScrollToTop]);

  useEffect(() => {
    if (!isClient) return undefined;
    forceUnlockDocumentScroll();
    const raf = window.requestAnimationFrame(() => forceUnlockDocumentScroll());
    return () => window.cancelAnimationFrame(raf);
  }, [isClient, location.pathname]);

  // Export dialog behaviour (implemented locally): initial focus, Escape to close, Tab focus
  // trap, body scroll lock while open, and focus restored to the opener on close.
  useEffect(() => {
    if (!isExportModalOpen || typeof document === 'undefined') return undefined;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusableSelector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const raf = window.requestAnimationFrame(() => {
      const dialog = exportDialogRef.current;
      if (!dialog) return;
      const first =
        dialog.querySelector('[role="radio"][aria-checked="true"]') || dialog.querySelector(focusableSelector);
      (first || dialog).focus();
    });
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!isExportingPDF) setIsExportModalOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const dialog = exportDialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(focusableSelector));
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
    };
  }, [isExportModalOpen, isExportingPDF]);
  
  // URL state management - moved after results definition


  const backendBaseUrl = useMemo(() => {
    try {
      const base = getApiBaseUrl();
      // Empty string = dev same-origin + Vite proxy (required for LAN hosts; do not force Render URL).
      if (base === '' || base === null || base === undefined) {
        return typeof base === 'string' ? '' : 'https://optra-backend.onrender.com';
      }
      return String(base).replace(/\/$/, '');
    } catch (error) {
      console.warn('⚠️ Falling back to default backend base URL for PDF export', error);
      return 'https://optra-backend.onrender.com';
    }
  }, []);

  const ensureBackendUrl = useCallback((path = '') => {
    if (!path) return backendBaseUrl;
    if (/^https?:\/\//i.test(path)) return path;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${backendBaseUrl}${normalizedPath}`;
  }, [backendBaseUrl]);

  const resolvedSnapshotId = useMemo(() => {
    if (!assessmentSource || typeof assessmentSource !== 'object') return null;

    const explicitId =
      assessmentSource.snapshot_id ||
      assessmentSource.correlation_id ||
      assessmentSource.trace_id ||
      locationJobId ||
      locationTraceId;
    if (explicitId) return String(explicitId);

    const companyName =
      assessmentSource?.inputs_used?.company_name ||
      assessmentSource?.inputs_used?.companyName ||
      formData?.company_name ||
      formData?.companyName ||
      '';
    const grantsSource =
      (Array.isArray(assessmentSource.grant_assessments) && assessmentSource.grant_assessments) ||
      (Array.isArray(assessmentSource.grants) && assessmentSource.grants) ||
      [];
    const grantKeys =
      grantsSource.length > 0
        ? grantsSource
            .map((grant) => resolveGrantKey(grant) || grant?.name || grant?.displayName || '')
            .filter(Boolean)
        : Object.keys(assessmentSource.eligibility_results || {});
    const generatedAt = assessmentSource.generated_at_sgt || assessmentSource.timestamp || '';
    const fingerprint = `${companyName}|${generatedAt}|${grantKeys.join('|')}`;
    if (!fingerprint.trim()) return null;
    return `SNAP_${hashString32(fingerprint)}`;
  }, [assessmentSource, formData, locationJobId, locationTraceId]);

  // Timeout mechanism
  useEffect(() => {
    if (!assessmentSource) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true);
        if (DEBUG) console.log('⚠️ Loading timeout reached - no assessment data received');
      }, 10000); // 10 second timeout

      return () => clearTimeout(timer);
    }
  }, [assessmentSource]);

  // Create unified results state object
  const results = useMemo(() => {
    try {
      if (!assessmentSource) {
        if (DEBUG) console.log('⚠️ No assessmentSource yet, results = null');
        return null;
      }

      if (DEBUG) {
        if (DEBUG) console.log('🔍 Processing assessment data - Grants:', assessmentSource?.grants?.length || 0);
      }
    
    // Handle comprehensive assessment response format (new backend)
    if (assessmentSource.eligibility_results && typeof assessmentSource.eligibility_results === 'object') {
      if (DEBUG) console.log('🔍 Using comprehensive assessment response structure');
      
      // Extract user inputs from backend response if available
      const backendInputs = assessmentSource.inputs_used || assessmentSource.company || assessmentSource.input_summary || {};
      if (DEBUG) console.log('📥 Backend inputs_used:', backendInputs);
      const annualRevenue = firstInputNumber(
        backendInputs.annual_revenue_sgd,
        backendInputs.annual_revenue,
        backendInputs.annualRevenue,
        formSource?.annual_revenue_sgd,
        formSource?.annual_revenue,
        formSource?.annualRevenue
      ) || 0;
      
      // Transform eligibility_results object to grant_assessments array format.
      // Normalize score (0-100), confidence, and coarse eligibility_status (do not mark low scores as hard ineligible).
      const grants = Object.entries(assessmentSource.eligibility_results).map(([key, grant]) => {
        const rawScore = grant.score != null ? (typeof grant.score === 'number' && grant.score <= 1 ? grant.score * 100 : Number(grant.score)) : 0;
        const score0to100 = Math.min(100, Math.max(0, rawScore));
        const rawConf = grant.confidence != null ? (typeof grant.confidence === 'number' && grant.confidence <= 1 ? grant.confidence * 100 : Number(grant.confidence)) : score0to100;
        const confidence0to100 = Math.min(100, Math.max(0, rawConf));
        const eligible = grant.eligible === true;
        let eligibilityStatus = 'borderline';
        if (eligible) eligibilityStatus = 'eligible';
        return {
          key: key,
          display_name: key,
          name: grant.name || key,
          acronym: grant.acronym || key,
          status: grant.eligible ? 'eligible' : 'not_eligible',
          eligibilityStatus,
          eligibility_status: eligibilityStatus,
          composite_score: score0to100 / 100,
          score: score0to100,
          scorePct: score0to100,
          confidence: confidence0to100,
          certainty: confidence0to100 / 100,
          scoring_breakdown: { overall: grant.score || 0 },
          rules_fired: grant.reasons || [],
          disqualifiers_triggered: [],
          missing_information: grant.missing || [],
          tailored_recommendations: {
            documents_to_prepare: [],
            next_steps: grant.next_steps || [],
            risk_mitigation: [],
            timeline_considerations: [],
            budget_tips: []
          },
          official_references: []
        };
      });

      return {
        snapshotId: resolvedSnapshotId || `snapshot_${generateSnapshotId()}`,
        generatedAt: assessmentSource.timestamp || new Date().toISOString(),
        inputsUsed: {
          ...backendInputs,
          company_name: backendInputs.company_name || backendInputs.companyName || formSource?.companyName || formSource?.company_name || 'Company Name',
          companyName: backendInputs.company_name || backendInputs.companyName || formSource?.companyName || formSource?.company_name || 'Company Name',
          industry: backendInputs.industry || formSource?.industry || 'Industry',
          employee_count: backendInputs.employee_count || backendInputs.employees || formSource?.employee_count || formSource?.employees || 0,
          employees: backendInputs.employee_count || backendInputs.employees || formSource?.employee_count || formSource?.employees || 0,
          annual_revenue_sgd: annualRevenue,
          annual_revenue: annualRevenue,
          annualRevenue,
          project_type: backendInputs.project_type || backendInputs.projectType || formSource?.project_type || formSource?.projectType || 'General',
          projectType: backendInputs.project_type || backendInputs.projectType || formSource?.project_type || formSource?.projectType || 'General',
          project_scope:
            backendInputs.project_scope ||
            backendInputs.projectScope ||
            formSource?.project_scope ||
            formSource?.projectScope ||
            formSource?.project_description ||
            (Array.isArray(formSource?.project_objectives) ? formSource.project_objectives.join(', ') : ''),
          projectScope:
            backendInputs.project_scope ||
            backendInputs.projectScope ||
            formSource?.project_scope ||
            formSource?.projectScope ||
            formSource?.project_description ||
            (Array.isArray(formSource?.project_objectives) ? formSource.project_objectives.join(', ') : ''),
          project_budget: parseFloat(backendInputs.project_budget) || parseFloat(backendInputs.projectBudget) || parseFloat(formSource?.project_budget) || parseFloat(formSource?.projectBudget) || 0,
          projectBudget: parseFloat(backendInputs.project_budget) || parseFloat(backendInputs.projectBudget) || parseFloat(formSource?.project_budget) || parseFloat(formSource?.projectBudget) || 0
        },
        confidence: assessmentSource.overall_score ? assessmentSource.overall_score / 100 : 0,
        confidenceBreakdown: null,
        cutoffSuggested: 65,
        sortSuggested: 'score-desc',
        visibleGrantIds: Object.keys(assessmentSource.eligibility_results),
        hiddenGrantIds: [],
        grants: grants,
        overallStatus: assessmentSource.ok ? 'completed' : 'error',
        meta: assessmentSource.meta || null,
        recommendations: assessmentSource.recommendations || [],
        nextSteps: assessmentSource.next_steps || [],
        _isFallbackMode: assessmentSource._fallback_mode || false,
        disclaimer: assessmentSource.disclaimer,
        is_heuristic_score: assessmentSource.is_heuristic_score,
        funding_engine: assessmentSource.funding_engine || null,
        assessment_methodology: assessmentSource.assessment_methodology || assessmentSource.assessmentMethodology || null,
        assessmentMethodology: assessmentSource.assessmentMethodology || assessmentSource.assessment_methodology || null,
        fit_gate: assessmentSource.fit_gate || assessmentSource.fitGate || null,
        fitGate: assessmentSource.fitGate || assessmentSource.fit_gate || null,
        output_vetting: assessmentSource.output_vetting || assessmentSource.outputVetting || null,
        outputVetting: assessmentSource.outputVetting || assessmentSource.output_vetting || null,
        ...extractResultLevelContractFields(assessmentSource),
      };
    }

    // Use enhanced backend response structure if available
    if (assessmentSource.grant_assessments && Array.isArray(assessmentSource.grant_assessments)) {
      if (DEBUG) console.log('🔍 Using enhanced backend response structure');
      
      // Extract user inputs from backend or formSource
      const backendInputs = assessmentSource.inputs_used || {};
      if (DEBUG) console.log('📥 Backend inputs_used:', backendInputs);
      const annualRevenue = firstInputNumber(
        backendInputs.annual_revenue_sgd,
        backendInputs.annual_revenue,
        backendInputs.annualRevenue,
        formSource?.annual_revenue_sgd,
        formSource?.annual_revenue,
        formSource?.annualRevenue
      ) || 0;
      
      return {
        snapshotId: resolvedSnapshotId || `snapshot_${generateSnapshotId()}`,
        generatedAt: assessmentSource.generated_at_sgt || new Date().toISOString(),
        inputsUsed: {
          company_name: backendInputs.company_name || backendInputs.companyName || formSource?.companyName || formSource?.company_name || 'Company Name',
          companyName: backendInputs.company_name || backendInputs.companyName || formSource?.companyName || formSource?.company_name || 'Company Name',
          industry: backendInputs.industry || formSource?.industry || 'Industry',
          employee_count: backendInputs.employee_count || backendInputs.employees || formSource?.employee_count || formSource?.employees || 0,
          employees: backendInputs.employee_count || backendInputs.employees || formSource?.employee_count || formSource?.employees || 0,
          annual_revenue_sgd: annualRevenue,
          annual_revenue: annualRevenue,
          annualRevenue,
          project_type: backendInputs.project_type || backendInputs.projectType || formSource?.project_type || formSource?.projectType || 'General',
          projectType: backendInputs.project_type || backendInputs.projectType || formSource?.project_type || formSource?.projectType || 'General',
          project_scope:
            backendInputs.project_scope ||
            backendInputs.projectScope ||
            formSource?.project_scope ||
            formSource?.projectScope ||
            formSource?.project_description ||
            (Array.isArray(formSource?.project_objectives) ? formSource.project_objectives.join(', ') : ''),
          projectScope:
            backendInputs.project_scope ||
            backendInputs.projectScope ||
            formSource?.project_scope ||
            formSource?.projectScope ||
            formSource?.project_description ||
            (Array.isArray(formSource?.project_objectives) ? formSource.project_objectives.join(', ') : ''),
          project_budget: (() => {
            const val = parseFloat(backendInputs.project_budget || backendInputs.projectBudget || formSource?.project_budget || formSource?.projectBudget || formSource?.project_budget_sgd || 0);
            return isNaN(val) ? 0 : val;
          })(),
          projectBudget: (() => {
            const val = parseFloat(backendInputs.project_budget || backendInputs.projectBudget || formSource?.project_budget || formSource?.projectBudget || formSource?.project_budget_sgd || 0);
            return isNaN(val) ? 0 : val;
          })(),
          ...backendInputs
        },
        confidence: assessmentSource.global_confidence || 0,
        confidenceBreakdown: assessmentSource.confidence_breakdown,
        cutoffSuggested: assessmentSource.cutoff_suggested || 65,
        sortSuggested: assessmentSource.sort_suggested || 'score-desc',
        visibleGrantIds: assessmentSource.visible_grant_ids || [],
        hiddenGrantIds: assessmentSource.hidden_grant_ids || [],
        grants: assessmentSource.grant_assessments, // Use grant_assessments from backend
        overallStatus: assessmentSource.overall_status || 'pending',
        disclaimer: assessmentSource.disclaimer,
        is_heuristic_score: assessmentSource.is_heuristic_score,
        funding_engine: assessmentSource.funding_engine || null,
        assessment_methodology: assessmentSource.assessment_methodology || assessmentSource.assessmentMethodology || null,
        assessmentMethodology: assessmentSource.assessmentMethodology || assessmentSource.assessment_methodology || null,
        fit_gate: assessmentSource.fit_gate || assessmentSource.fitGate || null,
        fitGate: assessmentSource.fitGate || assessmentSource.fit_gate || null,
        output_vetting: assessmentSource.output_vetting || assessmentSource.outputVetting || null,
        outputVetting: assessmentSource.outputVetting || assessmentSource.output_vetting || null,
        ...extractResultLevelContractFields(assessmentSource),
      };
    }

    // Fallback to legacy structure processing
    if (DEBUG) console.log('🔍 Using legacy structure processing');
    const snapshotId = resolvedSnapshotId || generateSnapshotId();
    const generatedAt = new Date().toISOString();
    // Accept either grant_assessments OR grants (backend compatibility)
    const grants = assessmentSource.grant_assessments || assessmentSource.grants || [];
    
    if (DEBUG) console.log('🔍 Raw grants data:', grants);
    if (DEBUG) console.log('🔍 Grants length:', grants?.length);
    if (DEBUG) console.log('🔍 First grant structure:', grants[0]);
    if (DEBUG) console.log('🔍 First grant keys:', grants[0] ? Object.keys(grants[0]) : 'No grant');
    
    // Calculate dynamic confidence from assessment data
    let confidence = 0;
    if (assessmentSource.confidence_score !== undefined) {
      confidence = assessmentSource.confidence_score > 1 ? 
        Math.round(assessmentSource.confidence_score) : 
        Math.round(assessmentSource.confidence_score * 100);
    } else if (assessmentSource.global_confidence !== undefined) {
      confidence = assessmentSource.global_confidence > 1 ? 
        Math.round(assessmentSource.global_confidence) : 
        Math.round(assessmentSource.global_confidence * 100);
    } else {
      // Calculate from individual grant confidences
      const validConfidences = grants
        .map(grant => grant.confidence)
        .filter(conf => conf !== undefined && conf !== null)
        .map(conf => conf > 1 ? conf : conf * 100);
      
      if (validConfidences.length > 0) {
        confidence = Math.round(validConfidences.reduce((sum, conf) => sum + conf, 0) / validConfidences.length);
      } else {
        confidence = 0; // No confidence data available
      }
    }
    
    // Ensure confidence is between 0-100
    confidence = Math.min(100, Math.max(0, confidence));

    if (DEBUG) console.log('🔍 About to process grants. Total count:', grants?.length || 0);

    // Safety check: ensure grants is an array
    if (!Array.isArray(grants)) {
      console.error('❌ Grants is not an array:', typeof grants, grants);
      return null;
    }

    const processedGrants = grants.map(grant => {
      try {
        // Safety check: ensure grant is an object
        if (!grant || typeof grant !== 'object') {
          console.warn('⚠️ Invalid grant object:', grant);
          return null;
        }
        
        if (DEBUG) console.log('🔍 Processing grant:', grant);
        if (DEBUG) console.log('🔍 Grant keys:', Object.keys(grant));
        
        // Handle NEW backend structure (from AssessmentServiceV2)
        // Backend returns: {code, eligible, score, confidence, why, why_eligible, why_not_eligible, citations, ...}
        if (grant.code && grant.score !== undefined && !grant.score0to100) {
          if (DEBUG) console.log('🔍 Using NEW backend structure (AssessmentServiceV2)');

          const normalizedGrant = {
            ...grant,
            fit: fitBadgeForGrant(grant, Math.min(100, Math.max(0, grant.score))),
            id: grant.code || grant.grantId || grant.grant_id || 'unknown',
            code: grant.code || grant.grantId || grant.grant_id,
            grantId: grant.grantId || grant.grant_id || grant.code,
            grantFullName: grant.grantFullName || grant.grant_full_name || grant.name || grant.display_name || grant.code,
            name: grant.grantFullName || grant.grant_full_name || grant.name || grant.display_name || grant.code,
            acronym: grant.grantId || grant.grant_id || grant.acronym || grant.code,
            score: Math.min(100, Math.max(0, grant.score)),
            scorePct: Math.min(100, Math.max(0, grant.score_pct || grant.score)),
            scoreBreakdown: grant.score_breakdown || grant.scoreBreakdown || {},
            displayPriority: grant.display_priority ?? grant.displayPriority ?? null,
            statusLabel: grant.status_label || grant.statusLabel || '',
            confidenceLabel: grant.confidence_label || grant.confidenceLabel || '',
            recommendedAction: grant.recommended_action || grant.recommendedAction || '',
            reasonNotPrioritized: grant.reason_not_prioritized || grant.reasonNotPrioritized || '',
            signalMatchLabel: grant.signal_match_label || grant.signalMatchLabel || '',
            signalMatchPct: getSignalMatchPct(grant),
            strategicFitScore: grant.strategic_fit_score ?? grant.strategicFitScore ?? null,
            strategicFitLabel: grant.strategic_fit_label || grant.strategicFitLabel || '',
            // Structural prerequisites + reference facts (new contract; absent on older stored results)
            prerequisiteChecks: getGrantPrerequisiteChecks(grant),
            blocked: isBlockedGrant(grant),
            blockReason: getGrantBlockReason(grant) || null,
            eligibilityAfterPrerequisites: getGrantEligibilityAfterPrerequisites(grant) || null,
            officialUrl: getGrantOfficialUrl(grant) || null,
            transitionNote: getGrantTransitionNote(grant) || null,
            pillarLabel: grant.pillar_label || grant.pillarLabel || '',
            rationaleBullets: Array.isArray(grant.rationale_bullets || grant.rationaleBullets) ? (grant.rationale_bullets || grant.rationaleBullets) : [],
            scoreInterpretation: grant.score_interpretation || grant.scoreInterpretation || '',
            eligibilityStatus: grant.eligibility_status || grant.eligibilityStatus || '',
            eligibilityState: grant.eligibility_state || grant.eligibilityState || '',
            unknownCriteria: grant.unknown_criteria || grant.unknownCriteria || [],
            summaryUserBenefits: grant.summary_user_benefits || grant.summaryUserBenefits || null,
            detailedAnalysis: grant.detailed_analysis || grant.detailedAnalysis || null,
            whatThisGrantIsFor: grant.what_this_grant_is_for || grant.whatThisGrantIsFor || '',
            nextSteps: grant.next_steps || grant.nextSteps || [],
            whyEligible: grant.why_eligible || grant.whyEligible || [],
            whyNotEligible: grant.why_not_eligible || grant.whyNotEligible || [],
            requirements: Array.isArray(grant.requirements) ? grant.requirements : [],
            citations: Array.isArray(grant.citations) ? grant.citations : [],
            why_match: grant.why_eligible || (grant.why ? [grant.why] : []),
            whats_missing: grant.why_not_eligible || [],
            matched_criteria: grant.matched_criteria || [],
            unmatched_criteria: grant.unmet_criteria || [],
            confidence_factors: grant.confidence_factors || [],
            confidence: (() => {
              if (typeof grant.confidence === 'number') return Math.min(100, Math.max(0, grant.confidence));
              if (typeof grant.certainty === 'number') return Math.round(Math.min(1, Math.max(0, grant.certainty)) * 100);
              return 70;
            })(),
            certainty: (() => {
              if (typeof grant.certainty === 'number') return Math.min(1, Math.max(0, grant.certainty));
              if (typeof grant.confidence === 'number') return Math.min(1, Math.max(0, grant.confidence / 100));
              return null;
            })(),
            funding_snapshot: grant.funding_snapshot || grant.funding_notes || '',
            docs_checklist: grant.docs_checklist || [],
            apply_steps: grant.apply_steps || grant.next_steps || [],
            official_link: getGrantOfficialUrl(grant) || '#',
            detailed_info: grant.why || grant.rationale || 'Grant eligibility assessment',
            risks: grant.why_not_eligible || [],
          };

          if (!normalizedGrant.funding_snapshot) {
            const reference = normalizedGrant.reference_data || normalizedGrant.referenceData;
            const refFunding = reference?.funding;
            const refFundingText = typeof refFunding === 'string' ? refFunding : refFunding?.text;
            if (refFundingText && !isBoilerplateReferenceText(refFundingText)) {
              normalizedGrant.funding_snapshot = refFundingText;
            }
          }
          if (isBoilerplateReferenceText(normalizedGrant.funding_snapshot) && normalizedGrant.funding_snapshot) {
            // Never surface the generic "support level depends…" boilerplate as a funding snapshot.
            normalizedGrant.funding_snapshot = '';
          }

          // Ensure deprecated legacy keys exist for downstream helpers
          normalizedGrant.summary_user_benefits = normalizedGrant.summaryUserBenefits;
          normalizedGrant.detailed_analysis = normalizedGrant.detailedAnalysis;
          normalizedGrant.next_steps = normalizedGrant.nextSteps;
          normalizedGrant.why_eligible = normalizedGrant.whyEligible;
          normalizedGrant.why_not_eligible = normalizedGrant.whyNotEligible;

          normalizedGrant.referenceData = normalizedGrant.reference_data || normalizedGrant.referenceData || null;

          return normalizedGrant;
        }
        
        // Handle enhanced backend structure
        if (grant.score0to100 !== undefined) {
          // Enhanced structure - use data as-is
          return {
            id: grant.name || grant.acronym || 'unknown',
            name: grant.name || 'Unknown Grant',
            acronym: grant.acronym || 'N/A',
            score: grant.score0to100,
            confidence: grant.confidence || 0,
            fit: fitBadgeForGrant(grant, grant.score0to100),
            why_match: grant.whyMatch || [],
            funding_snapshot: grant.funding ? 
              `${grant.funding.percent || 'Up to 70%'}${grant.funding.cap ? ` (${grant.funding.cap})` : ''}` : 
              'Funding details available upon application',
            eligibility_bullets: grant.eligibility || [],
            docs_checklist: grant.docs || [],
            apply_steps: grant.applySteps || [],
            official_link: grant.officialUrl || '#',
            detailed_info: grant.description || 'No detailed information available',
            whats_missing: grant.whatsMissing || [],
            risks: grant.risks || [],
            next_steps: grant.nextSteps || [],
            effort: grant.effort || 'Medium',
            timeline_weeks: grant.timelineWeeks
          };
        }
        
        // Handle both enhanced backend structure and legacy structure
        if (DEBUG) console.log('🔍 Grant scoring_breakdown:', grant.scoring_breakdown);
        if (DEBUG) console.log('🔍 Grant tailored_recommendations:', grant.tailored_recommendations);
        if (DEBUG) console.log('🔍 Grant rules_fired:', grant.rules_fired);
        if (DEBUG) console.log('🔍 Grant missing_information:', grant.missing_information);
        if (DEBUG) console.log('🔍 Grant official_references:', grant.official_references);
      
        // Fix scoring - composite_score should be 0-1, not 0-100
        let score = 0;
        if (grant.composite_score !== undefined) {
          // If composite_score is already 0-100, use it as is
          score = grant.composite_score > 1 ? grant.composite_score : Math.round(grant.composite_score * 100);
        } else if (grant.score !== undefined) {
          score = grant.score > 1 ? grant.score : Math.round(grant.score * 100);
        }
        
        // Fix confidence - use actual confidence, not hardcoded 0.7
        let confidence = 0;
        if (grant.confidence !== undefined) {
          confidence = grant.confidence > 1 ? grant.confidence : Math.round(grant.confidence * 100);
        }
      
      // Get detailed analysis from rules_fired (passed rules)
      let whyMatch = [];
      if (grant.rules_fired && Array.isArray(grant.rules_fired)) {
        // Get rules that passed and extract meaningful reasons
        const passedRules = grant.rules_fired.filter(rule => rule.outcome === 'pass' || rule.outcome === 'partial');
        whyMatch = passedRules.map(rule => {
          // Extract the reason from the rule
          if (rule.reason) {
            return rule.reason;
          } else if (rule.evidence) {
            return rule.evidence;
          } else {
            return `Rule ${rule.id} passed`;
          }
        }).filter(reason => reason && reason.length > 0);
      }
      
      // If no passed rules, try to get from tailored recommendations
      if (whyMatch.length === 0 && grant.tailored_recommendations) {
        if (grant.tailored_recommendations.next_steps && Array.isArray(grant.tailored_recommendations.next_steps)) {
          whyMatch = grant.tailored_recommendations.next_steps.slice(0, 3);
        } else if (grant.tailored_recommendations.documents_to_prepare && Array.isArray(grant.tailored_recommendations.documents_to_prepare)) {
          whyMatch = grant.tailored_recommendations.documents_to_prepare.slice(0, 3);
        }
      }
      
      // Fallback if still no analysis - show what we know about the grant
      if (whyMatch.length === 0) {
        const grantName = toDisplayGrantTitle(grant.display_name || grant.name || 'This grant');
        whyMatch = [
          `${grantName} is available for your industry`,
          'Detailed analysis based on your company profile',
          'Review requirements and eligibility criteria'
        ];
      }
      
      // Get funding information from various sources
      let fundingSnapshot = '';
      
      // Try budget tips first (most specific)
      if (grant.tailored_recommendations && grant.tailored_recommendations.budget_tips && grant.tailored_recommendations.budget_tips.length > 0) {
        fundingSnapshot = grant.tailored_recommendations.budget_tips[0];
      }
      // Try timeline considerations (often contains funding info)
      else if (grant.tailored_recommendations && grant.tailored_recommendations.timeline_considerations && grant.tailored_recommendations.timeline_considerations.length > 0) {
        fundingSnapshot = grant.tailored_recommendations.timeline_considerations[0];
      }
      // Try official references
      else if (grant.official_references && grant.official_references.length > 0) {
        fundingSnapshot = `Official funding details available at: ${grant.official_references[0].label}`;
      }
      // Generate contextual funding info
      else {
        const grantName = toDisplayGrantTitle(grant.display_name || grant.name || 'This grant');
        fundingSnapshot = `${grantName} provides funding support for qualifying projects. Visit the official page for current funding amounts and co-funding requirements.`;
      }
      
      const processedGrant = {
        id: grant.key || grant.grant_id || grant.name || 'unknown',
        name: toDisplayGrantTitle(grant.display_name || grant.name || grant.grant_name || 'Unknown Grant'),
        acronym: grant.acronym || grant.grant_acronym || 'N/A',
        score: Math.min(100, Math.max(0, score)), // Ensure score is between 0-100
        confidence: Math.min(100, Math.max(0, confidence)), // Ensure confidence is between 0-100
        fit: fitBadgeForGrant(grant, Math.min(100, Math.max(0, score))) || { label: 'Unknown', color: 'bg-gray-100 text-white/90', icon: AlertTriangle },
        why_match: whyMatch,
        funding_snapshot: fundingSnapshot,
        eligibility_bullets: getEligibilityBullets(grant),
        docs_checklist: getDocsChecklist(grant),
        apply_steps: getApplySteps(grant),
        official_link: grant.official_link || grant.link || '#',
        detailed_info: grant.detailed_info || grant.description || 'No detailed information available'
      };
      
        if (DEBUG) console.log('🔍 Processed grant:', processedGrant);
        if (DEBUG) console.log('🔍 Processed grant why_match:', processedGrant.why_match);
        if (DEBUG) console.log('🔍 Processed grant funding_snapshot:', processedGrant.funding_snapshot);
        if (DEBUG) console.log('🔍 Processed grant eligibility_bullets:', processedGrant.eligibility_bullets);
        if (DEBUG) console.log('🔍 Processed grant docs_checklist:', processedGrant.docs_checklist);
        if (DEBUG) console.log('🔍 Processed grant apply_steps:', processedGrant.apply_steps);
        return processedGrant;
      } catch (error) {
        console.error('❌ Error processing grant:', grant, error);
        // Return a safe fallback grant
        return {
          id: grant.key || grant.grant_id || grant.name || 'error',
          name: toDisplayGrantTitle(grant.display_name || grant.name || grant.grant_name || 'Error Processing Grant'),
          acronym: grant.acronym || grant.grant_acronym || 'ERR',
          score: 0,
          confidence: 0,
          fit: { label: 'Error', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
          why_match: ['Error processing grant data'],
          funding_snapshot: 'Unable to load funding information',
          eligibility_bullets: ['Error loading eligibility criteria'],
          docs_checklist: ['Error loading document requirements'],
          apply_steps: ['Error loading application steps'],
          official_link: '#',
          detailed_info: 'Error processing grant information'
        };
      }
    }).filter(grant => grant !== null && grant !== undefined); // Remove any null/undefined grants from processing errors

    if (DEBUG) console.log('✅ All processed grants:', processedGrants);
    if (DEBUG) console.log('✅ Processed count:', processedGrants.length);
    if (processedGrants.length > 0) {
      if (DEBUG) console.log('✅ First grant name:', processedGrants[0]?.name);
      if (DEBUG) console.log('✅ First grant score:', processedGrants[0]?.score);
    }

    // Debug logging for inputsUsed mapping
    const annualRevenue = firstInputNumber(
      formSource?.annual_revenue_sgd,
      formSource?.annual_revenue,
      formSource?.annualRevenue
    ) || 0;
    const inputsUsed = {
      company_name: formSource?.companyName || formSource?.company_name || 'Company Name',
      companyName: formSource?.companyName || formSource?.company_name || 'Company Name',
      industry: formSource?.industry || 'Industry',
      employee_count: formSource?.employee_count || formSource?.employees || 0,
      employees: formSource?.employee_count || formSource?.employees || 0,
      // Revenue: Check ALL variations including _sgd suffix
      annual_revenue_sgd: annualRevenue,
      annual_revenue: annualRevenue,
      annualRevenue,
      project_type: formSource?.project_type || formSource?.projectType || 'General',
      projectType: formSource?.project_type || formSource?.projectType || 'General',
      project_scope:
        formSource?.project_scope ||
        formSource?.projectScope ||
        formSource?.project_description ||
        (Array.isArray(formSource?.project_objectives) ? formSource.project_objectives.join(', ') : ''),
      projectScope:
        formSource?.project_scope ||
        formSource?.projectScope ||
        formSource?.project_description ||
        (Array.isArray(formSource?.project_objectives) ? formSource.project_objectives.join(', ') : ''),
      // Budget: Check ALL variations including _sgd suffix
      project_budget: parseFloat(formSource?.project_budget || formSource?.projectBudget || formSource?.project_budget_sgd) || 0,
      projectBudget: parseFloat(formSource?.project_budget || formSource?.projectBudget || formSource?.project_budget_sgd) || 0
    };
    
    if (DEBUG) console.log('🎯 INPUTS USED MAPPING:', inputsUsed);
    if (DEBUG) console.log('  - Company:', inputsUsed.company_name);
    if (DEBUG) console.log('  - Industry:', inputsUsed.industry);
    if (DEBUG) console.log('  - Employees:', inputsUsed.employee_count);
    if (DEBUG) console.log('  - Revenue (from formSource?.annual_revenue):', formSource?.annual_revenue);
    if (DEBUG) console.log('  - Revenue (mapped):', inputsUsed.annual_revenue);

    return {
      snapshotId,
      generatedAt,
      inputsUsed,
      confidence,
      grants: processedGrants,
      disclaimer: assessmentSource?.disclaimer,
      is_heuristic_score: assessmentSource?.is_heuristic_score,
      funding_engine: assessmentSource?.funding_engine || null,
      assessment_methodology: assessmentSource?.assessment_methodology || assessmentSource?.assessmentMethodology || null,
      assessmentMethodology: assessmentSource?.assessmentMethodology || assessmentSource?.assessment_methodology || null,
      fit_gate: assessmentSource?.fit_gate || assessmentSource?.fitGate || null,
      fitGate: assessmentSource?.fitGate || assessmentSource?.fit_gate || null,
      output_vetting: assessmentSource?.output_vetting || assessmentSource?.outputVetting || null,
      outputVetting: assessmentSource?.outputVetting || assessmentSource?.output_vetting || null,
      ...extractResultLevelContractFields(assessmentSource),
    };
    } catch (error) {
      console.error('❌ Error processing assessment data:', error);
      
      // Send to Sentry
      try {
        import('../utils/errorTracking').then(({ captureException }) => {
          captureException(error, {
            component: 'EligibilityResults',
            action: 'processAssessmentData',
            hasAssessmentData: !!assessmentSource,
            hasFormData: !!formSource
          });
        }).catch(() => {
          // Silently fail if Sentry not available
        });
      } catch {
        // Silently fail if Sentry not available
      }

      return null;
    }
  }, [assessmentSource, formSource, resolvedSnapshotId]);

  const clientFallbackFundingEngine = useMemo(
    () => buildClientFallbackFundingEngine(formSource, results?.grants || []),
    [formSource, results?.grants]
  );

  const fundingEngine = useMemo(() => {
    const assessmentPrivatePathways = assessmentSource && typeof assessmentSource === 'object'
      ? assessmentSource.privateFundingPathways
      : null;
    if (Array.isArray(assessmentPrivatePathways) && assessmentPrivatePathways.length > 0) {
      return buildPrivateFundingEngineFromPathways(assessmentPrivatePathways);
    }

    const fromAssessment = assessmentSource && typeof assessmentSource === 'object'
      ? assessmentSource.funding_engine
      : null;
    if (fromAssessment && typeof fromAssessment === 'object') {
      const grouped = fromAssessment.grouped_matches;
      const hasNonGov = grouped && typeof grouped === 'object' && (
        (grouped.additional_funding_opportunities || []).length > 0 ||
        (grouped.growth_and_investment_opportunities || []).length > 0 ||
        (grouped.financing_options || []).length > 0
      );
      if (hasNonGov) return fromAssessment;
    }

    const fromProcessed = results && typeof results === 'object' ? results.funding_engine : null;
    if (fromProcessed && typeof fromProcessed === 'object') {
      const grouped = fromProcessed.grouped_matches;
      const hasNonGov = grouped && typeof grouped === 'object' && (
        (grouped.additional_funding_opportunities || []).length > 0 ||
        (grouped.growth_and_investment_opportunities || []).length > 0 ||
        (grouped.financing_options || []).length > 0
      );
      if (hasNonGov) return fromProcessed;
    }

    const processedPrivatePathways = results && typeof results === 'object'
      ? results.privateFundingPathways
      : null;
    if (Array.isArray(processedPrivatePathways) && processedPrivatePathways.length > 0) {
      return buildPrivateFundingEngineFromPathways(processedPrivatePathways);
    }

    return clientFallbackFundingEngine;
  }, [assessmentSource, results, clientFallbackFundingEngine]);

  const fundingBucketSections = useMemo(() => {
    const grouped = fundingEngine?.grouped_matches;
    if (!grouped || typeof grouped !== 'object') return [];
    const dedupedItems = Array.from(
      new Map(
        [
          ...(Array.isArray(grouped.additional_funding_opportunities) ? grouped.additional_funding_opportunities : []),
          ...(Array.isArray(grouped.growth_and_investment_opportunities) ? grouped.growth_and_investment_opportunities : []),
          ...(Array.isArray(grouped.financing_options) ? grouped.financing_options : []),
        ]
          .filter((item) => item && typeof item === 'object')
          .filter(isClientSurfaceableOpportunity)
          .map((item) => [item.id || item.name, item])
      ).values()
    ).sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0));

    if (dedupedItems.length === 0) return [];

    return [{
      key: 'independent_funding_pathways',
      title: 'Additional independent funding routes',
      subtitle: 'Private and alternative non-dilutive routes that remain relevant after the primary grant recommendations above.',
      items: dedupedItems,
      accent: {
        icon: Lightbulb,
        iconColor: '#60A5FA',
        borderColor: 'rgba(96, 165, 250, 0.45)',
        badgeBg: 'rgba(239, 246, 255, 0.95)',
      },
    }];
  }, [fundingEngine]);

  const eligibleIndependentPathways = useMemo(() => {
    const seen = new Set();
    const ranked = fundingBucketSections
      .flatMap((section) => section.items || [])
      .filter((item) => {
        if (!item || typeof item !== 'object') return false;
        if (item.isGovernmentGrant) return false;
        if (!isClientSurfaceableOpportunity(item)) return false;
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      })
      .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

    const strong = ranked.filter((item) => (item.score ?? 0) >= 75);
    if (strong.length > 0) return strong;

    return ranked.filter((item) => (item.score ?? 0) >= 60).slice(0, 3);
  }, [fundingBucketSections]);

  const displayFundingBucketSections = useMemo(() => {
    const eligibleIds = new Set(eligibleIndependentPathways.map((item) => item.id));
    return fundingBucketSections
      .map((section) => ({
        ...section,
        items: (section.items || []).filter((item) => !eligibleIds.has(item.id)),
      }))
      .filter((section) => section.items.length > 0);
  }, [fundingBucketSections, eligibleIndependentPathways]);

  // URL state management - simplified for new Top-3/100% logic
  const expandedGrants = useMemo(() => {
    const expandedParam = searchParams.get('expanded');
    return expandedParam ? expandedParam.split(',') : [];
  }, [searchParams]);

  // Initialize localExpandedGrants with URL params
  useEffect(() => {
    if (expandedGrants.length > 0) {
      setLocalExpandedGrants(new Set(expandedGrants));
    }
  }, [expandedGrants]);

  // Computed values with defensive guards - restructured to avoid circular dependencies
  const perfectScoreGrants = useMemo(() => {
    if (!results?.grants) return [];
    return results.grants.filter(grant => 
      grant && typeof grant.score === 'number' && grant.score >= 99.5
    );
  }, [results]);

  // All analyzed grants (includes all grants regardless of score)
  // IMPORTANT: Keep the original grant objects, don't create copies to preserve all properties
  const allAnalyzedGrants = useMemo(() => {
    if (!results?.grants || !Array.isArray(results.grants)) return [];
    // Return all grants, sorted by score (grants without scores go to the end)
    // Use filter to ensure valid grants, but keep original objects
    const validGrants = results.grants.filter(grant => grant && typeof grant === 'object');
    // Sort by score while preserving original grant objects
    return [...validGrants].sort((a, b) => {
      const scoreA = typeof a.score === 'number' ? a.score : -1;
      const scoreB = typeof b.score === 'number' ? b.score : -1;
      return scoreB - scoreA;
    });
  }, [results?.grants]);

  const sortedGrants = useMemo(() => {
    if (!results?.grants) return [];
    
    // Special 100% rule: if any grants have score >= 99.5%, show only those
    if (perfectScoreGrants.length > 0) {
      return perfectScoreGrants;
    }
    
    // Normal case: show top 3 + remaining
    const sorted = [...results.grants]
      .filter(grant => grant && typeof grant.score === 'number')
      .sort((a, b) => b.score - a.score);
    
    return sorted; // Show all grants, we'll handle Top-3/remaining in rendering
  }, [results, perfectScoreGrants]);

  useEffect(() => {
    if (!allAnalyzedGrants.length) {
      setCustomSelectedGrantIds(new Set());
    }
  }, [allAnalyzedGrants.length]);

  const scoreCutoff = useMemo(() => {
    const suggested = results?.cutoffSuggested ?? assessmentSource?.cutoff_suggested ?? 65;
    const numeric = Number(suggested);
    if (Number.isFinite(numeric)) {
      return Math.min(100, Math.max(0, numeric));
    }
    return 65;
  }, [results, assessmentSource]);

  const visibleGrants = useMemo(() => {
    return sortedGrants.filter((grant) =>
      typeof grant?.score === 'number' ? grant.score >= scoreCutoff : true
    );
  }, [sortedGrants, scoreCutoff]);


  const bestMatchRangeText = `≥${TIER_LIKELY_MIN}% signal match · Actionable / likely`;
  const lessLikelyRangeText = `${TIER_LESS_LIKELY_MIN}–${TIER_LIKELY_MIN - 1}% · Partial / Low`;
  const weakFitRangeText = `${TIER_WEAK_MIN}–${TIER_LESS_LIKELY_MIN - 1}% · Weak`;
  const blockedRangeText = 'Prerequisite not met';
  const ineligibleRangeText = 'Mandatory requirement not met';

  const getNormalizedCertainty = useCallback((grant) => {
    if (!grant) return null;

    const rawValues = [
      typeof grant.certainty === 'number' ? grant.certainty : null,
      typeof grant.successProbability === 'number' ? grant.successProbability : null,
      typeof grant.confidence === 'number' ? grant.confidence : null,
    ].filter((value) => typeof value === 'number' && !Number.isNaN(value));

    for (const raw of rawValues) {
      if (raw === null) continue;
      const normalized = raw > 1 ? raw / 100 : raw;
      if (normalized >= 0 && normalized <= 1) {
        return normalized;
      }
    }

    if (typeof grant.score === 'number' && !Number.isNaN(grant.score)) {
      return Math.min(1, Math.max(0, grant.score / 100));
    }

    if (typeof grant.scorePct === 'number' && !Number.isNaN(grant.scorePct)) {
      return Math.min(1, Math.max(0, grant.scorePct / 100));
    }

    return null;
  }, []);

  // Shared helper: 0–100 score from certainty/confidence/score (same sources as categorization so sort order matches section).
  const getCertaintyPercentage = useCallback((grant) => {
    if (!grant) return null;
    const norm = getNormalizedCertainty(grant);
    if (norm !== null) return Math.round(norm * 100);
    if (typeof grant.score === 'number' && !Number.isNaN(grant.score)) {
      return Math.round(Math.min(100, Math.max(0, grant.score)));
    }
    if (typeof grant.scorePct === 'number' && !Number.isNaN(grant.scorePct)) {
      return Math.round(Math.min(100, Math.max(0, grant.scorePct)));
    }
    return null;
  }, [getNormalizedCertainty]);

  // Display/sort score: same numeric basis as resolveGrantStatus so sorting follows the percentage bands.
  const getDisplayScore = useCallback((grant) => {
    if (!grant) return null;
    const pct = getCertaintyPercentage(grant);
    if (typeof pct === 'number') return pct;
    if (typeof grant.score === 'number' && !Number.isNaN(grant.score)) {
      return Math.round(Math.min(100, Math.max(0, grant.score)));
    }
    if (typeof grant.scorePct === 'number' && !Number.isNaN(grant.scorePct)) {
      return Math.round(Math.min(100, Math.max(0, grant.scorePct)));
    }
    return null;
  }, [getCertaintyPercentage]);

  const getStrategicFitScore = useCallback((grant) => {
    if (!grant) return null;
    const direct = grant.strategicFitScore ?? grant.strategic_fit_score ?? grant.strategicFit ?? grant.strategic_fit;
    if (typeof direct === 'number' && !Number.isNaN(direct)) {
      return Math.round(Math.min(100, Math.max(0, direct)));
    }
    const breakdown = grant.scoreBreakdown || grant.score_breakdown || {};
    const candidates = [
      breakdown.project_alignment,
      breakdown.objective_alignment,
      breakdown.objective_fit,
      breakdown.project_fit,
    ];
    for (const value of candidates) {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        return Math.round(Math.min(100, Math.max(0, numeric)));
      }
    }
    return getDisplayScore(grant);
  }, [getDisplayScore]);

  /**
   * UI tiers: hard ineligible first, then blocked (structural prerequisite unmet), then the
   * contract fit bands 72+ / 60–71 / 45–59 / 1–44 (display: high → low).
   * Legacy stored statuses: likely → likely, lessLikely → lessLikely; unknown → weakFit.
   */
  const resolveGrantStatus = useCallback(
    (grant) => {
      if (!grant) return 'weakFit';
      if (isHardIneligibleGrant(grant)) return 'ineligible';
      // A blocking structural prerequisite (e.g. PSG without a pre-approved vendor)
      // is a hard gate: the grant is never shown among Eligible/Likely, whatever its score.
      if (isBlockedGrant(grant)) return 'blocked';
      // Invitation-only / closed-call programmes share the blocked bucket so a
      // high score never presents them as something the client can apply for.
      if (isNotOpenGrant(grant)) return 'blocked';

      // The assessment engine's explicit eligible verdict wins over the score
      // bands, so the Eligible section and its tab counts match what the
      // engine (and the card copy) call eligible.
      const backendStatus = String(grant.eligibilityStatus || grant.eligibility_status || '').toLowerCase();
      if (backendStatus === 'eligible') return 'eligible';

      const pct = getDisplayScore(grant);
      if (pct !== null) return resolveScoreTier(pct);

      if (backendStatus === 'ineligible') return 'ineligible';
      if (backendStatus === 'borderline') return 'lessLikely';
      if (backendStatus === 'likely') return 'likely';
      if (backendStatus === 'lesslikely') return 'lessLikely';
      return 'weakFit';
    },
    [getDisplayScore]
  );

  // Shared helper function for displaying score as text (used consistently across modal, results page, and PDF)
  const _getDisplayScoreText = useCallback((grant) => {
    const score = getDisplayScore(grant);
    if (score !== null) {
      return `${score}%`;
    }
    return 'No score';
  }, [getDisplayScore]);

  const compareByClientUsefulness = useCallback((a, b) => {
    const readinessRank = (grant) => {
      const rawReadiness = String(
        grant?.eligibilityAfterPrerequisites ||
        grant?.eligibility_after_prerequisites ||
        grant?.readinessStatus ||
        grant?.readiness_status ||
        grant?.surfacePriority ||
        grant?.surface_priority ||
        ''
      ).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      if (['eligible_now', 'ready', 'ready_to_consider', 'eligible', 'surface', 'recommended'].includes(rawReadiness)) {
        return 5;
      }
      if (['conditional', 'potentially_eligible', 'confirm', 'confirm_before_applying', 'needs_confirmation'].includes(rawReadiness)) {
        return 4;
      }
      if (['blocked', 'not_ready', 'not_ready_on_current_inputs', 'ineligible', 'unavailable', 'suppressed'].includes(rawReadiness)) {
        return 1;
      }

      const statusLabel = String(grant?.statusLabel || grant?.status_label || '').toLowerCase();
      if (/(prioriti[sz]ed|recommended|best match)/.test(statusLabel)) return 5;
      if (/(backup|secondary|consider)/.test(statusLabel)) return 4;
      return 3;
    };
    const readinessDelta = readinessRank(b) - readinessRank(a);
    if (readinessDelta) return readinessDelta;

    const statusPriority = {
      eligible: 6,
      likely: 5,
      lessLikely: 4,
      weakFit: 3,
      blocked: 2,
      ineligible: 1,
    };
    const statusDelta = (statusPriority[resolveGrantStatus(b)] || 0) - (statusPriority[resolveGrantStatus(a)] || 0);
    if (statusDelta) return statusDelta;

    const scoreDelta = (getDisplayScore(b) ?? -1) - (getDisplayScore(a) ?? -1);
    if (scoreDelta) return scoreDelta;

    const strategicDelta = (getStrategicFitScore(b) ?? -1) - (getStrategicFitScore(a) ?? -1);
    if (strategicDelta) return strategicDelta;

    const certaintyDelta = (getCertaintyPercentage(b) ?? -1) - (getCertaintyPercentage(a) ?? -1);
    if (certaintyDelta) return certaintyDelta;

    const nameA = String(a?.name || a?.grantFullName || a?.code || '').trim();
    const nameB = String(b?.name || b?.grantFullName || b?.code || '').trim();
    return nameA.localeCompare(nameB);
  }, [getCertaintyPercentage, getDisplayScore, getStrategicFitScore, resolveGrantStatus]);

  // Map of grant codes from GrantHistoryStep to their acronyms/codes used in results
  const GRANT_CODE_MAP = {
    'PSG': ['PSG', 'Productivity Solutions Grant'],
    'EDG': ['EDG', 'Enterprise Development Grant'],
    'MRA': ['MRA', 'Market Readiness Assistance'],
    'SFEC': ['SFEC', 'SkillsFuture Enterprise Credit'],
    'SFEG': ['SFEG', 'Skills Framework Enhancement Grant'],
    'EIS': ['EIS', 'Enterprise Innovation Scheme'],
    'ECI': ['ECI', 'Enterprise Compute Initiative'],
    'ESP': ['ESP', 'Enterprise Sustainability Programme'],
    'ADS': ['ADS', 'Advanced Digital Solutions'],
    'EFS': ['EFS', 'Enterprise Financing Scheme'],
    'EFS_GREEN': ['EFS_GREEN', 'EFS Green', 'Enterprise Financing Scheme - Green'],
    'BEIS': ['BEIS', 'Business Events in Singapore'],
    'HRF': ['HRF', 'Hotel Rejuvenation Fund'],
    'CDF': ['CDF', 'Cruise Development Fund'],
    'TPDF': ['TPDF', 'Tourism Product Development Fund'],
    'LEAD': ['LEAD', 'Local Enterprise and Association Development Programme'],
    'GSFP': ['GSFP', 'Germany-Singapore SME Funding Programme'],
    'DIGITAL': ['DIGITAL', 'SMEs Go Digital', 'SME Go Digital', 'SMEsGoDigital'],
    'FINTECH': ['FINTECH', 'MAS FinTech Grant', 'FinTech', 'MAS_FinTech'],
    'STARTUP_FOUNDER': ['STARTUP_FOUNDER', 'Startup SG Founder', 'StartupSG Founder', 'StartupSG'],
    'EEG': ['EEG', 'Energy Efficiency Grant'],
    'BIZADAPT': ['BIZADAPT', 'Business Adaptation Grant'],
    'SRG': ['SRG', 'Sustainability Reporting Grant'],
    'BIF': ['BIF', 'Business Improvement Fund'],
    'LEF': ['LEF', 'Leisure Events Fund'],
    'ESF': ['ESF', 'Experience Step-Up Fund'],
    'ACT': ['ACT', 'Agri-Food Cluster Transformation Fund'],
    'BETC': ['BETC', 'Built Environment Technology'],
    'GIA': ['GIA', 'Global Innovation Alliance'],
    'WDG_JR_PLUS': ['WDG_JR_PLUS', 'Workforce Development Grant', 'Job Redesign'],
  };

  // Get list of previously approved grant codes from form data
  const approvedGrantCodes = useMemo(() => {
    const previousGrants = formSource?.previous_grants || [];
    return new Set(previousGrants);
  }, [formSource?.previous_grants]);

  // Helper function to check if a grant matches any approved grant code
  const isGrantApproved = useCallback((grant) => {
    if (!grant || approvedGrantCodes.size === 0) return false;
    
    const grantName = (grant.name || grant.grantFullName || grant.acronym || grant.code || '').toLowerCase();
    const grantCode = (grant.code || grant.acronym || grant.grantId || '').toUpperCase();
    
    // Check each approved grant code
    for (const approvedCode of approvedGrantCodes) {
      const matchPatterns = GRANT_CODE_MAP[approvedCode] || [approvedCode];
      
      // Check if grant code or name matches any of the patterns
      for (const pattern of matchPatterns) {
        const patternLower = pattern.toLowerCase();
        if (grantCode === approvedCode || 
            grantCode === pattern.toUpperCase() ||
            grantName.includes(patternLower)) {
          return true;
        }
      }
    }
    
    return false;
  // GRANT_CODE_MAP is stable; omitted to avoid unnecessary callback identity churn
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedGrantCodes]);

  // Separate approved grants from regular grants
  const { approvedGrants, unapprovedGrants } = useMemo(() => {
    if (DEBUG) console.log('🔍 Computing approved/unapproved grants', allAnalyzedGrants?.length, approvedGrantCodes);

    const approved = [];
    const unapproved = [];

    if (!allAnalyzedGrants || !Array.isArray(allAnalyzedGrants)) {
      console.warn('⚠️ allAnalyzedGrants is not an array:', allAnalyzedGrants);
      return { approvedGrants: [], unapprovedGrants: [] };
    }

    allAnalyzedGrants.forEach((grant) => {
      if (isExcludedGrantForDisplay(grant)) return;
      try {
        if (isGrantApproved(grant)) {
          approved.push(grant);
        } else {
          unapproved.push(grant);
        }
      } catch (error) {
        console.error('❌ Error checking grant approval:', grant, error);
        unapproved.push(grant); // Default to unapproved on error
      }
    });

    if (DEBUG) console.log('✅ Approved/unapproved grants:', approved.length, unapproved.length);

    return { approvedGrants: approved, unapprovedGrants: unapproved };
  }, [allAnalyzedGrants, isGrantApproved, approvedGrantCodes]);

  // Hide only discontinued-style legacy schemes; "limited" programmes (e.g. invite-only) stay visible so the full assessment set (~40 SG) shows.
  const displayableUnapprovedGrants = useMemo(() => {
    if (!unapprovedGrants.length) return [];
    const filtered = unapprovedGrants.filter((g) => !isExcludedGrantForDisplay(g));
    if (showLegacySchemes) return filtered;
    return filtered.filter((g) => !isLegacyForNewApplicationsGrant(g));
  }, [unapprovedGrants, showLegacySchemes]);

  const hiddenLegacyCount = useMemo(() => {
    if (showLegacySchemes) return 0;
    return unapprovedGrants.filter((g) => isLegacyForNewApplicationsGrant(g)).length;
  }, [unapprovedGrants, showLegacySchemes]);

  const legacySchemeCount = useMemo(
    () => unapprovedGrants.filter((g) => isLegacyForNewApplicationsGrant(g)).length,
    [unapprovedGrants]
  );

  const getGrantParentMeta = useCallback((grant) => {
    const referenceData = grant?.referenceData || grant?.reference_data || {};
    const parentIdRaw = referenceData?.parent_grant_id || grant?.grantId || grant?.grant_id || grant?.code || grant?.acronym || grant?.id || 'OTHER';
    const parentId = normalizeMainGrantAcronym(parentIdRaw);
    const mappedFamily = GRANT_FAMILY_DISPLAY[parentId];
    const fallbackName = mappedFamily?.title || MAIN_GRANT_LABELS[parentId] || parentId;
    const parentNameRaw = referenceData?.parent_grant_name || fallbackName;
    const parentName = toDisplayGrantTitle(String(parentNameRaw || fallbackName).trim());
    return { parentId, parentName };
  }, []);

  const GRANT_STATUS_PRIORITY = useMemo(
    () => ({
      eligible: 6,
      likely: 5,
      lessLikely: 4,
      weakFit: 3,
      blocked: 2,
      ineligible: 1,
    }),
    []
  );

  const strongestStatusByParent = useMemo(() => {
    const map = new Map();
    for (const grant of displayableUnapprovedGrants || []) {
      const { parentId } = getGrantParentMeta(grant);
      const status = resolveGrantStatus(grant);
      const rank = GRANT_STATUS_PRIORITY[status] || 0;
      const prev = map.get(parentId);
      if (!prev || rank > prev.rank) {
        map.set(parentId, { status, rank });
      }
    }
    return map;
  }, [displayableUnapprovedGrants, getGrantParentMeta, resolveGrantStatus, GRANT_STATUS_PRIORITY]);

  const grantsByStatus = useMemo(() => {
    const buckets = {
      eligible: [],
      likely: [],
      lessLikely: [],
      weakFit: [],
      blocked: [],
      ineligible: [],
    };
    for (const grant of displayableUnapprovedGrants || []) {
      const ownStatus = resolveGrantStatus(grant);
      // Blocked grants always live in their own section, even when a sibling
      // sub-grant in the same family is eligible.
      if (ownStatus === 'blocked') {
        buckets.blocked.push(grant);
        continue;
      }
      const { parentId } = getGrantParentMeta(grant);
      const strongest = strongestStatusByParent.get(parentId)?.status || ownStatus;
      const target = strongest === 'blocked' ? ownStatus : strongest;
      if (buckets[target]) buckets[target].push(grant);
    }
    Object.keys(buckets).forEach((key) => {
      buckets[key].sort(compareByClientUsefulness);
    });
    return buckets;
  }, [displayableUnapprovedGrants, getGrantParentMeta, strongestStatusByParent, resolveGrantStatus, compareByClientUsefulness]);

  const eligibleGrants = useMemo(() => grantsByStatus.eligible || [], [grantsByStatus]);
  const likelyGrants = useMemo(() => grantsByStatus.likely || [], [grantsByStatus]);
  const lessLikelyGrants = useMemo(() => grantsByStatus.lessLikely || [], [grantsByStatus]);
  const weakFitGrants = useMemo(() => grantsByStatus.weakFit || [], [grantsByStatus]);
  const blockedGrants = useMemo(() => grantsByStatus.blocked || [], [grantsByStatus]);
  const ineligibleGrants = useMemo(() => grantsByStatus.ineligible || [], [grantsByStatus]);

  const groupGrantsByParent = useCallback((grantsList) => {
    const resolveVariantCode = (grant) => {
      const referenceData = grant?.referenceData || grant?.reference_data || {};
      return String(
        referenceData?.subgrant_id ||
        grant?.id ||
        grant?.key ||
        grant?.code ||
        grant?.grant_code ||
        grant?.grantCode ||
        grant?.grant_id ||
        grant?.grantId ||
        grant?.acronym ||
        ''
      ).toUpperCase().trim();
    };
    const grouped = new Map();
    (grantsList || []).forEach((grant) => {
      const { parentId, parentName } = getGrantParentMeta(grant);
      const existing = grouped.get(parentId) || { parentId, parentName, grants: [], topScore: -1 };
      existing.grants.push(grant);
      existing.topScore = Math.max(existing.topScore, getDisplayScore(grant) ?? -1);
      grouped.set(parentId, existing);
    });
    const groups = Array.from(grouped.values())
      .map((group) => ({
        ...group,
        grants: (() => {
          const sorted = [...group.grants].sort((a, b) => (getDisplayScore(b) ?? -1) - (getDisplayScore(a) ?? -1));
          const hasExplicitSubgrants = sorted.some((g) => {
            const code = resolveVariantCode(g);
            return code.startsWith(`${group.parentId}_`) || (code && code !== group.parentId);
          });
          if (!hasExplicitSubgrants) return sorted;
          // If sub-grants exist, hide umbrella/base rows like plain "EDG"/"PSG" to reduce clutter.
          return sorted.filter((g) => {
            const code = resolveVariantCode(g);
            return !!code && code !== group.parentId;
          });
        })(),
      }))
      .filter((group) => Array.isArray(group.grants) && group.grants.length > 0)
      .sort((a, b) => compareByClientUsefulness(a.grants[0], b.grants[0]) || b.topScore - a.topScore || a.parentName.localeCompare(b.parentName));
    return groups;
  }, [getGrantParentMeta, getDisplayScore, compareByClientUsefulness]);

  // Assessment coverage includes all surfaced grant entries (including subgrants),
  // after filters/exclusions are applied.
  const displayedGovernmentCount = useMemo(() => {
    return (
      (approvedGrants?.length || 0) +
      (eligibleGrants?.length || 0) +
      (likelyGrants?.length || 0) +
      (lessLikelyGrants?.length || 0) +
      (weakFitGrants?.length || 0) +
      (blockedGrants?.length || 0) +
      (ineligibleGrants?.length || 0)
    );
  }, [
    approvedGrants,
    eligibleGrants,
    likelyGrants,
    lessLikelyGrants,
    weakFitGrants,
    blockedGrants,
    ineligibleGrants,
  ]);

  const displayedNonGovernmentCount = useMemo(
    () =>
      (eligibleIndependentPathways?.length || 0) +
      displayFundingBucketSections.reduce((total, section) => total + (section.items?.length || 0), 0),
    [eligibleIndependentPathways, displayFundingBucketSections]
  );

  const displayedResultsCount = displayedGovernmentCount + displayedNonGovernmentCount;

  const renderParentGroupedGrantCards = (grantsList, options = {}) => {
    const { defaultOpen = false, defaultOpenGroup = null } = options || {};
    const groups = groupGrantsByParent(grantsList);
    const getClosedCardTitle = (parentNameRaw, parentIdRaw) => {
      const parentId = normalizeMainGrantAcronym(parentIdRaw);
      const mappedFamily = GRANT_FAMILY_DISPLAY[parentId];
      const parentName = mappedFamily?.title || toDisplayGrantTitle(String(parentNameRaw || '').trim());
      const acronym = mappedFamily?.acronym || String(parentId || '').trim().toUpperCase();
      if (!parentName) return acronym || 'Grant';
      if (!acronym) return parentName;
      if (parentName.toUpperCase().includes(`(${acronym})`)) return parentName;
      if (parentName.toUpperCase().endsWith(` ${acronym}`)) {
        const base = parentName.slice(0, -acronym.length).trim();
        return `${base} (${acronym})`;
      }
      return `${parentName} (${acronym})`;
    };
    const getParentDescription = (grant) => {
      const parentCode = normalizeMainGrantAcronym(grant?.grantId || grant?.grant_id || grant?.code || grant?.acronym || grant?.id || '');
      const mappedFamily = GRANT_FAMILY_DISPLAY[parentCode];
      if (mappedFamily?.description) return mappedFamily.description;
      return 'Relevant grant family based on your submitted profile and project details.';
    };
    const getSubgrantLabel = (grant) => {
      const referenceData = grant?.referenceData || grant?.reference_data || {};
      const name = String(
        referenceData?.subgrant_name ||
        grant?.grantFullName ||
        grant?.grant_full_name ||
        grant?.subgrant_name ||
        grant?.subgrantName ||
        grant?.name ||
        ''
      ).trim();
      return name || 'Subgrant';
    };
    const buildSubgrantRows = (group) => {
      const primary = group?.grants?.[0] || {};
      const variants = Array.isArray(primary?.variantsReviewed)
        ? primary.variantsReviewed
        : Array.isArray(primary?.variants_reviewed)
          ? primary.variants_reviewed
          : [];
      if (variants.length > 0) {
        return variants.map((variant, index) => ({
          id: String(variant?.variantKey || variant?.grantCode || `${group.parentId}-${index}`),
          name: String(variant?.displayName || variant?.fullName || variant?.name || '').trim() || 'Subgrant',
          score:
            typeof variant?.matchScore === 'number'
              ? variant.matchScore
              : typeof variant?.score === 'number'
                ? variant.score
                : null,
        }));
      }
      return (group?.grants || []).map((grant, index) => ({
        id: String(
          grant?.id ||
          grant?.code ||
          grant?.grantId ||
          grant?.grant_id ||
          `${group.parentId}-${index}`
        ),
        name: getSubgrantLabel(grant),
        score: getDisplayScore(grant),
      }));
    };
    const filterOutParentGrantRow = (rows, group) => {
      const norm = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const primary = group?.grants?.[0] || {};
      const parentName = norm(group?.parentName || '');
      const parentId = norm(group?.parentId || '');
      const bestVariantName = norm(
        primary?.bestVariant?.displayName ||
        primary?.bestVariant?.fullName ||
        primary?.bestVariant?.name ||
        ''
      );
      const bestVariantKey = norm(
        primary?.bestVariant?.variantKey ||
        primary?.bestVariant?.grantCode ||
        ''
      );
      const whatThisGrantIsFor = norm(primary?.whatThisGrantIsFor || primary?.what_this_grant_is_for || '');
      const parentTokens = new Set(parentName.split(' ').filter(Boolean));
      const looksLikeParentName = (name) => {
        const n = norm(name);
        if (!n) return false;
        if (n === parentName || n === parentId) return true;
        if (parentName && (n === `${parentName} ${parentId}` || n === `${parentId} ${parentName}`)) return true;
        const tokens = n.split(' ').filter(Boolean);
        if (!tokens.length) return false;
        const overlap = tokens.filter((t) => parentTokens.has(t)).length;
        // Treat as parent duplicate when nearly all tokens are parent tokens (e.g. "Enterprise Development Grant (EDG)")
        return overlap >= Math.max(2, tokens.length - 1) && tokens.length <= parentTokens.size + 2;
      };
      return (rows || []).filter((row) => {
        const name = norm(row?.name || '');
        const id = norm(row?.id || '');
        if (!name && !id) return false;
        if (bestVariantKey && id === bestVariantKey) return false;
        if (bestVariantName && name === bestVariantName) return false;
        if (whatThisGrantIsFor && name && whatThisGrantIsFor.includes(name)) return false;
        if (id && (id === parentName || id === parentId)) return false;
        if (looksLikeParentName(name)) return false;
        return true;
      });
    };

    return groups.map((group, groupIndex) => {
      const subgrantRows = filterOutParentGrantRow(buildSubgrantRows(group), group);
      const shouldShowSubgrantSection = subgrantRows.length > 1;
      const shouldOpenGroup =
        typeof defaultOpenGroup === 'function'
          ? defaultOpenGroup(group, groupIndex)
          : defaultOpen;
      return (
      <details
        key={`parent-group-${group.parentId}`}
        className="group bg-white border rounded-2xl shadow-sm"
        style={{ borderColor: '#BFDBFE' }}
        open={shouldOpenGroup || undefined}
      >
        <summary className="list-none cursor-pointer px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0 pr-3">
            <p className="text-base font-semibold leading-relaxed break-words" style={{ color: '#60A5FA' }}>
              {getClosedCardTitle(group.parentName, group.parentId)}
            </p>
            <p className="text-sm mt-1 leading-relaxed" style={{ color: '#6B7280' }}>
              {getParentDescription(group.grants[0])}
            </p>
            <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
              {group.grants.length} grant type{group.grants.length === 1 ? '' : 's'} eligible in this family
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className="text-xs font-semibold px-2.5 py-1 rounded-full border"
              style={{ borderColor: '#BFDBFE', color: '#4B5563', backgroundColor: '#FFFFFF' }}
            >
              {group.parentId}
            </span>
            <ChevronDown className="w-4 h-4 text-[#60A5FA] transition-transform duration-200 group-open:rotate-180" />
          </div>
        </summary>
        <div className="px-5 pb-5 border-t pt-4 space-y-4" style={{ borderColor: '#BFDBFE' }}>
          {renderGrantCard({
            ...(group.grants[0] || {}),
            id: `parent-${group.parentId}`,
            grantId: group.parentId,
            grant_id: group.parentId,
            code: group.parentId,
            acronym: group.parentId,
            grantFullName: group.parentName,
            grant_full_name: group.parentName,
            name: group.parentName,
          })}
          {shouldShowSubgrantSection ? (
            <>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#6B7280' }}>
                Subgrant types
              </p>
              <ul className="space-y-2">
                {subgrantRows.map((subgrant, index) => {
                  return (
                    <li
                      key={`subgrant-${group.parentId}-${subgrant.id || index}`}
                      className="flex items-start justify-between gap-3 text-sm border rounded-xl px-3 py-2"
                      style={{ color: '#4B5563', borderColor: '#DBEAFE' }}
                    >
                      <span className="min-w-0 pr-3 leading-relaxed break-words">
                        {subgrant.name}
                      </span>
                      {typeof subgrant.score === 'number' ? (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-md border flex-shrink-0" style={{ borderColor: '#BFDBFE', color: '#4B5563' }}>
                          {Math.round(subgrant.score)}%
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}
        </div>
      </details>
    )});
  };

  const bestMatchGovernmentGrants = useMemo(
    () => [...eligibleGrants, ...likelyGrants].sort(compareByClientUsefulness),
    [eligibleGrants, likelyGrants, compareByClientUsefulness]
  );

  const eligibleGrantSurfaceItems = useMemo(() => {
    if (eligibleSurfaceFilter === 'government') {
      return bestMatchGovernmentGrants.map((grant) => ({ type: 'grant', item: grant })).sort((a, b) => compareByClientUsefulness(a.item, b.item));
    }
    if (eligibleSurfaceFilter === 'independent') {
      return eligibleIndependentPathways.map((item) => ({ type: 'funding', item })).sort((a, b) => compareByClientUsefulness(a.item, b.item));
    }
    return [
      ...bestMatchGovernmentGrants.map((grant) => ({ type: 'grant', item: grant })),
      ...eligibleIndependentPathways.map((item) => ({ type: 'funding', item })),
    ].sort((a, b) => compareByClientUsefulness(a.item, b.item));
  }, [eligibleSurfaceFilter, bestMatchGovernmentGrants, eligibleIndependentPathways, compareByClientUsefulness]);

  // Every analyzed (unapproved, displayable) grant is exportable; the report-mode choice in the
  // export dialog decides how many are profiled in full.
  const exportableGrants = displayableUnapprovedGrants;

  const pdfExportViewModel = useMemo(() => {
    const companyContext = {
      companyName: results?.inputsUsed?.company_name || results?.inputsUsed?.companyName || formData?.companyName || formData?.company_name || 'Not available',
      industry: results?.inputsUsed?.industry || formData?.industry || 'Not available',
      employees: results?.inputsUsed?.employee_count || results?.inputsUsed?.employees || formData?.employee_count || formData?.employees || null,
      annualRevenue: results?.inputsUsed?.annual_revenue || results?.inputsUsed?.annualRevenue || formData?.annual_revenue || formData?.annualRevenue || null,
      localOwnership: results?.inputsUsed?.local_ownership_pct || results?.inputsUsed?.local_ownership_percentage || formData?.local_ownership_pct || formData?.local_ownership_percentage || null,
      projectType: results?.inputsUsed?.project_type || formData?.project_type || formData?.projectType || null,
      projectBudget: results?.inputsUsed?.project_budget || formData?.project_budget || formData?.projectBudget || null,
      projectScope: results?.inputsUsed?.project_scope || results?.inputsUsed?.projectScope || formData?.project_scope || formData?.projectScope || formData?.project_description || null,
      availableDocuments: results?.inputsUsed?.available_documents || formData?.available_documents || formData?.availableDocuments || [],
      assessmentDate: results?.generatedAt || new Date().toISOString(),
      assessmentId: results?.snapshotId || locationJobId || 'Not available',
    };

    const viewModelMode =
      pdfReportMode === 'compact_advisory'
        ? 'executive'
        : pdfReportMode === 'full_all_grants'
          ? 'full'
          : 'standard';
    const includeLowerPriority = PDF_INCLUDE_LOWER_PRIORITY && viewModelMode !== 'executive';
    const includeDetailedScoringAppendix = PDF_INCLUDE_TECHNICAL_APPENDIX || PDF_INCLUDE_DETAILED_SCORING || viewModelMode === 'full';
    const includeEvidenceTrace = PDF_INCLUDE_TECHNICAL_APPENDIX || PDF_INCLUDE_EVIDENCE_TRACE || viewModelMode === 'full';
    return buildPDFExportViewModel({
      grants: exportableGrants,
      mode: viewModelMode,
      minimumScoreThreshold,
      includeLowerPriority,
      includeFundingSnapshots: PDF_INCLUDE_FUNDING_SNAPSHOTS,
      includeDocumentChecklist: PDF_INCLUDE_DOCUMENT_CHECKLIST,
      includeDetailedScoringAppendix,
      includeEvidenceTrace,
      includeScoringAppendix: includeDetailedScoringAppendix,
      companyContext,
      rankingKeys: [
        ...(Array.isArray(results?.recommended) ? results.recommended : []),
        ...(Array.isArray(results?.backups) ? results.backups : []),
      ],
    });
  }, [
    exportableGrants,
    pdfReportMode,
    minimumScoreThreshold,
    results,
    formData,
    locationJobId,
  ]);

  const modalTopIncludedCount = (pdfExportViewModel?.topGrantProfiles?.length || 0);
  const modalLowerPriorityCount = (pdfExportViewModel?.lowerPriority?.length || 0);
  const modalGrantsIncludedCount = modalTopIncludedCount + modalLowerPriorityCount;
  const modalEstimatedPages = pdfExportViewModel?.stats?.estimatedPageCount ?? 'Not available';

  const canConfirmExport = useMemo(() => {
    const hasExpanded = (pdfExportViewModel?.topGrantProfiles?.length || 0) > 0;
    const hasCompact = (pdfExportViewModel?.lowerPriority?.length || 0) > 0;
    return (hasExpanded || hasCompact) && !!pdfExportViewModel?.validation?.valid;
  }, [pdfExportViewModel]);

  const handleOpenExportModal = () => {
    if (!allAnalyzedGrants.length) {
      setExportError('No grants are available to export yet. Complete the assessment first.');
      return;
    }
    setExportError('');
    setIsExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    if (isExportingPDF) return;
    setIsExportModalOpen(false);
  };

  // Top 3 grants (when no 100% grants exist) - computed from sortedGrants
  const top3Grants = useMemo(() => {
    if (perfectScoreGrants.length > 0) return [];
    return sortedGrants.slice(0, 3);
  }, [sortedGrants, perfectScoreGrants.length]);

  const _toggleCustomGrantSelection = useCallback((grantKey) => {
    if (!grantKey) return;
    setCustomSelectedGrantIds((prev) => {
      const next = new Set(prev);
      if (next.has(grantKey)) {
        next.delete(grantKey);
      } else {
        next.add(grantKey);
      }
      return next;
    });
  }, []);

  // Grants a client can choose to pursue: only actionable eligible/likely routes,
  // ordered the same way the client-facing recommendation sections are ordered.
  const selectableGrants = useMemo(() => {
    const seen = new Set();
    const rows = [];
	    const push = (grant) => {
	      const key = resolveGrantKey(grant);
	      if (!key || seen.has(key) || isLegacyForNewApplicationsGrant(grant)) return;
	      seen.add(key);
	      rows.push(grant);
    };
    bestMatchGovernmentGrants.forEach(push);
    (eligibleIndependentPathways || []).forEach(push);
    return rows.sort(compareByClientUsefulness);
  }, [bestMatchGovernmentGrants, eligibleIndependentPathways, compareByClientUsefulness]);

  // Preselect previously confirmed grants from the client-ops record.
  useEffect(() => {
    if (!embedded || adminView || !isClient || !selectableGrants.length || grantSelectionPreloadedRef.current) return;
    grantSelectionPreloadedRef.current = true;
    (async () => {
      try {
        const payload = await apiService.getMyClientOpsRecord();
        const stored = Array.isArray(payload?.record?.selected_grants) ? payload.record.selected_grants : [];
        if (!stored.length) return;
        const codes = new Set(
          stored
            .map((row) => String(row?.code || row?.name || '').trim().toLowerCase())
            .filter(Boolean)
        );
        setCustomSelectedGrantIds((prev) => {
          const next = new Set(prev);
          selectableGrants.forEach((grant) => {
            const key = resolveGrantKey(grant);
            const code = String(grant?.code || grant?.name || '').trim().toLowerCase();
            if (key && codes.has(code)) next.add(key);
          });
          return next;
        });
        setGrantSelectionSaved(true);
      } catch {
        // no stored selection available
      }
    })();
  }, [embedded, adminView, isClient, selectableGrants]);

  const handleConfirmSelectedGrants = useCallback(async () => {
    // The admin reads a client's results; their own selection is never saved from here.
    if (adminView) return;
    const chosen = selectableGrants.filter((grant) => customSelectedGrantIds.has(resolveGrantKey(grant)));
    if (!chosen.length && !grantSelectionSaved) {
      setGrantSelectionError('Select at least one grant first.');
      return;
    }
    setGrantSelectionSaving(true);
    setGrantSelectionError("");
    try {
      await apiService.updateSelectedGrants(
        chosen.map((grant) => ({
          code: String(grant?.code || grant?.name || '').trim(),
          name: String(grant?.name || grant?.displayName || grant?.code || '').trim(),
          score: Number.isFinite(Number(grant?.score)) ? Number(grant.score) : null,
        }))
      );
      setGrantSelectionSaved(true);
    } catch (error) {
      setGrantSelectionError(String(error?.message || 'Unable to save your selection. Please try again.'));
    } finally {
      setGrantSelectionSaving(false);
    }
  }, [adminView, selectableGrants, customSelectedGrantIds, grantSelectionSaved]);

  const handleScheduleConsultation = useCallback(() => {
    const chosenNames = selectableGrants
      .filter((grant) => customSelectedGrantIds.has(resolveGrantKey(grant)))
      .map((grant) => String(grant?.name || grant?.code || '').trim())
      .filter(Boolean);
    const email = adminView ? '' : getSessionEmail() || '';
    const name = String(formSource?.companyName || formSource?.company_name || '').trim();
    const config = {
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
      notes: chosenNames.length ? `Selected grants: ${chosenNames.join(', ')}` : 'Grant consultation',
    };
    try {
      if (window.Cal?.ns?.['15min']) {
        window.Cal.ns['15min']('modal', { calLink: 'optralabs/15min', config });
        return;
      }
    } catch {
      // fall through to the plain link
    }
    window.open('https://cal.com/optralabs/15min', '_blank', 'noopener');
  }, [adminView, selectableGrants, customSelectedGrantIds, formSource]);

  const overallStatus = useMemo(() => {
    if (!results || !results.grants || !Array.isArray(results.grants)) return 'ineligible';
    
    // Special 100% rule: if any grants have perfect scores, status is eligible
    if (perfectScoreGrants.length > 0) return 'eligible';
    
    // Otherwise, check if top 3 grants are eligible
    const topScore = top3Grants.length > 0 ? top3Grants[0]?.score || 0 : 0;
    if (topScore >= 80) return 'eligible';
    if (topScore >= 60) return 'pending';
    return 'ineligible';
  }, [results, perfectScoreGrants.length, top3Grants]);

  const buildMarkdownReport = useCallback(() => {
    const lines = [];
    const sourceGrants = exportableGrants || [];
    // Ranking source of truth: backend `recommended` (ordered), then `backups`
    // (ordered), then everything else by display score. Blocked grants sink to
    // the end of the "rest" so a gated scheme never reads as a top pick.
    const recommendedOrder = Array.isArray(results?.recommended) ? results.recommended : [];
    const backupOrder = Array.isArray(results?.backups) ? results.backups : [];
    const rankIndex = (grant, list) =>
      list.findIndex((key) => findGrantByKey([grant], key) !== null);
    const reportGrants = [...sourceGrants].sort((a, b) => {
      const recA = rankIndex(a, recommendedOrder);
      const recB = rankIndex(b, recommendedOrder);
      if (recA !== -1 || recB !== -1) {
        if (recA === -1) return 1;
        if (recB === -1) return -1;
        return recA - recB;
      }
      const bakA = rankIndex(a, backupOrder);
      const bakB = rankIndex(b, backupOrder);
      if (bakA !== -1 || bakB !== -1) {
        if (bakA === -1) return 1;
        if (bakB === -1) return -1;
        return bakA - bakB;
      }
      const blockedA = isBlockedGrant(a) ? 1 : 0;
      const blockedB = isBlockedGrant(b) ? 1 : 0;
      if (blockedA !== blockedB) return blockedA - blockedB;
      const scoreA = getDisplayScore(a) || 0;
      const scoreB = getDisplayScore(b) || 0;
      return scoreB - scoreA;
    });
    const lowerPriorityRows = pdfExportViewModel?.lowerPriority || [];
    const includeScoringDetails = PDF_INCLUDE_TECHNICAL_APPENDIX || PDF_INCLUDE_DETAILED_SCORING || pdfReportMode === 'full';
    const includeEvidenceTrace = PDF_INCLUDE_TECHNICAL_APPENDIX || PDF_INCLUDE_EVIDENCE_TRACE || pdfReportMode === 'full';
    const includeDocumentChecklist = PDF_INCLUDE_DOCUMENT_CHECKLIST;
    const includeFundingSnapshots = PDF_INCLUDE_FUNDING_SNAPSHOTS;
    const clientSnapshot = pdfExportViewModel?.clientSnapshot || {};
    const exportStats = pdfExportViewModel?.stats || {};
    
    // ============================================================================
    // TITLE PAGE - Professional Consultant Format (No HTML, Pure Markdown)
    // ============================================================================
    
    lines.push('# GRANT ELIGIBILITY ASSESSMENT REPORT');
    lines.push('');
    lines.push('**Prepared by:** OPTRA Labs Pte. Ltd.');
    lines.push('');
    
    // Company Information Box - Enhanced with all user input details
    const companyName = results?.inputsUsed?.company_name || results?.inputsUsed?.companyName || formData?.companyName || formData?.company_name || 'Company';
    const industry = results?.inputsUsed?.industry || formData?.industry || 'Industry';
    const employeeCount = results?.inputsUsed?.employee_count || results?.inputsUsed?.employees || formData?.employee_count || formData?.employees || 0;
    const revenue = results?.inputsUsed?.annual_revenue || results?.inputsUsed?.annualRevenue || formData?.annual_revenue || formData?.annualRevenue || 0;
    const projectType = results?.inputsUsed?.project_type || formData?.project_type || formData?.projectType || null;
    const projectScope = results?.inputsUsed?.project_scope || results?.inputsUsed?.projectScope || formData?.project_scope || formData?.projectScope || formData?.project_description || null;
    const projectBudget = results?.inputsUsed?.project_budget || formData?.project_budget || formData?.projectBudget || null;
    const _operationalPresence = results?.inputsUsed?.operational_presence || formData?.operational_presence || formData?.operationalPresence || null;
    const _availableDocuments = results?.inputsUsed?.available_documents || formData?.available_documents || formData?.availableDocuments || null;
    const generatedDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    
    lines.push(`**Date:** ${generatedDate}`);
    lines.push('');
    lines.push(`**Assessment ID:** ${results?.snapshotId || 'N/A'}`);
    lines.push('');
    const reportModeLabel = pdfReportMode === 'compact_advisory'
      ? 'Compact Advisory Report'
      : pdfReportMode === 'full_all_grants'
        ? 'Full All-Grants Advisory Report'
        : 'Standard Advisory Report';
    lines.push(`**Report mode:** ${reportModeLabel}`);
    lines.push('');
    lines.push(`**Estimated length:** ${exportStats?.estimatedPageCount || 'Not available'} pages`);
    lines.push('');
    lines.push(`**Export scope:** All analyzed grants (${reportGrants.length} grant${reportGrants.length === 1 ? '' : 's'})`);
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push('## CLIENT INFORMATION');
    lines.push('');
    lines.push(`**Company Name:** ${clientSnapshot.companyName || companyName}`);
    lines.push('');
    lines.push(`**Industry:** ${clientSnapshot.industry || industry}`);
    lines.push('');
    if ((employeeCount > 0) || (clientSnapshot.employees && clientSnapshot.employees !== 'Not available')) {
      lines.push(`**Number of Employees:** ${clientSnapshot.employees || employeeCount.toLocaleString()}`);
      lines.push('');
    }
    if (revenue > 0) {
      lines.push(`**Annual Revenue:** ${clientSnapshot.annualRevenue || `S$${revenue.toLocaleString()}`}`);
      lines.push('');
    }
    if (projectType) {
      const projectTypeDisplay = Array.isArray(projectType) ? projectType.join(', ') : String(projectType);
      lines.push(`**Project Type:** ${projectTypeDisplay}`);
      lines.push('');
    }
    if (projectScope) {
      const projectScopeDisplay = Array.isArray(projectScope) ? projectScope.join(', ') : String(projectScope);
      lines.push(`**Project Scope:** ${projectScopeDisplay}`);
      lines.push('');
    }
    if (projectBudget) {
      const budgetDisplay = typeof projectBudget === 'number' ? `S$${projectBudget.toLocaleString()}` : String(projectBudget);
      lines.push(`**Project Budget:** ${clientSnapshot.projectBudget || budgetDisplay}`);
      lines.push('');
    }
    lines.push('---');
    lines.push('');

    // ------------------------------------------------------------------------
    // CONTRACT SECTIONS (parsed by the PDF generator — keep headings exact):
    // ## Your Submission / ## Eligibility After Prerequisites / ## Programme Transitions
    // ------------------------------------------------------------------------
    const allResultGrants = Array.isArray(results?.grants) ? results.grants : [];
    const submissionRows = buildSubmissionRows({ ...(formData || {}), ...(results?.inputsUsed || {}) });
    lines.push('## Your Submission');
    lines.push('');
    if (submissionRows.length > 0) {
      submissionRows.forEach((row) => {
        lines.push(`- **${row.label}:** ${row.value.replace(/\s+/g, ' ').trim()}`);
      });
    } else {
      lines.push('- Submission details were not recorded with this assessment.');
    }
    lines.push('');

    const eligibilitySummaryPdf = results?.eligibilitySummary || (() => {
      // Derive from per-grant prerequisite data when the backend summary is absent.
      const eligibleNow = [];
      const conditional = [];
      const blocked = [];
      allResultGrants.forEach((g) => {
        const state = getGrantEligibilityAfterPrerequisites(g);
        const key = resolveGrantKey(g);
        if (!key) return;
        if (state === 'blocked') blocked.push({ key, reason: getGrantBlockReason(g) });
        else if (state === 'conditional') {
          const unknown = getGrantPrerequisiteChecks(g).find((c) => c.status !== 'met');
          conditional.push({ key, reason: unknown ? unknown.detail || unknown.label : '' });
        } else if (state === 'eligible_now') eligibleNow.push({ key, reason: '' });
      });
      if (!eligibleNow.length && !conditional.length && !blocked.length) return null;
      return { eligibleNow, conditional, blocked, excluded: [] };
    })();
    lines.push('## Eligibility After Prerequisites');
    lines.push('');
    if (eligibilitySummaryPdf) {
      const nameFor = (key) => grantDisplayNameForKey(allResultGrants, key);
      const eligibleNames = eligibilitySummaryPdf.eligibleNow.map((item) => nameFor(item.key)).filter(Boolean);
      lines.push(`- **Eligible now:** ${eligibleNames.length ? eligibleNames.join(', ') : 'None of the reviewed schemes clears every structural prerequisite yet.'}`);
      if (eligibilitySummaryPdf.conditional.length > 0) {
        eligibilitySummaryPdf.conditional.forEach((item) => {
          lines.push(`- **Conditional:** ${nameFor(item.key)}${item.reason ? ` — ${item.reason}` : ''}`);
        });
      } else {
        lines.push('- **Conditional:** None');
      }
      if (eligibilitySummaryPdf.blocked.length > 0) {
        eligibilitySummaryPdf.blocked.forEach((item) => {
          lines.push(`- **Blocked:** ${nameFor(item.key)}${item.reason ? ` — ${item.reason}` : ''}`);
        });
      } else {
        lines.push('- **Blocked:** None');
      }
    } else {
      lines.push('- Structural prerequisite checks were not recorded for this assessment.');
    }
    lines.push('');

    const transitionsPdf = Array.isArray(results?.programmeTransitions) ? results.programmeTransitions : [];
    lines.push('## Programme Transitions');
    lines.push('');
    if (transitionsPdf.length > 0) {
      transitionsPdf.forEach((t) => {
        const affected = t.affectedKeys.length ? t.affectedKeys.join(', ') : 'the schemes above';
        const head = t.timing ? `${t.successor} (${t.timing})` : t.successor;
        const source = t.sourceUrl ? ` (Source: ${t.sourceUrl})` : '';
        const verifiedHint = t.verified ? '' : ' [unverified]';
        lines.push(`- **${head}:** affects ${affected} — ${t.whatItMeansNow || 'Details pending.'}${source}${verifiedHint}`);
      });
    } else {
      lines.push('- No programme transitions currently affect the schemes in this report.');
    }
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push('## EXECUTIVE SUMMARY');
    lines.push('');
    
    const grantTitleForPdf = (g) => {
      return String(getGrantTrackLabel(g))
        .replace(/\|/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    };
    // The PDF keeps "blocked" strictly for unmet structural prerequisites; a
    // not-currently-open scheme stays in its score tier there because the
    // report generator already groups it by its application-window text.
    const pdfTier = (g) => {
      const status = resolveGrantStatus(g);
      if (status === 'blocked' && !isBlockedGrant(g)) {
        const pct = getDisplayScore(g);
        return pct !== null ? resolveScoreTier(pct) : 'weakFit';
      }
      return status;
    };
    const pdfEligible = reportGrants.filter((g) => pdfTier(g) === 'eligible');
    const pdfLikely = reportGrants.filter((g) => pdfTier(g) === 'likely');
    const pdfLessLikely = reportGrants.filter((g) => pdfTier(g) === 'lessLikely');
    const pdfWeak = reportGrants.filter((g) => pdfTier(g) === 'weakFit');
    const pdfBlocked = reportGrants.filter((g) => pdfTier(g) === 'blocked');
    const pdfHardIneligible = reportGrants.filter((g) => pdfTier(g) === 'ineligible');

    const pushTierList = (title, list) => {
      if (list.length === 0) return;
      lines.push(title);
      lines.push('');
      list.forEach((g) => {
        const sc = getDisplayScore(g);
        lines.push(`- **${grantTitleForPdf(g)}**${sc != null ? ` — ${sc}%` : ''}`);
      });
      lines.push('');
    };

    const recommendedActionForTopTable = (grant, status) => {
      const name = String(grant?.name || grant?.grantFullName || grant?.grant_full_name || '').toLowerCase();
      if (/psg|productivity solutions grant/.test(name)) {
        return 'Confirm the current pre-approved solution/vendor route and separate any bespoke AI, integration, or R&D scope.';
      }
      if (/edg|enterprise development grant/.test(name)) {
        return 'Prepare the business problem, solution, milestones, outcomes, team/vendor capability, and itemised costs.';
      }
      if (/mra|market readiness assistance/.test(name)) {
        return 'Confirm the named overseas market, customer segment, eligible market-entry activity, and budget.';
      }
      if (/startup sg|startupsg|startup singapore/.test(name)) {
        return 'Document proprietary technology, technical risk, POC/POV milestones, and validation evidence.';
      }
      if (status === 'likely_fit') {
        return 'Confirm the missing evidence items, then prepare the application pack around the strongest fit signals.';
      }
      return 'Prepare an application pack linking scope, budget, milestones, outcomes, and supporting evidence.';
    };
    const compactTopTableCell = (text, maxLen = 160) => {
      const s = String(text || '').replace(/[|]/g, ' ').replace(/\s+/g, ' ').trim();
      if (!s) return 'Not available';
      return s.length > maxLen ? `${s.slice(0, maxLen - 3)}...` : s;
    };

    lines.push(
      `This advisory reviews ${reportGrants.length} prioritized scheme${reportGrants.length === 1 ? '' : 's'} for **${companyName}** using strategic relevance and fit scoring.`
    );
    lines.push('');
    lines.push('### Top Recommended Grants');
    lines.push('');
    lines.push('| Rank | Grant | Status | Match Score | Confidence | Strategic Fit | Recommended Action |');
    lines.push('|---|---|---|---|---|---|---|');
    (pdfExportViewModel?.topGrantProfiles || []).forEach((item, idx) => {
      const action = recommendedActionForTopTable(item.grant || item, item.status);
      const statusLabel = item.status === 'strong_match'
        ? 'Strong Match'
        : item.status === 'likely_fit'
          ? 'Likely Fit'
          : item.status === 'possible_fit'
            ? 'Possible Fit'
            : 'Low Priority';
      const grantLabel = getGrantDisplayName(item.grant || item).replace(/\s+/g, ' ').trim();
      const matchScore = Math.round(item.alignmentScore);
      const strategicScore = Math.round(item.strategicRelevanceScore);
      lines.push(`| ${idx + 1} | ${compactTopTableCell(grantLabel, 120)} | ${compactTopTableCell(statusLabel, 60)} | ${matchScore}% (${scoreBandLabel(matchScore)}) | ${compactTopTableCell(item.confidenceBand || 'Not available', 80)} | ${strategicScore}% (${scoreBandLabel(strategicScore)}) | ${compactTopTableCell(action, 180)} |`);
    });
    lines.push('');
    if (pdfReportMode === 'full') {
      pushTierList(`### Eligible (${TIER_ELIGIBLE_MIN}%+ signal match · Strong / High)`, pdfEligible);
      pushTierList(`### Likely (${TIER_LIKELY_MIN}–${TIER_ELIGIBLE_MIN - 1}% · Promising / Medium)`, pdfLikely);
      pushTierList(`### Less likely (${TIER_LESS_LIKELY_MIN}–${TIER_LIKELY_MIN - 1}% · Partial / Low)`, pdfLessLikely);
      pushTierList(`### Weak fit (${TIER_WEAK_MIN}–${TIER_LESS_LIKELY_MIN - 1}%; low alignment, not automatically ineligible)`, pdfWeak);
      pushTierList('### Prerequisite needed before application', pdfBlocked);
      pushTierList('### Ineligible (mandatory requirement not met)', pdfHardIneligible);
    }
    lines.push('---');
    lines.push('');
    lines.push('## GRANT OPPORTUNITIES');
    lines.push('');

    // Helper function to clean text (same as in render)
    const cleanText = (text) => {
      if (!text || typeof text !== 'string') return '';
      const trimmed = text
        .replace(/[✓✔☑]/g, 'Met')
        .replace(/[✗✘✕✖]/g, 'Not met')
        .replace(/[•●▪◦]/g, '-')
        .replace(/[—–]/g, '-')
        .replace(/\bunknown\b/gi, '')
        .replace(/\byour\s+your\b/gi, 'your')
        .replace(/\s+/g, ' ')
        .trim();
      if (!trimmed) return '';
      const placeholderPhrases = new Set([
        'no detailed information available',
        'not available',
        'n/a',
        'no data provided'
      ]);
      if (placeholderPhrases.has(trimmed.toLowerCase())) return '';
      return trimmed;
    };

    const dedupeList = (items = []) => {
      const seen = new Set();
      const output = [];
      for (const item of items) {
        const cleaned = cleanText(item);
        if (!cleaned) continue;
        const key = cleaned.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          output.push(cleaned);
        }
      }
      return output;
    };

    const normalizePdfTextKey = (text) =>
      String(text || '')
        .toLowerCase()
        .replace(/[—–]/g, '-')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const overlapsPdfText = (candidate, existing = []) => {
      const key = normalizePdfTextKey(candidate);
      if (key.length < 18) return false;
      return existing.some((item) => {
        const other = normalizePdfTextKey(item);
        if (other.length < 18) return false;
        return key.includes(other) || other.includes(key);
      });
    };

    const vagueNextStepRegex =
      /^(edit project scope|review company fit|improve evidence|validate fit|check eligibility|review requirements|prepare documents|consider applying|research further|confirm suitability)\.?$/i;

    const isVaguePdfNextStep = (text) => {
      const cleaned = cleanText(text);
      if (!cleaned) return true;
      if (vagueNextStepRegex.test(cleaned)) return true;
      return cleaned.split(/\s+/).length < 5;
    };

    const fallbackNextStepForPdf = (grant, status) => {
      const name = String(grant?.name || grant?.grantFullName || grant?.grant_full_name || '').toLowerCase();
      if (/psg|productivity solutions grant/.test(name)) {
        return 'Confirm whether the proposed solution maps to a current PSG pre-approved solution and approved vendor; if only part qualifies, separate the standard adoption scope from bespoke AI, integration, or R&D work.';
      }
      if (/edg|enterprise development grant/.test(name)) {
        return 'Prepare a project brief covering the business problem, proposed solution, milestones, responsible vendor or internal team, measurable outcomes, and itemised project-cost breakdown.';
      }
      if (/mra|market readiness assistance/.test(name)) {
        return 'Define the named overseas market, target customer segment, eligible market-entry activity, and budget before treating this as an application-ready route.';
      }
      if (/startup sg|startupsg|startup singapore/.test(name)) {
        return 'Document the proprietary technology, unresolved technical risk, POC or POV milestones, and evidence that the work is more than ordinary software implementation.';
      }
      if (status === 'blocked') {
        const reason = cleanText(getGrantBlockReason(grant));
        return reason
          ? `Resolve the recorded prerequisite before pursuing this route: ${reason}`
          : 'Resolve the recorded prerequisite before treating this route as application-ready.';
      }
      return 'Prepare a focused application pack that links the submitted project scope, budget, milestones, expected outcomes, and supporting documents to the programme criteria.';
    };

    /** Markdown table cell: single line, no | characters, bounded length for PDF layout. */
    const pdfTableCell = (text, maxLen = 400) => {
      let s = cleanText(text);
      if (!s) return '';
      s = s.replace(/\|/g, ' ');
      if (s.length > maxLen) return `${s.slice(0, maxLen - 3)}...`;
      return s;
    };

    reportGrants.forEach((grant, index) => {
      // Use the exact same logic as renderGrantCard for consistency
      const grantKey = resolveGrantKey(grant);
      const grantStatus = resolveGrantStatus(grant);
      
      // Status label (matching results page exactly)
      const statusLabels = {
        eligible: 'Eligible',
        likely: 'Likely',
        lessLikely: 'Less likely',
        weakFit: 'Weak fit',
        blocked: 'Prerequisite needed',
        ineligible: 'Not eligible',
      };
      const statusLabel = statusLabels[grantStatus] || grant.fit?.label || 'Needs review';

      // Use shared helper functions for consistent score calculation
      const certaintyPct = getCertaintyPercentage(grant);
      const displayScore = getDisplayScore(grant);
      const strategicFitScore = getStrategicFitScore(grant);
      // Signal match number + label are always the same pair the backend derived
      // (signalMatchPct); fall back to the display score for older results.
      const signalMatchPctPdf = getSignalMatchPct(grant) ?? displayScore;
      const signalMatchLabel = grant.signalMatchLabel || grant.signal_match_label || scoreBandLabel(signalMatchPctPdf);
      const strategicFitLabel = grant.strategicFitLabel || grant.strategic_fit_label || scoreBandLabel(strategicFitScore);
      const grantIsBlockedPdf = grantStatus === 'blocked';
      const prerequisiteChecksPdf = getGrantPrerequisiteChecks(grant);
      const referenceRowsPdf = buildReferenceRows(grant);
      const officialUrlPdf = getGrantOfficialUrl(grant);
      const transitionNotePdf = getGrantTransitionNote(grant);
      const reasonNotPrioritizedPdf = cleanText(grant.reasonNotPrioritized || grant.reason_not_prioritized || '');
      const pillarLabelPdf = cleanText(grant.pillarLabel || grant.pillar_label || '');
      
      // Grant identifiers (matching results page exactly - line 1984-1985)
      const grantIdValue = grant.grantId || grant.grant_id || grant.code || grant.acronym || grantKey || 'N/A';
      const grantFullName = getGrantTrackLabel(grant) || grantKey || `Grant ${index + 1}`;
      
      // Get tailored text (matching renderGrantCard exactly - line 1878-1892)
      let tailoredText;
      try {
        tailoredText = formatTailoredText(grant, results?.inputsUsed, results);
      } catch {
        tailoredText = {
          quickVerdict: 'Unable to generate tailored analysis',
          whyMatch: [],
          remediationActions: [],
          documentsNeeded: [],
          budgetSnapshot: {},
          risks: [],
          nextSteps: []
        };
      }

      // Get detailed analysis (matching renderGrantCard exactly - line 1919-1937)
      const detailedAnalysis = grant.detailedAnalysis || grant.detailed_analysis || {};
      const enhancedAdvisor = getEnhancedAdvisorAnalysis(detailedAnalysis);
      const summaryBenefits = grant.summaryUserBenefits || grant.summary_user_benefits || null;
      const summaryHeadlineRaw = cleanText(summaryBenefits?.headline || '');
      const ruleHits = Array.isArray(detailedAnalysis.rule_hits) ? detailedAnalysis.rule_hits : [];
      const weightedBreakdown = Array.isArray(detailedAnalysis.weighted_breakdown)
        ? detailedAnalysis.weighted_breakdown
        : [];
      const publicBreakdownPdf =
        detailedAnalysis.weighted_breakdown_public || detailedAnalysis.weightedBreakdownPublic;
      const breakdownForDisplayPdf = (
        Array.isArray(publicBreakdownPdf) && publicBreakdownPdf.length > 0
          ? publicBreakdownPdf
          : weightedBreakdown
      ).filter((item) => !isInternalCalibrationFactorName(item?.factor));
      const nextBestActions = Array.isArray(detailedAnalysis.next_best_actions) && detailedAnalysis.next_best_actions.length > 0
        ? detailedAnalysis.next_best_actions
        : grant.nextSteps || [];

      // Derive positive and negative reasons
      const derivePositiveReasons = () => {
        const fromAdvisor = dedupeList(enhancedAdvisor.matchedCriteriaAdvisor || []);
        if (fromAdvisor.length > 0) {
          return fromAdvisor;
        }
        if (ruleHits.length > 0) {
          return dedupeList(
            ruleHits
              .filter((hit) => hit.result === 'pass' && cleanText(hit.evidence))
              .map((hit) => cleanText(hit.evidence))
          );
        }
        if (Array.isArray(grant.whyEligible) && grant.whyEligible.length > 0) {
          return dedupeList(grant.whyEligible);
        }
        return dedupeList(tailoredText.whyMatch || []);
      };

      const deriveNegativeReasons = () => {
        const unmetAdvisorPdf = dedupeList(enhancedAdvisor.unmetCriteriaAdvisor || []);
        if (unmetAdvisorPdf.length > 0) {
          return unmetAdvisorPdf;
        }
        if (ruleHits.length > 0) {
          return dedupeList(
            ruleHits
              .filter((hit) => hit.result === 'fail' && cleanText(hit.evidence))
              .map((hit) => cleanText(hit.evidence))
          );
        }
        if (Array.isArray(grant.whyNotEligible) && grant.whyNotEligible.length > 0) {
          return dedupeList(grant.whyNotEligible);
        }
        return dedupeList(grant.whats_missing || []);
      };

      const positiveReasons = derivePositiveReasons();
      const negativeReasons = deriveNegativeReasons();
      const positiveCoreLinesPdf = dedupeList(
        dedupeList([...(enhancedAdvisor.matchedCriteriaAdvisor || []), ...positiveReasons])
          .map((r) =>
            cleanText(stripConsultantChurnTail(stripFactorColonPrefix(stripAdvisoryFiller(String(r)))))
          )
          .filter((line) => line && !isBannedInsightLine(line))
      );
      const displayPositivePdf = dedupeList(
        positiveCoreLinesPdf.map((line) => enrichWhyMatchConsultantLine(line)).filter(Boolean)
      );
      const displayNegativePdf = dedupeList([
        ...(enhancedAdvisor.unmetCriteriaAdvisor || []),
        ...negativeReasons,
      ]);
      const whereToImproveFromRisksPdf = (enhancedAdvisor.sections || []).flatMap((sec) =>
        Array.isArray(sec?.risks) ? sec.risks.map((x) => cleanText(String(x))).filter(Boolean) : []
      );
      const displayWhereToImprovePdf = filterConsultantInsightList(
        [
          ...displayNegativePdf,
          ...(enhancedAdvisor.missingInformation || []),
          ...whereToImproveFromRisksPdf,
        ],
        positiveCoreLinesPdf,
        cleanText
      );
      const submissionStrategyPdf =
        enhancedAdvisor.submissionStrategy ||
        detailedAnalysis?.submission_strategy ||
        detailedAnalysis?.submissionStrategy ||
        grant?.submission_strategy ||
        grant?.submissionStrategy ||
        null;
      const advisoryTraceabilityPdf =
        enhancedAdvisor.advisoryTraceability ||
        detailedAnalysis?.advisory_traceability ||
        detailedAnalysis?.advisoryTraceability ||
        grant?.advisory_traceability ||
        grant?.advisoryTraceability ||
        null;
      const whenApplyLabelPdf = (() => {
        const raw = String(
          submissionStrategyPdf?.when_to_apply || submissionStrategyPdf?.whenToApply || ''
        ).toLowerCase();
        if (raw === 'apply_now') return 'Apply now';
        if (raw === 'not_recommended') return 'Not recommended to prioritise now';
        return 'Apply after improvements';
      })();
      const llmRationale = stripScopeMissingBoilerplateSentences(
        cleanText(detailedAnalysis.llm_rationale || '')
      );
      
      const displayHeadline = summaryHeadlineRaw || cleanText(grant.summary) || tailoredText.quickVerdict;
      const advisoryExecPdf = cleanText(enhancedAdvisor.executiveSummary || '');

      // Start grant section with prominent status banner
      // ============================================================================
      // GRANT SECTION - Professional Consultant Format
      // ============================================================================
      
      lines.push(`### ${index + 1}. ${grantFullName}`);
      lines.push('');
      lines.push('| Field | Value |');
      lines.push('|-------|-------|');
      lines.push(`| Grant Code | ${pdfTableCell(String(grantIdValue), 120)} |`);
      lines.push(`| Eligibility Status | ${pdfTableCell(statusLabel, 80)} |`);
      if (pillarLabelPdf) {
        lines.push(`| Pillar | ${pdfTableCell(pillarLabelPdf, 80)} |`);
      }
      lines.push(`| Signal Match | ${signalMatchPctPdf !== null ? `${signalMatchPctPdf}% (${signalMatchLabel})` : 'N/A'} |`);
      lines.push(`| Strategic Fit | ${strategicFitScore !== null ? `${strategicFitScore}% (${strategicFitLabel})` : 'N/A'} |`);
      if (certaintyPct !== null && certaintyPct > 0) {
        lines.push(`| Assessment Confidence | ${certaintyPct}% (${certaintyBandLabel(certaintyPct)}) |`);
      }
      if (officialUrlPdf) {
        lines.push(`| Official Page | ${officialUrlPdf} |`);
      }
      lines.push('');
      lines.push('');
      if (grantIsBlockedPdf) {
        const blockReasonPdf = cleanText(getGrantBlockReason(grant));
        lines.push(`**Prerequisite needed before application.** ${blockReasonPdf || 'A mandatory requirement for this scheme is not met on the submitted inputs.'}`);
        lines.push('');
      }
      if (reasonNotPrioritizedPdf) {
        lines.push(`- **Why not prioritized:** ${reasonNotPrioritizedPdf}`);
        lines.push('');
      }
      if (
        typeof displayScore === 'number' &&
        displayScore < TIER_LESS_LIKELY_MIN &&
        grantStatus !== 'ineligible'
      ) {
        lines.push(
          '*Note: Lower preliminary alignment — treat as a secondary option unless you have a specific strategic reason to proceed.*'
        );
        lines.push('');
      }

      const grantPurposeForPdf = cleanText(getGrantPurposeText(grant));
      if (grantPurposeForPdf) {
        lines.push('### What this grant is for');
        lines.push('');
        lines.push(grantPurposeForPdf);
        lines.push('');
      }

      lines.push('### Detailed Analysis');
      lines.push('');
      const advisorSections = Array.isArray(enhancedAdvisor.sections) ? enhancedAdvisor.sections : [];
      const getWeightedFactorPdf = (...tokens) => {
        const lowered = tokens.map((t) => String(t).toLowerCase());
        return weightedBreakdown.find((item) =>
          lowered.some((token) => String(item?.factor || '').toLowerCase().includes(token))
        );
      };
      const formatFactorLinePdf = (title, factor, fallbackText) => {
        const facet = String(title || '').toLowerCase();
        if (!factor) {
          const fb = cleanText(fallbackText);
          if (!fb) return '';
          return `On ${facet}, ${fb.charAt(0).toLowerCase()}${fb.slice(1)}`;
        }
        const weightPct = Math.round((factor.weight || 0) * 100);
        const contrib = typeof factor.contribution === 'number' ? `${factor.contribution >= 0 ? '+' : ''}${factor.contribution}` : 'N/A';
        const evidence = cleanText(factor.evidence || '');
        return `On ${facet}, the scoring model applied a ${weightPct}% weight and recorded a contribution of ${contrib}. ${evidence || fallbackText}`;
      };
      const pdfNarrativeParts = [];
      const pdfSlots = [
        {
          title: 'Objective Fit',
          find: (item) => String(item?.factor || '').toLowerCase().includes('objective'),
          w: ['objective'],
          fb: 'Project objectives and outcomes should be tied to measurable grant priorities and national value creation.',
        },
        {
          title: 'Company Eligibility',
          find: (item) => String(item?.factor || '').toLowerCase().includes('company'),
          w: ['company', 'eligibility'],
          fb: 'Headcount, ownership, operating footprint, and financial stability materially impact committee confidence.',
        },
        {
          title: 'Project Alignment',
          find: (item) => String(item?.factor || '').toLowerCase().includes('alignment'),
          w: ['alignment', 'fit'],
          fb: 'Execution readiness, milestones, and outcomes determine whether the committee views the project as feasible and fundable.',
        },
        {
          title: 'Budget Feasibility',
          find: (item) => String(item?.factor || '').toLowerCase().includes('budget'),
          w: ['budget'],
          fb: 'Budget must be proportionate to scope, deliverables, and likely co-funding requirements.',
        },
        {
          title: 'Documentation Readiness',
          find: (item) => String(item?.factor || '').toLowerCase().includes('documentation'),
          w: ['doc', 'documentation'],
          fb: 'Missing mandatory files can delay or block approval even when strategic fit is good.',
        },
      ];
      pdfSlots.forEach((slot) => {
        const section = advisorSections.find(slot.find);
        if (section?.analysis) {
          if (isInternalCalibrationFactorName(section.factor)) return;
          pdfNarrativeParts.push(cleanText(section.analysis));
        } else {
          pdfNarrativeParts.push(
            cleanText(formatFactorLinePdf(slot.title, getWeightedFactorPdf(...slot.w), slot.fb))
          );
        }
      });
      const pdfNarrative = pdfNarrativeParts.filter(Boolean).join('\n\n');
      const advisorSectionsPdf = (enhancedAdvisor.sections || []).filter(
        (s) => s && !isInternalCalibrationFactorName(s.factor)
      );
      const hasStructuredAdvisorPdf = advisorSectionsPdf.some((s) => cleanText(s.analysis));
      if (hasStructuredAdvisorPdf) {
        advisorSectionsPdf.forEach((sec, sidx) => {
          const title =
            pdfTableCell(displayBreakdownFactorName(sec.factor) || String(sec.factor || `Section ${sidx + 1}`), 200);
          const analysis = cleanText(sec.analysis);
          const ev = Array.isArray(sec.evidence)
            ? sec.evidence.map((x) => cleanText(x)).filter(Boolean)
            : [];
          const risks = Array.isArray(sec.risks)
            ? sec.risks
                .map((x) => cleanText(x))
                .filter(Boolean)
                .filter((line) => !isBannedInsightLine(line))
            : [];
          if (!analysis && ev.length === 0 && risks.length === 0) return;
          lines.push(`#### ${title}`);
          lines.push('');
          if (analysis) {
            lines.push(analysis);
            lines.push('');
          }
          if (ev.length > 0) {
            lines.push('*Evidence from your inputs:*');
            ev.slice(0, 8).forEach((e) => lines.push(`- ${e}`));
            lines.push('');
          }
          if (risks.length > 0) {
            lines.push('*Risks and caveats:*');
            risks.slice(0, 6).forEach((e) => lines.push(`- ${e}`));
            lines.push('');
          }
        });
      } else {
        lines.push(
          pdfNarrative ||
            advisoryExecPdf ||
            cleanText(enhancedAdvisor.summaryOfKeyFactors || displayHeadline) ||
            'The grant assessment reflects objective fit, company eligibility, project alignment, budget feasibility, and documentation readiness.'
        );
        lines.push('');
      }
      // Why you match
      if (displayPositivePdf.length > 0) {
        lines.push('### Why you match');
        lines.push('');
        displayPositivePdf.slice(0, 3).forEach((reason) => {
          lines.push(`- ${reason}`);
        });
        lines.push('');
      }

      // Where to improve (only when filtered list has real items)
      if (displayWhereToImprovePdf.length > 0) {
        lines.push('### Where to improve');
        lines.push('');
        displayWhereToImprovePdf.slice(0, 6).forEach((reason) => {
          lines.push(`- ${reason}`);
        });
        lines.push('');
      }

      // Contract sub-sections (parsed by the PDF generator — keep headings exact)
      lines.push('### Prerequisites');
      lines.push('');
      if (prerequisiteChecksPdf.length > 0) {
        prerequisiteChecksPdf.forEach((check) => {
          const detail = cleanText(check.detail);
          const resolve = cleanText(check.howToResolve);
          let line = `- [${check.status}] ${cleanText(check.label)}`;
          if (detail) line += ` — ${detail}`;
          if (check.status !== 'met' && resolve) line += ` (How to resolve: ${resolve})`;
          lines.push(line);
        });
      } else {
        lines.push('- No structural prerequisites were recorded for this scheme — confirm requirements on the official page.');
      }
      lines.push('');

      const officialReferenceLines = [];
      if (officialUrlPdf) officialReferenceLines.push(`- Official page: ${officialUrlPdf}`);
      if (hasReferenceValue(referenceRowsPdf.supportLevel)) officialReferenceLines.push(`- Support level: ${referenceRowsPdf.supportLevel}`);
      if (hasReferenceValue(referenceRowsPdf.fundingCap)) officialReferenceLines.push(`- Funding cap: ${referenceRowsPdf.fundingCap}`);
      if (hasReferenceValue(referenceRowsPdf.applicationWindow)) officialReferenceLines.push(`- Application window: ${referenceRowsPdf.applicationWindow}`);
      if (hasReferenceValue(referenceRowsPdf.processingTime)) officialReferenceLines.push(`- Processing time: ${referenceRowsPdf.processingTime}`);
      if (hasReferenceValue(referenceRowsPdf.portal)) officialReferenceLines.push(`- Portal: ${referenceRowsPdf.portal}`);
      if (hasReferenceValue(referenceRowsPdf.lastVerified)) officialReferenceLines.push(`- Last verified: ${referenceRowsPdf.lastVerified}`);
      if (officialReferenceLines.length > 0) {
        lines.push('### Official Reference');
        lines.push('');
        lines.push(...officialReferenceLines);
        lines.push('');
      }

      if (transitionNotePdf) {
        lines.push('### Programme Transition');
        lines.push('');
        lines.push(`- ${cleanText(transitionNotePdf)}`);
        lines.push('');
      }

      if (
        submissionStrategyPdf &&
        (submissionStrategyPdf.when_to_apply ||
          submissionStrategyPdf.whenToApply ||
          submissionStrategyPdf.prep_timeline_weeks ||
          submissionStrategyPdf.prepTimelineWeeks ||
          (submissionStrategyPdf.budget_funding_scenarios &&
            Object.keys(submissionStrategyPdf.budget_funding_scenarios).length > 0) ||
          submissionStrategyPdf.alternative_grant_path ||
          submissionStrategyPdf.alternativeGrantPath)
      ) {
        lines.push('### Submission strategy');
        lines.push('');
        lines.push(`**When to apply:** ${whenApplyLabelPdf}`);
        lines.push('');
        const ratPdf = cleanText(
          submissionStrategyPdf.when_to_apply_rationale || submissionStrategyPdf.whenToApplyRationale || ''
        );
        if (ratPdf) {
          lines.push(ratPdf);
          lines.push('');
        }
        const weeksPdf = submissionStrategyPdf.prep_timeline_weeks || submissionStrategyPdf.prepTimelineWeeks;
        if (Array.isArray(weeksPdf) && weeksPdf.length > 0) {
          lines.push('**Estimated prep timeline (week-by-week):**');
          lines.push('');
          weeksPdf.slice(0, 12).forEach((row, widx) => {
            const week = row?.week ?? widx + 1;
            const milestone = cleanText(row?.milestone || row?.title || '');
            if (milestone) lines.push(`- Week ${week}: ${milestone}`);
          });
          lines.push('');
        }
        const bfsPdf = submissionStrategyPdf.budget_funding_scenarios;
        if (bfsPdf && typeof bfsPdf === 'object') {
          const b = cleanText(bfsPdf.best_case || bfsPdf.bestCase || '');
          const ba = cleanText(bfsPdf.base_case || bfsPdf.baseCase || '');
          const c = cleanText(bfsPdf.conservative || '');
          if (b || ba || c) {
            lines.push('**Budget / funding scenarios:**');
            lines.push('');
            if (b) lines.push(`- **Best case:** ${b}`);
            if (ba) lines.push(`- **Base case:** ${ba}`);
            if (c) lines.push(`- **Conservative:** ${c}`);
            lines.push('');
          }
        }
        const altPdf = cleanText(
          submissionStrategyPdf.alternative_grant_path || submissionStrategyPdf.alternativeGrantPath || ''
        );
        if (altPdf) {
          lines.push(`**Alternative grant path:** ${altPdf}`);
          lines.push('');
        }
      }

      if (
        advisoryTraceabilityPdf &&
        Array.isArray(advisoryTraceabilityPdf.keyConclusions) &&
        advisoryTraceabilityPdf.keyConclusions.length > 0 &&
        includeEvidenceTrace
      ) {
        lines.push('### Evidence and conclusions');
        lines.push('');
        lines.push(
          'Key judgments with source references. Official scheme pages remain the source of truth.'
        );
        lines.push('');
        advisoryTraceabilityPdf.keyConclusions.forEach((kc, kidx) => {
          const conclusion = cleanText(kc.conclusion || '');
          const rationale = cleanText(kc.rationale || '');
          const dq =
            typeof kc.data_quality_score === 'number'
              ? kc.data_quality_score
              : typeof kc.dataQualityScore === 'number'
                ? kc.dataQualityScore
                : null;
          const isAsm = Boolean(kc.is_assumption || kc.isAssumption);
          const asmNote = cleanText(kc.assumption_note || kc.assumptionNote || '');
          if (!conclusion && !rationale) return;
          lines.push(`#### ${kidx + 1}. ${conclusion || 'Assessment point'}`);
          lines.push('');
          if (rationale) {
            lines.push(rationale);
            lines.push('');
          }
          if (typeof dq === 'number' && !Number.isNaN(dq)) {
            lines.push(`*Data quality for this conclusion: ${Math.min(100, Math.max(0, dq))}%*`);
            lines.push('');
          }
          if (isAsm) {
            const asmLine =
              asmNote ||
              'Based partly on unstated or incomplete evidence—verify against the official scheme text.';
            lines.push(`*Assumption: ${asmLine}*`);
            lines.push('');
          }
          const sources = Array.isArray(kc.sources) ? kc.sources : [];
          if (sources.length > 0) {
            lines.push('*Sources:*');
            sources.slice(0, 4).forEach((src) => {
              const title = cleanText(src.title || '');
              const url = (src.url || '').trim();
              const excerpt = cleanText(src.criterion_excerpt || src.criterionExcerpt || '');
              if (!title && !url && !excerpt) return;
              const head = title || url || 'Reference';
              lines.push(
                url ? `- [${head}](${url})` : `- ${head}`
              );
              if (excerpt) lines.push(`  - Criterion: _${excerpt}_`);
            });
            lines.push('');
          }
        });
      }

      // Funding Snapshot
      if (includeFundingSnapshots && grant.funding_snapshot && cleanText(grant.funding_snapshot) && cleanText(grant.funding_snapshot) !== 'Funding details available upon application') {
        lines.push('### Funding Snapshot');
        lines.push('');
        lines.push(cleanText(grant.funding_snapshot));
        lines.push('');
      }

	        // Scoring detail
	        if (includeScoringDetails && (llmRationale || scoreSummaryParagraph || breakdownForDisplayPdf.length > 0 || ruleHits.length > 0)) {
	          lines.push('### Scoring detail');
        lines.push('');
        
        if (llmRationale) {
          lines.push(llmRationale);
          lines.push('');
        } else {
          lines.push('Our assessment evaluated your company profile across eligibility criteria, project alignment, documentation readiness, and historical success patterns.');
          lines.push('');
        }
        
        if (breakdownForDisplayPdf.length > 0) {
          lines.push('We combined the factor contributions below to arrive at your overall score.');
          lines.push('');
        }

	        // Signal contributions
	        if (breakdownForDisplayPdf.length > 0) {
	          lines.push('#### Signal contributions');
	          lines.push('');
	          lines.push('| Signal | Weight | Contribution | Status | Evidence |');
	          lines.push('|--------|--------|--------------|--------|----------|');
          breakdownForDisplayPdf.forEach((item) => {
            const weightPct = Math.round((item.weight || 0) * 100);
            const status = item.hit ? 'Strength' : 'Gap';
            const factorCell = pdfTableCell(displayBreakdownFactorName(item.factor), 56);
            const evidenceCell = pdfTableCell(
              item.evidence ? displayBreakdownEvidence(item.evidence) : '',
              520
            );
            lines.push(
              `| ${factorCell} | ${weightPct}% | ${pdfTableCell(String(item.contribution), 12)} | ${status} | ${evidenceCell} |`
            );
          });
          lines.push('');
        }

	        // Checks reviewed
	        if (ruleHits.length > 0) {
	          lines.push('#### Checks reviewed');
          lines.push('');
          ruleHits.forEach((hit) => {
            const status = hit.result === 'pass' ? 'Met' : 'Not Met';
            const evidence = hit.evidence ? cleanText(hit.evidence) : '';
            lines.push(`- **${hit.rule_name || hit.rule_id || 'Rule'}:** ${status}${evidence ? ` - ${evidence}` : ''}`);
          });
          lines.push('');
        }
      }

      // Documents Checklist
      const allDocuments = dedupeList([
        ...(Array.isArray(grant.docs_checklist) ? grant.docs_checklist : []),
        ...(Array.isArray(tailoredText.documentsNeeded) ? tailoredText.documentsNeeded : []),
        ...(Array.isArray(grant.documents_needed) ? grant.documents_needed : [])
      ]);
      if (includeDocumentChecklist && allDocuments.length > 0) {
        lines.push('### Documents to Prepare');
        lines.push('');
        allDocuments.slice(0, 3).forEach((doc) => {
          lines.push(`- ${doc}`);
        });
        lines.push('');
      }

      // Next Best Actions
      const allNextSteps = dedupeList([
        ...nextBestActions,
        ...(Array.isArray(tailoredText.nextSteps) ? tailoredText.nextSteps : []),
        ...(Array.isArray(grant.next_steps) ? grant.next_steps : [])
      ]).filter((action) => !isVaguePdfNextStep(action));
      if (allNextSteps.length === 0) {
        allNextSteps.push(fallbackNextStepForPdf(grant, grantStatus));
      }
      if (allNextSteps.length > 0) {
        lines.push('### Next Best Actions');
        lines.push('');
        allNextSteps.slice(0, 3).forEach((action) => {
          lines.push(`- ${action}`);
        });
        lines.push('');
      }

      // Funding Notes (appendix-only)
      if (includeScoringDetails && grant.funding_notes && cleanText(grant.funding_notes)) {
        lines.push('### Funding Notes');
        lines.push('');
        lines.push(cleanText(grant.funding_notes));
        lines.push('');
      }

      // Important Caveats (appendix-only)
      const caveats = (grant.caveats || [])
        .map((item) => stripScopeMissingBoilerplateSentences(cleanText(item)))
        .filter((item) => item && item.length > 0 && !isBannedInsightLine(item));
      if (includeScoringDetails && caveats.length > 0) {
        lines.push('### Important Caveats');
        lines.push('');
        caveats.slice(0, 3).forEach((item) => {
          lines.push(`- ${item}`);
        });
        lines.push('');
      }

      // Score Breakdown (appendix-only)
      if (includeScoringDetails && grant.scoreBreakdown && Object.keys(grant.scoreBreakdown).length > 0) {
        lines.push('### Score Breakdown');
        lines.push('');
        Object.entries(grant.scoreBreakdown).forEach(([key, value]) => {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          lines.push(`- **${label}:** ${value}%`);
        });
        lines.push('');
      }

      // Matched Criteria (matching results page — includes advisor LLM list)
      const matchedCriteria = dedupeList([
        ...(enhancedAdvisor.matchedCriteriaAdvisor || []),
        ...(Array.isArray(grant.whyEligible) ? grant.whyEligible : []),
        ...(Array.isArray(grant.matched_criteria) ? grant.matched_criteria : []),
      ]).filter((item) => !overlapsPdfText(item, displayPositivePdf));
      if (includeScoringDetails && matchedCriteria.length > 0) {
        lines.push('### Matched Criteria');
        lines.push('');
        matchedCriteria.slice(0, 3).forEach((item) => {
          const cleaned = cleanText(item);
          if (cleaned) {
            lines.push(`- ${cleaned}`);
          }
        });
        lines.push('');
      }

      // Unmet Criteria (matching results page — includes advisor LLM list)
      const unmetCriteria = dedupeList([
        ...(enhancedAdvisor.unmetCriteriaAdvisor || []),
        ...(Array.isArray(grant.whyNotEligible) ? grant.whyNotEligible : []),
        ...(Array.isArray(grant.unmet_criteria) ? grant.unmet_criteria : []),
      ]).filter((item) => !overlapsPdfText(item, displayWhereToImprovePdf));
      if (includeScoringDetails && unmetCriteria.length > 0) {
        lines.push('### Unmet Criteria');
        lines.push('');
        unmetCriteria.slice(0, 3).forEach((item) => {
          const cleaned = cleanText(item);
          if (cleaned) {
            lines.push(`- ${cleaned}`);
          }
        });
        lines.push('');
      }

      // Confidence Factors (matching results page - line 2397-2420)
      const confidenceFactors = (grant.confidence_factors || [])
        .filter(item => item && cleanText(item).length > 0);
      if (includeScoringDetails && confidenceFactors.length > 0) {
        lines.push('### Confidence Factors');
        lines.push('');
        confidenceFactors.slice(0, 3).forEach((item) => {
          const cleaned = cleanText(item);
          if (cleaned) {
            lines.push(`- ${cleaned}`);
          }
        });
        lines.push('');
      }

      lines.push('---');
      lines.push('');
    });

    // ============================================================================
    // INDEPENDENT FUNDING PATHWAYS (if available)
    // ============================================================================
    const fundingItemsMap = new Map();
    const upsertFundingItem = (item, sectionTitle = null) => {
      if (!item || typeof item !== 'object') return;
      const key = item.id || item.name || `${item.fundingType || 'funding'}-${Math.random()}`;
      if (!fundingItemsMap.has(key)) {
        fundingItemsMap.set(key, { ...item, _sectionTitle: sectionTitle || item._sectionTitle || '' });
      }
    };
    eligibleIndependentPathways.forEach((item) => upsertFundingItem(item, 'Eligible Independent Funding Pathways'));
    displayFundingBucketSections.forEach((section) => {
      (section.items || []).forEach((item) => upsertFundingItem(item, section.title));
    });
    const pdfFundingItems = Array.from(fundingItemsMap.values()).sort(
      (a, b) => (b?.score ?? 0) - (a?.score ?? 0)
    );

    if (pdfFundingItems.length > 0 && pdfReportMode !== 'executive') {
      lines.push('## INDEPENDENT FUNDING PATHWAYS');
      lines.push('');
      lines.push(
        `The following ${pdfFundingItems.length} independent funding pathway${pdfFundingItems.length === 1 ? ' is' : 's are'} included separately from the Singapore government grant recommendations.`
      );
      lines.push('');

      pdfFundingItems.forEach((opportunity, index) => {
        const purposeText = cleanText(getGrantPurposeText(opportunity));
        const scoreText = typeof opportunity.score === 'number' ? `${Math.round(opportunity.score)}%` : 'N/A';
        const confidenceText = String(opportunity.confidence || 'N/A');
        const provider = cleanText(opportunity.provider || 'Non-government provider');
        const fundingType = cleanText(
          FUNDING_TYPE_DISPLAY_LABELS[opportunity.fundingType] || opportunity.fundingType || 'Independent funding pathway'
        );
        const sectionTitle = cleanText(opportunity._sectionTitle || '');
        lines.push(
          `### ${index + 1}. ${cleanText(
            stripRepresentativePlaceholderSuffix(opportunity.name || 'Independent Funding Pathway')
          )}`
        );
        lines.push('');
        lines.push('| Field | Value |');
        lines.push('|-------|-------|');
        lines.push(`| Category | ${pdfTableCell(opportunity.opportunityCategoryLabel || 'Independent Funding Pathway', 120)} |`);
        lines.push(`| Type | ${pdfTableCell(fundingType, 120)} |`);
        lines.push(`| Provider | ${pdfTableCell(provider, 180)} |`);
        lines.push(`| Match Score | ${pdfTableCell(scoreText, 40)} |`);
        lines.push(`| Confidence | ${pdfTableCell(confidenceText, 40)} |`);
        if (sectionTitle) {
          lines.push(`| Results Group | ${pdfTableCell(sectionTitle, 180)} |`);
        }
        lines.push('');

        if (purposeText) {
          lines.push('### What this opportunity is for');
          lines.push('');
          lines.push(purposeText);
          lines.push('');
        }

        if (Array.isArray(opportunity.reasons) && opportunity.reasons.length > 0) {
          lines.push('### Why it was surfaced');
          lines.push('');
          opportunity.reasons.slice(0, 3).forEach((reason) => {
            const cleaned = cleanText(reason);
            if (cleaned) lines.push(`- ${cleaned}`);
          });
          lines.push('');
        }

        if (opportunity.why_it_matches || opportunity.what_to_expect) {
          lines.push('### About this opportunity');
          lines.push('');
          if (opportunity.why_it_matches) {
            lines.push('**Why it may suit your business**');
            lines.push('');
            lines.push(cleanText(opportunity.why_it_matches));
            lines.push('');
          }
          if (opportunity.what_to_expect) {
            lines.push('**What to expect**');
            lines.push('');
            lines.push(cleanText(opportunity.what_to_expect));
            lines.push('');
          }
        }

        if (
          opportunity.typicalSupportStructure ||
          opportunity.typical_support_structure ||
          opportunity.capitalTermsSnapshot ||
          opportunity.capital_terms_snapshot
        ) {
          lines.push('### Support and terms');
          lines.push('');
          if (opportunity.typicalSupportStructure || opportunity.typical_support_structure) {
            lines.push('**Typical support structure**');
            lines.push('');
            lines.push(cleanText(opportunity.typicalSupportStructure || opportunity.typical_support_structure));
            lines.push('');
          }
          if (opportunity.capitalTermsSnapshot || opportunity.capital_terms_snapshot) {
            lines.push('**Capital terms snapshot**');
            lines.push('');
            lines.push(cleanText(opportunity.capitalTermsSnapshot || opportunity.capital_terms_snapshot));
            lines.push('');
          }
        }

        if (opportunity.riskWatchoutNote || opportunity.risk_watchout_note) {
          lines.push('### Risk / watchout note');
          lines.push('');
          lines.push(cleanText(opportunity.riskWatchoutNote || opportunity.risk_watchout_note));
          lines.push('');
        }

        lines.push('---');
        lines.push('');
      });
    }

    const reportGrantKeysForPdf = new Set(reportGrants.map((grant) => getCanonicalGrantKey(grant)));
    const lowerPriorityRowsForPdf = lowerPriorityRows.filter((row) => {
      const key = row?.canonicalGrantKey || getCanonicalGrantKey(row?.grant || row);
      return key && !reportGrantKeysForPdf.has(key);
    });

    if (lowerPriorityRowsForPdf.length > 0 && pdfReportMode !== 'executive') {
      lines.push('## LOWER PRIORITY GRANTS');
      lines.push('');
      lines.push('| Grant | Score | Reason not prioritized | Suggested action |');
      lines.push('|---|---|---|---|');
      const lowerPriorityCap = pdfReportMode === 'full' ? 40 : 24;
      lowerPriorityRowsForPdf.slice(0, lowerPriorityCap).forEach((row) => {
        const specificReason = cleanText(
          row.grant?.reasonNotPrioritized || row.grant?.reason_not_prioritized || ''
        );
        const reason = specificReason
          ? specificReason
          : row.status === 'blocked'
            ? cleanText(getGrantBlockReason(row.grant)) || 'Prerequisite not met on the submitted answers'
            : row.status === 'excluded'
              ? 'Low strategic relevance or sector mismatch'
              : row.status === 'low_priority'
                ? 'Low fit against current company/project context'
                : 'Below current export priority threshold';
        const rowScore = Number(row.alignmentScore);
        const rowScoreLabel = Number.isFinite(rowScore)
          ? `${formatPercent(rowScore)} (${scoreBandLabel(rowScore)})`
          : formatPercent(row.alignmentScore);
        lines.push(`| ${pdfTableCell(row.displayName, 120)} | ${rowScoreLabel} | ${pdfTableCell(reason, 140)} | ${pdfTableCell(fallbackNextStepForPdf(row.grant, row.status), 180)} |`);
      });
      lines.push('');
    }

    // ============================================================================
    // METHODOLOGY (contract section — always emitted, parsed by the PDF generator)
    // ============================================================================
    const methodologyPdf = results?.methodology || null;
    lines.push('---');
    lines.push('');
    lines.push('## Methodology');
    lines.push('');
    {
      const signalDesc = cleanText(methodologyPdf?.signalMatch?.description || '') ||
        'Signal Match is a 0–100 weighted match between the company/project you submitted and the published criteria of each scheme.';
      const weightsText = (methodologyPdf?.signalMatch?.weights || [])
        .map((w) => `${w.label} ${w.pct}%`)
        .join(', ');
      lines.push(`- **Signal Match:** ${signalDesc}${weightsText ? ` Weights: ${weightsText}.` : ''}`);
      const strategicDesc = cleanText(methodologyPdf?.strategicFit?.description || '') ||
        'Strategic Fit is scored separately from Signal Match. It asks whether this is the right scheme for this specific project (project type, maturity, use of funds), not merely whether the company could qualify.';
      lines.push(`- **Strategic Fit:** ${strategicDesc}`);
      const approvalDesc = cleanText(methodologyPdf?.approvalLikelihood?.description || '');
      if (approvalDesc) {
        lines.push(`- **Approval likelihood:** ${approvalDesc}`);
      }
      lines.push(`- **Bands:** ${FIT_BANDS_TEXT}`);
      lines.push('- **Routes with prerequisites:** any scheme with an unmet mandatory prerequisite is capped at Low and listed separately, whatever its score.');
    }
    lines.push('');

    // ============================================================================
    // SCORING FRAMEWORK NARRATIVE (APPEARS ONCE, AT THE END ONLY)
    // ============================================================================
    if (pdfReportMode === 'full' || includeScoringDetails) {
      lines.push('---');
      lines.push('');
      lines.push('## HOW WE ASSESS GRANT ELIGIBILITY');
    lines.push('');
    lines.push('### Understanding Your Scores');
    lines.push('');
    lines.push(
      "**Projected Success Score:** OPTRA's overall fit score (0-100%) after mandatory eligibility requirements are checked. It combines company eligibility, project fit, budget/timeline fit, evidence quality, and expected outcomes. It is indicative, not an official agency decision."
    );
    lines.push('');
    lines.push(
      `**Signal Match bands:** ${FIT_BAND_STRONG_MIN}%+ is strong, ${FIT_BAND_PROMISING_MIN}-${FIT_BAND_STRONG_MIN - 1}% is promising, ${FIT_BAND_PARTIAL_MIN}-${FIT_BAND_PROMISING_MIN - 1}% is partial, and below ${FIT_BAND_PARTIAL_MIN}% is weak. A strong score usually means the company profile, activity type, budget, and intended outcomes all point toward the scheme. A partial or weak score usually means the route is missing a key fit signal, such as a named overseas market for MRA, a pre-approved solution route for PSG, or evidence that an R&D project involves genuine technical uncertainty.`
    );
    lines.push('');
    lines.push(
      '**Strategic Fit:** Scored separately from broad signal match. It asks whether this is the right grant for this project, not just whether the company could generally qualify. For example, a technology company may satisfy basic SME criteria, but a tourism, hotel, cruise, or Germany-specific collaboration scheme should only rank well when the submitted project clearly matches that scheme purpose.'
    );
    lines.push('');
    lines.push(
      '**Certainty Percentage:** How reliable the assessment is based on completeness and specificity. Certainty improves when the form includes concrete project scope, implementation stage, cost breakdown, vendor or partner status, target market, compliance position, and supporting documents. Lower certainty means the recommendation may change once those facts are confirmed.'
    );
    lines.push('');
    lines.push(
      '**Note:** A high score with low certainty means the route may fit, but the evidence is incomplete. A lower score with high certainty usually means the mismatch is clearer. Ineligible means a mandatory requirement or structural condition was not met, not simply that the score was low.'
    );
    lines.push('');
    lines.push('### Scoring Thresholds');
    lines.push('');
    lines.push(
      `Groups use projected score, strategic fit, and certainty together: ${TIER_ELIGIBLE_MIN}%+ Eligible (Strong / High), ${TIER_LIKELY_MIN}-${TIER_ELIGIBLE_MIN - 1}% Likely (Promising / Medium), ${TIER_LESS_LIKELY_MIN}-${TIER_LIKELY_MIN - 1}% Less likely (Partial / Low), ${TIER_WEAK_MIN}-${TIER_LESS_LIKELY_MIN - 1}% Weak fit. Prerequisite needed means a structural requirement (for example a PSG pre-approved vendor) is not met yet; Ineligible is reserved for mandatory requirements such as ownership, sector scope, vendor route, geography, availability, project timing, or programme-specific structural conditions.`
    );
    lines.push('');
    lines.push('---');
    lines.push('');
      lines.push('*This report was generated by OPTRA Eligibility Assessment System.*');
      lines.push('');
      lines.push(`*For questions or support, visit https://optra-labs.com*`);
      lines.push('');
      lines.push(`*Generated: ${new Date().toLocaleString('en-SG', { timeZone: 'Asia/Singapore', dateStyle: 'long', timeStyle: 'short' })}*`);
    }
    
    return lines.join('\n');
  }, [
    exportableGrants,
    pdfExportViewModel,
    pdfReportMode,
    results,
    resolveGrantStatus,
    formData,
    getCertaintyPercentage,
    getDisplayScore,
    getStrategicFitScore,
    eligibleIndependentPathways,
    displayFundingBucketSections,
  ]);

  // Test-only hook: lets unit tests assert the exact markdown contract the PDF
  // generator parses without driving the export modal + network round trip.
  useEffect(() => {
    if (import.meta.env.MODE !== 'test' || typeof window === 'undefined') return undefined;
    window.__optraBuildMarkdownReport = buildMarkdownReport;
    return () => {
      if (window.__optraBuildMarkdownReport === buildMarkdownReport) {
        delete window.__optraBuildMarkdownReport;
      }
    };
  }, [buildMarkdownReport]);

  // Debug logging will be added after safeGrants is defined

  // URL state synchronization (disabled to prevent navigation throttling)
  // The rapid URL updates were causing browser navigation throttling warnings
  // Dropdown state is now managed purely in React state (localExpandedGrants Set)
  // No URL sync needed - dropdowns work perfectly without it
  
  // useEffect(() => {
  //   const params = new URLSearchParams();
  //   if (Array.from(localExpandedGrants).length > 0) {
  //     params.set('expanded', Array.from(localExpandedGrants).join(','));
  //   }
  //   setSearchParams(params);
  // }, [localExpandedGrants, setSearchParams]);

  // Toggle grant expansion
  const toggleGrantExpansion = (grantId) => {
    setLocalExpandedGrants(prev => {
      const newSet = new Set(prev);
      if (newSet.has(grantId)) {
        newSet.delete(grantId);
      } else {
        newSet.add(grantId);
      }
      return newSet;
    });
  };

  const toggleScoreBreakdown = (grantId) => {
    setExpandedScoreBreakdowns((prev) => {
      const next = new Set(prev);
      if (next.has(grantId)) next.delete(grantId);
      else next.add(grantId);
      return next;
    });
  };

  useEffect(() => {
    if (!isClient) return;
    forceUnlockDocumentScroll();
    const raf = window.requestAnimationFrame(() => forceUnlockDocumentScroll());
    return () => window.cancelAnimationFrame(raf);
  }, [isClient, localExpandedGrants.size]);

  const saveAssessmentHistory = useCallback(async () => {
    // Admin view: the run belongs to the client, not the signed-in admin.
    if (adminView) return;
    if (!results || !assessmentSource) return;

    try {
      const sessionEmail = getSessionEmail() || localStorage.getItem('user_email') || null;
      const normalizedEmail = normalizeEmail(sessionEmail);

      const historyEntry = {
        id: results.snapshotId,
        timestamp: new Date().toISOString(),
        companyName: results.inputsUsed?.company_name || results.inputsUsed?.companyName || 'Company',
        industry: results.inputsUsed?.industry || 'Industry',
        grantType: 'comprehensive',
        overallScore: Math.max(...(results.grants?.map(g => g.score) || [0])),
        grantsAssessed: results.grants?.length || 0,
        assessmentData: assessmentSource,
        formData: formSource,
        eligibleGrants: eligibleGrants.length,
        likelyGrants: likelyGrants.length,
        lessLikelyGrants: lessLikelyGrants.length,
        weakFitGrants: weakFitGrants.length,
        ineligibleGrants: ineligibleGrants.length,
        ownerEmail: normalizedEmail
      };

      const storageKey = getHistoryStorageKey(normalizedEmail);
      let historyPool = [];

      try {
        historyPool = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch {
        historyPool = [];
      }

      if (sessionEmail) {
        try {
          const legacyHistory = JSON.parse(localStorage.getItem(getHistoryStorageKey(null)) || '[]');
          if (legacyHistory.length > 0) {
            historyPool = mergeHistoryEntries(historyPool, legacyHistory);
            localStorage.removeItem(getHistoryStorageKey(null));
          }
        } catch {
          // Ignore legacy parsing errors
        }
      }

      const existingEntry = historyPool.find((entry) => entry?.id === historyEntry.id);
      if (existingEntry?.timestamp) {
        // Keep the original action timestamp so revisits don't look like new actions.
        historyEntry.timestamp = existingEntry.timestamp;
      }

      const mergedHistory = mergeHistoryEntries(historyPool, [historyEntry]);
      const trimmedHistory = mergedHistory.slice(0, HISTORY_STORAGE_LIMIT);
      try {
        localStorage.setItem(storageKey, JSON.stringify(trimmedHistory));
      } catch {
        // If quota is exceeded, keep a compact history without heavy payload fields.
        const compactHistory = trimmedHistory
          .map((entry) => {
            const { assessmentData: _assessmentData, formData: _formData, ...rest } = entry || {};
            return rest;
          })
          .slice(0, Math.min(20, HISTORY_STORAGE_LIMIT));
        localStorage.setItem(storageKey, JSON.stringify(compactHistory));
      }
      if (DEBUG) console.log('✅ Assessment auto-saved to localStorage history');

      if (normalizedEmail && !existingEntry) {
        try {
          await apiService.saveAssessment(
            normalizedEmail,
            'comprehensive',
            historyEntry.companyName,
            historyEntry.overallScore
          );
          if (DEBUG) console.log('✅ Assessment auto-saved to backend for user:', sessionEmail);
        } catch (error) {
          console.warn('⚠️ Failed to save to backend during auto-save, local history updated:', error);
        }
      } else if (existingEntry && DEBUG) {
        console.log('ℹ️ Existing assessment history found; skipping duplicate backend save');
      }

      setHasSavedHistory(true);
    } catch (error) {
      console.error('❌ Error saving assessment history:', error);
    }
  }, [
    assessmentSource,
    eligibleGrants.length,
    formSource,
    likelyGrants.length,
    lessLikelyGrants.length,
    weakFitGrants.length,
    results,
    ineligibleGrants.length,
    adminView,
  ]);

  useEffect(() => {
    if (adminView || !isClient || !locationClientId || !results) return;
    try {
      const sessionEmail = getSessionEmail() || localStorage.getItem("user_email") || null;
      const normalizedEmail = normalizeEmail(sessionEmail);
      const storageKey = `dashboard_manual_clients_${normalizedEmail || "default"}`;
      const raw = localStorage.getItem(storageKey);
      const clients = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(clients) || clients.length === 0) return;
      const idx = clients.findIndex((item) => item?.id === locationClientId);
      if (idx === -1) return;
      const entryId = String(results.snapshotId || locationJobId || `assessment_${Date.now()}`);
      const nextEntry = {
        id: entryId,
        jobId: locationJobId || null,
        timestamp: new Date().toISOString(),
        overallScore: Math.max(...(results.grants?.map((g) => g?.score || 0) || [0])),
      };
      const existing = Array.isArray(clients[idx].assessments) ? clients[idx].assessments : [];
      if (existing.some((item) => item?.id === entryId)) return;
      clients[idx] = {
        ...clients[idx],
        assessments: [nextEntry, ...existing].slice(0, 20),
      };
      localStorage.setItem(storageKey, JSON.stringify(clients));
    } catch {
      // ignore storage errors
    }
  }, [adminView, isClient, locationClientId, results, locationJobId]);

  useEffect(() => {
    if (!adminView && results && assessmentSource && !hasSavedHistory) {
      saveAssessmentHistory().catch((err) => console.error('❌ Auto-save history failed:', err));
    }
  }, [adminView, results, assessmentSource, hasSavedHistory, saveAssessmentHistory]);

  // Active filter tab is marked with `aria-pressed` and a tint + ink border (the sheet theme keys
  // on `aria-pressed`), never white-on-tint text.
  const getEligibleFilterButtonClasses = (isActive) => ({
    className: `px-4 py-1 rounded-full text-xs font-semibold border transition-transform duration-150 hover:-translate-y-0.5 focus:outline-none focus:ring-0 ${
      isActive ? 'bg-[#EEF4FF]' : 'shadow-lg'
    }`,
    style: isActive
      ? {
          color: '#1F2937',
          backgroundColor: '#EEF4FF',
          borderColor: '#1F2937',
        }
      : {
          color: '#4B5563',
          backgroundColor: '#FFFFFF',
          borderColor: '#BFDBFE',
        },
  });

  // Category badges share the hairline chip recipe used by the sibling tag chips (ink on white).
  const resultPillStyle = {
    backgroundColor: '#FFFFFF',
    borderColor: '#BFDBFE',
    color: '#4B5563',
  };

  const renderFundingOpportunityCard = (opportunity, sectionTitle) => {
    if (!opportunity || typeof opportunity !== 'object') return null;
    const opportunityPurpose = getGrantPurposeText(opportunity);
    const humanizeToken = (value) => String(value || '').replace(/_/g, ' ').trim();
    const fundingScore = Number.isFinite(Number(opportunity.score)) ? Math.round(Number(opportunity.score)) : null;
    const fundingFitBadge = getFitBadge(fundingScore ?? 0);
    const FundingFitIcon = fundingFitBadge.icon || CheckCircle;
    const fundingFitColor = '#60A5FA';
    const formatComplexityLabel = (value) => {
      const normalized = humanizeToken(value).toLowerCase();
      if (!normalized) return '';
      return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)} complexity`;
    };

    const amountRange = (() => {
      if (!opportunity.minFundingAmount && !opportunity.maxFundingAmount) return '';
      const prefix = opportunity.currency === 'SGD' ? 'S$' : `${opportunity.currency || ''} `;
      const min = opportunity.minFundingAmount ? `${prefix}${Number(opportunity.minFundingAmount).toLocaleString()}` : '';
      const max = opportunity.maxFundingAmount ? `${prefix}${Number(opportunity.maxFundingAmount).toLocaleString()}` : '';
      if (min && max) return `${min} - ${max}`;
      return min || max;
    })();

    const purposeDetailLine = (() => {
      const focuses = Array.isArray(opportunity.capitalObjectives)
        ? opportunity.capitalObjectives
        : Array.isArray(opportunity.tags)
          ? opportunity.tags
          : [];
      const cleanedFocuses = focuses
        .map((item) => humanizeToken(item))
        .filter(Boolean)
        .slice(0, 3);
      if (cleanedFocuses.length === 0) {
        return 'This pathway is typically evaluated on strategic fit, execution readiness, and practical commercialization potential.';
      }
      if (cleanedFocuses.length === 1) {
        return `Typical focus areas include ${cleanedFocuses[0]}.`;
      }
      if (cleanedFocuses.length === 2) {
        return `Typical focus areas include ${cleanedFocuses[0]} and ${cleanedFocuses[1]}.`;
      }
      return `Typical focus areas include ${cleanedFocuses[0]}, ${cleanedFocuses[1]}, and ${cleanedFocuses[2]}.`;
    })();

    const expandedOpportunityPurpose = (() => {
      const base = String(opportunityPurpose || '').trim();
      const providerLine = opportunity.provider
        ? `It is offered by ${opportunity.provider} and is positioned as a ${humanizeToken(opportunity.fundingType)} pathway for Singapore-based companies.`
        : '';
      const amountLine = amountRange
        ? `Typical funding range is ${amountRange}.`
        : 'Funding quantum varies by programme scope and sponsor criteria.';
      const timelineLine = opportunity.timelineEstimate
        ? `Current availability is ${formatAvailabilityLabel(opportunity.timelineEstimate) || opportunity.timelineEstimate}.`
        : '';
      return [base, providerLine, purposeDetailLine, amountLine, timelineLine]
        .filter(Boolean)
        .join(' ');
    })();

  const opportunityDisplayName = stripRepresentativePlaceholderSuffix(
    opportunity.name || 'Independent Funding Pathway'
  );
  const displayCategoryLabel = formatDisplayLabel(
    opportunity.opportunityCategoryLabel || 'Independent Funding Pathway'
  );
    const displayFundingTypeLabel = formatDisplayLabel(
      FUNDING_TYPE_DISPLAY_LABELS[opportunity.fundingType] || opportunity.fundingType || ''
    );
    const showFundingTypeBadge =
      Boolean(displayFundingTypeLabel) &&
      opportunity.fundingType !== 'private_non_dilutive' &&
      normalizeLabelForComparison(displayFundingTypeLabel) !== normalizeLabelForComparison(displayCategoryLabel);
    const opportunityTags = Array.from(
      new Set(
        (Array.isArray(opportunity.tags) ? opportunity.tags : [])
          .map((tag) => String(tag || '').trim())
          .filter(Boolean)
      )
    );

    return (
      <div
        key={opportunity.id}
        id={`result-funding-${opportunity.id}`}
        className="bg-white rounded-3xl p-8 shadow-xl transition-all duration-300 hover:shadow-2xl"
      >
        <div
          className="border rounded-2xl mb-6 px-6 py-4 flex items-start sm:items-center justify-between gap-3 min-h-[4.5rem] sm:min-h-0 bg-white"
          style={{ borderColor: "#BFDBFE" }}
        >
          <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
            <FundingFitIcon className="w-6 h-6" style={{ color: fundingFitColor }} aria-hidden />
            <span className="text-sm md:text-base font-semibold leading-tight break-words" style={{ color: '#1F2937' }}>
              {`${String(fundingFitBadge.label || 'Likely').toUpperCase()} (NON-GOVERNMENT)`}
            </span>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-semibold" style={{ color: '#60A5FA' }}>
              {fundingScore != null ? `${fundingScore}%` : 'N/A'}
            </div>
          </div>
        </div>

        <div className="mb-3">
          <span className="text-[11px] tracking-wide uppercase" style={{ color: '#94A3B8' }}>
            {sectionTitle}
          </span>
        </div>

        <div className="mb-4">
          <h3 className="text-2xl font-bold mb-2" style={{ color: '#60A5FA' }}>
            {opportunityDisplayName}
          </h3>
          <div className="flex flex-wrap items-start content-start gap-2 min-h-[2.75rem] sm:min-h-0">
            <span className="px-3 py-1 rounded-md text-xs font-semibold border" style={resultPillStyle}>
              {displayCategoryLabel || 'Independent Funding Pathway'}
            </span>
            {showFundingTypeBadge ? (
              <span className="px-3 py-1 rounded-md text-xs font-semibold border" style={{ borderColor: '#BFDBFE', color: '#4B5563', backgroundColor: '#FFFFFF' }}>
                {displayFundingTypeLabel}
              </span>
            ) : null}
            {opportunityTags.length > 0 ? opportunityTags.slice(0, 3).map((tag) => (
              <span
                key={`${opportunity.id}-tag-${tag}`}
                className="px-3 py-1 rounded-md text-xs font-semibold border"
                style={{ borderColor: '#BFDBFE', color: '#4B5563', backgroundColor: '#FFFFFF' }}
              >
                {tag}
              </span>
            )) : null}
          </div>
        </div>

        {opportunityPurpose ? (
          <div
            className="border rounded-2xl mb-6 px-6 py-4 bg-white"
            style={{ borderColor: "#BFDBFE" }}
          >
            <h4 className="text-ui text-sm mb-2" style={{ color: '#60A5FA', fontWeight: 500 }}>
              What this opportunity is for
            </h4>
            <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
              {expandedOpportunityPurpose}
            </p>
          </div>
        ) : null}

        <div className="mb-5 flex flex-wrap items-center gap-2">
          {opportunity.provider ? (
            <span className="px-3 py-1 rounded-md text-xs font-semibold border" style={{ borderColor: '#BFDBFE', color: '#4B5563', backgroundColor: '#FFFFFF' }}>
              {opportunity.provider}
            </span>
          ) : null}
          {amountRange ? (
            <span className="px-3 py-1 rounded-md text-xs font-semibold border" style={{ borderColor: '#BFDBFE', color: '#4B5563', backgroundColor: '#FFFFFF' }}>
              {amountRange}
            </span>
          ) : null}
          {opportunity.timelineEstimate ? (
            <span className="px-3 py-1 rounded-md text-xs font-semibold border" style={{ borderColor: '#BFDBFE', color: '#4B5563', backgroundColor: '#FFFFFF' }}>
              {formatAvailabilityLabel(opportunity.timelineEstimate)}
            </span>
          ) : null}
          {opportunity.applicationComplexity ? (
            <span className="px-3 py-1 rounded-md text-xs font-semibold border" style={{ borderColor: '#BFDBFE', color: '#4B5563', backgroundColor: '#FFFFFF' }}>
              {formatComplexityLabel(opportunity.applicationComplexity)}
            </span>
          ) : null}
        </div>

        {Array.isArray(opportunity.reasons) && opportunity.reasons.length > 0 ? (
          <div className="mb-5">
            <h4 className="text-ui text-sm mb-3" style={{ color: '#60A5FA', fontWeight: 500 }}>Why it was surfaced</h4>
            <ul className="space-y-2">
              {opportunity.reasons.slice(0, 3).map((reason, idx) => (
                <li key={`${opportunity.id}-reason-${idx}`} className="flex items-start leading-relaxed" style={{ color: '#4B5563' }}>
                  <div className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0 bg-green-400" />
                  <span className="text-base">{reason}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(opportunity.why_it_matches || opportunity.what_to_expect) ? (
          <div className="mb-5">
            <h4 className="text-ui text-sm mb-3" style={{ color: '#60A5FA', fontWeight: 500 }}>About this opportunity</h4>
            {opportunity.why_it_matches ? (
              <div className="mb-3">
                <p className="text-xs font-semibold mb-1" style={{ color: '#6B7280' }}>
                  Why it may suit your business
                </p>
                <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#4B5563' }}>
                  {opportunity.why_it_matches}
                </p>
              </div>
            ) : null}
            {opportunity.what_to_expect ? (
              <div>
                <p className="text-xs font-semibold mb-1" style={{ color: '#6B7280' }}>
                  What to expect
                </p>
                <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#4B5563' }}>
                  {opportunity.what_to_expect}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        {(opportunity.typicalSupportStructure || opportunity.typical_support_structure || opportunity.capitalTermsSnapshot || opportunity.capital_terms_snapshot) ? (
          <div className="mb-5">
            <h4 className="text-ui text-sm mb-3" style={{ color: '#60A5FA', fontWeight: 500 }}>Support and terms</h4>
            {(opportunity.typicalSupportStructure || opportunity.typical_support_structure) ? (
              <div className="mb-3">
                <p className="text-xs font-semibold mb-1" style={{ color: '#6B7280' }}>
                  Typical support structure
                </p>
                <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#4B5563' }}>
                  {opportunity.typicalSupportStructure || opportunity.typical_support_structure}
                </p>
              </div>
            ) : null}
            {(opportunity.capitalTermsSnapshot || opportunity.capital_terms_snapshot) ? (
              <div>
                <p className="text-xs font-semibold mb-1" style={{ color: '#6B7280' }}>
                  Capital terms snapshot
                </p>
                <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#4B5563' }}>
                  {opportunity.capitalTermsSnapshot || opportunity.capital_terms_snapshot}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        {(opportunity.riskWatchoutNote || opportunity.risk_watchout_note) ? (
          <div className="mb-5">
            <h4 className="text-ui text-sm mb-2" style={{ color: '#60A5FA', fontWeight: 500 }}>Risk / watchout note</h4>
            <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#4B5563' }}>
              {opportunity.riskWatchoutNote || opportunity.risk_watchout_note}
            </p>
          </div>
        ) : null}

        {Array.isArray(opportunity.blockers) && opportunity.blockers.length > 0 ? (
          <div className="mb-5">
            <h4 className="text-ui text-sm mb-3" style={{ color: '#60A5FA', fontWeight: 500 }}>Watchouts</h4>
            <ul className="space-y-2">
              {opportunity.blockers.slice(0, 2).map((blocker, idx) => (
                <li key={`${opportunity.id}-blocker-${idx}`} className="flex items-start leading-relaxed" style={{ color: '#4B5563' }}>
                  <div className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0 bg-red-400" />
                  <span className="text-base">{blocker}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  };

  // Helper function to render a grant card
  const renderGrantCard = (grant) => {
    const grantKey = resolveGrantKey(grant);
    const grantPurposeText = getGrantPurposeText(grant);
    const isExpanded = localExpandedGrants.has(grantKey);
    const isScoreBreakdownExpanded = expandedScoreBreakdowns.has(grantKey);
    const grantStatus = resolveGrantStatus(grant);

    let FitIcon = CheckCircle;
    if (grantStatus === 'ineligible') {
      FitIcon = XCircle;
    } else if (grantStatus === 'lessLikely' || grantStatus === 'weakFit') {
      FitIcon = AlertTriangle;
    }
    
    if (DEBUG) console.log('🔍 Rendering grant card:', {
      grantKey,
      isExpanded,
      expandedSet: Array.from(localExpandedGrants)
    });
    
    // Safety check for grant data
    if (!grant || typeof grant !== 'object') {
      console.error('❌ Invalid grant data:', grant);
      return null;
    }

    // Get tailored text
    let tailoredText;
    try {
      tailoredText = formatTailoredText(grant, results?.inputsUsed, results);
    } catch (error) {
      console.error('❌ Error generating tailored text:', error);
      tailoredText = {
        quickVerdict: 'Unable to generate tailored analysis',
        whyMatch: ['Error loading analysis'],
        remediationActions: [],
        documentsNeeded: [],
        budgetSnapshot: { estimatedCoFunding: 'N/A', effortLevel: 'Unknown', timelineWeeks: 'N/A', roiPotential: 'Unknown' },
        risks: ['Unable to assess risks'],
        nextSteps: ['Please try again']
      };
    }

    // Clean text helper - remove "unknown", duplicates, and format numbers
    const cleanText = (text) => {
      if (!text || typeof text !== 'string') return '';
      const trimmed = text
        .replace(/[—–]/g, '-')
        .replace(/\bunknown\b/gi, '')
        .replace(/\byour\s+your\b/gi, 'your')
        .replace(/\s+/g, ' ')
        .trim();

      if (!trimmed) return '';

      const placeholderPhrases = new Set([
        'no detailed information available',
        'not available',
        'n/a',
        'no data provided'
      ]);

      if (placeholderPhrases.has(trimmed.toLowerCase())) {
        return '';
      }

      return trimmed;
    };

    const detailedAnalysis = grant.detailedAnalysis || grant.detailed_analysis || {};
    const enhancedAdvisor = getEnhancedAdvisorAnalysis(detailedAnalysis);
    const ruleHits = Array.isArray(detailedAnalysis.rule_hits) ? detailedAnalysis.rule_hits : [];
    const weightedBreakdown = Array.isArray(detailedAnalysis.weighted_breakdown)
      ? detailedAnalysis.weighted_breakdown
      : [];
    const publicBreakdownRaw =
      detailedAnalysis.weighted_breakdown_public || detailedAnalysis.weightedBreakdownPublic;
    const breakdownForDisplay = (
      Array.isArray(publicBreakdownRaw) && publicBreakdownRaw.length > 0
        ? publicBreakdownRaw
        : weightedBreakdown
    ).filter((item) => !isInternalCalibrationFactorName(item?.factor));
    const dedupeList = (items = []) => {
      const seen = new Set();
      const output = [];
      for (const item of items) {
        const cleaned = cleanText(item);
        if (!cleaned) continue;
        const key = cleaned.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          output.push(cleaned);
        }
      }
      return output;
    };

    const derivePositiveReasons = () => {
      const fromAdvisor = dedupeList(enhancedAdvisor.matchedCriteriaAdvisor || []);
      if (fromAdvisor.length > 0) {
        return fromAdvisor;
      }
      const fromGrantCard = dedupeList([
        ...(Array.isArray(grant.matched_criteria) ? grant.matched_criteria : []),
        ...(Array.isArray(grant.whyEligible) ? grant.whyEligible : []),
      ]);
      if (fromGrantCard.length > 0) {
        return fromGrantCard;
      }
      if (ruleHits.length > 0) {
        return dedupeList(
          ruleHits
            .filter((hit) => hit.result === 'pass' && cleanText(hit.evidence))
            .map((hit) => cleanText(hit.evidence))
        );
      }
      return dedupeList(tailoredText.whyMatch || []);
    };

    const deriveNegativeReasons = () => {
      const unmetAdvisor = dedupeList(enhancedAdvisor.unmetCriteriaAdvisor || []);
      if (unmetAdvisor.length > 0) {
        return unmetAdvisor;
      }
      if (ruleHits.length > 0) {
        return dedupeList(
          ruleHits
            .filter((hit) => hit.result === 'fail' && cleanText(hit.evidence))
            .map((hit) => cleanText(hit.evidence))
        );
      }
      if (Array.isArray(grant.whyNotEligible) && grant.whyNotEligible.length > 0) {
        return dedupeList(grant.whyNotEligible);
      }
      return dedupeList(grant.whats_missing || []);
    };

    const positiveReasons = derivePositiveReasons();
    const negativeReasons = deriveNegativeReasons();
    const grantIdValue = grant.grantId || grant.grant_id || grant.code || grant.acronym || grantKey || 'N/A';
    const grantFullName = toDisplayGrantTitle(
      grant.grantFullName || grant.grant_full_name || grant.name || grant.displayName || grantKey
    );
    const rawPositiveReasons = dedupeList([
      ...(enhancedAdvisor.matchedCriteriaAdvisor || []),
      ...positiveReasons,
    ]);
    const positiveCoreLines = dedupeList(
      rawPositiveReasons
        .map((r) =>
          cleanText(stripConsultantChurnTail(stripFactorColonPrefix(stripAdvisoryFiller(String(r)))))
        )
        .filter((line) => line && !isBannedInsightLine(line))
    );
    const displayPositiveReasons = dedupeList(
      positiveCoreLines.map((line) => enrichWhyMatchConsultantLine(line)).filter(Boolean)
    );
    const displayNegativeReasons = dedupeList([
      ...(enhancedAdvisor.unmetCriteriaAdvisor || []),
      ...negativeReasons,
    ]);

    const whereToImproveFromRisks = (enhancedAdvisor.sections || []).flatMap((sec) =>
      Array.isArray(sec?.risks) ? sec.risks.map((x) => cleanText(String(x))).filter(Boolean) : []
    );

    const whereToImproveItems = filterConsultantInsightList(
      [
        ...displayNegativeReasons,
        ...(enhancedAdvisor.missingInformation || []),
        ...whereToImproveFromRisks,
      ],
      positiveCoreLines,
      cleanText
    );

    const submissionStrategySourceRaw = enhancedAdvisor.submissionStrategy
      ? 'enhanced_advisor_analysis'
      : (detailedAnalysis?.submission_strategy || detailedAnalysis?.submissionStrategy)
        ? 'detailed_analysis'
        : (grant?.submission_strategy || grant?.submissionStrategy)
          ? 'grant'
          : 'none';
    const advisoryTraceabilitySourceRaw = enhancedAdvisor.advisoryTraceability
      ? 'enhanced_advisor_analysis'
      : (detailedAnalysis?.advisory_traceability || detailedAnalysis?.advisoryTraceability)
        ? 'detailed_analysis'
        : (grant?.advisory_traceability || grant?.advisoryTraceability)
          ? 'grant'
          : 'none';
    const submissionStrategyRaw =
      enhancedAdvisor.submissionStrategy ||
      detailedAnalysis?.submission_strategy ||
      detailedAnalysis?.submissionStrategy ||
      grant?.submission_strategy ||
      grant?.submissionStrategy ||
      null;
    const advisoryTraceabilityRaw =
      enhancedAdvisor.advisoryTraceability ||
      detailedAnalysis?.advisory_traceability ||
      detailedAnalysis?.advisoryTraceability ||
      grant?.advisory_traceability ||
      grant?.advisoryTraceability ||
      null;
    let submissionStrategy =
      submissionStrategyRaw && typeof submissionStrategyRaw === 'object'
        ? submissionStrategyRaw
        : {};
    let advisoryTraceability =
      advisoryTraceabilityRaw && typeof advisoryTraceabilityRaw === 'object'
        ? advisoryTraceabilityRaw
        : {};
    const submissionBudgetScenarios =
      submissionStrategy.budget_funding_scenarios ||
      submissionStrategy.budgetFundingScenarios ||
      {};
    const submissionTimelineRows = Array.isArray(
      submissionStrategy.prep_timeline_weeks || submissionStrategy.prepTimelineWeeks
    )
      ? (submissionStrategy.prep_timeline_weeks || submissionStrategy.prepTimelineWeeks)
      : [];
    let hasSubmissionStrategyData =
      Boolean(submissionStrategy.when_to_apply || submissionStrategy.whenToApply) ||
      Boolean(
        submissionStrategy.when_to_apply_rationale ||
          submissionStrategy.whenToApplyRationale
      ) ||
      submissionTimelineRows.length > 0 ||
      (submissionBudgetScenarios &&
        typeof submissionBudgetScenarios === 'object' &&
        Object.keys(submissionBudgetScenarios).length > 0) ||
      Boolean(
        submissionStrategy.alternative_grant_path ||
          submissionStrategy.alternativeGrantPath
      );
    const advisoryKeyConclusions = Array.isArray(
      advisoryTraceability.keyConclusions || advisoryTraceability.key_conclusions
    )
      ? (advisoryTraceability.keyConclusions || advisoryTraceability.key_conclusions)
      : [];
    let hasAdvisoryTraceabilityData = advisoryKeyConclusions.length > 0;
    const certaintyPct = getCertaintyPercentage(grant);
    const _displayScore = getDisplayScore(grant);
    const strategicFitScore = getStrategicFitScore(grant);
    // The number shown next to the signal-match label is the backend's
    // signalMatchPct (the value the label was derived from), never a different
    // probability figure — so the pair is always internally consistent.
    const signalMatchPct = getSignalMatchPct(grant) ?? (typeof grant.successProbability === 'number' ? grant.successProbability : _displayScore);
    const signalMatchLabel = grant.signalMatchLabel || grant.signal_match_label || scoreBandLabel(signalMatchPct);
    const strategicFitLabel = grant.strategicFitLabel || grant.strategic_fit_label || scoreBandLabel(strategicFitScore);
    const isBlockedCard = grantStatus === 'blocked';
    const blockReason = cleanText(getGrantBlockReason(grant));
    const prerequisiteChecks = getGrantPrerequisiteChecks(grant);
    const blockingUnmetChecks = prerequisiteChecks.filter((c) => c.blocking && c.status === 'unmet');
    const officialUrl = getGrantOfficialUrl(grant);
    const transitionNote = cleanText(getGrantTransitionNote(grant));
    const referenceRows = buildReferenceRows(grant);
    const pillarLabel = cleanText(grant.pillarLabel || grant.pillar_label || '');
    const eligibilityAfterPrerequisites = getGrantEligibilityAfterPrerequisites(grant);
    const certaintyLabel = certaintyBandLabel(certaintyPct);
    const scoreInterpretation = cleanText(grant.scoreInterpretation || grant.score_interpretation || '');
    const approvalLikelihoodRaw =
      grant.approvalLikelihood ??
      grant.approval_likelihood ??
      detailedAnalysis.approval_likelihood ??
      detailedAnalysis.approvalLikelihood ??
      null;
    const approvalLikelihood =
      typeof approvalLikelihoodRaw === 'number' && !Number.isNaN(approvalLikelihoodRaw)
        ? Math.max(0, Math.min(100, Math.round(approvalLikelihoodRaw)))
        : null;
    const scoreChangeLevers = Array.isArray(grant.scoreChangeLevers)
      ? grant.scoreChangeLevers
      : Array.isArray(grant.score_change_levers)
        ? grant.score_change_levers
        : Array.isArray(detailedAnalysis.score_change_levers)
          ? detailedAnalysis.score_change_levers
          : [];
    const advisoryProvenance = Array.isArray(detailedAnalysis.advisory_provenance)
      ? detailedAnalysis.advisory_provenance
      : [];

    // Frontend deterministic fallback: if backend/deployed API omits these fields,
    // still show actionable content in grant cards using already-available score signals.
    if (!hasSubmissionStrategyData) {
      const effectiveScore =
        typeof _displayScore === 'number' && !Number.isNaN(_displayScore)
          ? _displayScore
          : typeof grant?.score === 'number' && !Number.isNaN(grant.score)
            ? grant.score
            : 0;
      const fallbackWhenToApply =
        grantStatus === 'ineligible' || effectiveScore < 50
          ? 'not_recommended'
          : grantStatus === 'eligible' || effectiveScore >= 70
            ? 'apply_now'
            : 'after_improvements';
      const fallbackAlt = String(grantIdValue || '').toUpperCase() === 'EDG' ? 'PSG' : 'EDG';
      submissionStrategy = {
        when_to_apply: fallbackWhenToApply,
        when_to_apply_rationale:
          fallbackWhenToApply === 'apply_now'
            ? 'Current fit indicators are stronger. Proceed with submission while tightening evidence quality to minimise follow-up queries.'
            : fallbackWhenToApply === 'not_recommended'
              ? 'Current fit is weak or blocked by mandatory conditions. Prioritise remediation and consider a better-matched scheme first.'
              : 'Apply after improving readiness signals, especially scope-to-budget clarity and supporting evidence quality.',
        prep_timeline_weeks: [
          { week: 1, milestone: 'Confirm scope, objectives, and measurable outcomes for this grant.' },
          { week: 2, milestone: 'Strengthen quotations, budget rationale, and implementation milestones.' },
          { week: 3, milestone: 'Consolidate declarations and supporting documents for submission.' },
          { week: 4, milestone: 'Submit via portal and prepare responses for potential clarifications.' },
        ],
        budget_funding_scenarios: {
          best_case:
            'Stronger evidence alignment supports a smoother review path with fewer clarification rounds.',
          base_case:
            'Typical review path with targeted clarifications on scope, budget assumptions, and readiness.',
          conservative:
            'If evidence remains thin or fit is mixed, review may be slower and support confidence lower.',
        },
        alternative_grant_path: fallbackWhenToApply === 'apply_now' ? '' : fallbackAlt,
      };
      hasSubmissionStrategyData = true;
    }

    if (!hasAdvisoryTraceabilityData) {
      const rawSources =
        grant?.referenceInfo?.sources ||
        grant?.reference_info?.sources ||
        grant?.reference?.sources ||
        detailedAnalysis?.reference_info?.sources ||
        detailedAnalysis?.referenceInfo?.sources ||
        [];
      const fallbackSources = Array.isArray(rawSources)
        ? rawSources
            .map((s) => ({
              title: cleanText(s?.title || '') || 'Official scheme reference',
              url: (s?.url || '').trim(),
              criterion_excerpt: cleanText(
                s?.criterion_excerpt ||
                  s?.criterionExcerpt ||
                  s?.note ||
                  s?.description ||
                  'Mapped to submitted eligibility, scope, and readiness signals.'
              ),
            }))
            .filter((s) => s.title || s.url)
            .slice(0, 3)
        : [];
      const sourcesForFallback =
        fallbackSources.length > 0
          ? fallbackSources
          : [
              {
                title: 'Official scheme reference',
                url: '',
                criterion_excerpt:
                  'Mapped to submitted eligibility, scope, and readiness signals.',
              },
            ];
      const primaryPositive = displayPositiveReasons[0] || positiveReasons[0] || 'Baseline fit signals are present';
      const primaryNegative = whereToImproveItems[0] || displayNegativeReasons[0] || 'Evidence quality can be strengthened';
      advisoryTraceability = {
        keyConclusions: [
          {
            conclusion: 'Overall scheme fit signal',
            rationale: `Assessment indicates ${grantStatus === 'eligible' ? 'stronger' : 'mixed'} alignment based on submitted company and project inputs.`,
            sources: sourcesForFallback,
            data_quality_score: typeof certaintyPct === 'number' ? Math.max(35, Math.min(95, certaintyPct)) : 62,
            is_assumption: false,
            assumption_note: '',
          },
          {
            conclusion: 'Readiness and execution risk',
            rationale: primaryNegative,
            sources: sourcesForFallback,
            data_quality_score: typeof certaintyPct === 'number' ? Math.max(30, Math.min(90, certaintyPct - 8)) : 54,
            is_assumption: true,
            assumption_note: 'Assumed missing supporting evidence can be produced before submission.',
          },
          {
            conclusion: 'Strength to retain in submission',
            rationale: primaryPositive,
            sources: sourcesForFallback,
            data_quality_score: typeof certaintyPct === 'number' ? Math.max(35, Math.min(98, certaintyPct + 4)) : 68,
            is_assumption: false,
            assumption_note: '',
          },
        ],
      };
      hasAdvisoryTraceabilityData = true;
    }
    const submissionStrategySource =
      submissionStrategySourceRaw === 'none' ? 'frontend_fallback' : submissionStrategySourceRaw;
    const advisoryTraceabilitySource =
      advisoryTraceabilitySourceRaw === 'none' ? 'frontend_fallback' : advisoryTraceabilitySourceRaw;
    // Hard overrides: a blocked grant or an invitation-only programme must never
    // carry an "Apply now" call to action, whatever the strategy engine suggested.
    if (isBlockedCard || referenceRows.isInvitationOnly) {
      submissionStrategy = {
        ...submissionStrategy,
        when_to_apply: 'not_recommended',
        when_to_apply_rationale: isBlockedCard
          ? (blockReason
              ? `${blockReason} Resolve this prerequisite before investing application effort.`
              : 'A structural prerequisite is not met yet. Resolve it before investing application effort.')
          : 'This programme is invitation only — it is not open for application. Focus on the schemes you can apply to now.',
      };
      hasSubmissionStrategyData = true;
    }
    const whenApplyRaw = String(
      submissionStrategy?.when_to_apply || submissionStrategy?.whenToApply || ''
    ).toLowerCase();
    const whenApplyLabel =
      whenApplyRaw === 'apply_now'
        ? 'APPLY NOW'
        : whenApplyRaw === 'not_recommended'
          ? 'NOT RECOMMENDED'
          : 'APPLY AFTER IMPROVEMENTS';
    const showApplyNowProcess = whenApplyRaw === 'apply_now';
    const applyNowProcessSteps = (() => {
      const code = String(grantIdValue || '').toUpperCase();
      const schemeLabel = code.includes('PSG')
        ? 'PSG'
        : code.includes('EDG')
          ? 'EDG'
          : code.includes('MRA')
            ? 'MRA'
            : (grantIdValue || 'this scheme');
      const docsHint = code.includes('PSG')
        ? 'solution scope, vendor quotations, and implementation evidence aligned to PSG support criteria'
        : code.includes('EDG')
          ? 'workplan, measurable outcomes/KPIs, budget rationale, and capability-development evidence aligned to EDG review requirements'
          : code.includes('MRA')
            ? 'target-market entry plan, overseas activity scope, and execution evidence aligned to MRA requirements'
            : `project scope, budget justification, and supporting documents required for ${schemeLabel}`;
      return [
        `Access the Business Grants Portal, sign in with CorpPass, and open a ${schemeLabel} application under your UEN.`,
        `Prepare and submit ${docsHint}.`,
        'Before final submission, use OPTRA Labs Application Composer and Grant Compliance Review to strengthen narrative quality and evidence completeness.',
      ];
    })();

    const statusLabels = {
      eligible: 'Eligible',
      likely: 'Likely',
      lessLikely: 'Less likely',
      weakFit: 'Weak fit',
      blocked: 'Blocked — prerequisite not met',
      ineligible: 'Not eligible',
    };
    // A blocked grant must never advertise a backend "Prioritized"/"Apply now" label.
    const statusLabel = isBlockedCard
      ? statusLabels.blocked
      : grant.statusLabel || grant.status_label || statusLabels[grantStatus] || grant.fit?.label || 'Needs review';
    const llmRationale = stripScopeMissingBoilerplateSentences(
      cleanText(detailedAnalysis.llm_rationale || '')
    );

    // Build a concise Funding & Compliance Highlights list from structured scoring signals
    const buildFundingComplianceHighlights = () => {
      const highlights = [];
      const fd = detailedAnalysis.form_data || {};

      const runway = (fd.cash_runway_band || '').toString().trim();
      if (runway) {
        const lowRunway = /less than 3 months/i.test(runway);
        const strongRunway = /more than 12 months/i.test(runway) || /6[-\u2013]12 months/i.test(runway);
        if (lowRunway) {
          highlights.push({ type: 'negative', text: 'Short cash runway may limit the company’s ability to sustain co-funding during the project.' });
        } else if (strongRunway) {
          highlights.push({ type: 'positive', text: 'Healthy cash runway supports the company’s ability to co-fund and sustain the project.' });
        }
      }

      const canFund = (fd.can_fund_upfront || '').toString().trim().toLowerCase();
      if (canFund === 'yes') {
        highlights.push({ type: 'positive', text: 'The company can fund project costs upfront before reimbursement, which is favourable for most grants.' });
      } else if (canFund === 'no') {
        highlights.push({ type: 'negative', text: 'Limited ability to fund project costs upfront could weaken committee confidence in delivery.' });
      }

      const quotationCount = (fd.quotation_count || '').toString().trim();
      if (quotationCount) {
        if (/(none|nil|0)/i.test(quotationCount) || quotationCount === '1') {
          highlights.push({ type: 'negative', text: 'Few or no vendor quotations yet — procurement readiness is still developing.' });
        } else if (quotationCount === '2' || /3 or more/i.test(quotationCount)) {
          highlights.push({ type: 'positive', text: 'Multiple vendor quotations strengthen procurement readiness and price benchmarking.' });
        }
      }

      const financialsRecency = (fd.financials_recency || '').toString().trim();
      if (financialsRecency) {
        if (/within 6 months/i.test(financialsRecency)) {
          highlights.push({ type: 'positive', text: 'Recent financial statements (≤ 6 months) support documentation strength.' });
        } else if (/more than 24 months/i.test(financialsRecency) || /not available/i.test(financialsRecency)) {
          highlights.push({ type: 'negative', text: 'Older or unavailable financial statements may trigger more queries on financial health.' });
        }
      }

      const cpfOk = (fd.cpf_compliance_ok || '').toString().trim().toLowerCase();
      if (cpfOk === 'yes') {
        highlights.push({ type: 'positive', text: 'CPF contributions are declared up-to-date, which supports baseline compliance.' });
      } else if (cpfOk === 'no') {
        highlights.push({ type: 'negative', text: 'Outstanding CPF-related issues could be viewed as a compliance risk.' });
      }

      const taxOk = (fd.tax_compliance_ok || '').toString().trim().toLowerCase();
      if (taxOk === 'yes') {
        highlights.push({ type: 'positive', text: 'Tax filings with IRAS are declared compliant, reducing eligibility risk.' });
      } else if (taxOk === 'no') {
        highlights.push({ type: 'negative', text: 'Outstanding IRAS / tax issues may materially weaken eligibility for some schemes.' });
      }

      const regOk = (fd.regulatory_compliance_ok || '').toString().trim().toLowerCase();
      if (regOk === 'no') {
        highlights.push({ type: 'negative', text: 'Ongoing legal or regulatory issues have been declared, which may concern assessors.' });
      }

      const priorBreach = (fd.prior_grant_breach || '').toString().trim().toLowerCase();
      if (priorBreach === 'yes') {
        highlights.push({ type: 'negative', text: 'Previous grant default, breach, or clawback is a major eligibility risk signal.' });
      }

      const projectNotCommenced = (fd.project_not_commenced || '').toString().trim().toLowerCase();
      if (projectNotCommenced === 'no') {
        highlights.push({ type: 'negative', text: 'The project appears to have already commenced or commitments have been made, which many grants do not allow.' });
      }

      const debarmentOk = (fd.no_debarment_or_insolvency || '').toString().trim().toLowerCase();
      if (debarmentOk === 'no') {
        highlights.push({ type: 'negative', text: 'Debarment, insolvency, or similar disqualifying events will typically block new grant approvals.' });
      }

      // Only keep the highest-signal items and avoid overwhelming the user
      return highlights.slice(0, 4);
    };

    const fundingComplianceHighlights = buildFundingComplianceHighlights();
    const getWeightedFactor = (...tokens) => {
      const lowered = tokens.map((t) => String(t).toLowerCase());
      return weightedBreakdown.find((item) =>
        lowered.some((token) => String(item?.factor || '').toLowerCase().includes(token))
      );
    };
    const formatFactorLine = (title, factor, fallbackText) => {
      const facet = String(title || '').toLowerCase();
      if (!factor) {
        const fb = cleanText(fallbackText);
        if (!fb) return '';
        return `On ${facet}, ${fb.charAt(0).toLowerCase()}${fb.slice(1)}`;
      }
      const weightPct = Math.round((factor.weight || 0) * 100);
      const contrib = typeof factor.contribution === 'number' ? `${factor.contribution >= 0 ? '+' : ''}${factor.contribution}` : 'N/A';
      const ev = displayBreakdownEvidence(factor.evidence || '');
      return `On ${facet}, the scoring model applied a ${weightPct}% weight and recorded a contribution of ${contrib}. ${ev || fallbackText}`;
    };
    /** Single narrative for Detailed Analysis: consultant paragraphs only, no bullets. */
    const buildDetailedAnalysisBlurb = () => {
      const slots = [
        {
          find: (item) => String(item?.factor || '').toLowerCase().includes('objective'),
          fallback: () =>
            formatFactorLine(
              'Objective Fit',
              getWeightedFactor('objective'),
              'Project objectives and outcomes should be tied to measurable grant priorities and national value creation.'
            ),
        },
        {
          find: (item) => String(item?.factor || '').toLowerCase().includes('company'),
          fallback: () =>
            formatFactorLine(
              'Company Eligibility',
              getWeightedFactor('company', 'eligibility'),
              'Headcount, ownership, operating footprint, and financial stability materially impact committee confidence.'
            ),
        },
        {
          find: (item) => String(item?.factor || '').toLowerCase().includes('alignment'),
          fallback: () =>
            formatFactorLine(
              'Project Alignment',
              getWeightedFactor('alignment', 'fit'),
              'Execution readiness, milestones, and outcomes determine whether the committee views the project as feasible and fundable.'
            ),
        },
        {
          find: (item) => String(item?.factor || '').toLowerCase().includes('budget'),
          fallback: () =>
            formatFactorLine(
              'Budget Feasibility',
              getWeightedFactor('budget'),
              'Budget must be proportionate to scope, deliverables, and likely co-funding requirements.'
            ),
        },
        {
          find: (item) => String(item?.factor || '').toLowerCase().includes('documentation'),
          fallback: () =>
            formatFactorLine(
              'Documentation Readiness',
              getWeightedFactor('doc', 'documentation'),
              'Missing mandatory files can delay or block approval even when strategic fit is good.'
            ),
        },
      ];
      const parts = slots.map(({ find, fallback }) => {
        const section = enhancedAdvisor.sections.find(find);
        if (section?.analysis) {
          if (isInternalCalibrationFactorName(section.factor)) return '';
          return cleanText(section.analysis);
        }
        return cleanText(fallback());
      }).filter(Boolean);
      return parts.join('\n\n');
    };

    const advisorSectionsForCards = (enhancedAdvisor.sections || []).filter(
      (s) => s && !isInternalCalibrationFactorName(s.factor)
    );
    const hasStructuredAdvisorSections = advisorSectionsForCards.some((s) => {
      if (!s) return false;
      if (cleanText(s.analysis)) return true;
      if (Array.isArray(s.evidence) && s.evidence.length > 0) return true;
      if (Array.isArray(s.risks) && s.risks.length > 0) return true;
      return false;
    });
    const advisoryExecutive = cleanText(enhancedAdvisor.executiveSummary);
    const isWeakFitCard = grantStatus === 'weakFit';
    const isLocalhostDev =
      import.meta.env.DEV &&
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const isUsingFallbackDebug =
      submissionStrategySource === 'frontend_fallback' ||
      advisoryTraceabilitySource === 'frontend_fallback';
    const debugRenderSourceChip =
      isLocalhostDev && isUsingFallbackDebug
        ? `Debug source: submission=${submissionStrategySource}; traceability=${advisoryTraceabilitySource}`
        : null;
	    const programmeGuidance = getProgrammeCardGuidance(grant);
	    const reasonNotPrioritized = cleanText(grant.reasonNotPrioritized || grant.reason_not_prioritized || '');
	    const isLegacyForNewApplications = isLegacyForNewApplicationsGrant(grant);
	    const clientVerdict = getClientVerdictForGrant({
	      grantStatus,
	      isBlockedCard,
	      isLegacyForNewApplications,
	      whenApplyRaw,
	    });
	    const displayedTransitionNote = isLegacyForNewApplications
	      ? 'Legacy scheme: no new applications after 29 September 2026. Use EDGE for new applications. Existing approvals, submissions, and claims continue under their original terms.'
	      : transitionNote;
	    const nextStepSeen = new Set();
    const firstUsefulNextStep = (grant.nextSteps || grant.next_steps || [])
      .map((step) => cleanText(step))
      .filter(Boolean)
      .find((step) => {
        const key = step.toLowerCase();
        if (nextStepSeen.has(key)) return false;
        nextStepSeen.add(key);
        if (/^(edit project scope|review company fit|improve evidence|validate fit|check eligibility|review requirements|prepare documents|consider applying|research further|confirm suitability)\.?$/i.test(step)) return false;
        return step.split(/\s+/).length >= 5;
      });
    const whatToDoNext = (() => {
      if (isBlockedCard) {
        const remediation = blockingUnmetChecks.find((check) => cleanText(check.howToResolve));
        return cleanText(remediation?.howToResolve) || blockReason || 'Resolve the blocking prerequisite before treating this route as application-ready.';
      }
	      if (isLegacyForNewApplications || transitionNote) {
	        return 'For a new project, review the EDGE route first. For an existing PSG, EDG, or MRA approval or submission, continue preparing claim evidence against the original letter of offer and scheme terms.';
	      }
      if (firstUsefulNextStep) return firstUsefulNextStep;
      const rationale = cleanText(submissionStrategy.when_to_apply_rationale || submissionStrategy.whenToApplyRationale);
      if (rationale) return rationale;
      if (whenApplyRaw === 'apply_now') return 'Prepare the application pack and validate the evidence before submission.';
      if (whenApplyRaw === 'not_recommended') return 'Prioritise a better-matched route before spending effort on this application.';
      return 'Close the evidence gaps first, then reassess whether this route is ready to pursue.';
    })();

    return (
      <div 
        key={grant.id || grantKey} 
        id={`result-grant-${grantKey}`}
        className="bg-white rounded-3xl p-8 shadow-xl transition-all duration-300 hover:shadow-2xl"
      >
        {isWeakFitCard && grantStatus !== 'ineligible' ? (
          <p className="text-xs font-semibold mb-3 px-1" style={{ color: '#B45309' }}>
            Lower priority / weaker thematic match — review fit rationale before investing application effort.
          </p>
        ) : null}
        {/* Top status row: match card style used elsewhere (light border, white background) */}
        <div
          className="border rounded-2xl mb-6 px-6 py-4 flex items-start sm:items-center justify-between gap-3 min-h-[4.5rem] sm:min-h-0 bg-white"
          style={{ borderColor: "#BFDBFE" }}
        >
          <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
            <FitIcon className="w-6 h-6" style={{ color: '#60A5FA' }} aria-hidden />
            <span className="text-ui text-sm md:text-base font-semibold leading-tight break-words" style={{ color: '#1F2937' }}>
              {String(statusLabel || '').toUpperCase()}
            </span>
          </div>
        </div>

        {/* Grant ID - Moved to top for easy identification */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
	          <div className="bg-white border px-3 py-1.5 rounded-lg" style={{ borderColor: '#BFDBFE' }}>
	            <span className="text-xs uppercase tracking-wide" style={{ color: '#4B5563' }}>Application route:</span>
	            <span className="text-data text-sm ml-2" style={{ color: '#4B5563', fontWeight: 400 }}>{grantIdValue}</span>
	          </div>
          
          {/* Certainty Info - Next to Grant ID */}
          {typeof certaintyPct === 'number' && certaintyPct > 0 && (
            <div className="text-data bg-white border px-3 py-1.5 rounded-lg text-xs" style={{ borderColor: '#BFDBFE', color: '#4B5563', fontWeight: 400 }}>
              ~{certaintyPct}% certainty · {certaintyLabel}
            </div>
          )}

          {grantStatus !== 'ineligible' && typeof signalMatchPct === 'number' && (
            <div className="bg-white border px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ borderColor: '#BFDBFE', color: '#4B5563' }}>
              {signalMatchPct}% signal match · {signalMatchLabel}
            </div>
          )}
          {grantStatus !== 'ineligible' && typeof strategicFitScore === 'number' && (
            <div className="bg-white border px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ borderColor: '#BFDBFE', color: '#4B5563' }}>
              {strategicFitScore}% strategic fit · {strategicFitLabel}
            </div>
          )}
          {grantStatus !== 'ineligible' && typeof approvalLikelihood === 'number' && (
            <div className="bg-white border px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ borderColor: '#BFDBFE', color: '#4B5563' }}>
              {approvalLikelihood}% estimated approval likelihood
            </div>
          )}

          {pillarLabel ? (
            <div className="bg-white border px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ borderColor: '#BFDBFE', color: '#4B5563' }}>
              Pillar: {pillarLabel}
            </div>
          ) : null}

          {officialUrl ? (
            <a
              href={officialUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-white border px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-50 transition-colors"
              style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
              data-testid={`official-page-link-${grantKey}`}
              aria-label={`Official page for ${grantFullName}`}
            >
              <ExternalLink className="w-3.5 h-3.5" aria-hidden />
              Official page
            </a>
          ) : null}
	        </div>
	        {clientVerdict ? (
	          <div
	            className="border rounded-2xl mb-4 px-5 py-3 bg-slate-50/80"
	            style={{ borderColor: '#BFDBFE' }}
	            data-testid={`client-verdict-${grantKey}`}
	          >
	            <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
	              <span className="font-semibold" style={{ color: '#1F2937' }}>{clientVerdict.label}.</span>{' '}
	              {clientVerdict.detail}
	            </p>
	          </div>
	        ) : null}
	        {grantStatus !== 'ineligible' && typeof signalMatchPct === 'number' && typeof approvalLikelihood === 'number' ? (
	          <p className="text-xs mb-4" style={{ color: '#6B7280' }}>
	            OPTRA estimate: signal match reflects strategic grant alignment. Estimated approval likelihood reflects evidence readiness, compliance signals, and mandatory-rule stability.
	          </p>
	        ) : null}
        {grantStatus !== 'ineligible' && scoreInterpretation ? (
          <p className="text-xs mb-4" style={{ color: '#6B7280' }}>
            {scoreInterpretation}
          </p>
        ) : null}

        {/* Grant Name and optional ineligible label */}
        <div className="mb-4">
          <h3 className="text-2xl font-bold mb-1" style={{ color: '#60A5FA' }}>
            {grantFullName}
          </h3>
          <div className="flex flex-wrap items-start content-start gap-2 mt-2 min-h-[2.5rem] sm:min-h-0">
            {getGrantCardBadges(grant).map((badge) => (
              <span key={badge} className="px-3 py-1 rounded-md text-xs font-semibold border" style={resultPillStyle}>
                {badge}
              </span>
            ))}
          </div>
          {getGrantCardTags(grant).length > 0 ? (
            <div className="flex flex-wrap items-start gap-2 mt-3">
              {getGrantCardTags(grant).map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="border-sky-200 bg-sky-50/80 text-slate-700 text-[11px] font-medium px-2.5 py-1"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          ) : null}
          {(grant.displayLabel || grant.display_label || '').startsWith('Not eligible') && grantFullName !== (grant.displayLabel || grant.display_label) && (
            <p className="text-sm mt-1" style={{ color: '#4B5563' }}>{grantFullName}</p>
          )}
        </div>
        {grantPurposeText ? (
          <div
            className="border rounded-2xl mb-6 px-6 py-4 bg-white"
            style={{ borderColor: "#BFDBFE" }}
          >
            <h4 className="text-ui text-sm mb-2" style={{ color: '#60A5FA', fontWeight: 500 }}>
              What this grant is for
            </h4>
            <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
              {grantPurposeText}
            </p>
          </div>
        ) : null}
        {isBlockedCard ? (
          <div
            className="border rounded-2xl mb-6 px-6 py-4"
            style={{ borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' }}
            data-testid={`blocked-banner-${grantKey}`}
          >
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#DC2626' }} aria-hidden />
              <div className="min-w-0">
                <h4 className="text-ui text-sm mb-1" style={{ color: '#991B1B', fontWeight: 600 }}>
                  Blocked by a structural prerequisite
                </h4>
                <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                  {blockReason || 'A mandatory prerequisite for this scheme is not met on the inputs you submitted, so it is not eligible now regardless of its score.'}
                </p>
                {blockingUnmetChecks.some((c) => c.howToResolve) ? (
                  <div className="mt-3 space-y-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#991B1B' }}>How to resolve</p>
                    <ul className="space-y-1">
                      {blockingUnmetChecks.filter((c) => c.howToResolve).map((c, idx) => (
                        <li key={`resolve-${grantKey}-${idx}`} className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                          {c.howToResolve}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
	        {displayedTransitionNote ? (
	          <div
	            className="border rounded-2xl mb-6 px-6 py-4"
	            style={{ borderColor: '#FCD34D', backgroundColor: '#FFFBEB' }}
	            data-testid={`transition-note-${grantKey}`}
	          >
	            <h4 className="text-ui text-sm mb-1" style={{ color: '#92400E', fontWeight: 600 }}>
	              Legacy context
	            </h4>
		            <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>{displayedTransitionNote}</p>
		          </div>
		        ) : null}
        {whatToDoNext ? (
          <div
            className="border rounded-2xl mb-6 px-6 py-4 bg-white"
            style={{ borderColor: '#BFDBFE' }}
            data-testid={`next-action-${grantKey}`}
          >
            <h4 className="text-ui text-sm mb-1" style={{ color: '#60A5FA', fontWeight: 600 }}>
              What to do next
            </h4>
            <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
              {whatToDoNext}
            </p>
          </div>
        ) : null}
	        {prerequisiteChecks.length > 0 ? (
          <div
            className="border rounded-2xl mb-6 px-6 py-4 bg-white"
            style={{ borderColor: '#BFDBFE' }}
            data-testid={`prerequisites-${grantKey}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h4 className="text-ui text-sm" style={{ color: '#60A5FA', fontWeight: 500 }}>
                Prerequisites
              </h4>
              {eligibilityAfterPrerequisites ? (
                <span
                  className="px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wide border"
                  style={
                    eligibilityAfterPrerequisites === 'eligible_now'
                      ? { borderColor: '#6EE7B7', backgroundColor: '#D1FAE5', color: '#065F46' }
                      : eligibilityAfterPrerequisites === 'blocked'
                        ? { borderColor: '#FCA5A5', backgroundColor: '#FEF2F2', color: '#991B1B' }
                        : { borderColor: '#FCD34D', backgroundColor: '#FFFBEB', color: '#92400E' }
                  }
                >
                  {eligibilityAfterPrerequisites === 'eligible_now'
                    ? 'Eligible now'
                    : eligibilityAfterPrerequisites === 'blocked'
                      ? 'Blocked'
                      : eligibilityAfterPrerequisites === 'conditional'
                        ? 'Conditional'
                        : 'Unknown'}
                </span>
              ) : null}
            </div>
            <ul className="space-y-2.5">
              {prerequisiteChecks.map((check, idx) => {
                const marker =
                  check.status === 'met'
                    ? { Icon: CheckCircle, color: '#047857', text: 'Met' }
                    : check.status === 'unmet'
                      ? { Icon: XCircle, color: '#DC2626', text: check.blocking ? 'Not met — blocking' : 'Not met' }
                      : { Icon: HelpCircle, color: '#D97706', text: 'Unknown — confirm' };
                return (
                  <li key={`prereq-${grantKey}-${check.code || idx}`} className="flex items-start gap-2.5">
                    <marker.Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: marker.color }} aria-hidden />
                    <div className="min-w-0">
                      <p className="text-sm leading-snug" style={{ color: '#1F2937' }}>
                        <span className="font-medium">{check.label}</span>
                        <span className="text-xs ml-2" style={{ color: marker.color }}>{marker.text}</span>
                      </p>
                      {check.detail ? (
                        <p className="text-xs mt-0.5 leading-relaxed" style={{ color: '#6B7280' }}>{check.detail}</p>
                      ) : null}
                      {check.status !== 'met' && check.howToResolve ? (
                        <p className="text-xs mt-0.5 leading-relaxed" style={{ color: '#4B5563' }}>
                          <span className="font-semibold">How to resolve:</span> {check.howToResolve}
                        </p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        {(() => {
          const referenceItems = [
            ['Support level', referenceRows.supportLevel],
            ['Funding cap', referenceRows.fundingCap],
            ['Application window', referenceRows.applicationWindow],
            ['Processing time', referenceRows.processingTime],
            ['Portal', referenceRows.portal],
            ['Last verified', referenceRows.lastVerified],
          ].filter(([, value]) => hasReferenceValue(value));

          if (referenceItems.length === 0 && !officialUrl) return null;

          return (
          <div
            className="border rounded-2xl mb-6 px-6 py-4 bg-white"
            style={{ borderColor: '#BFDBFE' }}
            data-testid={`reference-data-${grantKey}`}
          >
	            <div className="flex items-center gap-2 mb-3">
	              <DollarSign className="w-4 h-4" style={{ color: '#60A5FA' }} aria-hidden />
	              <h4 className="text-ui text-sm" style={{ color: '#60A5FA', fontWeight: 500 }}>Official scheme facts</h4>
	            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {referenceItems.map(([label, value]) => (
                <div key={`${grantKey}-${label}`}>
                  <dt className="text-xs uppercase tracking-wide" style={{ color: '#94A3B8' }}>{label}</dt>
                  <dd style={{ color: '#1F2937' }}>{value}</dd>
                </div>
              ))}
            </dl>
            {officialUrl ? (
              <a
                href={officialUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium hover:underline"
                style={{ color: '#60A5FA' }}
              >
                <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                Open the official page
              </a>
            ) : null}
          </div>
          );
        })()}
        {programmeGuidance?.disclaimer ? (
          <div
            className="border rounded-2xl mb-6 px-6 py-4 bg-slate-50/70"
            style={{ borderColor: '#BFDBFE' }}
          >
            <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
              <span style={{ color: '#60A5FA', fontWeight: 600 }}>*</span>{' '}
              {programmeGuidance.disclaimer}
            </p>
          </div>
        ) : null}
        {reasonNotPrioritized ? (
          <div
            className="border rounded-2xl mb-6 px-6 py-4 bg-amber-50/70"
            style={{ borderColor: '#FCD34D' }}
          >
            <h4 className="text-ui text-sm mb-2" style={{ color: '#92400E', fontWeight: 600 }}>
              Why this was not prioritised
            </h4>
            <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
              {reasonNotPrioritized}
            </p>
          </div>
        ) : null}
        {debugRenderSourceChip ? (
          <div
            className="mb-4 inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-medium"
            style={{ borderColor: '#D1D5DB', backgroundColor: '#F9FAFB', color: '#6B7280' }}
          >
            {debugRenderSourceChip}
          </div>
        ) : null}

        {/* Why you match / Where to improve */}
        {(displayPositiveReasons.length > 0 || whereToImproveItems.length > 0) && (
          <div className="mb-6 space-y-6">
            {displayPositiveReasons.length > 0 && (
              <div>
                <h4 className="text-ui text-sm mb-3" style={{ color: '#60A5FA', fontWeight: 500 }}>Why you match</h4>
                <ul className="space-y-2">
                  {displayPositiveReasons.slice(0, 3).map((reason, idx) => (
                    <li key={idx} className="flex items-start leading-relaxed" style={{ color: '#4B5563' }}>
                      <div className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0 bg-green-400" />
                      <span className="text-base min-w-0">
                        {reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {whereToImproveItems.length > 0 && (
              <div>
                <h4 className="text-ui text-sm mb-3" style={{ color: '#60A5FA', fontWeight: 500 }}>Where to improve</h4>
                <ul className="space-y-2">
                  {whereToImproveItems.slice(0, 3).map((reason, idx) => (
                    <li key={idx} className="flex items-start leading-relaxed" style={{ color: '#4B5563' }}>
                      <div className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0 bg-red-400" />
                      <span className="text-base">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Submission strategy (LLM + backend normalisation) */}
        {hasSubmissionStrategyData ? (
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h4
                className="text-ui text-sm"
                style={{ color: '#60A5FA', fontWeight: 500 }}
              >
                Submission strategy
              </h4>
              <span
                className="text-[10px] font-medium px-2.5 py-1 rounded-md border uppercase tracking-wide"
                style={{ backgroundColor: '#FFFFFF', borderColor: '#BFDBFE', color: '#4B5563', borderWidth: '1px' }}
              >
                {whenApplyLabel}
              </span>
            </div>
            {(submissionStrategy.when_to_apply_rationale || submissionStrategy.whenToApplyRationale) ? (
              <div className="mb-2 flex items-start gap-2.5">
                <div
                  className="w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0"
                  style={{ backgroundColor: 'rgba(96, 165, 250, 0.7)' }}
                />
                <p className="text-base leading-relaxed" style={{ color: '#4B5563' }}>
                  {cleanText(submissionStrategy.when_to_apply_rationale || submissionStrategy.whenToApplyRationale)}
                </p>
              </div>
            ) : null}
            {showApplyNowProcess ? (
              <div className="mb-3">
                <ul className="space-y-1.5">
                  {applyNowProcessSteps.slice(0, 3).map((step, idx) => (
                    <li
                      key={`apply-now-step-${grantKey}-${idx}`}
                      className="flex items-start gap-2 text-sm leading-relaxed"
                      style={{ color: '#4B5563' }}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                        style={{ backgroundColor: 'rgba(96, 165, 250, 0.55)' }}
                      />
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {submissionTimelineRows.length > 0 ? (
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>
                  Estimated prep timeline
                </p>
                <ol className="space-y-2 list-decimal list-inside text-sm" style={{ color: '#374151' }}>
                  {submissionTimelineRows
                    .slice(0, 12)
                    .map((row, idx) => {
                      const week = row?.week ?? idx + 1;
                      const milestone = cleanText(row?.milestone || row?.title || '');
                      if (!milestone) return null;
                      return (
                        <li key={`prep-${grantKey}-${idx}`} className="leading-relaxed pl-1">
                          <span className="font-medium">Week {week}:</span> {milestone}
                        </li>
                      );
                    })}
                </ol>
              </div>
            ) : null}
            {submissionBudgetScenarios &&
            typeof submissionBudgetScenarios === 'object' && (
              <div className="mb-4 grid gap-3 sm:grid-cols-3">
                {[
                  { key: 'best_case', label: 'Best case', alt: 'bestCase' },
                  { key: 'base_case', label: 'Base case', alt: 'baseCase' },
                  { key: 'conservative', label: 'Conservative', alt: 'conservative' },
                ].map(({ key, label, alt }) => {
                  const text =
                    submissionBudgetScenarios[key] ||
                    submissionBudgetScenarios[alt];
                  const t = cleanText(text || '');
                  if (!t) return null;
                  return (
                    <div key={key} className="border rounded-xl p-3 bg-slate-50/80" style={{ borderColor: '#E5E7EB' }}>
                      <p className="text-xs font-semibold mb-1" style={{ color: '#60A5FA' }}>
                        {label}
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                        {t}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
            {(submissionStrategy.alternative_grant_path || submissionStrategy.alternativeGrantPath) &&
              cleanText(submissionStrategy.alternative_grant_path || submissionStrategy.alternativeGrantPath) && (
                <div
                  className="text-sm rounded-xl border px-3 py-1.5 bg-white"
                  style={{ borderColor: '#BFDBFE', color: '#4B5563' }}
                >
                  <span className="font-semibold">Alternative path: </span>
                  {cleanText(submissionStrategy.alternative_grant_path || submissionStrategy.alternativeGrantPath)}
                </div>
              )}
            {programmeGuidance?.strategyNote ? (
              <p className="mt-3 text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                <span style={{ color: '#60A5FA', fontWeight: 600 }}>*</span>{' '}
                {programmeGuidance.strategyNote}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Evidence-backed conclusions (traceability) */}
        {hasAdvisoryTraceabilityData ? (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <h4
                  className="text-ui text-sm"
                  style={{ color: '#60A5FA', fontWeight: 500 }}
                >
                  Evidence and conclusions
                </h4>
              </div>
              <div className="mb-4 flex items-start gap-2.5">
                <div
                  className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: 'rgba(96, 165, 250, 0.7)' }}
                />
                <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>
                  Key judgments with source references and data-quality signals. Official pages remain the source of truth.
                </p>
              </div>
              <div className="space-y-4">
                {advisoryKeyConclusions.map((kc, idx) => {
                  const conclusion = cleanText(kc.conclusion || '');
                  const rationale = cleanText(kc.rationale || '');
                  const sources = Array.isArray(kc.sources) ? kc.sources : [];
                  const dq =
                    typeof kc.data_quality_score === 'number'
                      ? kc.data_quality_score
                      : typeof kc.dataQualityScore === 'number'
                        ? kc.dataQualityScore
                        : null;
                  const isAsm = Boolean(kc.is_assumption || kc.isAssumption);
                  const asmNote = cleanText(kc.assumption_note || kc.assumptionNote || '');
                  if (!conclusion && !rationale) return null;
                  return (
                    <div
                      key={`trace-${grantKey}-${idx}`}
                      className="border rounded-xl p-4"
                      style={{ borderColor: '#E5E7EB' }}
                    >
                      {conclusion ? (
                        <p className="text-sm font-semibold mb-1" style={{ color: '#111827' }}>
                          {conclusion}
                        </p>
                      ) : null}
                      {rationale ? (
                        <p className="text-sm leading-relaxed mb-2" style={{ color: '#4B5563' }}>
                          {rationale}
                        </p>
                      ) : null}
                      {typeof dq === 'number' && !Number.isNaN(dq) ? (
                        <p className="text-xs mb-2" style={{ color: '#6B7280' }}>
                          <span className="font-semibold" style={{ color: '#374151' }}>
                            Data quality for this conclusion:{' '}
                          </span>
                          {Math.min(100, Math.max(0, dq))}%
                        </p>
                      ) : null}
                      {isAsm ? (
                        <p
                          className="text-xs rounded-lg px-2 py-1.5 mb-2 inline-block"
                          style={{ backgroundColor: '#FFFBEB', color: '#92400E', border: '1px solid #FCD34D' }}
                        >
                          <span className="font-semibold">Assumption: </span>
                          {asmNote ||
                            'Based partly on unstated or incomplete evidence—verify against the official scheme text.'}
                        </p>
                      ) : null}
                      {sources.length > 0 ? (
                        <div className="mt-2 space-y-2">
                          <p className="text-xs font-semibold uppercase" style={{ color: '#9CA3AF' }}>
                            Sources
                          </p>
                          <ul className="space-y-2">
                            {sources.slice(0, 4).map((src, sidx) => {
                              const title = cleanText(src.title || '');
                              const url = (src.url || '').trim();
                              const excerpt = cleanText(
                                src.criterion_excerpt || src.criterionExcerpt || ''
                              );
                              if (!title && !url && !excerpt) return null;
                              return (
                                <li key={`src-${grantKey}-${idx}-${sidx}`} className="text-sm">
                                  <span className="font-medium" style={{ color: '#1F2937' }}>
                                    {title || 'Official website reference'}
                                  </span>
                                  {excerpt ? (
                                    <p className="text-xs mt-1 pl-0 leading-relaxed italic" style={{ color: '#6B7280' }}>
                                      Criterion: {excerpt}
                                    </p>
                                  ) : null}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

        {/* Funding Snapshot - Only show if meaningful data exists */}
        {grant.funding_snapshot && cleanText(grant.funding_snapshot) && cleanText(grant.funding_snapshot) !== 'Funding details available upon application' && (
          <div className="mb-6 bg-white border rounded-2xl p-5" style={{ borderColor: '#BFDBFE' }}>
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4" style={{ color: '#60A5FA' }} />
              <h4 className="text-ui text-sm" style={{ color: '#60A5FA', fontWeight: 500 }}>Funding Snapshot</h4>
            </div>
            <p className="text-base leading-relaxed" style={{ color: '#4B5563' }}>
              {cleanText(grant.funding_snapshot)}
            </p>
          </div>
        )}

        {/* View Detailed Analysis - Accordion matching Application Flow style */}
        <div className="border-t pt-6 mt-6" style={{ borderColor: '#BFDBFE' }}>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (DEBUG) console.log('🖱️ Detailed analysis clicked for grant:', grantKey, 'current state:', isExpanded);
              toggleGrantExpansion(grantKey);
            }}
            type="button"
            aria-expanded={isExpanded}
            aria-controls={`detailed-analysis-${toDomId(grantKey)}`}
            className="w-full flex items-center justify-between bg-white border rounded-2xl px-4 py-3 font-semibold hover:bg-gray-50 transition-all duration-200 focus:outline-none group cursor-pointer"
            style={{ borderColor: isExpanded ? '#BFDBFE' : '#60A5FA', color: '#60A5FA' }}
          >
            <span className="font-semibold text-base text-left flex-1 min-w-0" style={{ color: '#60A5FA' }}>
              {isExpanded ? 'Hide Detailed Analysis' : 'View Detailed Analysis'}
            </span>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 ml-3 flex-shrink-0 transition-transform duration-200" style={{ color: '#60A5FA' }} aria-hidden />
            ) : (
              <ChevronDown className="w-5 h-5 ml-3 flex-shrink-0 transition-transform duration-200" style={{ color: '#60A5FA' }} aria-hidden />
            )}
          </button>

          {isExpanded && (
            <div id={`detailed-analysis-${toDomId(grantKey)}`} className="mt-6 space-y-6 bg-white border rounded-2xl p-6" style={{ borderColor: '#BFDBFE' }}>
              <div>
                <h4 className="text-ui text-sm mb-3 uppercase" style={{ color: '#60A5FA', fontWeight: 500, letterSpacing: '0.05em' }}>Detailed Analysis</h4>
                {hasStructuredAdvisorSections ? (
                  <div className="space-y-5">
                    {advisorSectionsForCards.map((sec, sidx) => {
                      const title = displayBreakdownFactorName(sec.factor) || cleanText(sec.factor) || `Section ${sidx + 1}`;
                      const analysis = stripScopeMissingBoilerplateSentences(cleanText(sec.analysis));
                      const ev = Array.isArray(sec.evidence)
                        ? sec.evidence.map((x) => cleanText(x)).filter(Boolean)
                        : [];
                      const risks = Array.isArray(sec.risks)
                        ? sec.risks
                            .map((x) => cleanText(x))
                            .filter(Boolean)
                            .filter((line) => !isBannedInsightLine(line))
                        : [];
                      if (!analysis && ev.length === 0 && risks.length === 0) return null;
                      return (
                        <div key={`${title}-${sidx}`} className="border-b border-slate-100 pb-4 last:border-0">
                          <h6 className="text-sm font-semibold mb-2" style={{ color: '#1F2937' }}>{title}</h6>
                          {analysis ? (
                            <p className="text-sm leading-relaxed whitespace-pre-line mb-2" style={{ color: '#4B5563' }}>{analysis}</p>
                          ) : null}
                          {risks.length > 0 ? (
                            <div>
                              <p className="text-xs font-semibold uppercase mb-1" style={{ color: '#B45309' }}>Risks & caveats</p>
                              <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#4B5563' }}>
                                {risks.slice(0, 6).map((line, i) => (
                                  <li key={i}>{line}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#4B5563' }}>
                    {buildDetailedAnalysisBlurb() ||
                      advisoryExecutive ||
                      cleanText(llmRationale || grant.rationale || '') ||
                      'No detailed narrative was returned for this grant.'}
                  </p>
                )}
              </div>

              {scoreChangeLevers.length > 0 && (
                <div>
                  <h4 className="text-ui text-sm mb-3 uppercase" style={{ color: '#60A5FA', fontWeight: 500, letterSpacing: '0.05em' }}>
	                    What would improve this result most
                  </h4>
                  <div className="space-y-3">
                    {scoreChangeLevers.slice(0, 3).map((lever, idx) => {
                      const action = cleanText(lever.action || '');
                      const minUp = Number(lever.estimated_uplift_min ?? lever.estimatedUpliftMin ?? 0);
                      const maxUp = Number(lever.estimated_uplift_max ?? lever.estimatedUpliftMax ?? 0);
                      const why = cleanText(lever.why_this_moves_score || lever.whyThisMovesScore || '');
                      const conf = String(lever.confidence_tag || lever.confidenceTag || '').toLowerCase();
                      if (!action) return null;
                      return (
                        <div key={idx} className="bg-white border rounded-xl p-3" style={{ borderColor: '#BFDBFE' }}>
                          <p className="text-sm font-semibold" style={{ color: '#1F2937' }}>{action}</p>
                          <p className="text-xs mt-1" style={{ color: '#4B5563' }}>
                            Estimated uplift: +{Math.max(0, Math.round(minUp))} to +{Math.max(0, Math.round(maxUp))} points
                            {conf ? ` · confidence: ${conf}` : ''}
                          </p>
                          {why ? (
                            <p className="text-xs mt-1 leading-relaxed" style={{ color: '#6B7280' }}>{why}</p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {advisoryProvenance.length > 0 && (
                <div>
                  <h4 className="text-ui text-sm mb-3 uppercase" style={{ color: '#60A5FA', fontWeight: 500, letterSpacing: '0.05em' }}>
	                    Evidence basis
                  </h4>
                  <ul className="space-y-2">
                    {advisoryProvenance.slice(0, 4).map((item, idx) => {
                      const text = cleanText(item.text || '');
                      const sourceList = Array.isArray(item.source_types) ? item.source_types : ['derived_scoring'];
                      const sourceLabel = sourceList
                        .map((s) => String(s || '').replace(/_/g, ' ').toUpperCase())
                        .join(' • ');
                      if (!text) return null;
                      return (
                        <li key={idx} className="bg-white border rounded-lg px-3 py-2.5" style={{ borderColor: '#E5E7EB' }}>
                          <div className="text-[11px] tracking-wide mb-1 font-medium" style={{ color: '#6B7280' }}>
                            {sourceLabel}
                          </div>
                          <p className="text-xs leading-relaxed m-0" style={{ color: '#4B5563' }}>
                            {text}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {fundingComplianceHighlights.length > 0 && (
                <div>
                  <h4
                    className="text-ui text-sm mb-3 uppercase"
                    style={{ color: '#60A5FA', fontWeight: 500, letterSpacing: '0.05em' }}
                  >
                    Funding & Compliance Highlights
                  </h4>
                  <ul className="space-y-1 text-sm">
                    {fundingComplianceHighlights.map((item, idx) => {
                      const text = typeof item === 'string' ? item : item?.text;
                      if (!text) return null;
                      // Sign comes from the highlight builder, and is spelled out in text so it
                      // does not rely on bullet colour alone.
                      const isNegative = typeof item === 'object' && item?.type === 'negative';
                      return (
                        <li
                          key={idx}
                          className="flex items-start gap-2"
                          style={{ color: '#4B5563' }}
                        >
                          <span className="flex-shrink-0 mt-0.5" style={{ color: isNegative ? '#DC2626' : '#047857' }} aria-hidden>
                            •
                          </span>
                          <span className="leading-relaxed">
                            <span className="font-semibold">{isNegative ? 'Risk: ' : 'Strength: '}</span>
                            {text}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Score breakdown (collapsible) — extra affordances so users notice it expands */}
              <div className="border rounded-xl px-4 py-3 bg-white transition-colors" style={{ borderColor: '#93C5FD' }}>
                <button
                  type="button"
                  onClick={() => toggleScoreBreakdown(grantKey)}
                  aria-expanded={isScoreBreakdownExpanded}
                  aria-controls={`score-breakdown-${toDomId(grantKey)}`}
                  className="w-full cursor-pointer flex items-center justify-between gap-3 py-1 text-left"
                >
                  <div className="flex flex-col gap-1 min-w-0 flex-1">
	                      <span className="font-semibold text-sm uppercase tracking-wide" style={{ color: '#60A5FA' }}>Scoring detail</span>
	                    <span className="text-xs font-normal normal-case tracking-normal leading-snug" style={{ color: '#6B7280' }}>
	                      Tap to expand - scoring inputs, checks reviewed, and how your answers shaped the result
                    </span>
                  </div>
                  {isScoreBreakdownExpanded ? (
                    <ChevronUp className="w-5 h-5 ml-3 flex-shrink-0 transition-transform duration-200" style={{ color: '#60A5FA' }} aria-hidden />
                  ) : (
                    <ChevronDown className="w-5 h-5 ml-3 flex-shrink-0 transition-transform duration-200" style={{ color: '#60A5FA' }} aria-hidden />
                  )}
                </button>
                {isScoreBreakdownExpanded && (
                <div id={`score-breakdown-${toDomId(grantKey)}`} className="pt-4 space-y-4 border-t mt-3" style={{ borderColor: '#E5E7EB' }}>
                <div>
                  {breakdownForDisplay.length > 0 ? (
                    <div className="overflow-x-auto border rounded-lg px-3" style={{ borderColor: '#E5E7EB' }}>
                      <table className="min-w-full text-xs">
                        <caption className="sr-only">Score breakdown for {grantFullName}: factor and weight, score contribution, status and rationale</caption>
                        <thead>
                          <tr className="uppercase text-xs tracking-wide border-b" style={{ color: '#6B7280', borderColor: '#E5E7EB' }}>
                            <th
                              scope="col"
                              className="text-left py-2 pr-3 leading-tight whitespace-normal border-r"
                              style={{ borderColor: '#E5E7EB', minWidth: '9rem' }}
                            >
	                              SIGNAL
	                              <br />
	                              &amp; WEIGHT
                            </th>
                            <th
                              scope="col"
                              className="text-left py-2 px-3 leading-tight whitespace-normal border-r"
                              style={{ borderColor: '#E5E7EB', minWidth: '6.5rem' }}
                            >
                              SCORE
                              <br />
                              CONTRIBUTION
                            </th>
                            <th
                              scope="col"
                              className="text-left py-2 pl-3 pr-1 leading-tight whitespace-normal"
                              style={{ minWidth: '14rem' }}
                            >
                              STATUS
                              <br />
                              &amp; RATIONALE
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {breakdownForDisplay.map((item, idx) => {
                            const factorName = displayBreakdownFactorName(item.factor);
                            const factorKey = (factorName || '').toLowerCase();
                            const scopeText = resolveProjectScopeText(results, formData);
                            const factorExplanations = grant.factor_explanations || grant.factorExplanations || [];
                            const factorExplanationMap = new Map(
                              factorExplanations.map((fx) => [
                                String(fx.factor_name || fx.factorName || '').toLowerCase(),
                                fx.explanation,
                              ])
                            );
                            let explanation =
                              factorExplanationMap.get(factorKey) ||
                              displayBreakdownEvidence(item.evidence);
                            if (
                              scopeText &&
                              (factorKey.includes('project alignment') || factorKey.includes('objective fit')) &&
                              !/project scope/i.test(String(explanation || ''))
                            ) {
                              explanation = `${explanation} Project scope considered: ${scopeText.slice(0, 180)}${scopeText.length > 180 ? '...' : ''}`;
                            }

                            return (
                              <tr key={idx} className={idx === 0 ? '' : 'border-t'} style={idx === 0 ? undefined : { borderColor: '#E5E7EB' }}>
                                <td className="py-1.5 pr-3 font-medium align-top border-r" style={{ color: '#4B5563', borderColor: '#E5E7EB' }}>
                                  <div>{factorName}</div>
                                  <div className="text-[11px] mt-0.5" style={{ color: '#6B7280', fontWeight: 400 }}>
                                    {Math.round((item.weight || 0) * 100)}% of score
                                  </div>
                                  <span
                                    className={`mt-1 inline-flex px-2 py-1 rounded text-xs font-medium border ${item.hit ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
                                    style={{ color: item.hit ? '#047857' : '#B91C1C' }}
                                  >
                                    {item.hit ? 'Strength' : 'Gap'}
                                  </span>
                                </td>
                                <td className="text-data py-1.5 px-3 align-top border-r" style={{ color: '#4B5563', fontWeight: 400, borderColor: '#E5E7EB' }}>{item.contribution}</td>
                                <td className="py-2 pl-3 pr-1 align-top">
                                  {explanation ? (
                                    <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>
                                      {explanation}
                                    </p>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm" style={{ color: '#4B5563' }}>No factor-level contributions were returned by the scoring engine.</p>
                  )}
                </div>

                <div>
	                  <h6 className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#4B5563' }}>Checks reviewed</h6>
                  {ruleHits.length > 0 ? (
                    <div className="space-y-3">
                      {ruleHits.map((hit, idx) => (
                        <div key={idx} className="bg-white border rounded-xl p-3" style={{ borderColor: '#BFDBFE' }}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium" style={{ color: '#4B5563' }}>{hit.rule}</span>
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${hit.result === 'pass' ? 'bg-emerald-50 border-emerald-300' : hit.result === 'fail' ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-300'}`} style={{ color: hit.result === 'pass' ? '#10B981' : hit.result === 'fail' ? '#EF4444' : '#4B5563' }}>
                              {hit.result === 'pass' ? 'Pass' : hit.result === 'fail' ? 'Gap' : 'N/A'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm" style={{ color: '#4B5563' }}>No individual rule assessments were returned for this grant.</p>
                  )}
                </div>
                </div>
                )}
              </div>

              {grant.scoreBreakdown && Object.keys(grant.scoreBreakdown).length > 0 && (
                <div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {Object.entries(grant.scoreBreakdown).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between bg-white border rounded-xl px-3 py-2" style={{ borderColor: '#BFDBFE' }}>
                        <span className="capitalize" style={{ color: '#4B5563' }}>{key.replace(/_/g, ' ')}</span>
                        <span className="text-data" style={{ color: '#60A5FA', fontWeight: 400 }}>{value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Footer removed - Grant ID now at top */}
      </div>
    );
  };

  // Handle PDF export using markdown-to-PDF conversion
  const handleExportPDF = async () => {
    if (!results) {
      console.error('No results data available for PDF export');
      setExportError('No results are available to export. Please complete the eligibility assessment first.');
      return;
    }

    if (((pdfExportViewModel?.topGrantProfiles?.length || 0) + (pdfExportViewModel?.lowerPriority?.length || 0)) === 0) {
      setExportError('No grants are available for export with the selected report type. Please choose another report type.');
      return;
    }

    setExportError('');
    setIsExportingPDF(true);

    const fetchWithTimeout = async (url, options = {}, timeoutMs = 45000) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } finally {
        clearTimeout(timer);
      }
    };

    const downloadBlob = (blob, filename) => {
      const blobUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = filename;
      downloadLink.rel = 'noopener noreferrer';
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
    };
    
    try {
      // Validate markdown report before export
      let markdownReport = sanitizeMarkdownForPdfExport(buildMarkdownReport());
      if (!markdownReport || markdownReport.trim().length === 0) {
        throw new Error('Markdown report is empty. Cannot generate PDF.');
      }
      
      // Validate that at least one grant is included
      if (((pdfExportViewModel?.topGrantProfiles?.length || 0) + (pdfExportViewModel?.lowerPriority?.length || 0)) === 0) {
        throw new Error('No grants available for export with the current selection.');
      }
      
      if (DEBUG) console.log('📄 Starting PDF export...');
      if (DEBUG) console.log('📊 Export data:', {
        status: overallStatus,
        exportableGrantsCount:
          (pdfExportViewModel?.topGrantProfiles?.length || 0) + (pdfExportViewModel?.lowerPriority?.length || 0),
        allAnalyzedGrantsCount: allAnalyzedGrants.length,
        eligibleGrantsCount: eligibleGrants.length,
        likelyGrantsCount: likelyGrants.length,
        lessLikelyGrantsCount: lessLikelyGrants.length,
        customSelectedCount: customSelectedGrantIds.size,
        pdfReportMode,
        snapshotId: results.snapshotId,
        markdownReportLength: markdownReport.length
      });
      
      const exportFilenameSlug =
        pdfReportMode === 'compact_advisory'
          ? 'compact-advisory'
          : pdfReportMode === 'full_all_grants'
            ? 'full-all-grants'
            : 'standard-advisory';
      const companyForFilename = String(
        results?.inputsUsed?.company_name ||
        results?.inputsUsed?.companyName ||
        formData?.companyName ||
        formData?.company_name ||
        'company'
      )
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 64) || 'company';
      const exportJobId = String(results?.snapshotId || locationJobId || 'job').replace(/[^a-zA-Z0-9_-]+/g, '');
      const filename = `${companyForFilename}_${exportFilenameSlug}_${exportJobId}.pdf`;

      // Use markdown export with resilient endpoint fallback.
      if (DEBUG) console.log('🔄 Attempting PDF export via markdown...');
      const exportPayload = {
        markdown: markdownReport,
        filename,
        mode: pdfReportMode,
      };
      const exportEndpoints = ['/api/assessments/pdf', '/export/pdf', '/api/export/pdf'];
      let response = null;
      let lastError = null;

      for (const endpoint of exportEndpoints) {
        try {
          response = await fetchWithTimeout(
            ensureBackendUrl(endpoint),
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/pdf',
              },
              body: JSON.stringify(exportPayload),
            },
            45000
          );

          // 405/404 often indicate wrong route prefix; try next endpoint.
          if (response.status === 404 || response.status === 405) {
            lastError = new Error(`Export endpoint ${endpoint} returned ${response.status}`);
            response = null;
            continue;
          }
          break;
        } catch (err) {
          lastError = err;
          // Retry next endpoint on abort/network failures.
          response = null;
        }
      }

      if (!response) {
        if (DEBUG && lastError) console.warn('PDF export endpoint fallback failed:', lastError);
        throw new Error('PDF export is temporarily unavailable. Please restart the local backend/frontend servers and try again.');
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Markdown export endpoint failed (${response.status})`;
        
        // Parse error detail if available
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.detail) {
            errorMessage = errorJson.detail;
          }
        } catch {
          // If not JSON, use the raw text
          if (errorText) {
            errorMessage = errorText.length > 200 ? errorText.substring(0, 200) + '...' : errorText;
          }
        }
        
        // Provide helpful message for 503 (service unavailable)
        if (response.status === 503) {
          // Try to check health endpoint for more details
          try {
            const healthResponse = await fetch(ensureBackendUrl('/export/health'), {
              method: 'GET',
              headers: { 'Accept': 'application/json' }
            });
            if (healthResponse.ok) {
              const healthData = await healthResponse.json();
              // Backend /export/health uses xhtml2pdf (see backend/app/api/export.py)
              const pdfReady = healthData.status === 'ready' && healthData.xhtml2pdf_installed;
              if (pdfReady) {
                errorMessage = 'PDF generation service is temporarily unavailable. Please try again in a few moments.';
              } else {
                errorMessage =
                  healthData.error ||
                  'PDF generation is not available on the server (export libraries missing). Please contact support.';
              }
            } else {
              errorMessage =
                'PDF generation service is currently unavailable. Please try again later or contact support.';
            }
          } catch {
            errorMessage =
              'PDF generation service is currently unavailable. Please try again later or contact support.';
          }
        }
        
        throw new Error(errorMessage);
      }

      const pdfBlob = await response.blob();
      
      if (DEBUG) console.log(`✅ PDF export successful (${pdfBlob.size} bytes)`);
      
      downloadBlob(pdfBlob, filename);
      recordExportFile({
        file_name: filename,
        file_type: "pdf",
        source_tool: "eligibility_assessment",
        title: "Eligibility Assessment Export",
        description: `Eligibility report (${(pdfExportViewModel?.topGrantProfiles?.length || 0) + (pdfExportViewModel?.lowerPriority?.length || 0)} grants)`,
        size_bytes: pdfBlob.size,
      });
      if (DEBUG) console.log('✅ PDF exported successfully');
    } catch (error) {
      // Only log to console if it's not a known service unavailable error
      const isServiceUnavailable = error.message && (
        error.message.includes('not available') ||
        error.message.includes('not installed') ||
        error.message.includes('service unavailable') ||
        error.message.includes('503')
      );
      
      if (!isServiceUnavailable) {
        console.error('💥 PDF export error:', error);
      } else {
        // Log service unavailable errors at info level, not error
        console.info('ℹ️ PDF export service unavailable:', error.message);
      }
      
      // Send to Sentry (but skip for expected service unavailable errors)
      if (!isServiceUnavailable) {
        try {
          const { captureException } = await import('../utils/errorTracking');
          captureException(error, {
            component: 'EligibilityResults',
            action: 'handleExportPDF',
            exportableGrantsCount: exportableGrants?.length || 0,
            hasResults: !!results,
            snapshotId: results?.snapshotId
          });
        } catch (sentryError) {
          // Silently fail if Sentry not available
          console.warn('Failed to send error to Sentry:', sentryError);
        }
      }
      
      const isAbortError = error?.name === 'AbortError' || String(error?.message || '').toLowerCase().includes('aborted');
      const errorMessage = isAbortError
        ? 'PDF generation timed out. Please try again; the report is large and may need more time.'
        : (error?.message || 'Unknown error occurred. Please try again.');
      setExportError(`Export failed: ${errorMessage}`);
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleConfirmExport = async () => {
    if (isExportingPDF || !canConfirmExport) return;

    if (!pdfExportViewModel?.validation?.valid) {
      const firstIssue = pdfExportViewModel?.validation?.issues?.[0] || 'Export validation failed.';
      setExportError(`Cannot export yet: ${firstIssue}`);
      return;
    }

    setExportError('');
    setIsExportModalOpen(false);
    await handleExportPDF();
  };

  // Per-grant export removed - only page-level export available

  // Check for preview mode
  const isPreviewMode = searchParams.get('preview') === 'loading';

  // Loading state logic - ONLY redirect if truly no data after timeout
  const waitingOnSharedFetch = Boolean(sharedTokenParam) && !sharedFetchCompleted && !assessmentSource;
  const shouldShowLoading = isPreviewMode || waitingOnSharedFetch || (!assessmentSource && !loadingTimeout);
  
  if (DEBUG) console.log(`🔍 Loading state check:`, {
    isPreviewMode,
    hasResults: !!results,
    loadingTimeout,
    shouldShowLoading,
    isClient
  });

  // Avoid an extra loading screen on first paint when navigation already carried assessment payload (SPA).
  if (!isClient && !assessmentSource) {
    return (
      <div className="bg-white px-6 py-5 eligibility-results-ui">
        <SEO
          title="Assessment results | OPTRA"
          description="Assessment results are generated per assessment and are not intended to appear in search."
          url={seoUrl}
          robots="noindex, follow"
        />
        <p className="mx-auto max-w-6xl text-sm" style={{ color: '#4B5563' }} role="status">Fetching assessment results...</p>
      </div>
    );
  }

  if (shouldShowLoading) {
    // Keep the results route mounted while the backend report is retrieved.
    const DEBUG = import.meta.env.VITE_DEBUG_LOGS === 'true';
    if (DEBUG) console.log('⚠️ No data yet, showing loading skeleton...');

    return (
      <div className="bg-white px-6 py-5 eligibility-results-ui">
        <SEO
          title="Assessment results | OPTRA"
          description="Assessment results are generated per assessment and are not intended to appear in search."
          url={seoUrl}
          robots="noindex, follow"
        />
        <div className="max-w-6xl mx-auto">
          <p className="text-sm" style={{ color: '#4B5563' }} role="status" aria-live="polite">
            Fetching assessment results. Keep this tab open while we prepare the report.
          </p>
        </div>
      </div>
    );
  }

  // Single consolidated safety check with fallback data
  if (!results) {
    const DEBUG = import.meta.env.VITE_DEBUG_LOGS === 'true';
    console.error('❌ No results data available');
    if (DEBUG) {
      if (DEBUG) console.log('🔍 Checking if we should redirect to loading page...');
      if (DEBUG) console.log('  - assessmentSource exists?', !!assessmentSource);
      if (DEBUG) console.log('  - formData exists?', !!formData);
    if (DEBUG) console.log('  - location.state.formData exists?', !!locationFormData);
      if (DEBUG) console.log('  - loadingTimeout?', loadingTimeout);
    }
    
    // If we have formData but no results, redirect to the loading page to fetch them.
    // Never silently re-run the one-time assessment from the embedded dashboard view -
    // a refresh there recovers via the stored jobId instead.
    const availableFormData = formData || locationFormData;
    if (!embedded && availableFormData && !loadingTimeout) {
      const DEBUG = import.meta.env.VITE_DEBUG_LOGS === 'true';
      if (DEBUG) console.log('✅ Redirecting to assessment loading flow with form data');
      const recoveryTraceId = `trace_recovery_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      navigate('/dashboard/assessment-progress?pending=true', {
        replace: true,
        state: {
          formData: availableFormData,
          createJobInBackground: true,
          jobId: 'creating',
          traceId: recoveryTraceId,
        },
      });
      return null;
    }
    
    // Show error state with better messaging
    const errorMessage = sharedTokenParam
      ? (sharedFetchError || 'This shared results link is invalid, has expired, or is temporarily unavailable.')
      : loadError
        || (!locationAssessmentData
          ? 'Please complete the eligibility assessment first to view your results.'
          : 'The assessment data could not be processed. Please re-run the assessment.');
    return (
      <div className={`${embedded ? 'py-16' : 'min-h-screen'} flex items-center justify-center bg-white relative eligibility-results-ui`}>
        <SEO
          title="Assessment results | OPTRA"
          description="Assessment results are generated per assessment and are not intended to appear in search."
          url={seoUrl}
          robots="noindex, follow"
        />
        <div className="text-center max-w-md mx-auto p-6 relative z-10">
          <div className="w-16 h-16 bg-white border rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ borderColor: '#BFDBFE' }}>
            <XCircle className="w-8 h-8" style={{ color: '#EF4444' }} aria-hidden />
          </div>
          <h1 className="text-2xl font-bold mb-4" style={{ color: '#60A5FA' }}>No results available</h1>
          <p className="mb-6" style={{ color: '#4B5563' }} role="alert">
            {errorMessage}
          </p>
          <div className="space-y-3">
            {adminView ? (
              <button
                type="button"
                onClick={() => navigate(adminClientPath)}
                className="w-full bg-white border px-4 py-2 rounded-lg font-medium"
                style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
              >
                Back to client
              </button>
            ) : sharedTokenParam ? (
              <>
                <p className="text-sm" style={{ color: '#4B5563' }}>
                  Ask the person who shared this report to send you a new link.
                </p>
                <a
                  href="/sg"
                  className="block w-full bg-white border px-4 py-2 rounded-lg font-medium text-center"
                  style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
                >
                  Get your own assessment
                </a>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => navigate(assessmentEntryPath)}
                  className="w-full bg-white border px-4 py-2 rounded-lg font-medium"
                  style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
                >
                  {loadError || locationAssessmentData ? 'Re-run assessment' : 'Go to eligibility assessment'}
                </button>
                {locationAssessmentData && !loadError && (
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="w-full border px-4 py-2 rounded-lg font-medium"
                    style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
                  >
                    Try again
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Ensure grants array exists with fallback
  const safeGrants = results.grants && Array.isArray(results.grants) ? results.grants : [];
  // Assessment date from the result itself (not "today"), so a shared link opened later is right.
  const assessmentDateLabel = (() => {
    const raw =
      results.generatedAt ||
      assessmentSource?.generated_at_sgt ||
      assessmentSource?.created_at ||
      assessmentSource?.timestamp ||
      '';
    const parsed = raw ? new Date(raw) : new Date();
    const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  })();
  const assessmentMethodology = results.assessment_methodology || results.assessmentMethodology || {};
  const legacyMethodologyGlossary = assessmentMethodology.glossary || {};
  // New-contract result-level views (all degrade to empty for older stored results).
  const submissionRows = buildSubmissionRows({ ...(formData || {}), ...(results.inputsUsed || {}) });
  const programmeTransitionsView = (Array.isArray(results.programmeTransitions) ? results.programmeTransitions : []).map((t) => ({
    ...t,
    affectedNames: t.affectedKeys.map((k) => grantDisplayNameForKey(safeGrants, k) || k),
  }));
  const methodologyView = results.methodology || null;
  const glossaryView = Array.isArray(results.glossary) && results.glossary.length > 0
    ? results.glossary
    : normalizeGlossary(legacyMethodologyGlossary);

  // Debug logging after all variables are defined
  if (DEBUG) console.log('🔍 Rendering results page with data:', {
    hasResults: !!results,
    grantsCount: safeGrants.length,
    visibleCount: visibleGrants.length,
    assessmentDataKeys: assessmentSource ? Object.keys(assessmentSource) : 'none'
  });

  if (isLocked && !sharedTokenParam && !adminView) {
    return <TierLockedView requiredTier={requiredTier} signedIn={signedIn} redirectPath={location.pathname} />;
  }

  return (
    <div
      className={`${embedded ? '' : 'min-h-screen'} relative overflow-x-hidden w-full max-w-full eligibility-results-ui`}
      style={{
        background: 'white',
        overscrollBehaviorY: 'auto',
      }}
    >
      {!embedded && (
        <SEO
          title="Assessment results | OPTRA"
          description="Assessment results are generated per assessment and are not intended to appear in search."
          url={seoUrl}
          robots="noindex, follow"
        />
      )}

      {/* Header */}
      <div className={`${embedded ? 'pt-6' : 'pt-20'} pb-8 relative z-[20]`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {adminView ? (
            <AdminViewingBanner targetClient={targetClient} backPath={adminClientPath} />
          ) : null}
          {/* Results Header */}
          <div className="text-center mb-8">
            <div className="flex flex-col items-center justify-center mb-6">
              <h1
                className="text-emphasis eligibility-results-heading text-[2.1rem] sm:text-5xl md:text-6xl lg:text-[3.75rem] leading-[1.08]"
                style={{
                  fontFamily: '"Georgia", serif',
                  letterSpacing: '-0.022em',
                  margin: 0,
                  marginBottom: '0.75rem',
                  fontWeight: 400,
                }}
              >
                Assessment results
              </h1>

              {/*
               * Intentionally no subtext under the header.
               * Disclaimer is shown elsewhere (or can be reintroduced later).
               */}
              {results && (results.disclaimer || results.is_heuristic_score) && (
                <p className="text-xs italic mt-1" style={{ color: '#6B7280' }}>
                  Note: {results.disclaimer || 'Fit scores are internal estimates and not approval guarantees.'}
                </p>
              )}
            </div>
            
            <div className="inline-block w-full max-w-full sm:w-auto">
              <div className="bg-white rounded-2xl p-5 sm:p-8 border shadow-xl" style={{ borderColor: '#BFDBFE' }}>
                <div className="text-center">
                  <h2 className="text-3xl font-bold mb-2" style={{ color: '#60A5FA' }}>
                    {results.inputsUsed?.company_name || results.inputsUsed?.companyName || formData?.companyName || formData?.company_name || 'Company'}
                  </h2>
                  <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mb-3" style={{ color: '#4B5563' }}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" aria-hidden />
                      <span className="text-sm">{results.inputsUsed?.industry || formData?.industry || 'Industry'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4" aria-hidden />
                      <span className="text-sm">
                        {(() => {
                          const revenue = firstInputNumber(
                            results.inputsUsed?.annual_revenue_sgd,
                            results.inputsUsed?.annual_revenue,
                            results.inputsUsed?.annualRevenue,
                            formData?.annual_revenue_sgd,
                            formData?.annual_revenue,
                            formData?.annualRevenue
                          );
                          if (revenue === null) {
                            return 'Not specified';
                          }
                          return `S$${revenue.toLocaleString('en-SG')}`;
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" aria-hidden />
                      <span className="text-sm">
                        {(() => {
                          const count = results.inputsUsed?.employee_count || results.inputsUsed?.employees || formData?.employee_count || formData?.employees || 0;
                          return count > 0 ? `${count.toLocaleString()} employees` : 'Not specified';
                        })()}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-xs" style={{ color: '#4B5563' }}>
                    <div className="flex items-center gap-2">
                      <Hash className="w-3 h-3" aria-hidden />
                      <span className="font-mono">{results.snapshotId}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3" aria-hidden />
                      <span>{assessmentDateLabel}</span>
                    </div>
                  </div>
                  <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row eligibility-results-controls">
                    <button
                      type="button"
                      onClick={handleOpenExportModal}
                      disabled={isExportingPDF || allAnalyzedGrants.length === 0}
                      className="inline-flex items-center justify-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                      style={{ borderColor: '#60A5FA', color: '#60A5FA', backgroundColor: '#FFFFFF' }}
                    >
                      {isExportingPDF ? (
                        <>
                          <Clock className="w-4 h-4" style={{ color: '#60A5FA' }} aria-hidden />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" style={{ color: '#60A5FA' }} aria-hidden />
                          Export PDF
                        </>
                      )}
                    </button>
                    <p className="text-xs m-0" style={{ color: '#6B7280' }}>
                      {displayedResultsCount} reviewed route{displayedResultsCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  {exportError && !isExportModalOpen ? (
                    <p className="mt-3 text-sm font-medium" style={{ color: '#DC2626' }} role="alert">
                      {exportError}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Main Content - Centered Layout - Wider for better card display */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 relative z-[20]">

            {/* Best matches first — the most actionable routes before supporting context */}
            <div className="space-y-6 mb-8" data-testid="best-matches-section">
              {results && safeGrants.length > 0 && (
                <div className="space-y-6">
                  <div className="space-y-3">
                    <h3 className="text-2xl font-bold" style={{ color: '#60A5FA' }}>
                      Best Matches to Consider First ({eligibleGrantSurfaceItems.length})
                    </h3>
                    <p className="text-sm m-0" style={{ color: '#4B5563' }}>
                      Each route is labelled Eligible, Likely, Less likely or Weak fit from its signal match, strategic fit and certainty.{' '}
                      <a href="#understanding-scores" className="underline underline-offset-2" style={{ color: '#60A5FA' }}>
                        How to read these scores
                      </a>
                    </p>
                    <div className="flex flex-wrap items-center gap-2 pt-4 mt-2">
                      <div
                        className="bg-white border px-4 py-1 rounded-full text-xs font-semibold shadow-lg"
                        style={{ borderColor: '#BFDBFE', color: '#4B5563' }}
                      >
                        {bestMatchRangeText}
                      </div>
                      {[
                        { key: 'all', label: `All (${bestMatchGovernmentGrants.length + eligibleIndependentPathways.length})` },
                        { key: 'government', label: `Government Grants (${bestMatchGovernmentGrants.length})` },
                        { key: 'independent', label: `Independent Pathways (${eligibleIndependentPathways.length})` },
                      ].map((tab) => {
                        const isActiveTab = eligibleSurfaceFilter === tab.key;
                        const tabButton = getEligibleFilterButtonClasses(isActiveTab);
                        return (
                          <button
                            key={tab.key}
                            type="button"
                            onClick={() => setEligibleSurfaceFilter(tab.key)}
                            aria-pressed={isActiveTab}
                            className={tabButton.className}
                            style={tabButton.style}
                          >
                            {tab.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {eligibleGrantSurfaceItems.length > 0 ? (
                    <>
                      {renderParentGroupedGrantCards(
                        eligibleGrantSurfaceItems
                          .filter((entry) => entry.type === 'grant')
                          .map((entry) => entry.item),
                        { defaultOpenGroup: (_group, groupIndex) => groupIndex === 0 }
                      )}
                      {eligibleGrantSurfaceItems
                        .filter((entry) => entry.type !== 'grant')
                        .map((entry) => renderFundingOpportunityCard(entry.item, 'Eligible Independent Funding Pathways'))}
                    </>
                  ) : (
                    <div className="bg-white border rounded-3xl p-6 shadow-sm" style={{ borderColor: '#BFDBFE' }}>
                      <p className="text-sm" style={{ color: '#6B7280' }}>
                        No eligible results are currently visible for this filter.
                      </p>
                    </div>
                  )}
                </div>
              )}

            </div>

            {/* Next step for readers outside the dashboard flow */}
            {sharedTokenParam ? (
              <div className="mb-8 rounded-3xl border bg-white p-5 sm:p-8 shadow-xl" style={{ borderColor: '#BFDBFE' }}>
                <p className="text-sm font-semibold" style={{ color: '#60A5FA' }}>Next step</p>
                <h2 className="mt-1 text-xl font-semibold leading-tight" style={{ color: '#60A5FA' }}>
                  Get your own assessment
                </h2>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                  This report was shared with you and reflects another company&apos;s inputs. Run your own assessment to see which grants fit your business.
                </p>
                <a
                  href="/sg"
                  className="mt-5 inline-flex items-center justify-center rounded-lg border px-5 py-2.5 text-sm font-semibold"
                  style={{ borderColor: '#60A5FA', color: '#60A5FA', backgroundColor: '#FFFFFF' }}
                >
                  Start an assessment
                </a>
              </div>
            ) : null}
            {adminView ? <ClientSelectedGrantsReadOnly grants={clientSelectedGrants} targetClient={targetClient} /> : null}

            {/* Grant selection + consultation scheduling (embedded dashboard flow) */}
            {embedded && !adminView && !sharedTokenParam ? (
              <div className="mb-8 rounded-3xl border bg-white p-5 sm:p-8 shadow-xl" style={{ borderColor: '#BFDBFE' }}>
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#60A5FA' }}>
                      Next step
                    </p>
                    <h2 className="mt-1 text-xl font-semibold leading-tight" style={{ color: '#60A5FA' }}>
                      Select the grants you want to pursue
                    </h2>
                    <p className="mt-2 max-w-3xl text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                      Choose from your matched grants below, confirm your selection, then schedule a consultation call with our team.
                    </p>
                  </div>
                  <div className="inline-flex w-fit shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold" style={{ borderColor: '#E5E7EB', color: '#4B5563', backgroundColor: '#FFFFFF' }}>
                    {customSelectionCount} selected
                  </div>
                </div>
                {selectableGrants.length ? (
                  <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
                    {selectableGrants.map((grant) => {
                      const key = resolveGrantKey(grant);
                      const checked = customSelectedGrantIds.has(key);
                      const checkboxId = `grant-select-${String(key || '').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                      const selectionDescription = getGrantPurposeText(grant);
                      const selectionScore = getDisplayScore(grant);
                      return (
                        <label
                          key={key}
                          htmlFor={checkboxId}
                          className={`group relative flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 text-sm transition-colors duration-200 focus-within:border-[#6FA8FA] ${
                            checked ? 'border-[#60A5FA]' : 'border-[#E5E7EB]'
                          }`}
                          style={{
                            backgroundColor: '#FFFFFF',
                            color: '#1F2937',
                            boxShadow: checked ? '0 0 0 1px rgba(96,165,250,0.16)' : 'none',
                          }}
                        >
                          <input
                            id={checkboxId}
                            type="checkbox"
                            checked={checked}
                            onChange={() => _toggleCustomGrantSelection(key)}
                            className="sr-only"
                          />
                          <span
                            aria-hidden="true"
                            className="mt-0.5 flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded border-2 transition-colors duration-200"
                            style={{
                              borderColor: '#60A5FA',
                              backgroundColor: checked ? '#60A5FA' : '#FFFFFF',
                            }}
                          >
                            {checked ? <Check className="h-3 w-3 text-white" strokeWidth={3} /> : null}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold leading-snug" style={{ color: '#60A5FA' }}>
                              {grant?.name || grant?.code}
                            </span>
                            {selectionDescription ? (
                              <span className="mt-1 block text-xs leading-relaxed" style={{ color: '#6B7280' }}>
                                {selectionDescription}
                              </span>
                            ) : null}
                          </span>
                          {Number.isFinite(Number(selectionScore)) ? (
                            <span className="shrink-0 text-xs font-semibold" style={{ color: '#60A5FA' }}>
                              {Math.round(Number(selectionScore))}%
                            </span>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-5 text-sm" style={{ color: '#6B7280' }}>
                    No matched grants are available to select from this assessment.
                  </p>
                )}
                <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center">
                  <button
                    type="button"
                    onClick={handleConfirmSelectedGrants}
                    disabled={grantSelectionSaving || (!grantSelectionSaved && customSelectionCount === 0)}
                    className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    style={{ backgroundColor: '#60A5FA' }}
                  >
                    {grantSelectionSaving ? 'Saving...' : grantSelectionSaved && customSelectionCount === 0 ? 'Clear selected grants' : grantSelectionSaved ? 'Update selected grants' : 'Confirm selected grants'}
                  </button>
                  {grantSelectionSaved ? (
                    <button
                      type="button"
                      onClick={handleScheduleConsultation}
                      className="rounded-lg border px-5 py-2.5 text-sm font-semibold"
                      style={{ borderColor: '#60A5FA', color: '#60A5FA', backgroundColor: '#FFFFFF' }}
                    >
                      Schedule a consultation call
                    </button>
                  ) : null}
                  {grantSelectionError ? (
                    <p className="text-sm font-medium" style={{ color: '#DC2626' }} role="alert">
                      {grantSelectionError}
                    </p>
                  ) : grantSelectionSaved && !grantSelectionSaving ? (
                    <p className="text-sm" style={{ color: '#16A34A' }}>
                      {customSelectionCount === 0 ? 'Selected grants cleared.' : 'Selection saved. Next step: schedule your consultation call.'}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* Your submission — audit trail after the decision/action sections */}
            {results && submissionRows.length > 0 ? (
              <section
                className="mb-8 rounded-3xl border bg-white p-5 sm:p-8 shadow-xl"
                style={{ borderColor: '#BFDBFE' }}
                aria-labelledby="your-submission-heading"
                data-testid="your-submission-panel"
              >
                <div className="flex items-center gap-3 mb-2">
                  <CheckSquare className="w-5 h-5 shrink-0" style={{ color: '#60A5FA' }} aria-hidden />
                  <h3 id="your-submission-heading" className="text-xl font-bold" style={{ color: '#60A5FA' }}>Your submission</h3>
                </div>
                <p className="text-sm mb-4 leading-relaxed" style={{ color: '#6B7280' }}>
                  These are the inputs the assessment was scored against. If anything here is not what you meant, re-run the assessment — every score below depends on it.
                </p>
                <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                  {submissionRows.map((row) => (
                    <div key={row.key} data-testid={`submission-${row.key}`}>
                      <dt className="text-xs uppercase tracking-wide" style={{ color: '#94A3B8' }}>{row.label}</dt>
                      <dd className="leading-relaxed break-words" style={{ color: '#1F2937' }}>{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}

            {/* Programme transitions (e.g. EDGE) — supporting context after the client has seen the actionable routes */}
            {results && programmeTransitionsView.length > 0 ? (
              <section
                className="mb-8 rounded-3xl border bg-white p-5 sm:p-8 shadow-xl"
                style={{ borderColor: '#BFDBFE' }}
                aria-labelledby="programme-transitions-heading"
                data-testid="programme-transitions-panel"
              >
                <div className="flex items-center gap-3 mb-2">
                  <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: '#60A5FA' }} aria-hidden />
                  <h3 id="programme-transitions-heading" className="text-xl font-bold" style={{ color: '#60A5FA' }}>Programme transitions</h3>
                </div>
                <ul className="space-y-3">
                  {programmeTransitionsView.map((t, idx) => (
                    <li key={`transition-${idx}`} className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                      <span className="font-semibold" style={{ color: '#1F2937' }}>
                        {t.successor}{t.timing ? ` (${t.timing})` : ''}
                      </span>
                      {t.affectedNames.length > 0 ? (
                        <span> — affects {t.affectedNames.join(', ')}</span>
                      ) : null}
                      {t.whatItMeansNow ? <span>. {t.whatItMeansNow}</span> : null}
                      {t.sourceUrl ? (
                        <>
                          {' '}
                          <a
                            href={t.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 font-medium hover:underline"
                            style={{ color: '#60A5FA' }}
                          >
                            <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                            Source
                          </a>
                        </>
                      ) : null}
                      {!t.verified ? (
                        <span className="ml-2 text-xs italic" style={{ color: '#92400E' }}>(unverified — confirm on the official page)</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {/* Assessment coverage — audit context, not another recommendation list */}
            {results && displayedResultsCount > 0 ? (
              <section
                className="mb-8 rounded-3xl border bg-white p-5 sm:p-6 shadow-sm"
                style={{ borderColor: '#BFDBFE' }}
                aria-labelledby="assessment-coverage-heading"
                data-testid="assessment-coverage-panel"
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h3 id="assessment-coverage-heading" className="text-lg font-semibold" style={{ color: '#60A5FA' }}>
                      Assessment coverage
                    </h3>
                    <p className="mt-1 max-w-3xl text-sm leading-relaxed" style={{ color: '#6B7280' }}>
                      OPTRA reviewed {displayedResultsCount} surfaced route{displayedResultsCount === 1 ? '' : 's'} across this assessment. The best matches above are the strongest routes to consider first; the sections below explain weaker, previously received, or prerequisite-dependent options.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:justify-end">
                    <span
                      className="rounded-full border px-3 py-1.5 text-xs font-semibold"
                      style={{ borderColor: '#BFDBFE', backgroundColor: 'rgba(239, 246, 255, 0.95)', color: '#4B5563' }}
                    >
                      {displayedGovernmentCount} government
                    </span>
                    <span
                      className="rounded-full border px-3 py-1.5 text-xs font-semibold"
                      style={{ borderColor: '#BFDBFE', backgroundColor: 'rgba(239, 246, 255, 0.95)', color: '#4B5563' }}
                    >
                      {displayedNonGovernmentCount} independent
                    </span>
                    {approvedGrants.length > 0 ? (
                      <span
                        className="rounded-full border px-3 py-1.5 text-xs font-semibold"
                        style={{ borderColor: '#6EE7B7', backgroundColor: '#D1FAE5', color: '#047857' }}
                      >
                        {approvedGrants.length} previously approved
                      </span>
                    ) : null}
                    {hiddenLegacyCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowLegacySchemes(true)}
                        className="text-xs px-3 py-1.5 rounded-full border font-medium transition hover:bg-[#60A5FA]/10"
                        style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
                      >
                        Show legacy schemes ({hiddenLegacyCount})
                      </button>
                    )}
                    {showLegacySchemes && legacySchemeCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowLegacySchemes(false)}
                        className="text-xs px-3 py-1.5 rounded-full border font-medium transition hover:bg-gray-100"
                        style={{ borderColor: '#D1D5DB', color: '#4B5563' }}
                      >
                        Hide legacy schemes
                      </button>
                    )}
                  </div>
                </div>
              </section>
            ) : null}

            {displayFundingBucketSections.length > 0 && (
              <div className="mb-8 space-y-4">
                {displayFundingBucketSections.map((section) => {
                  const Icon = section.accent.icon;
                  return (
                    <Card key={section.key} className="bg-white border shadow-sm rounded-3xl" style={{ borderColor: '#E5E7EB' }}>
                      <div className="p-6 md:p-7">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className="w-5 h-5" style={{ color: section.accent.iconColor }} />
                              <h3 className="text-lg font-semibold" style={{ color: '#60A5FA' }}>{section.title}</h3>
                            </div>
                            <p className="text-sm" style={{ color: '#6B7280' }}>{section.subtitle}</p>
                          </div>
                          <Badge
                            className="self-start rounded-full px-3 py-1 text-xs font-semibold"
                            style={{
                              backgroundColor: 'rgba(239, 246, 255, 0.95)',
                              color: '#60A5FA',
                              border: '1px solid rgba(96, 165, 250, 0.45)',
                            }}
                          >
                            {section.items.length} surfaced
                          </Badge>
                        </div>

                        <div className="grid gap-4 mt-5">
                          {section.items.map((item) => renderFundingOpportunityCard(item, section.title))}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            

            {/* Grant Cards - Organized by Eligibility Categories */}
            <div className="space-y-6">
              {/* Less likely (45–59% · Partial / Low) - Collapsible */}
              {results && safeGrants.length > 0 && lessLikelyGrants.length > 0 && (
                <div className="space-y-4">
                  <div
                    className="bg-white border rounded-3xl overflow-hidden shadow-xl"
                    style={{ borderColor: '#BFDBFE' }}
                  >
                    <button
                      type="button"
                      onClick={() => setIsLessLikelyExpanded((prev) => !prev)}
                      aria-expanded={isLessLikelyExpanded}
                      className="w-full flex items-start sm:items-center justify-between gap-3 p-6 hover:bg-gray-50 transition-all duration-200 focus:outline-none"
                    >
                      <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1 pr-3">
                        <AlertTriangle className="w-6 h-6 shrink-0" style={{ color: '#60A5FA' }} aria-hidden />
                        <h3 className="text-2xl font-bold text-left leading-tight break-words" style={{ color: '#60A5FA' }}>
                          Less likely grants ({lessLikelyGrants.length})
                        </h3>
                        <div
                          className="bg-white border px-3 py-1 rounded-full text-xs font-semibold shrink-0"
                          style={{ borderColor: '#BFDBFE', color: '#4B5563' }}
                        >
                          {lessLikelyRangeText}
                            </div>
                          </div>
                      <ChevronDown
                        className={`w-6 h-6 shrink-0 transition-transform duration-200 ${isLessLikelyExpanded ? 'rotate-180' : ''}`}
                        style={{ color: '#60A5FA' }}
                        aria-hidden
                      />
                    </button>
                    
                    {isLessLikelyExpanded && (
                      <div
                        className="px-6 pb-6 space-y-6 border-t pt-6"
                        style={{ borderColor: '#BFDBFE' }}
                      >
                        {renderParentGroupedGrantCards(lessLikelyGrants)}
                      </div>
                    )}
                      </div>
                        </div>
              )}

              {/* Weak fit (1–44%; not hard ineligible) - Collapsible */}
              {results && safeGrants.length > 0 && weakFitGrants.length > 0 && (
                <div className="space-y-4">
                  <div className="bg-white border rounded-3xl overflow-hidden shadow-xl" style={{ borderColor: '#BFDBFE' }}>
                    <button
                      type="button"
                      onClick={() => setIsWeakFitExpanded((prev) => !prev)}
                      aria-expanded={isWeakFitExpanded}
                      className="w-full flex items-start sm:items-center justify-between gap-3 p-6 hover:bg-gray-50 transition-all duration-200 focus:outline-none"
                    >
                      <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1 pr-3">
                        <AlertTriangle className="w-6 h-6 shrink-0" style={{ color: '#9CA3AF' }} aria-hidden />
                        <h3 className="text-2xl font-bold text-left leading-tight break-words" style={{ color: '#60A5FA' }}>Weak fit grants ({weakFitGrants.length})</h3>
                        <div className="bg-white border px-3 py-1 rounded-full text-xs font-semibold shrink-0" style={{ borderColor: '#BFDBFE', color: '#4B5563' }}>
                          {weakFitRangeText}
                        </div>
                      </div>
                      <ChevronDown className={`w-6 h-6 shrink-0 transition-transform duration-200 ${isWeakFitExpanded ? 'rotate-180' : ''}`} style={{ color: '#60A5FA' }} aria-hidden />
                    </button>
                    {isWeakFitExpanded && (
                      <div className="px-6 pb-6 space-y-6 border-t pt-6" style={{ borderColor: '#BFDBFE' }}>
                        {renderParentGroupedGrantCards(weakFitGrants)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Blocked by a structural prerequisite - own section, never mixed with Eligible/Likely */}
              {results && safeGrants.length > 0 && blockedGrants.length > 0 && (
                <div className="space-y-4" data-testid="blocked-grants-section">
                  <div className="bg-white border rounded-3xl overflow-hidden shadow-xl" style={{ borderColor: '#FCA5A5' }}>
                    <button
                      type="button"
                      onClick={() => setIsBlockedExpanded((prev) => !prev)}
                      aria-expanded={isBlockedExpanded}
                      className="w-full flex items-start sm:items-center justify-between gap-3 p-6 hover:bg-gray-50 transition-all duration-200 focus:outline-none"
                    >
                      <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1 pr-3">
                        <ShieldAlert className="w-6 h-6 shrink-0" style={{ color: '#DC2626' }} aria-hidden />
                        <h3 className="text-2xl font-bold text-left leading-tight break-words" style={{ color: '#60A5FA' }}>
                          Prerequisite needed or not currently open ({blockedGrants.length})
                        </h3>
                        <div className="bg-white border px-3 py-1 rounded-full text-xs font-semibold shrink-0" style={{ borderColor: '#FCA5A5', color: '#991B1B' }}>
                          {blockedRangeText}
                        </div>
                      </div>
                      <ChevronDown className={`w-6 h-6 shrink-0 transition-transform duration-200 ${isBlockedExpanded ? 'rotate-180' : ''}`} style={{ color: '#60A5FA' }} aria-hidden />
                    </button>
                    {isBlockedExpanded && (
                      <div className="px-6 pb-6 space-y-6 border-t pt-6" style={{ borderColor: '#FCA5A5' }}>
                        <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                          These schemes may score well on paper, but a mandatory prerequisite is not met on the inputs you submitted. Each card explains what is missing and how to resolve it.
                        </p>
                        {renderParentGroupedGrantCards(blockedGrants)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Hard ineligible - Collapsible */}
              {results && safeGrants.length > 0 && ineligibleGrants.length > 0 && (
                <div className="space-y-4">
                  <div className="bg-white border rounded-3xl overflow-hidden shadow-xl" style={{ borderColor: '#BFDBFE' }}>
                    <button
                      type="button"
                      onClick={() => setIsIneligibleExpanded((prev) => !prev)}
                      aria-expanded={isIneligibleExpanded}
                      className="w-full flex items-start sm:items-center justify-between gap-3 p-6 hover:bg-gray-50 transition-all duration-200 focus:outline-none"
                    >
                      <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1 pr-3">
                        <XCircle className="w-6 h-6 shrink-0" style={{ color: '#EF4444' }} aria-hidden />
                        <h3 className="text-2xl font-bold text-left leading-tight break-words" style={{ color: '#60A5FA' }}>Ineligible ({ineligibleGrants.length})</h3>
                        <div className="bg-white border px-3 py-1 rounded-full text-xs font-semibold shrink-0" style={{ borderColor: '#BFDBFE', color: '#4B5563' }}>
                          {ineligibleRangeText}
                        </div>
                      </div>
                      <ChevronDown className={`w-6 h-6 shrink-0 transition-transform duration-200 ${isIneligibleExpanded ? 'rotate-180' : ''}`} style={{ color: '#60A5FA' }} aria-hidden />
                    </button>
                    {isIneligibleExpanded && (
                      <div className="px-6 pb-6 space-y-6 border-t pt-6" style={{ borderColor: '#BFDBFE' }}>
                        {renderParentGroupedGrantCards(ineligibleGrants)}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Empty State - No Grants (the single client-facing empty state) */}
              {results && safeGrants.length === 0 && (
                <div className="bg-white border rounded-3xl p-12 shadow-2xl text-center" style={{ borderColor: '#BFDBFE' }} role="status">
                  <AlertTriangle className="w-16 h-16 mx-auto mb-4" style={{ color: '#60A5FA' }} aria-hidden />
                  <h3 className="text-2xl font-bold mb-3" style={{ color: '#60A5FA' }}>No grants were returned for this assessment</h3>
                  <p className="mb-6 text-lg" style={{ color: '#4B5563' }}>
                    {sharedTokenParam
                      ? 'This shared report does not contain any grant results. Ask the sender to re-run their assessment and share a new link.'
                      : 'The assessment completed without producing any grant matches. Re-run it to generate a fresh set of results.'}
                  </p>
                  {!sharedTokenParam && !adminView ? (
                    <button
                      type="button"
                      onClick={() => navigate(assessmentEntryPath)}
                      className="bg-white border-2 px-6 py-3 rounded-full font-semibold transition-all duration-200"
                      style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
                    >
                      Re-run assessment
                    </button>
                  ) : null}
                              </div>
                            )}
                            </div>

            {/* Already Approved Grants Section - Collapsible */}
            {results && approvedGrants && approvedGrants.length > 0 && (
              <div className="mt-8">
                  <div className="bg-white border rounded-3xl overflow-hidden shadow-xl" style={{ borderColor: '#BFDBFE' }}>
                    <button
                      type="button"
                      onClick={() => setIsApprovedExpanded((prev) => !prev)}
                      aria-expanded={isApprovedExpanded}
                      aria-controls="approved-grants-panel"
                      className="w-full flex items-start sm:items-center justify-between gap-3 p-6 hover:bg-gray-50 transition-all duration-200 focus:outline-none"
                    >
                      <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1 pr-3">
                        <CheckCircle2 className="w-6 h-6 shrink-0" style={{ color: '#047857' }} aria-hidden />
                        <div className="text-left min-w-0">
                          <h3 className="text-xl font-bold" style={{ color: '#60A5FA' }}>
                            Already Approved / Received ({approvedGrants.length})
                          </h3>
                          <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
                            Grants you've indicated as previously received
                          </p>
                        </div>
                      </div>
                      <ChevronDown 
                        className={`w-6 h-6 shrink-0 transition-transform duration-200 ${isApprovedExpanded ? 'rotate-180' : ''}`} 
                        style={{ color: '#60A5FA' }} 
                        aria-hidden
                      />
                    </button>
                    
                    {isApprovedExpanded && (
                      <div id="approved-grants-panel" className="px-6 pb-6 space-y-6 border-t pt-6" style={{ borderColor: '#BFDBFE' }}>
                        {approvedGrants.map((grant, index) => {
                          try {
                            // Safe fallback for grant data
                            const grantKey = grant?.code || grant?.grantId || grant?.id || grant?.acronym || `approved-${index}`;
                            const grantName = grant?.name || grant?.grantFullName || grant?.code || 'Unknown Grant';
                            const grantAcronym = grant?.acronym || grant?.code || '';
                            
                            // Handle description - extract string from object if needed
                            let grantDescription = 'This grant has been marked as already received.';
                            if (grant?.summaryUserBenefits) {
                              if (typeof grant.summaryUserBenefits === 'string') {
                                grantDescription = grant.summaryUserBenefits;
                              } else if (typeof grant.summaryUserBenefits === 'object' && grant.summaryUserBenefits.headline) {
                                grantDescription = grant.summaryUserBenefits.headline;
                              }
                            } else if (grant?.detailedAnalysis) {
                              if (typeof grant.detailedAnalysis === 'string') {
                                grantDescription = grant.detailedAnalysis;
                              } else if (typeof grant.detailedAnalysis === 'object' && grant.detailedAnalysis.headline) {
                                grantDescription = grant.detailedAnalysis.headline;
                              }
                            } else if (grant?.detailed_info) {
                              if (typeof grant.detailed_info === 'string') {
                                grantDescription = grant.detailed_info;
                              }
                            } else if (grant?.summary) {
                              if (typeof grant.summary === 'string') {
                                grantDescription = grant.summary;
                              }
                            }
                            
                            const officialLink = grant?.official_link || grant?.link || null;
                            
                            return (
                              <div key={grantKey} className="bg-white border rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-200" style={{ borderColor: '#BFDBFE' }}>
                                {/* Grant Header */}
                                <div className="flex items-start justify-between mb-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                                      <h4 className="text-xl font-bold" style={{ color: '#60A5FA' }}>
                                        {grantName}
                                      </h4>
                                      <Badge 
                                        className="px-3 py-1 text-xs font-semibold"
                                        style={{ backgroundColor: '#D1FAE5', color: '#065F46', border: '1px solid #047857' }}
                                      >
                                        Already Approved / Received
                                      </Badge>
                                    </div>
                                    {grantAcronym && grantAcronym !== grantName && (
                                      <p className="text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                                        {grantAcronym}
                                      </p>
                                    )}
                                  </div>
                                  {officialLink && officialLink !== '#' && (
                                    <span
                                      className="bg-white border px-2.5 py-1.5 rounded-xl text-xs font-medium flex-shrink-0"
                                      style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
                                      title="Official grant website reference"
                                    >
                                      Official website reference
                                    </span>
                                  )}
                                </div>

                                {/* Description */}
                                <div className="mb-0">
                                  <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                                    {grantDescription}
                                  </p>
                                </div>
                              </div>
                            );
                          } catch (error) {
                            console.error('❌ Error rendering approved grant:', grant, error);
                            return (
                              <div key={`error-${index}`} className="bg-red-50 border border-red-200 rounded-2xl p-6">
                                <p className="text-sm text-red-600">Error loading grant details</p>
                              </div>
                            );
                          }
                        })}
                      </div>
                    )}
                  </div>
              </div>
            )}

            {results && safeGrants.length > 0 && (
              <details id="understanding-scores" className="group bg-white border rounded-3xl p-6 sm:p-8 shadow-xl mt-8" style={{ borderColor: '#BFDBFE' }}>
                <summary className="list-none cursor-pointer flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Target className="w-5 h-5 shrink-0" style={{ color: '#60A5FA' }} aria-hidden />
                    <div className="min-w-0">
                      <h3 className="text-xl font-bold" style={{ color: '#60A5FA' }}>Understanding Your Scores</h3>
                      <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
                        How projected score, strategic fit, and certainty should be read together.
                      </p>
                    </div>
                  </div>
                  <ChevronDown className="w-5 h-5 shrink-0 text-[#60A5FA] transition-transform duration-200 group-open:rotate-180" aria-hidden />
                </summary>
                <div className="mt-6 space-y-3 text-sm leading-relaxed" style={{ color: '#4B5563' }} data-testid="methodology-panel">
                  {methodologyView ? (
                    <>
                      {methodologyView.signalMatch.description ? (
                        <p>
                          <strong>Signal Match:</strong> {methodologyView.signalMatch.description}
                          {methodologyView.signalMatch.weights.length > 0 ? (
                            <>
                              {' '}
                              <span>Weights: {methodologyView.signalMatch.weights.map((w) => `${w.label} ${w.pct}%`).join(', ')}.</span>
                            </>
                          ) : null}
                        </p>
                      ) : null}
                      {methodologyView.strategicFit.description ? (
                        <p>
                          <strong>Strategic Fit:</strong> {methodologyView.strategicFit.description}
                        </p>
                      ) : null}
                      {methodologyView.approvalLikelihood.description ? (
                        <p>
                          <strong>Approval likelihood:</strong> {methodologyView.approvalLikelihood.description}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>Projected Success Score:</strong> OPTRA’s overall fit score (<strong>0-100%</strong>) after mandatory eligibility requirements are checked. It combines company eligibility, project fit, budget/timeline fit, evidence quality, and expected outcomes. It is <strong>indicative</strong>, not an <strong>official agency decision</strong>.
                      </p>
                      <p>
                        <strong>Signal Match:</strong> A weighted match between the company and project you submitted and each scheme’s published criteria. A strong score usually means the company profile, activity type, budget, and intended outcomes all point toward the scheme. A partial or weak score usually means the route is missing a key fit signal, such as a named overseas market for MRA, a pre-approved solution route for PSG, or evidence that an R&D project involves genuine technical uncertainty.
                      </p>
                      <p>
                        <strong>Strategic Fit:</strong> Scored separately from broad signal match. It asks whether this is the right grant for this project, not just whether the company could generally qualify. For example, a technology company may satisfy basic SME criteria, but a tourism, hotel, cruise, or Germany-specific collaboration scheme should only rank well when the submitted project clearly matches that scheme purpose.
                      </p>
                      <p>
                        <strong>Certainty Percentage:</strong> How reliable the assessment is based on completeness and specificity. Certainty improves when the form includes concrete project scope, implementation stage, cost breakdown, vendor or partner status, target market, compliance position, and supporting documents. Lower certainty means the recommendation may change once those facts are confirmed.
                      </p>
                    </>
                  )}
                  <p>
                    <strong>Fit bands</strong> (used for both Signal Match and Strategic Fit): <strong>Strong / High</strong> {FIT_BAND_STRONG_MIN}%+ · <strong>Promising / Medium</strong> {FIT_BAND_PROMISING_MIN}–{FIT_BAND_STRONG_MIN - 1}% · <strong>Partial / Low</strong> {FIT_BAND_PARTIAL_MIN}–{FIT_BAND_PROMISING_MIN - 1}% · <strong>Weak</strong> below {FIT_BAND_PARTIAL_MIN}%. Any grant with an unmet structural requirement is capped at Low regardless of score.
                  </p>
                  <p className="text-xs italic" style={{ color: '#4B5563' }}>
                    <strong>Note:</strong> A high score with low certainty means the route may fit, but the evidence is incomplete. A lower score with high certainty usually means the mismatch is clearer. Prerequisite needed means a structural requirement is not met yet; Ineligible means a mandatory requirement was not met, not simply that the score was low.
                  </p>
                </div>
                <div className="pt-5 mt-5 border-t space-y-2" style={{ borderColor: '#E5E7EB' }}>
                  <h4 className="text-base font-semibold mb-2" style={{ color: '#4B5563' }}>Scoring Thresholds</h4>
                  <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                    Groups follow the fit bands: <strong>{TIER_ELIGIBLE_MIN}%+</strong> Eligible (Strong / High), <strong>{TIER_LIKELY_MIN}–{TIER_ELIGIBLE_MIN - 1}%</strong> Likely (Promising / Medium), <strong>{TIER_LESS_LIKELY_MIN}–{TIER_LIKELY_MIN - 1}%</strong> Less likely (Partial / Low), <strong>{TIER_WEAK_MIN}–{TIER_LESS_LIKELY_MIN - 1}%</strong> Weak fit. Routes with unmet prerequisites sit in their own section until the requirement is resolved; <strong>Ineligible</strong> is reserved for mandatory requirements such as ownership, sector scope, vendor route, geography, availability, project timing, or programme-specific structural conditions.
                  </p>
                </div>
                {glossaryView.length > 0 ? (
                  <div className="pt-5 mt-5 border-t" style={{ borderColor: '#E5E7EB' }} data-testid="glossary-panel">
                    <h4 className="text-base font-semibold mb-3" style={{ color: '#4B5563' }}>Glossary</h4>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      {glossaryView.map((entry) => (
                        <div key={entry.term}>
                          <dt className="font-semibold" style={{ color: '#60A5FA' }}>{entry.term}</dt>
                          <dd className="leading-relaxed" style={{ color: '#4B5563' }}>{entry.definition}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ) : null}
              </details>
            )}

            {/* Feedback Section - Stores to Supabase for continuous improvement (not for the admin's read-only view) */}
            {!adminView ? (
            <div className="bg-white border rounded-3xl p-8 shadow-2xl mb-8 mt-8" style={{ borderColor: '#60A5FA' }}>
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#60A5FA' }}>Help us improve</h3>
              <p className="text-base mb-6 leading-relaxed" style={{ color: '#4B5563' }}>
                Your feedback helps us strengthen assessment quality, recommendation clarity, and submission-readiness guidance.
              </p>
                
                {/* Quick thumbs feedback - Stores to Supabase feedback table */}
                <div className="mb-6">
                  <ThumbsFeedback 
                    reviewId={results?.snapshotId || `eligibility_${feedbackMountTs}`}
                    pageName="eligibility-results"
                    grantType={safeGrants[0]?.code || 'multiple-grants'}
                    sessionId={`session_${feedbackMountTs}`}
                    contextTag="eligibility-assessment"
                    tokenHash={`eligibility_${results?.generatedAt || feedbackMountTs}`}
                    upLabel="Results were useful"
                    downLabel="Results were not useful"
                    query={`Eligibility assessment for ${formData?.companyName || formData?.company_name || 'Company'} in ${formData?.industry || 'industry'}`}
                    output={`Overall score: ${results?.overall_score || results?.overallScore || 'N/A'}, ${sortedGrants.length} grants assessed`}
                    industry={formData?.industry}
                    onFeedbackSubmitted={(success) => {
                      if (success) {
                        if (DEBUG) console.log('✅ Feedback stored to Supabase for learning');
                      }
                    }}
                  />
                </div>

              {/* Removed detailed feedback section - keep it simple */}
                  </div>
            ) : null}
      </div>

      {isExportModalOpen && isClient && createPortal((
        <div
          className="fixed inset-0 z-[120] p-4 bg-black/40"
          onMouseDown={(event) => {
            // Backdrop click closes; clicks inside the dialog do not bubble here as the target.
            if (event.target === event.currentTarget) handleCloseExportModal();
          }}
        >
          <div
            ref={exportDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={exportDialogTitleId}
            tabIndex={-1}
            className="w-full max-w-3xl bg-white border rounded-3xl shadow-2xl p-6 md:p-8 focus:outline-none"
            style={{
              borderColor: '#BFDBFE',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h3 id={exportDialogTitleId} className="text-2xl font-bold" style={{ color: '#60A5FA' }}>Choose export type</h3>
                <p id={`${exportDialogTitleId}-desc`} className="text-sm mt-1" style={{ color: '#4B5563' }}>
                  Select the PDF format before exporting. Government grants remain primary, and independent pathways are included when they appear in the current results.
                </p>
              </div>
            </div>

            <div
              role="radiogroup"
              aria-labelledby={exportDialogTitleId}
              className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5"
              onKeyDown={(event) => {
                const order = ['compact_advisory', 'standard_advisory', 'full_all_grants'];
                const index = order.indexOf(pdfReportMode);
                let next = null;
                if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = order[(index + 1) % order.length];
                if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = order[(index - 1 + order.length) % order.length];
                if (!next) return;
                event.preventDefault();
                setPdfReportMode(next);
                event.currentTarget.querySelector(`[data-mode="${next}"]`)?.focus();
              }}
            >
              {[
                { id: 'compact_advisory', title: 'Top 5 grants report', desc: 'Your top 5 prioritised grants with concise advisory detail.' },
                { id: 'standard_advisory', title: 'Top 10 grants report', desc: 'Your top 10 reviewed grants with concise profiles and backup context.' },
                { id: 'full_all_grants', title: 'All grants report', desc: 'Every reviewed grant with expanded backup and deprioritised guidance.' },
              ].map((mode) => {
                const selected = pdfReportMode === mode.id;
                return (
                  <button
                    key={mode.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    tabIndex={selected ? 0 : -1}
                    data-mode={mode.id}
                    onClick={() => setPdfReportMode(mode.id)}
                    className="text-left px-5 pt-3 pb-5 border rounded-2xl transition flex flex-col items-start gap-2 min-h-[8rem]"
                    style={{
                      borderColor: selected ? '#60A5FA' : '#BFDBFE',
                      backgroundColor: selected ? '#EEF4FF' : '#FFFFFF',
                    }}
                  >
                    <span className="flex items-center gap-2 w-full text-sm font-semibold leading-5" style={{ color: '#1F2937' }}>
                      {selected ? <Check className="w-4 h-4 shrink-0" style={{ color: '#60A5FA' }} aria-hidden /> : null}
                      {mode.title}
                    </span>
                    <span className="text-xs leading-5 w-full" style={{ color: '#4B5563' }}>
                      {mode.desc}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mb-5 text-xs md:text-sm" style={{ color: '#4B5563' }}>
              <p className="leading-relaxed m-0">
                <span className="font-medium">Grants included:</span> {modalGrantsIncludedCount}
                {' · '}
                <span className="font-medium">Estimated pages:</span> {modalEstimatedPages}
              </p>
            </div>

            {exportError ? (
              <p className="mb-4 text-sm font-medium" style={{ color: '#DC2626' }} role="alert">
                {exportError}
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseExportModal}
                disabled={isExportingPDF}
                className="px-4 py-2 rounded-xl border font-medium"
                style={{ borderColor: '#D1D5DB', color: '#4B5563', backgroundColor: '#FFFFFF' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmExport}
                disabled={isExportingPDF || !canConfirmExport}
                className="px-5 py-2 rounded-xl font-semibold disabled:opacity-50"
                style={{ backgroundColor: '#60A5FA', color: 'white' }}
              >
                {isExportingPDF ? 'Exporting...' : 'Export PDF'}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
      
    </div>
  );
}
